import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import {
  getAssessmentV2Label,
  getAssessmentV2ResultLabel,
  isAssessmentV2ReportKey,
} from '../registry/assessmentRegistry';
import {
  INITIAL_ASSESSMENT_FIELD_LABELS,
  INITIAL_ASSESSMENT_MOMENT_LABELS,
  PHYSICAL_EXAMINATION_STATUS_LABELS,
  MEASURABLE_GOAL_STATUS_LABELS,
  normalizeInitialAssessmentPayload,
} from '../initial-assessment/initialAssessmentDomain';
import {
  buildAssessmentLongitudinalSeries,
  formatLongitudinalNumber,
  type AssessmentLongitudinalSeries,
} from '../reports/assessmentLongitudinalDomain';
import { ANAMNESIS_SECTION_DEFINITIONS } from '../../../utils/clinicalDocuments';
import {
  getAssessmentReportPeriod,
  normalizeAssessmentReportPayload,
  validateAssessmentReportPayload,
  type AssessmentReportHistoricalResultSnapshotV2,
  type AssessmentReportPayloadV2,
  type AssessmentReportSourceSnapshotV2,
} from '../reports/assessmentReportDomain';
import {
  getAssessmentReportPresentation,
  getAssessmentReportPurposeText,
} from '../reports/assessmentReportPresentation';
import {
  ASSESSMENT_REPORT_TEST_KEY,
  getAssessmentReportAudienceLabel,
  getAssessmentReportFindingStatusLabel,
  getAssessmentReportModeLabel,
  formatIcfQualifiedCode,
  getCbdfChapterLabel,
  getIcfDomainByCode,
  getIcfQualifierContextLabel,
  getIcfQualifierLabel,
} from '../reports/assessmentReportCatalogs';
import { AssessmentTempChartStorageService, type TemporaryChartStageMap } from './AssessmentTempChartStorageService';
import { AssessmentResultsV2Service } from './AssessmentResultsV2Service';
import { InitialAssessmentIntegrityService } from './InitialAssessmentIntegrityService';
import { getACTInterpretationText, getACTSeverityLabel } from '../renderers/ACTResultRenderer';
import { getAQLQInterpretationText, getAQLQSeverityLabel } from '../renderers/AQLQResultRenderer';
import {
  CAT_ITEM_DEFINITIONS,
  getCATInterpretationText,
  getCATItemImpactLabel,
  getCATSeverityLabel,
  getCATSeverityRangeLabel,
} from '../renderers/CATResultRenderer';
import { getHADSClassificationLabel, getHADSInterpretationText, getHADSOverallSeverityLabel } from '../renderers/HADSResultRenderer';
import { getHandgripInterpretationText, getHandgripSeverityLabel } from '../renderers/HandgripResultRenderer';
import { getOneRMInterpretationText } from '../renderers/OneRMResultRenderer';
import { getLCADLInterpretationText, getLCADLSeverityLabel } from '../renderers/LCADLResultRenderer';
import { getMLHFQInterpretationText, getMLHFQSeverityLabel } from '../renderers/MLHFQResultRenderer';
import { getPFSDQInterpretationText, getPFSDQSeverityLabel } from '../renderers/PFSDQMResultRenderer';
import { getPSQIInterpretationText, getPSQISeverityLabel } from '../renderers/PSQIResultRenderer';
import { describeSaqScore, getSAQInterpretationText, getSAQOverallScore, getSAQSeverityLabel } from '../renderers/SAQResultRenderer';
import { getSGRQInterpretationText, getSGRQSeverityLabel } from '../renderers/SGRQResultRenderer';
import { getMMRCInterpretationText, getMMRCSeverityLabel, getMMRCSeverityRangeLabel } from '../renderers/MMRCResultRenderer';
import {
  formatHandgripPercentile,
  getHandgripChartAxisConfig,
  getHandgripChartData,
  normalizeHandgripPayload,
  summarizeHandgripResult,
  type HandgripChartImages,
  type HandgripResultPayload,
} from '../handgrip/handgripDomain';
import { TOMKINSON_HANDGRIP_2025_PERCENTILES } from '../handgrip/handgripTomkinson2025';
import {
  getPeripheralStrengthInterpretationText,
  normalizePeripheralMuscleStrengthPayload,
  summarizePeripheralMuscleStrengthResult,
  type PeripheralMuscleStrengthResultPayload,
} from '../peripheral-muscle/peripheralMuscleDomain';
import { ONE_RM_STUDY_REFERENCES, formatOneRMKg, normalizeOneRMPayload, summarizeOneRMPayload, type OneRMResultPayload } from '../one-rm/oneRmDomain';
import {
  buildEnduranceInterpretation,
  getPimaxPositionSeries,
  getPimaxPositionLabel,
  pimaxSeriesAreComparable,
  getRespiratoryEnduranceProtocolLines,
  summarizePimaxPayload,
  summarizePemaxPayload,
  getPemaxPositionSeries,
  pemaxSeriesAreComparable,
  summarizeRespiratoryEndurancePayload,
  summarizeSIndexPayload,
  type PemaxResultPayload,
  type PimaxResultPayload,
  type RespiratoryEnduranceResultPayload,
  type SIndexResultPayload,
} from '../respiratory-muscle/respiratoryMuscleDomain';
import { buildNeuromuscularReport, type NeuromuscularReportKey } from '../respiratory-muscle/neuromuscularReport';
import type { PeakCoughFlowReferenceScale } from '../peak-cough-flow/peakCoughFlowDomain';
import { buildNeuromuscularHistoryRows, parseNeuromuscularHistoryDate } from '../respiratory-muscle/neuromuscularHistory';
import {
  summarizePeakFlowDiary, peakFlowDiaryHourlyProfile, diaryPeriodLabel, diaryMedicationLabel,
  PEAK_FLOW_DIARY_SYMPTOMS, PEAK_FLOW_DIARY_DANGER_SIGNS, type PeakFlowDiaryExportData,
} from '../peak-flow-diary/peakFlowDiaryDomain';
import { normalizePatientHeightCm } from '../utils/patientMetrics';
import { getAssessmentInterpretationOverride, resolveAssessmentInterpretationText } from '../utils/interpretationOverride';
import {
  GAIT_SPEED_BAR_SCALE_TICKS,
  GAIT_SPEED_REFERENCE_NOTE,
  GAIT_SPEED_STANDARD_PROTOCOL,
  formatGaitSpeed,
  formatGaitSpeedPercent,
  formatGaitSpeedSeconds,
  getGaitSpeedAttemptEntries,
  getGaitSpeedBarProgress,
  getGaitSpeedScoreColor,
  normalizeGaitSpeedPayload,
  summarizeGaitSpeedResult,
  type GaitSpeedResultPayload,
} from '../gait-speed/gaitSpeedDomain';
import {
  TUG_BAR_SCALE_TICKS,
  TUG_REFERENCE_NOTE,
  TUG_STANDARD_PROTOCOL,
  formatTugSeconds,
  getTUGBarProgress,
  getTUGReferenceComparisonNote,
  getTUGScoreColor,
  getTugAttemptEntries,
  normalizeTugPayload,
  summarizeTugResult,
  type TugResultPayload,
} from '../tug/tugDomain';
import {
  PEAK_FLOW_REFERENCE_NOTE,
  formatPeakFlowPercent,
  formatPeakFlowValue,
  getPeakFlowScoreColor,
  normalizePeakFlowPayload,
  summarizePeakFlowPayload,
  type PeakFlowResultPayload,
} from '../peak-flow/peakFlowDomain';
import {
  formatBorgValue,
  formatStsPrimaryValue,
  formatStsReferenceNumber,
  formatStsRepetitionCount,
  formatStsRestInterval,
  getStsTestLabel,
  getStsReferenceUnit,
  normalizeFiveSTSPayload,
  normalizeStsRepetitionPayload,
  summarizeStsResult,
  type StsRepetitionAttemptPayload,
  type StsTestKey,
} from '../sts/stsDomain';
import {
  buildTC6AutoInterpretation,
  computeTC6PrimaryReference,
  getTc6Attempts,
  getTc6BestAttempt,
  getTc6DistanceSeries,
  getTc6VitalsSeries,
  getTC6ReferenceLabel,
  recalculateTc6Payload,
  resolveTC6Interpretation,
  summarizeTc6Result,
  type Tc6AttemptPayload,
  type Tc6ChartImages,
  type Tc6ResultPayload,
  type Tc6Summary,
} from '../tc6/tc6Domain';
import {
  CHESTER_PROTOCOL_METADATA,
  formatBorgValue as formatStepTestBorgValue,
  formatStepStopReasonLabel,
  hasRecordedStepTestText,
  hasSixMSTMinuteReadingData,
  hasStepTestVitalsData,
  normalizeChesterPayload,
  normalizeSixMSTPayload,
  resolveChesterInterpretation,
  resolveSixMSTInterpretation,
  summarizeChesterResult,
  summarizeSixMSTResult,
  type ChesterResultPayload,
  type SixMSTResultPayload,
} from '../step-tests/stepTestsDomain';
import {
  ESWT_PROTOCOL_METADATA,
  ISWT_PROTOCOL_METADATA,
  formatClock,
  formatISWTLevelLabel,
  normalizeESWTPayload,
  normalizeISWTPayload,
  resolveESWTInterpretation,
  resolveISWTInterpretation,
  summarizeESWTResult,
  summarizeISWTResult,
  type ESWTResultPayload,
  type ISWTResultPayload,
} from '../shuttle-walk/shuttleWalkDomain';
import { ACT_QUESTIONS, type ACTResultPayload } from '../tests/ACTTest';
import {
  AQLQ_DOMAIN_LABELS,
  AQLQ_QUESTIONS,
  AQLQ_SOURCE_MISSING_ITEMS,
  getAQLQAnswerLabel,
  type AQLQDomainKey,
  type AQLQResultPayload,
} from '../tests/AQLQTest';
import { getHADSItemScore, HADS_ITEMS } from '../tests/HADSTest';
import { LCADL_OPTIONS, LCADL_SECTIONS, type LCADLResultPayload, type LCADLSectionKey } from '../tests/LCADLTest';
import {
  MLHFQ_EMOTIONAL_1BASED,
  MLHFQ_ITEMS_PT,
  MLHFQ_OPTIONS,
  MLHFQ_PHYSICAL_1BASED,
  type MLHFQResultPayload,
} from '../tests/MLHFQTest';
import { MMRC_ITEMS } from '../tests/MMRCTest';
import { PFSDQ_ACTIVITIES, type PFSDQMResultPayload } from '../tests/PFSDQMTest';
import {
  PSQI_COMPONENT_METADATA,
  PSQI_DAYTIME_PROBLEM_OPTIONS,
  PSQI_DISTURBANCE_ITEMS,
  PSQI_FREQUENCY_OPTIONS,
  PSQI_PARTNER_OBSERVATION_ITEMS,
  PSQI_PARTNER_STATUS_OPTIONS,
  PSQI_SUBJECTIVE_QUALITY_OPTIONS,
  normalizePSQIPayload,
  type PSQIResultPayload,
} from '../tests/PSQITest';
import {
  SAQ_DOMAIN_LABELS,
  SAQ_DOMAIN_SEQUENCE,
  SAQ_LIMITATION_OPTIONS,
  SAQ_PHYSICAL_ITEMS,
  type SAQResultPayload,
  type SaqDomainKey,
} from '../tests/SAQTest';
import {
  SGRQ_DOMAIN_LABELS,
  type SGRQDomainKey,
  type SGRQResultPayload,
} from '../tests/SGRQTest';
import type { AssessmentResultV2, ResolvedClientReportBranding } from '../types';
import {
  buildMedicalRecordAuthorInfo,
  hasNonApplicableProfessionalRegistration,
} from '../../../utils/medicalRecordAuthor';
import {
  buildVerificationUrl,
  createVerificationQrDataUrl,
  generateVerificationToken,
  getAssessmentSignatureMetadata,
  hashBinaryContent,
  type GeneratedDocumentFileVerificationInput,
  type VerificationGenerationChannel,
} from '../../../utils/documentVerification';
import { DocumentVerificationService } from '../../../services/DocumentVerificationService';

interface AssessmentPDFInput {
  result: AssessmentResultV2;
  branding: ResolvedClientReportBranding;
  previousResults?: AssessmentResultV2[];
  tc6ChartImagesOverride?: Tc6ChartImages | null;
  tenantSlug?: string | null;
  tenantName?: string | null;
  generatedByUserId?: string | null;
  generatedByName?: string | null;
  verification?: {
    token: string;
    url: string;
    qrDataUrl: string;
  } | null;
  assessmentReportPackage?: ResolvedAssessmentReportPackage;
}

interface ResolvedAssessmentReportPackage {
  payload: AssessmentReportPayloadV2;
  sourceResults: AssessmentResultV2[];
  attachmentResults: AssessmentResultV2[];
  historyBySourceId: Map<string, AssessmentReportHistoricalResultSnapshotV2[]>;
}

interface MetricCard {
  label: string;
  value: string;
  details?: string | string[];
  accentColor?: RGB;
}

interface Tc6ComparisonSnapshot {
  result: AssessmentResultV2;
  dateLabel: string;
  summary: Tc6Summary;
  bestAttempt: Tc6AttemptPayload | null;
  hrMin: number | null;
  hrMax: number | null;
  spo2Min: number | null;
  spo2Max: number | null;
  stopCount: number;
  stopSeconds: number;
}

type Tc6ComparisonTone = 'positive' | 'negative' | 'neutral';

type Tc6ComparisonRow =
  | { kind: 'section'; label: string }
  | {
      kind: 'metric';
      label: string;
      previous: string;
      current: string;
      evolution: string;
      tone: Tc6ComparisonTone;
    };

type RGB = [number, number, number];

const COLORS = {
  slate900: [15, 23, 42] as RGB,
  slate800: [30, 41, 59] as RGB,
  slate700: [51, 65, 85] as RGB,
  slate600: [71, 85, 105] as RGB,
  slate500: [100, 116, 139] as RGB,
  slate300: [203, 213, 225] as RGB,
  slate200: [226, 232, 240] as RGB,
  slate100: [241, 245, 249] as RGB,
  slate50: [248, 250, 252] as RGB,
  brandBlue: [46, 90, 136] as RGB,
  emerald: [23, 201, 164] as RGB,
  emeraldSoft: [236, 253, 245] as RGB,
  roseSoft: [255, 241, 242] as RGB,
  white: [255, 255, 255] as RGB,
} as const;

const PDF_FOOTER_HEIGHT_MM = 22;
const PDF_BOTTOM_SAFE_MARGIN_MM = 30;
const PDF_CONTINUATION_HEADER_BOTTOM_MM = 17;

interface PDFDocumentPageContext {
  branding?: ResolvedClientReportBranding;
  testLabel?: string;
  fullHeaderPages: Set<number>;
  continuationHeaderPages: Set<number>;
}

const PDF_QUESTIONNAIRE_LABELS: Record<string, string> = {
  CAT: 'Questionário CAT (COPD Assessment Test)',
  ACT: 'Questionário ACT (Asthma Control Test)',
  mMRC: 'mMRC (Escala de Dispneia do Medical Research Council)',
  'PFSDQ-M': 'PFSDQ-M (Pulmonary Functional Status and Dyspnea Questionnaire - Modified)',
  HADS: 'HADS (Hospital Anxiety and Depression Scale)',
  PSQI: 'PSQI (Pittsburgh Sleep Quality Index)',
  AQLQ: 'AQLQ (Asthma Quality of Life Questionnaire)',
  SGRQ: 'SGRQ (Saint George Respiratory Questionnaire)',
  LCADL: 'LCADL (London Chest Activity of Daily Living)',
  MLHFQ: 'MLHFQ (Minnesota Living with Heart Failure Questionnaire)',
  SAQ: 'Questionário de angina adaptado — versão legada',
};

const buildAssessmentProfessionalInfo = (result: AssessmentResultV2) =>
  buildMedicalRecordAuthorInfo({
    userId: result.professionalUserId ?? null,
    name: result.professionalNameSnapshot ?? null,
    role: result.professionalRoleSnapshot ?? null,
    councilNumber: result.professionalRegistrationSnapshot ?? null,
  });

const getAssessmentProfessionalRegistrationLabel = (result: AssessmentResultV2): string => {
  const professionalInfo = buildAssessmentProfessionalInfo(result);

  if (hasNonApplicableProfessionalRegistration(professionalInfo.role)) {
    return 'Registro profissional não aplicável';
  }

  return professionalInfo.councilNumber || 'Registro profissional pendente';
};

export class AssessmentPDFV2Service {
  private static readonly pdfDocumentPageContexts = new WeakMap<jsPDF, PDFDocumentPageContext>();

  private static getPDFDocumentPageContext(doc: jsPDF): PDFDocumentPageContext {
    const existingContext = this.pdfDocumentPageContexts.get(doc);
    if (existingContext) return existingContext;

    const context: PDFDocumentPageContext = {
      fullHeaderPages: new Set<number>(),
      continuationHeaderPages: new Set<number>(),
    };
    this.pdfDocumentPageContexts.set(doc, context);
    return context;
  }

  private static runAutoTable(doc: jsPDF, options: Parameters<typeof autoTable>[1]): void {
    const initialPageNumber = doc.getCurrentPageInfo().pageNumber;
    const originalWillDrawPage = options?.willDrawPage;
    const rawMargin = options?.margin;
    const normalizedMargin =
      typeof rawMargin === 'number'
        ? {
            top: rawMargin,
            right: rawMargin,
            bottom: Math.max(rawMargin, PDF_BOTTOM_SAFE_MARGIN_MM),
            left: rawMargin,
          }
        : {
            top: rawMargin?.top ?? PDF_CONTINUATION_HEADER_BOTTOM_MM,
            right: rawMargin?.right ?? 0,
            bottom: Math.max(rawMargin?.bottom ?? 0, PDF_BOTTOM_SAFE_MARGIN_MM),
            left: rawMargin?.left ?? 0,
          };

    autoTable(doc, {
      ...options,
      margin: normalizedMargin,
      rowPageBreak: options?.rowPageBreak ?? 'avoid',
      willDrawPage: (hookData) => {
        const currentPageNumber = doc.getCurrentPageInfo().pageNumber;
        const pageContext = this.getPDFDocumentPageContext(doc);
        if (currentPageNumber !== initialPageNumber && pageContext.branding) {
          this.renderContinuationHeader(doc, pageContext.branding);
        }
        return originalWillDrawPage?.(hookData);
      },
    });
  }

  private static getAssessmentPDFLabel(testKey: string): string {
    if (testKey === '5xSTS' || testKey === 'STS_30S' || testKey === 'STS_1MIN') {
      return getStsTestLabel(testKey);
    }
    return PDF_QUESTIONNAIRE_LABELS[testKey] ?? getAssessmentV2Label(testKey);
  }

  private static async loadImageAsDataUrl(url?: string | null): Promise<string | null> {
    if (!url) return null;

    try {
      const response = await fetch(url);
      const blob = await response.blob();

      return await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.warn('Não foi possível carregar imagem para o PDF V2:', error);
      return null;
    }
  }

  private static async createGradientBarDataUrl(width = 1600, height = 120): Promise<string | null> {
    try {
      const svg = `
        <svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">
          <defs>
            <linearGradient id="catGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stop-color="#1cc8a0" />
              <stop offset="30%" stop-color="#9cd26e" />
              <stop offset="52%" stop-color="#f2d61a" />
              <stop offset="76%" stop-color="#ff9e44" />
              <stop offset="100%" stop-color="#f43f5e" />
            </linearGradient>
          </defs>
          <rect x="0" y="0" width="${width}" height="${height}" rx="${height / 2}" fill="url(#catGradient)" />
        </svg>
      `;

      const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
      const objectUrl = URL.createObjectURL(blob);

      try {
        const img = await new Promise<HTMLImageElement>((resolve, reject) => {
          const image = new Image();
          image.onload = () => resolve(image);
          image.onerror = reject;
          image.src = objectUrl;
        });

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const context = canvas.getContext('2d');
        if (!context) return null;

        context.drawImage(img, 0, 0, width, height);
        return canvas.toDataURL('image/png');
      } finally {
        URL.revokeObjectURL(objectUrl);
      }
    } catch (error) {
      console.warn('Não foi possível montar a barra gradiente do PDF V2:', error);
      return null;
    }
  }

  private static formatDate(value?: string | null): string {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString('pt-BR');
  }

  private static formatDateOnly(value?: string | null): string {
    if (!value) return '—';

    const isoMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) {
      return `${isoMatch[3]}/${isoMatch[2]}/${isoMatch[1]}`;
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString('pt-BR');
  }

  private static extractCATPayload(result: AssessmentResultV2): {
    answers: number[];
    total: number | null;
  } {
    const payload = result.payloadJson ?? {};
    const answers = Array.isArray(payload.answers)
      ? payload.answers.map((value) => (typeof value === 'number' ? Math.max(0, Math.min(5, value)) : -1))
      : [];
    const total = typeof payload.total === 'number' ? Math.max(0, Math.min(40, payload.total)) : null;
    return {
      answers,
      total,
      ...(payload.report && typeof payload.report === 'object' ? { report: payload.report as Record<string, unknown> } : {}),
    };
  }

  private static extractACTPayload(result: AssessmentResultV2): ACTResultPayload | null {
    const payload = result.payloadJson ?? {};
    const answers = Array.isArray(payload.answers)
      ? payload.answers.map((value: unknown) => {
          const parsed = typeof value === 'number' ? value : Number.parseFloat(String(value ?? ''));
          return Number.isFinite(parsed) ? Math.max(1, Math.min(5, parsed)) : -1;
        })
      : [];
    const totalRaw =
      typeof payload.total === 'number'
        ? payload.total
        : Number.parseFloat(String(payload.total ?? result.summaryJson?.total ?? ''));
    const total =
      Number.isFinite(totalRaw) ? Math.max(5, Math.min(25, totalRaw)) : answers.length ? answers.reduce((sum, value) => sum + (value > 0 ? value : 0), 0) : null;

    if (total == null) return null;

    return {
      answers,
      total,
      classification:
        typeof payload.classification === 'string' && payload.classification.trim().length > 0
          ? payload.classification
          : getACTSeverityLabel(total),
      finishedAt: typeof payload.finishedAt === 'string' ? payload.finishedAt : undefined,
      ...(payload.report && typeof payload.report === 'object' ? { report: payload.report as Record<string, unknown> } : {}),
    };
  }

  private static extractAQLQPayload(result: AssessmentResultV2): AQLQResultPayload | null {
    const payload = result.payloadJson ?? {};
    const answersSource = payload.answersByItem && typeof payload.answersByItem === 'object' ? payload.answersByItem : {};
    const answersByItem = Object.entries(answersSource).reduce<Record<string, number>>((accumulator, [key, value]) => {
      const parsed = typeof value === 'number' ? value : Number.parseFloat(String(value ?? ''));
      if (Number.isFinite(parsed)) {
        accumulator[key] = Math.max(1, Math.min(7, parsed));
      }
      return accumulator;
    }, {});

    const readScore = (value: unknown): number | null => {
      const parsed = typeof value === 'number' ? value : Number.parseFloat(String(value ?? ''));
      return Number.isFinite(parsed) ? Math.max(1, Math.min(7, Number(parsed.toFixed(2)))) : null;
    };

    const total =
      readScore(payload.total ?? result.summaryJson?.total) ??
      (() => {
        const values = Object.values(answersByItem);
        if (!values.length) return null;
        return Number((values.reduce((sum, value) => sum + value, 0) / values.length).toFixed(2));
      })();

    if (total == null) return null;

    const domainKeys = Object.keys(AQLQ_DOMAIN_LABELS) as AQLQDomainKey[];
    const domainScores = domainKeys.reduce<Record<AQLQDomainKey, AQLQResultPayload['domainScores'][AQLQDomainKey]>>(
      (accumulator, key) => {
        const payloadDomain = payload.domainScores?.[key] ?? {};
        const fallbackScore =
          key === 'symptoms'
            ? result.summaryJson?.symptomsScore
            : key === 'emotionalFunction'
              ? result.summaryJson?.emotionalFunctionScore
              : key === 'activityLimitation'
                ? result.summaryJson?.activityLimitationScore
                : result.summaryJson?.environmentalStimuliScore;
        const itemNumbers = Array.isArray(payloadDomain.itemNumbers)
          ? payloadDomain.itemNumbers.map((item: unknown) => Number(item)).filter((item: number) => Number.isFinite(item))
          : AQLQ_QUESTIONS.filter((question) => question.domain === key).map((question) => question.itemNumber);
        const answered =
          typeof payloadDomain.answered === 'number'
            ? payloadDomain.answered
            : itemNumbers.filter((itemNumber) => typeof answersByItem[String(itemNumber)] === 'number').length;

        accumulator[key] = {
          score: readScore(payloadDomain.score ?? fallbackScore),
          answered,
          itemNumbers,
        };
        return accumulator;
      },
      {} as Record<AQLQDomainKey, AQLQResultPayload['domainScores'][AQLQDomainKey]>,
    );

    return {
      answersByItem,
      total,
      answeredCount:
        typeof payload.answeredCount === 'number' ? payload.answeredCount : Object.keys(answersByItem).length,
      domainScores,
      sourceMissingItems: Array.isArray(payload.sourceMissingItems)
        ? payload.sourceMissingItems.map((item: unknown) => Number(item)).filter((item: number) => Number.isFinite(item))
        : [],
      interpretation: resolveAssessmentInterpretationText(
        payload,
        getAQLQInterpretationText(
          total,
          domainScores,
          Array.isArray(payload.sourceMissingItems)
            ? payload.sourceMissingItems.map((item: unknown) => Number(item)).filter((item: number) => Number.isFinite(item))
            : [],
        ),
      ),
      finishedAt: typeof payload.finishedAt === 'string' ? payload.finishedAt : undefined,
    };
  }

  private static extractMMRCPayload(result: AssessmentResultV2): {
    grade: number | null;
    description: string;
  } {
    const payload = result.payloadJson ?? {};
    const rawGrade = typeof payload.grade === 'number' ? payload.grade : Number.parseFloat(String(payload.grade ?? ''));
    const grade = Number.isFinite(rawGrade) ? Math.max(0, Math.min(4, rawGrade)) : null;
    const description =
      typeof payload.description === 'string' && payload.description.trim().length > 0
        ? payload.description.trim()
        : MMRC_ITEMS.find((item) => item.grade === grade)?.text ?? 'Descrição não informada.';

    return {
      grade,
      description,
      ...(payload.report && typeof payload.report === 'object' ? { report: payload.report as Record<string, unknown> } : {}),
    };
  }

  private static extractTUGPayload(result: AssessmentResultV2): TugResultPayload | null {
    const patientContext = this.extractPatientContext(result);
    const normalized = normalizeTugPayload({
      ...(result.payloadJson ?? {}),
      // O campo direto era uma interpretação automática antiga; personalizações ficam em report.interpretation.
      interpretation: undefined,
      patientContext,
    });
    if (normalized) return normalized;

    if (result.summaryJson && typeof result.summaryJson === 'object') {
      return normalizeTugPayload({
        completed: result.summaryJson.completed !== false,
        timeSeconds: result.summaryJson.timeSeconds,
        walkingAidLabel: result.summaryJson.walkingAid,
        familiarizationPerformed: result.summaryJson.familiarizationPerformed,
        physicalAssistance: result.summaryJson.physicalAssistance,
        classification: result.summaryJson.classification,
        referenceNote: TUG_REFERENCE_NOTE,
        protocol: TUG_STANDARD_PROTOCOL,
        patientContext,
      });
    }

    return null;
  }

  private static extractPeakFlowPayload(result: AssessmentResultV2): PeakFlowResultPayload | null {
    const normalized = normalizePeakFlowPayload(result.payloadJson ?? {});
    if (normalized) return normalized;

    if (result.summaryJson && typeof result.summaryJson === 'object') {
      return normalizePeakFlowPayload({
        preReadings: Array.isArray(result.payloadJson?.preReadings) ? result.payloadJson.preReadings : [],
        postReadings: Array.isArray(result.payloadJson?.postReadings) ? result.payloadJson.postReadings : [],
        bronchodilatorUsed: result.summaryJson.bronchodilatorUsed,
        referenceMode: result.summaryJson.referenceMode,
        referenceValue: result.summaryJson.referenceValue,
        referenceLabel: result.summaryJson.referenceLabel,
        interpretation: result.payloadJson?.interpretation,
        referenceNote: PEAK_FLOW_REFERENCE_NOTE,
      });
    }

    return null;
  }

  private static extractGaitSpeedPayload(result: AssessmentResultV2): GaitSpeedResultPayload | null {
    const normalized = normalizeGaitSpeedPayload(result.payloadJson ?? {});
    if (normalized) return normalized;

    if (result.summaryJson && typeof result.summaryJson === 'object') {
      return normalizeGaitSpeedPayload({
        completed: result.summaryJson.completed !== false,
        timeSeconds: result.summaryJson.timeSeconds,
        speedMs: result.summaryJson.speedMs,
        walkingAidLabel: result.summaryJson.walkingAid,
        physicalAssistance: result.summaryJson.physicalAssistance,
        oxygenUsed: result.summaryJson.oxygenUsed,
        classification: result.summaryJson.classification,
        referenceNote: GAIT_SPEED_REFERENCE_NOTE,
        protocol: GAIT_SPEED_STANDARD_PROTOCOL,
      });
    }

    return null;
  }

  private static extractFiveSTSPayload(result: AssessmentResultV2) {
    const patientContext = this.extractPatientContext(result);
    const normalized = normalizeFiveSTSPayload({
      ...(result.payloadJson ?? {}),
      patientContext,
    });
    if (normalized) return normalized;

    return normalizeFiveSTSPayload({
      attempts: Array.isArray(result.payloadJson?.attempts) ? result.payloadJson.attempts : [],
      best: result.summaryJson?.best,
      chairHeightCm: result.summaryJson?.chairHeightCm,
      usedArms: result.summaryJson?.usedArms,
      workflowStatus: 'completed',
      patientContext,
    });
  }

  private static extractStsRepetitionPayload(result: AssessmentResultV2, testKey: Extract<StsTestKey, 'STS_30S' | 'STS_1MIN'>) {
    const patientContext = this.extractPatientContext(result);
    const normalized = normalizeStsRepetitionPayload(testKey, {
      ...(result.payloadJson ?? {}),
      patientContext,
    });
    if (normalized) return normalized;

    return normalizeStsRepetitionPayload(testKey, {
      attempts: Array.isArray(result.payloadJson?.attempts) ? result.payloadJson.attempts : [],
      best: result.summaryJson?.best,
      attempt1: result.payloadJson?.attempt1,
      attempt2: result.payloadJson?.attempt2,
      restIntervalSeconds: result.summaryJson?.restIntervalSeconds,
      workflowStatus: result.summaryJson?.workflowStatus,
      patientContext,
    });
  }

  private static extractHandgripPayload(result: AssessmentResultV2): (HandgripResultPayload & { chartImages?: HandgripChartImages }) | null {
    const normalized = normalizeHandgripPayload(result.payloadJson ?? {});
    if (!normalized) return null;

    const chartImages =
      result.assetsJson?.chartImages && typeof result.assetsJson.chartImages === 'object'
        ? (result.assetsJson.chartImages as HandgripChartImages)
        : undefined;

    return chartImages ? { ...normalized, chartImages } : normalized;
  }

  private static extractHADSPayload(result: AssessmentResultV2): {
    answers: number[];
    anxietyScore: number | null;
    depressionScore: number | null;
  } {
    const payload = result.payloadJson ?? {};
    const answers = Array.isArray(payload.answers)
      ? payload.answers.map((value) => {
          const parsed = typeof value === 'number' ? value : Number.parseFloat(String(value ?? ''));
          return Number.isFinite(parsed) ? Math.max(0, Math.min(3, parsed)) : -1;
        })
      : [];
    const anxietyRaw =
      typeof payload.anxietyScore === 'number'
        ? payload.anxietyScore
        : Number.parseFloat(String(payload.anxietyScore ?? result.summaryJson?.anxietyScore ?? ''));
    const depressionRaw =
      typeof payload.depressionScore === 'number'
        ? payload.depressionScore
        : Number.parseFloat(String(payload.depressionScore ?? result.summaryJson?.depressionScore ?? ''));

    return {
      answers,
      anxietyScore: Number.isFinite(anxietyRaw) ? Math.max(0, Math.min(21, anxietyRaw)) : null,
      depressionScore: Number.isFinite(depressionRaw) ? Math.max(0, Math.min(21, depressionRaw)) : null,
      ...(payload.report && typeof payload.report === 'object' ? { report: payload.report as Record<string, unknown> } : {}),
    };
  }

  private static getPSQIChoiceLabel(
    options: ReadonlyArray<{ value: number | string; label: string }>,
    value: number | string | null | undefined,
  ): string {
    return options.find((option) => option.value === value)?.label ?? 'Não informado';
  }

  private static extractPSQIPayload(result: AssessmentResultV2): PSQIResultPayload | null {
    const payload = result.payloadJson ?? {};
    const normalized = normalizePSQIPayload(payload);
    if (!normalized) return null;
    return payload.report && typeof payload.report === 'object'
      ? ({
          ...normalized,
          report: payload.report as Record<string, unknown>,
        } as PSQIResultPayload)
      : normalized;
  }

  private static extractLCADLPayload(result: AssessmentResultV2): LCADLResultPayload | null {
    const payload = result.payloadJson ?? {};
    const totalScore = typeof payload.totalScore === 'number' ? payload.totalScore : Number.parseFloat(String(payload.totalScore ?? ''));
    const maxScore = typeof payload.maxScore === 'number' ? payload.maxScore : Number.parseFloat(String(payload.maxScore ?? ''));
    const percentage = typeof payload.percentage === 'number' ? payload.percentage : Number.parseFloat(String(payload.percentage ?? ''));

    if (!Number.isFinite(totalScore) || !Number.isFinite(maxScore) || !Number.isFinite(percentage)) {
      return null;
    }

    const sectionPayload = {} as Pick<LCADLResultPayload, LCADLSectionKey>;
    (Object.keys(LCADL_SECTIONS) as LCADLSectionKey[]).forEach((sectionKey) => {
      const sectionData = payload[sectionKey] ?? {};
      sectionPayload[sectionKey] = {
        activities: Array.isArray(sectionData.activities) ? sectionData.activities.map((item: unknown) => String(item)) : [...LCADL_SECTIONS[sectionKey].activities],
        answers: Array.isArray(sectionData.answers)
          ? sectionData.answers.map((value: unknown) => {
              const parsed = typeof value === 'number' ? value : Number.parseFloat(String(value ?? ''));
              return Number.isFinite(parsed) ? Math.max(0, Math.min(5, parsed)) : 0;
            })
          : Array(LCADL_SECTIONS[sectionKey].activities.length).fill(0),
        score:
          typeof sectionData.score === 'number'
            ? sectionData.score
            : Number.parseFloat(String(sectionData.score ?? 0)),
        maxScore:
          typeof sectionData.maxScore === 'number'
            ? sectionData.maxScore
            : Number.parseFloat(String(sectionData.maxScore ?? LCADL_SECTIONS[sectionKey].activities.length * 5)),
      };
    });

    return {
      answers: Array.isArray(payload.answers)
        ? payload.answers.map((value: unknown) => {
            const parsed = typeof value === 'number' ? value : Number.parseFloat(String(value ?? ''));
            return Number.isFinite(parsed) ? Math.max(0, Math.min(5, parsed)) : 0;
          })
        : [],
      selfCare: sectionPayload.selfCare,
      domestic: sectionPayload.domestic,
      physical: sectionPayload.physical,
      leisure: sectionPayload.leisure,
      totalScore,
      maxScore,
      percentage,
      applicableItemsCount: typeof payload.applicableItemsCount === 'number' ? payload.applicableItemsCount : undefined,
      denominatorPoints: typeof payload.denominatorPoints === 'number' ? payload.denominatorPoints : undefined,
      rawMaximumScore: typeof payload.rawMaximumScore === 'number' ? payload.rawMaximumScore : undefined,
      instrumentVersion: payload.instrumentVersion === 'LCADL_BR_15_CARPES_2008' ? payload.instrumentVersion : undefined,
      scoringVersion: payload.scoringVersion === 'LCADL_PERCENT_APPLICABLE_ITEMS_V1' ? payload.scoringVersion : undefined,
      finishedAt: typeof payload.finishedAt === 'string' ? payload.finishedAt : undefined,
      ...(payload.report && typeof payload.report === 'object' ? { report: payload.report as Record<string, unknown> } : {}),
    };
  }

  private static extractMLHFQPayload(result: AssessmentResultV2): MLHFQResultPayload | null {
    const payload = result.payloadJson ?? {};
    const total = typeof payload.total === 'number' ? payload.total : Number.parseFloat(String(payload.total ?? ''));
    const maxScore =
      typeof payload.maxScore === 'number' ? payload.maxScore : Number.parseFloat(String(payload.maxScore ?? result.summaryJson?.maxScore ?? 105));

    if (!Number.isFinite(total) || !Number.isFinite(maxScore)) {
      return null;
    }

    const answers = Array.isArray(payload.answers)
      ? payload.answers.map((value: unknown) => {
          const parsed = typeof value === 'number' ? value : Number.parseFloat(String(value ?? ''));
          return Number.isFinite(parsed) ? Math.max(0, Math.min(5, parsed)) : -1;
        })
      : Array(MLHFQ_ITEMS_PT.length).fill(-1);

    const otherItems = MLHFQ_ITEMS_PT.map((_, index) => index + 1).filter(
      (itemNumber) =>
        !MLHFQ_PHYSICAL_1BASED.includes(itemNumber as (typeof MLHFQ_PHYSICAL_1BASED)[number]) &&
        !MLHFQ_EMOTIONAL_1BASED.includes(itemNumber as (typeof MLHFQ_EMOTIONAL_1BASED)[number]),
    );

    const readDomain = (
      key: 'physical' | 'emotional' | 'other',
      fallbackItems: number[],
      fallbackMaxScore: number,
    ) => {
      const domain = payload[key] ?? {};
      const score = typeof domain.score === 'number' ? domain.score : Number.parseFloat(String(domain.score ?? 0));
      const domainMaxScore =
        typeof domain.maxScore === 'number' ? domain.maxScore : Number.parseFloat(String(domain.maxScore ?? fallbackMaxScore));
      const items = Array.isArray(domain.items)
        ? domain.items.map((item: unknown) => Number(item)).filter((item: number) => Number.isFinite(item))
        : fallbackItems;

      return {
        score: Number.isFinite(score) ? score : 0,
        maxScore: Number.isFinite(domainMaxScore) ? domainMaxScore : fallbackMaxScore,
        items,
      };
    };

    return {
      answers,
      total,
      maxScore,
      physical: readDomain('physical', [...MLHFQ_PHYSICAL_1BASED], MLHFQ_PHYSICAL_1BASED.length * 5),
      emotional: readDomain('emotional', [...MLHFQ_EMOTIONAL_1BASED], MLHFQ_EMOTIONAL_1BASED.length * 5),
      other: readDomain('other', otherItems, otherItems.length * 5),
      finishedAt: typeof payload.finishedAt === 'string' ? payload.finishedAt : undefined,
      ...(payload.report && typeof payload.report === 'object' ? { report: payload.report as Record<string, unknown> } : {}),
    };
  }

  private static extractPFSDQPayload(result: AssessmentResultV2): PFSDQMResultPayload | null {
    const payload = result.payloadJson ?? {};
    const readScaleArray = (value: unknown): (number | null)[] =>
      Array.isArray(value)
        ? value.map((item) => {
            const parsed = typeof item === 'number' ? item : item == null ? null : Number.parseFloat(String(item));
            return Number.isFinite(parsed) ? Math.max(0, Math.min(10, parsed)) : null;
          })
        : Array(PFSDQ_ACTIVITIES.length).fill(null);

    const readGeneral = (value: unknown) => {
      const general = value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
      return {
        q1: typeof general.q1 === 'boolean' ? general.q1 : null,
        q2_per_month: typeof general.q2_per_month === 'string' ? general.q2_per_month : String(general.q2_per_month ?? ''),
        q3:
          typeof general.q3 === 'number'
            ? Math.max(0, Math.min(10, general.q3))
            : Number.isFinite(Number(general.q3))
              ? Math.max(0, Math.min(10, Number(general.q3)))
              : null,
        q4:
          typeof general.q4 === 'number'
            ? Math.max(0, Math.min(10, general.q4))
            : Number.isFinite(Number(general.q4))
              ? Math.max(0, Math.min(10, Number(general.q4)))
              : null,
        q5:
          typeof general.q5 === 'number'
            ? Math.max(0, Math.min(10, general.q5))
            : Number.isFinite(Number(general.q5))
              ? Math.max(0, Math.min(10, Number(general.q5)))
              : null,
      };
    };

    const dyspnea = payload.dyspnea && typeof payload.dyspnea === 'object' ? (payload.dyspnea as Record<string, any>) : {};
    const fatigue = payload.fatigue && typeof payload.fatigue === 'object' ? (payload.fatigue as Record<string, any>) : {};
    const change = payload.change && typeof payload.change === 'object' ? (payload.change as Record<string, any>) : {};

    const total = typeof payload.total === 'number' ? payload.total : Number.parseFloat(String(payload.total ?? ''));
    if (!Number.isFinite(total)) return null;

    const changeActivities = Array.isArray(change.activities)
      ? change.activities.map((item: any) => ({
          value:
            typeof item?.value === 'number'
              ? Math.max(0, Math.min(10, item.value))
              : Number.isFinite(Number(item?.value))
                ? Math.max(0, Math.min(10, Number(item.value)))
                : null,
          never: Boolean(item?.never),
        }))
      : Array(PFSDQ_ACTIVITIES.length)
          .fill(null)
          .map(() => ({ value: null, never: false }));

    return {
      dyspnea: {
        general: readGeneral(dyspnea.general),
        activities: readScaleArray(dyspnea.activities),
        score: typeof dyspnea.score === 'number' ? dyspnea.score : Number.parseFloat(String(dyspnea.score ?? 0)),
      },
      fatigue: {
        general: readGeneral(fatigue.general),
        activities: readScaleArray(fatigue.activities),
        score: typeof fatigue.score === 'number' ? fatigue.score : Number.parseFloat(String(fatigue.score ?? 0)),
      },
      change: {
        activities: changeActivities,
        score: typeof change.score === 'number' ? change.score : Number.parseFloat(String(change.score ?? 0)),
      },
      manualCounts: {
        dyspneaAnswered:
          typeof payload.manualCounts?.dyspneaAnswered === 'number'
            ? payload.manualCounts.dyspneaAnswered
            : readScaleArray(dyspnea.activities).filter((value) => value != null).length,
        fatigueAnswered:
          typeof payload.manualCounts?.fatigueAnswered === 'number'
            ? payload.manualCounts.fatigueAnswered
            : readScaleArray(fatigue.activities).filter((value) => value != null).length,
        changeAnswered:
          typeof payload.manualCounts?.changeAnswered === 'number'
            ? payload.manualCounts.changeAnswered
            : changeActivities.filter((item) => !item.never && item.value != null).length,
      },
      total,
      finishedAt: typeof payload.finishedAt === 'string' ? payload.finishedAt : undefined,
      ...(payload.report && typeof payload.report === 'object' ? { report: payload.report as Record<string, unknown> } : {}),
    };
  }

  private static extractSAQPayload(result: AssessmentResultV2): SAQResultPayload | null {
    const payload = result.payloadJson ?? {};

    const readIndexArray = (value: unknown, maxIndex: number, length: number): number[] =>
      Array.isArray(value)
        ? value.map((item) => {
            const parsed = typeof item === 'number' ? item : Number.parseFloat(String(item ?? ''));
            return Number.isFinite(parsed) ? Math.max(-1, Math.min(maxIndex, parsed)) : -1;
          })
        : Array(length).fill(-1);

    const physicalAnswers = readIndexArray(payload.physicalAnswers, SAQ_LIMITATION_OPTIONS.length - 1, SAQ_PHYSICAL_ITEMS.length);
    const satisfactionAnswers = readIndexArray(payload.satisfactionAnswers, 4, 3);
    const qolAnswers = readIndexArray(payload.qolAnswers, 4, 3);
    const stabilityRaw = typeof payload.stability === 'number' ? payload.stability : Number.parseFloat(String(payload.stability ?? ''));
    const episodesRaw =
      typeof payload.frequency?.episodes === 'number'
        ? payload.frequency.episodes
        : Number.parseFloat(String(payload.frequency?.episodes ?? ''));
    const nitroRaw =
      typeof payload.frequency?.nitro === 'number'
        ? payload.frequency.nitro
        : Number.parseFloat(String(payload.frequency?.nitro ?? ''));

    const domainScores = SAQ_DOMAIN_SEQUENCE.reduce<Record<SaqDomainKey, number | null>>((accumulator, key) => {
      const value = payload.domainScores?.[key];
      const parsed = typeof value === 'number' ? value : Number.parseFloat(String(value ?? ''));
      accumulator[key] = Number.isFinite(parsed) ? parsed : null;
      return accumulator;
    }, {} as Record<SaqDomainKey, number | null>);

    const interpretation = Array.isArray(payload.interpretation) ? payload.interpretation.map((item: unknown) => String(item)) : [];

    return {
      physicalAnswers,
      stability: Number.isFinite(stabilityRaw) ? Math.max(-1, Math.min(4, stabilityRaw)) : -1,
      frequency: {
        episodes: Number.isFinite(episodesRaw) ? Math.max(-1, Math.min(5, episodesRaw)) : -1,
        nitro: Number.isFinite(nitroRaw) ? Math.max(-1, Math.min(5, nitroRaw)) : -1,
      },
      satisfactionAnswers,
      qolAnswers,
      domainScores,
      interpretation,
      finishedAt: typeof payload.finishedAt === 'string' ? payload.finishedAt : undefined,
      ...(payload.report && typeof payload.report === 'object' ? { report: payload.report as Record<string, unknown> } : {}),
    };
  }

  private static extractSGRQPayload(result: AssessmentResultV2): SGRQResultPayload | null {
    const payload = result.payloadJson ?? {};

    const readScore = (value: unknown): number | null => {
      const parsed = typeof value === 'number' ? value : Number.parseFloat(String(value ?? ''));
      if (!Number.isFinite(parsed)) return null;
      return Math.max(0, Math.min(100, Number(parsed.toFixed(1))));
    };

    const symptoms = readScore(payload.symptoms ?? payload.domains?.symptoms?.score);
    const activity = readScore(payload.activity ?? payload.domains?.activity?.score);
    const impact = readScore(payload.impact ?? payload.domains?.impact?.score ?? payload.impacts);
    const total = readScore(payload.total ?? payload.totalScore ?? payload.overall);

    if ([symptoms, activity, impact, total].some((value) => value == null)) return null;

    const interpretation = resolveAssessmentInterpretationText(
      payload,
      getSGRQInterpretationText(total, { symptoms, activity, impact }),
    );
    const clinicalNotes = Array.isArray(payload.clinicalNotes) ? payload.clinicalNotes.map((item: unknown) => String(item)) : [];

    return {
      symptoms,
      activity,
      impact,
      total,
      domains: {
        symptoms: { score: symptoms },
        activity: { score: activity },
        impact: { score: impact },
      },
      totalMethod: payload.totalMethod === 'manual' ? 'manual' : 'manual',
      interpretation,
      clinicalNotes,
      finishedAt: typeof payload.finishedAt === 'string' ? payload.finishedAt : undefined,
    };
  }

  private static calculateAgeAtDate(birthDate?: string | null, referenceDate?: string | null): number | null {
    if (!birthDate || !referenceDate) return null;

    const birth = new Date(birthDate);
    const reference = new Date(referenceDate);

    if (Number.isNaN(birth.getTime()) || Number.isNaN(reference.getTime())) return null;

    let age = reference.getFullYear() - birth.getFullYear();
    const monthDiff = reference.getMonth() - birth.getMonth();
    const dayDiff = reference.getDate() - birth.getDate();

    if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
      age -= 1;
    }

    return age >= 0 ? age : null;
  }

  private static extractPatientContext(result: AssessmentResultV2): {
    birthDate: string | null;
    ageYears: number | null;
    sex: string | null;
    weightKg: number | null;
    heightCm: number | null;
    identifier: string | null;
  } {
    const payloadContext = result.payloadJson?.patientContext ?? {};
    const coughFieldWasCleared = (field: string): boolean => result.testKey === 'PEAK_COUGH_FLOW'
      && Number(result.payloadJson?.schemaVersion) >= 2
      && Object.prototype.hasOwnProperty.call(payloadContext, field) && payloadContext[field] === null;
    const birthDate =
      typeof payloadContext.birthDate === 'string'
        ? payloadContext.birthDate
        : typeof result.patientBirthDateSnapshot === 'string'
          ? result.patientBirthDateSnapshot
          : null;
    const ageYears = coughFieldWasCleared('ageYears') ? null :
      typeof payloadContext.ageYears === 'number'
        ? payloadContext.ageYears
        : typeof result.summaryJson?.patientAgeYears === 'number'
          ? result.summaryJson.patientAgeYears
          : this.calculateAgeAtDate(birthDate, result.assessmentDate);
    const sex = coughFieldWasCleared('sex') ? null :
      typeof payloadContext.sex === 'string'
        ? payloadContext.sex
        : typeof result.patientSexSnapshot === 'string'
          ? result.patientSexSnapshot
          : null;
    const weightKg = coughFieldWasCleared('weightKg') ? null :
      typeof payloadContext.weightKg === 'number'
        ? payloadContext.weightKg
        : typeof result.patientWeightSnapshot === 'number'
          ? result.patientWeightSnapshot
          : null;
    const rawHeightCm = coughFieldWasCleared('heightCm') ? null :
      typeof payloadContext.heightCm === 'number'
        ? payloadContext.heightCm
        : typeof result.patientHeightSnapshot === 'number'
          ? result.patientHeightSnapshot
          : null;
    const heightCm = normalizePatientHeightCm(rawHeightCm);
    const identifier =
      typeof payloadContext.identifier === 'string'
        ? payloadContext.identifier
        : typeof result.patientMedicalRecordSnapshot === 'string'
          ? result.patientMedicalRecordSnapshot
          : null;

    return { birthDate, ageYears, sex, weightKg, heightCm, identifier };
  }

  private static extractTC6Payload(result: AssessmentResultV2): (Tc6ResultPayload & { chartImages?: Tc6ChartImages }) | null {
    const payload = result.payloadJson ?? {};
    const chartImages =
      result.assetsJson?.chartImages && typeof result.assetsJson.chartImages === 'object'
        ? (result.assetsJson.chartImages as Tc6ChartImages)
        : payload.chartImages && typeof payload.chartImages === 'object'
          ? (payload.chartImages as Tc6ChartImages)
          : null;
    const attempts = getTc6Attempts(payload);
    if (!attempts.length) return null;
    return chartImages
      ? ({ ...payload, chartImages } as Tc6ResultPayload & {
          chartImages?: Tc6ChartImages;
        })
      : (payload as Tc6ResultPayload);
  }

  private static extract6MSTPayload(result: AssessmentResultV2): SixMSTResultPayload | null {
    return normalizeSixMSTPayload(result.payloadJson ?? {});
  }

  private static extractChesterPayload(result: AssessmentResultV2): ChesterResultPayload | null {
    return normalizeChesterPayload(result.payloadJson ?? {});
  }

  private static extractISWTPayload(result: AssessmentResultV2): ISWTResultPayload | null {
    return normalizeISWTPayload(result.payloadJson ?? {});
  }

  private static extractESWTPayload(result: AssessmentResultV2): ESWTResultPayload | null {
    return normalizeESWTPayload(result.payloadJson ?? {});
  }

  private static async stageTC6ChartsForPdf(
    result: AssessmentResultV2,
    payload: Tc6ResultPayload & { chartImages?: Tc6ChartImages },
  ): Promise<{
    chartImages: Tc6ChartImages;
    stagedEntries: TemporaryChartStageMap;
  }> {
    const sourceImages = payload.chartImages && typeof payload.chartImages === 'object' ? payload.chartImages : {};
    const imageEntries = Object.entries(sourceImages).filter(
      (entry): entry is [string, string] => typeof entry[1] === 'string' && entry[1].startsWith('data:image/'),
    );

    if (!imageEntries.length) {
      return {
        chartImages: sourceImages,
        stagedEntries: {},
      };
    }

    try {
      const stagedEntries = await AssessmentTempChartStorageService.stageDataUrlMap(
        result.clientId,
        'tc6-pdf',
        result.resultId,
        Object.fromEntries(imageEntries),
        { signedUrlExpiresInSeconds: 120 },
      );

      const hydratedImages = await Promise.all(
        Object.entries(stagedEntries).map(async ([key, entry]) => {
          const fetchedDataUrl = await this.loadImageAsDataUrl(entry.signedUrl);
          return [key, fetchedDataUrl ?? sourceImages[key as keyof Tc6ChartImages] ?? null] as const;
        }),
      );

      return {
        chartImages: {
          ...sourceImages,
          ...Object.fromEntries(hydratedImages.filter((entry): entry is [string, string] => typeof entry[1] === 'string')),
        },
        stagedEntries,
      };
    } catch (error) {
      console.warn('Erro ao usar staging temporário dos charts do TC6. Mantendo imagens inline do payload.', error);
      return {
        chartImages: sourceImages,
        stagedEntries: {},
      };
    }
  }

  private static renderMetricCards(
    doc: jsPDF,
    startY: number,
    cards: MetricCard[],
    options?: {
      compact?: boolean;
      columns?: number;
      dense?: boolean;
      detailFontSize?: number;
      detailLineHeight?: number;
      minCardHeight?: number;
      backgroundColor?: RGB;
      borderColor?: RGB;
      shadowColor?: RGB;
    }
  ): number {
    const compact = options?.compact ?? false;
    const dense = options?.dense ?? false;
    if (!cards.length) return startY;

    const pageWidth = doc.internal.pageSize.getWidth();
    const usableWidth = pageWidth - 30;
    const cardsPerRow = options?.columns ?? 3;
    const cardSpacing = dense ? 2.5 : compact ? 4 : 6;
    const cardWidth = (usableWidth - cardSpacing * (cardsPerRow - 1)) / cardsPerRow;
    const cardBackgroundColor = options?.backgroundColor ?? COLORS.white;
    const cardBorderColor = options?.borderColor ?? COLORS.slate200;
    const cardShadowColor = options?.shadowColor ?? COLORS.slate200;
    const detailFontSize = options?.detailFontSize ?? (dense ? 7 : compact ? 8 : 9);
    const detailLineHeight = options?.detailLineHeight ?? (dense ? 3.1 : compact ? 3.5 : 4);

    let currentY = startY;
    let currentRowHeight = 0;
    let currentX = 15;

    cards.forEach((card, index) => {
      const rowIndex = index % cardsPerRow;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(detailFontSize);

      const detailsArray = Array.isArray(card.details) ? card.details : card.details ? [card.details] : [];
      const detailLines = detailsArray.flatMap((detail) => doc.splitTextToSize(detail, cardWidth - (dense ? 5 : 8)));
      const contentHeight = (dense ? 14.5 : compact ? 18 : 24) + (detailLines.length ? detailLines.length * detailLineHeight : 0);
      const cardHeight = Math.max(options?.minCardHeight ?? 0, contentHeight);

      if (rowIndex === 0) {
        if (index > 0) currentY += currentRowHeight + cardSpacing;
        currentX = 15;
        currentRowHeight = cardHeight;
      } else {
        currentX += cardWidth + cardSpacing;
        currentRowHeight = Math.max(currentRowHeight, cardHeight);
      }

      const shadowOffset = dense ? 1 : 1.5;
      doc.setFillColor(...cardShadowColor);
      doc.setDrawColor(...cardShadowColor);
      doc.roundedRect(currentX + shadowOffset, currentY + shadowOffset, cardWidth, cardHeight, dense ? 4.5 : 6, dense ? 4.5 : 6, 'F');
      doc.setFillColor(...cardBackgroundColor);
      doc.setDrawColor(...cardBorderColor);
      doc.setLineWidth(0.4);
      doc.roundedRect(currentX, currentY, cardWidth, cardHeight, dense ? 4.5 : 6, dense ? 4.5 : 6, 'FD');
      doc.setLineWidth(0.2);

      doc.setFontSize(dense ? 6 : compact ? 6.5 : 7);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...COLORS.slate500);
      doc.text(card.label.toUpperCase(), currentX + 4, currentY + (dense ? 4.2 : compact ? 4.5 : 6));

      doc.setFontSize(dense ? 10.8 : compact ? 12 : 14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...(card.accentColor || COLORS.slate900));
      doc.text(card.value, currentX + 4, currentY + (dense ? 11 : compact ? 12.5 : 16));

      if (detailLines.length) {
        doc.setFontSize(detailFontSize);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(82, 94, 113);

        let detailY = currentY + (dense ? 13.5 : compact ? 17 : 22);
        detailLines.forEach((line) => {
          doc.text(line, currentX + 4, detailY);
          detailY += detailLineHeight;
        });
      }
    });

    return currentY + currentRowHeight + cardSpacing;
  }

  private static drawBarChart(
    doc: jsPDF,
    x: number,
    y: number,
    width: number,
    height: number,
    data: { label: string; value: number; color?: RGB }[],
    title: string,
    yAxisLabel: string,
    yMax?: number
  ): void {
    const toPastel = (rgb: RGB): RGB => [
      Math.min(255, rgb[0] + (255 - rgb[0]) * 0.35),
      Math.min(255, rgb[1] + (255 - rgb[1]) * 0.35),
      Math.min(255, rgb[2] + (255 - rgb[2]) * 0.35),
    ];

    if (title) {
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(60, 60, 70);
      doc.text(title, x + width / 2, y - 5, { align: 'center' });
    }

    doc.setFillColor(230, 230, 235);
    doc.rect(x + 1, y + 1, width, height, 'F');
    doc.setFillColor(...COLORS.white);
    doc.rect(x, y, width, height, 'F');
    doc.setDrawColor(200, 200, 210);
    doc.setLineWidth(0.3);
    doc.rect(x, y, width, height);

    const effectiveMax =
      yMax && Number.isFinite(yMax)
        ? yMax
        : Math.max(1, Math.ceil(Math.max(...data.map((item) => item.value), 0) * 1.1));

    doc.setDrawColor(240, 240, 245);
    doc.setLineWidth(0.2);
    for (let grid = 0; grid <= 5; grid += 1) {
      const gridY = y + (height / 5) * grid;
      doc.line(x, gridY, x + width, gridY);
    }

    const barWidth = (width / Math.max(data.length, 1)) * 0.7;
    const barSpacing = width / Math.max(data.length, 1);

    data.forEach((item, index) => {
      const barHeight = (item.value / effectiveMax) * height;
      const barX = x + index * barSpacing + (barSpacing - barWidth) / 2;
      const barY = y + height - barHeight;
      const color = item.color || COLORS.brandBlue;
      const pastelColor = toPastel(color);

      doc.setFillColor(pastelColor[0] * 0.6, pastelColor[1] * 0.6, pastelColor[2] * 0.6);
      doc.rect(barX + barWidth, barY + 1, 2, barHeight, 'F');
      doc.setFillColor(pastelColor[0] * 0.5, pastelColor[1] * 0.5, pastelColor[2] * 0.5);
      doc.rect(barX + 2, y + height, barWidth, 2, 'F');

      const gradientSteps = 8;
      for (let step = 0; step < gradientSteps; step += 1) {
        const factor = 1 - (step / gradientSteps) * 0.15;
        const segmentHeight = barHeight / gradientSteps;
        const segmentY = barY + step * segmentHeight;
        doc.setFillColor(
          pastelColor[0] * factor,
          pastelColor[1] * factor,
          pastelColor[2] * factor,
        );
        doc.rect(barX, segmentY, barWidth, segmentHeight, 'F');
      }

      doc.setDrawColor(pastelColor[0] * 0.8, pastelColor[1] * 0.8, pastelColor[2] * 0.8);
      doc.setLineWidth(0.3);
      doc.rect(barX, barY, barWidth, barHeight);

      doc.setFillColor(255, 255, 255);
      const highlightHeight = Math.min(barHeight * 0.15, 3);
      doc.rect(barX + 1, barY + 1, barWidth - 2, highlightHeight, 'F');

      const valueBoxWidth = Math.min(barWidth * 0.8, 20);
      const valueBoxHeight = 6;
      const valueBoxX = barX + (barWidth - valueBoxWidth) / 2;
      const valueBoxY = barY - valueBoxHeight - 2;

      doc.setFillColor(220, 220, 230);
      doc.roundedRect(valueBoxX + 0.5, valueBoxY + 0.5, valueBoxWidth, valueBoxHeight, 1, 1, 'F');
      doc.setFillColor(...COLORS.white);
      doc.setDrawColor(200, 200, 210);
      doc.setLineWidth(0.2);
      doc.roundedRect(valueBoxX, valueBoxY, valueBoxWidth, valueBoxHeight, 1, 1, 'FD');

      doc.setFontSize(7.5);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(60, 60, 70);
      doc.text(item.value.toFixed(1), valueBoxX + valueBoxWidth / 2, valueBoxY + 4, { align: 'center' });

      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(80, 80, 90);
      const labelLines = doc.splitTextToSize(item.label, barWidth + 5);
      doc.text(labelLines, barX + barWidth / 2, y + height + 5, {
        align: 'center',
      });
    });

    doc.setFontSize(8);
    doc.setTextColor(80, 80, 90);
    doc.text(yAxisLabel, x - 5, y + height / 2, { align: 'center', angle: 90 });
  }

  private static toFiniteNumber(value: unknown): number | null {
    if (value == null || value === '') return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private static normalizePdfParagraphText(value: string): string {
    if (!value) return '';

    let text = String(value)
      .replace(/SpO[₂¸]/g, 'SpO2')
      .replace(/₂/g, '2')
      .replace(/Δ/g, 'Delta')
      .replace(/≥/g, '>=')
      .replace(/≤/g, '<=')
      .replace(/[‐‑‒–—−]/g, '-')
      .replace(/→/g, '->')
      .replace(/[“”]/g, '"')
      .replace(/[‘’´`]/g, "'")
      .replace(/(\d)\s*[!']+\s*(\d)/g, '$1->$2');

    text = text.replace(/((?:[A-Za-zÀ-ÖØ-öø-ÿ0-9]\s+){3,}[A-Za-zÀ-ÖØ-öø-ÿ0-9])/g, (match) =>
      match.replace(/\s+/g, '')
    );

    return text
      .replace(/\s*\|\s*/g, ' | ')
      .replace(/\s{2,}/g, ' ')
      .trim();
  }

  private static renderTC6OverviewCards(doc: jsPDF, payload: Tc6ResultPayload, y: number): number {
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const attempts = getTc6Attempts(payload);
    if (!attempts.length) return y;

    const bestAttempt = getTc6BestAttempt(payload).attempt || attempts[0];
    if (!bestAttempt) return y;

    const perMinute = bestAttempt.per_minute || [];
    const perMinuteDistance = bestAttempt.per_minute_distance_m || [];
    const baseline = bestAttempt.baseline || {};
    const final = bestAttempt.final || {};
    const derived = bestAttempt.derivedMetrics || {};

    const spo2Values = [this.toFiniteNumber(baseline?.spo2), ...perMinute.map((minute) => this.toFiniteNumber(minute?.spo2))].filter(
      (value): value is number => value != null
    );
    const minSpo2 = spo2Values.length ? Math.min(...spo2Values) : null;
    const baselineSpo2 = spo2Values.length ? spo2Values[0] : null;
    const spo2Drop = baselineSpo2 != null && minSpo2 != null ? baselineSpo2 - minSpo2 : null;
    const totalDistance =
      this.toFiniteNumber(bestAttempt.total_distance_m) ??
      this.toFiniteNumber(perMinuteDistance[perMinuteDistance.length - 1]) ??
      0;
    const hrDelta =
      this.toFiniteNumber(final?.hr) != null && this.toFiniteNumber(baseline?.hr) != null
        ? this.toFiniteNumber(final.hr)! - this.toFiniteNumber(baseline.hr)!
        : null;
    const borgDyStart = this.toFiniteNumber(baseline?.borg?.dyspnea);
    const borgDyEnd = this.toFiniteNumber(final?.borg?.dyspnea);
    const borgFaStart = this.toFiniteNumber(baseline?.borg?.fatigue);
    const borgFaEnd = this.toFiniteNumber(final?.borg?.fatigue);
    const borgDyDelta = borgDyStart != null && borgDyEnd != null ? borgDyEnd - borgDyStart : null;
    const borgFaDelta = borgFaStart != null && borgFaEnd != null ? borgFaEnd - borgFaStart : null;
    const formatDelta = (value: number | null) => (value == null || Number.isNaN(value) ? '—' : `${value > 0 ? '+' : ''}${value.toFixed(0)}`);
    const primaryReference = computeTC6PrimaryReference(bestAttempt, payload.referenceEquation);
    const summary = summarizeTc6Result(payload);
    const performanceAccent: RGB =
      summary.percentPredicted == null
        ? COLORS.brandBlue
        : summary.percentPredicted >= 90
          ? COLORS.emerald
          : summary.percentPredicted >= 75
            ? [101, 163, 13]
            : summary.percentPredicted >= 60
              ? [217, 119, 6]
              : [225, 29, 72];
    const spo2Accent: RGB = summary.minSpo2 == null ? COLORS.slate900 : summary.hasSignificantDesaturation ? [225, 29, 72] : COLORS.emerald;

    const metricCards: MetricCard[] = [
      {
        label: 'Maior distância',
        value: `${totalDistance} m`,
        details: bestAttempt.pause_reason ? `Nota: ${bestAttempt.pause_reason}` : undefined,
        accentColor: performanceAccent,
      },
      {
        label: 'SpO2 mínima',
        value: minSpo2 != null ? `${minSpo2.toFixed(1)}%` : '—',
        details: spo2Drop != null ? `Queda de ${spo2Drop.toFixed(1)}%` : undefined,
        accentColor: spo2Accent,
      },
      {
        label: 'FC final',
        value: this.toFiniteNumber(final?.hr) != null ? `${final.hr} bpm` : '—',
        details: hrDelta != null ? `Diferença ${hrDelta > 0 ? '+' : ''}${hrDelta.toFixed(0)} bpm` : undefined,
      },
      {
        label: 'Delta Borg (dispneia / fadiga)',
        value: `${formatDelta(borgDyDelta)} / ${formatDelta(borgFaDelta)}`,
        details: `Dispneia ${borgDyStart ?? '-'}->${borgDyEnd ?? '-'} | Fadiga ${borgFaStart ?? '-'}->${borgFaEnd ?? '-'}`,
      },
    ];

    if (primaryReference?.predicted != null) {
      const status =
        primaryReference.belowLLN == null
          ? 'Sem limite inferior para classificação'
          : primaryReference.belowLLN
            ? 'Abaixo do limite esperado'
            : 'Dentro da faixa esperada';
      const llnSummary = primaryReference.lln != null ? `${primaryReference.lln.toFixed(1)} m` : '—';
      metricCards.push({
        label: `Predito (${primaryReference.label})`,
        value: `${primaryReference.predicted.toFixed(1)} m`,
        details: [
          primaryReference.percentPredicted != null
            ? `Paciente atingiu ${primaryReference.percentPredicted.toFixed(1)}% do previsto`
            : 'Paciente atingiu —% do previsto',
          `Limite inferior esperado: ${llnSummary} | ${status}`,
        ],
      });
    }

    if (this.toFiniteNumber(derived?.sixMinuteWork_kgm) != null) {
      metricCards.push({
        label: 'Trabalho 6MWT',
        value: `${derived.sixMinuteWork_kgm} kg·m`,
        details: this.toFiniteNumber(derived?.sixMinuteWork_kJ) != null ? `${derived.sixMinuteWork_kJ} kJ estimados` : undefined,
      });
    }

    if (this.toFiniteNumber(derived?.meanSpeed_kmh) != null) {
      metricCards.push({
        label: 'Velocidade média',
        value: `${derived.meanSpeed_kmh} km/h`,
        details: this.toFiniteNumber(derived?.distancePerMinute_m) != null ? `${derived.distancePerMinute_m} m/min` : undefined,
      });
    }

    if (this.toFiniteNumber(derived?.vo2Estimate_mlkgmin) != null) {
      metricCards.push({
        label: 'VO2 estimado',
        value: `${derived.vo2Estimate_mlkgmin} mL/kg/min`,
        details: this.toFiniteNumber(derived?.metsEstimate) != null ? `${derived.metsEstimate} METs (estimado)` : undefined,
      });
    }

    if (bestAttempt.stop_report?.intervals?.length) {
      metricCards.push({
        label: 'Paradas registradas',
        value: `${bestAttempt.stop_report.intervals.length}x`,
        details: bestAttempt.stop_report.total_stopped_fmt
          ? `Tempo total parado: ${bestAttempt.stop_report.total_stopped_fmt}`
          : 'Ver detalhes na seção da tentativa',
      });
    }

    y =
      this.renderMetricCards(doc, y, metricCards, {
        compact: true,
        dense: true,
        borderColor: COLORS.slate200,
        shadowColor: COLORS.slate200,
      }) + 2;
    doc.setDrawColor(...COLORS.slate200);
    doc.line(15, y, pageWidth - 15, y);
    y += 3;

    const hasPredictedMetric = primaryReference?.predicted != null || this.toFiniteNumber(derived?.predictedDistance_m) != null;
    const hasWorkMetric = this.toFiniteNumber(derived?.sixMinuteWork_kgm) != null;
    const hasSpeedMetric = this.toFiniteNumber(derived?.meanSpeed_kmh) != null;

    if (!hasPredictedMetric && !hasWorkMetric && !hasSpeedMetric) {
      if (y > pageHeight - 30) {
        doc.addPage();
        y = 20;
      }

      doc.setFillColor(255, 243, 205);
      doc.rect(15, y, pageWidth - 30, 25, 'F');
      doc.setDrawColor(251, 191, 36);
      doc.rect(15, y, pageWidth - 30, 25, 'S');

      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(146, 64, 14);
      doc.text('Métricas calculadas não disponíveis', 20, y + 7);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.text(
        doc.splitTextToSize(
          'Esta avaliação não contém métricas derivadas (Predito, Trabalho 6MWT, Velocidade média). Para novos testes, essas informações são calculadas automaticamente.',
          pageWidth - 50,
        ),
        20,
        y + 13,
      );

      y += 30;
    }

    return y;
  }

  private static renderTC6InterpretationBlock(doc: jsPDF, payload: Tc6ResultPayload, y: number): number {
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    if (y > pageHeight - 55) {
      doc.addPage();
      y = 20;
    }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...COLORS.brandBlue);
    doc.text('Interpretação Clínica do TC6', 15, y);
    y += 6;

    const customText =
      typeof (payload as { report?: { interpretation?: string | null } }).report?.interpretation === 'string'
        ? (payload as { report?: { interpretation?: string | null } }).report?.interpretation?.trim() || ''
        : '';

    const paragraphs = (customText ? customText.split(/\n+/) : (resolveTC6Interpretation(payload) || buildTC6AutoInterpretation(payload)).split(/\s*\|\s*/))
      .map((paragraph) => this.normalizePdfParagraphText(paragraph))
      .filter(Boolean);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(40, 40, 40);

    paragraphs.forEach((paragraph) => {
      const lines = doc.splitTextToSize(paragraph, pageWidth - 30);
      doc.text(lines, 15, y);
      y += lines.length * 4 + 2;
      if (y > pageHeight - 40) {
        doc.addPage();
        y = 20;
      }
    });

    return y + 2;
  }

  private static getTc6AttemptMetricValues(attempt: Tc6AttemptPayload | null | undefined, key: 'hr' | 'spo2'): number[] {
    if (!attempt) return [];

    const values = [
      this.toFiniteNumber(attempt.baseline?.[key]),
      ...(Array.isArray(attempt.per_minute) ? attempt.per_minute.map((entry) => this.toFiniteNumber(entry?.[key])) : []),
      this.toFiniteNumber(attempt.final?.[key]),
    ];

    return values.filter((value): value is number => value != null);
  }

  private static buildTc6ComparisonSnapshot(result: AssessmentResultV2, payload: Tc6ResultPayload): Tc6ComparisonSnapshot {
    const normalizedPayload = recalculateTc6Payload(payload);
    const summary = summarizeTc6Result(normalizedPayload);
    const bestAttempt = getTc6BestAttempt(normalizedPayload).attempt;
    const hrValues = this.getTc6AttemptMetricValues(bestAttempt, 'hr');
    const spo2Values = this.getTc6AttemptMetricValues(bestAttempt, 'spo2');
    const stopCount = bestAttempt?.stop_report?.intervals?.length ?? 0;
    const stopSeconds = this.toFiniteNumber(bestAttempt?.stop_report?.total_stopped_s) ?? 0;

    return {
      result,
      dateLabel: this.formatDateOnly(result.assessmentDate),
      summary,
      bestAttempt,
      hrMin: hrValues.length ? Math.min(...hrValues) : null,
      hrMax: hrValues.length ? Math.max(...hrValues) : null,
      spo2Min: spo2Values.length ? Math.min(...spo2Values) : null,
      spo2Max: spo2Values.length ? Math.max(...spo2Values) : null,
      stopCount,
      stopSeconds,
    };
  }

  private static resolveLatestTc6ComparisonSnapshot(
    currentResult: AssessmentResultV2,
    previousResults: AssessmentResultV2[],
  ): Tc6ComparisonSnapshot | null {
    const candidates = previousResults
      .filter((item) => item.resultId !== currentResult.resultId)
      .map((item) => ({ result: item, payload: this.extractTC6Payload(item) }))
      .filter((entry): entry is { result: AssessmentResultV2; payload: Tc6ResultPayload } => Boolean(entry.payload));

    if (!candidates.length) return null;

    candidates.sort((left, right) => {
      const leftDate = new Date(left.result.assessmentDate).getTime();
      const rightDate = new Date(right.result.assessmentDate).getTime();
      if (leftDate !== rightDate) return leftDate - rightDate;
      const leftCreated = new Date(left.result.createdAt).getTime();
      const rightCreated = new Date(right.result.createdAt).getTime();
      return leftCreated - rightCreated;
    });

    const latest = candidates[candidates.length - 1];
    return this.buildTc6ComparisonSnapshot(latest.result, latest.payload);
  }

  private static formatSignedValue(value: number | null, unit: string, decimals = 0): string {
    if (value == null || Number.isNaN(value)) return '—';
    const absolute = Math.abs(value);
    const formatted =
      decimals > 0 ? absolute.toFixed(decimals).replace(/\.0+$/, '') : String(Math.round(absolute));
    const prefix = value > 0 ? '+' : value < 0 ? '-' : '';
    const suffix = unit === '%' ? '%' : ` ${unit}`;
    return `${prefix}${formatted}${suffix}`;
  }

  private static formatSignedCount(value: number | null, singular: string, plural: string): string {
    if (value == null || Number.isNaN(value)) return '—';
    const absolute = Math.abs(Math.round(value));
    const prefix = value > 0 ? '+' : value < 0 ? '-' : '';
    const unit = absolute === 1 ? singular : plural;
    return `${prefix}${absolute} ${unit}`;
  }

  private static renderTC6ComparisonTable(
    doc: jsPDF,
    result: AssessmentResultV2,
    payload: Tc6ResultPayload,
    previousResults: AssessmentResultV2[],
    y: number,
  ): number {
    const previousSnapshot = this.resolveLatestTc6ComparisonSnapshot(result, previousResults);
    if (!previousSnapshot) return y;

    const currentSnapshot = this.buildTc6ComparisonSnapshot(result, payload);
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const tableWidth = pageWidth - 30;

    if (y > pageHeight - 70) {
      doc.addPage();
      y = 20;
    }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...COLORS.brandBlue);
    doc.text('Comparação com avaliação anterior', 15, y);
    y += 5;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...COLORS.slate500);
    doc.text(`Anterior: ${previousSnapshot.dateLabel} | Atual: ${currentSnapshot.dateLabel}`, 15, y);
    y += 5;

    const previousDistance = previousSnapshot.summary.bestDistance_m;
    const currentDistance = currentSnapshot.summary.bestDistance_m;
    const distanceDelta =
      previousDistance != null && currentDistance != null ? currentDistance - previousDistance : null;
    const distancePctDelta =
      previousDistance != null && currentDistance != null && previousDistance > 0
        ? ((currentDistance - previousDistance) / previousDistance) * 100
        : null;

    const previousPercent = previousSnapshot.summary.percentPredicted;
    const currentPercent = currentSnapshot.summary.percentPredicted;
    const percentDelta =
      previousPercent != null && currentPercent != null ? currentPercent - previousPercent : null;

    const previousHrMax = previousSnapshot.hrMax;
    const currentHrMax = currentSnapshot.hrMax;
    const hrDelta = previousHrMax != null && currentHrMax != null ? currentHrMax - previousHrMax : null;

    const previousSpo2Min = previousSnapshot.spo2Min;
    const currentSpo2Min = currentSnapshot.spo2Min;
    const spo2Delta =
      previousSpo2Min != null && currentSpo2Min != null ? currentSpo2Min - previousSpo2Min : null;

    const stopCountDelta = currentSnapshot.stopCount - previousSnapshot.stopCount;
    const stopSecondsDelta = currentSnapshot.stopSeconds - previousSnapshot.stopSeconds;

    const comparisonRows: Tc6ComparisonRow[] = [
      { kind: 'section', label: 'Desempenho' },
      {
        kind: 'metric',
        label: 'Distância Percorrida',
        previous: previousDistance != null ? `${previousDistance} m` : '—',
        current: currentDistance != null ? `${currentDistance} m` : '—',
        evolution:
          distanceDelta != null
            ? `${this.formatSignedValue(distanceDelta, 'm')}${distancePctDelta != null ? ` (${this.formatSignedValue(distancePctDelta, '%', 1)})` : ''}`
            : '—',
        tone:
          distanceDelta == null ? 'neutral' : distanceDelta > 0 ? 'positive' : distanceDelta < 0 ? 'negative' : 'neutral',
      },
      {
        kind: 'metric',
        label: '% do Predito',
        previous: previousPercent != null ? `${previousPercent.toFixed(1)}%` : '—',
        current: currentPercent != null ? `${currentPercent.toFixed(1)}%` : '—',
        evolution: percentDelta != null ? this.formatSignedValue(percentDelta, '%', 1) : '—',
        tone: percentDelta == null ? 'neutral' : percentDelta > 0 ? 'positive' : percentDelta < 0 ? 'negative' : 'neutral',
      },
      { kind: 'section', label: 'Sinais Vitais' },
      {
        kind: 'metric',
        label: 'SpO2 (Mín/Máx)',
        previous:
          previousSnapshot.spo2Min != null && previousSnapshot.spo2Max != null
            ? `${previousSnapshot.spo2Min}% - ${previousSnapshot.spo2Max}%`
            : '—',
        current:
          currentSnapshot.spo2Min != null && currentSnapshot.spo2Max != null
            ? `${currentSnapshot.spo2Min}% - ${currentSnapshot.spo2Max}%`
            : '—',
        evolution: spo2Delta != null ? `${this.formatSignedValue(spo2Delta, '%', 1)} (Mín)` : '—',
        tone: spo2Delta == null ? 'neutral' : spo2Delta > 0 ? 'positive' : spo2Delta < 0 ? 'negative' : 'neutral',
      },
      {
        kind: 'metric',
        label: 'Freq. Cardíaca (Mín/Máx)',
        previous:
          previousSnapshot.hrMin != null && previousSnapshot.hrMax != null
            ? `${previousSnapshot.hrMin} - ${previousSnapshot.hrMax} bpm`
            : '—',
        current:
          currentSnapshot.hrMin != null && currentSnapshot.hrMax != null
            ? `${currentSnapshot.hrMin} - ${currentSnapshot.hrMax} bpm`
            : '—',
        evolution: hrDelta != null ? `${this.formatSignedValue(hrDelta, 'bpm')} (Máx)` : '—',
        tone: hrDelta == null ? 'neutral' : hrDelta < 0 ? 'positive' : hrDelta > 0 ? 'negative' : 'neutral',
      },
      { kind: 'section', label: 'Intercorrências' },
      {
        kind: 'metric',
        label: 'Número de Paradas',
        previous: this.formatSignedCount(previousSnapshot.stopCount, 'parada', 'paradas').replace(/^[+-]/, '') || '0 paradas',
        current: this.formatSignedCount(currentSnapshot.stopCount, 'parada', 'paradas').replace(/^[+-]/, '') || '0 paradas',
        evolution: this.formatSignedCount(stopCountDelta, 'parada', 'paradas'),
        tone: stopCountDelta == null ? 'neutral' : stopCountDelta < 0 ? 'positive' : stopCountDelta > 0 ? 'negative' : 'neutral',
      },
      {
        kind: 'metric',
        label: 'Tempo de Paradas',
        previous: `${previousSnapshot.stopSeconds} seg`,
        current: `${currentSnapshot.stopSeconds} seg`,
        evolution: this.formatSignedValue(stopSecondsDelta, 'seg'),
        tone: stopSecondsDelta == null ? 'neutral' : stopSecondsDelta < 0 ? 'positive' : stopSecondsDelta > 0 ? 'negative' : 'neutral',
      },
    ];

    this.runAutoTable(doc, {
      startY: y + 1,
      margin: { left: 15, right: 15 },
      tableWidth,
      head: [[
        'Parâmetro',
        `Anterior (${previousSnapshot.dateLabel})`,
        `Atual (${currentSnapshot.dateLabel})`,
        'Evolução Clínica',
      ]],
      body: comparisonRows.map((row) =>
        row.kind === 'section'
          ? [row.label, '', '', '']
          : [row.label, row.previous, row.current, row.evolution],
      ),
      theme: 'striped',
      headStyles: {
        fillColor: COLORS.brandBlue,
        textColor: COLORS.white,
        fontSize: 7.9,
        fontStyle: 'bold',
        halign: 'center',
        cellPadding: 2.5,
      },
      bodyStyles: {
        fontSize: 7.5,
        textColor: COLORS.slate800,
        cellPadding: 2.4,
        valign: 'middle',
      },
      alternateRowStyles: {
        fillColor: [250, 252, 255],
      },
      columnStyles: {
        0: { cellWidth: tableWidth * 0.3, fontStyle: 'bold' },
        1: { cellWidth: tableWidth * 0.23, halign: 'center' },
        2: { cellWidth: tableWidth * 0.23, halign: 'center' },
        3: {
          cellWidth: tableWidth * 0.24,
          halign: 'center',
          fontStyle: 'bold',
        },
      },
      didParseCell: (hookData) => {
        if (hookData.section !== 'body') {
          return;
        }

        const row = comparisonRows[hookData.row.index];
        if (!row) return;

        if (row.kind === 'section') {
          if (hookData.column.index === 0) {
            hookData.cell.colSpan = 4;
            hookData.cell.text = [row.label];
            hookData.cell.styles.fillColor = COLORS.slate100;
            hookData.cell.styles.textColor = COLORS.slate500;
            hookData.cell.styles.fontStyle = 'bold';
            hookData.cell.styles.fontSize = 7.6;
            hookData.cell.styles.halign = 'left';
          } else {
            hookData.cell.text = [];
            hookData.cell.styles.fillColor = COLORS.slate100;
            hookData.cell.styles.textColor = COLORS.slate100;
          }
          return;
        }

        if (hookData.column.index === 3) {
          const tone =
            row.tone === 'positive'
              ? { fillColor: COLORS.emeraldSoft, textColor: COLORS.emerald }
              : row.tone === 'negative'
                ? {
                    fillColor: COLORS.roseSoft,
                    textColor: [190, 24, 93] as RGB,
                  }
                : { fillColor: COLORS.slate50, textColor: COLORS.slate500 };

          hookData.cell.styles.fillColor = tone.fillColor;
          hookData.cell.styles.textColor = tone.textColor;
          hookData.cell.styles.fontStyle = 'bold';
        }
      },
    });

    return (doc as any).lastAutoTable?.finalY ?? y;
  }

  private static renderTC6AttemptHeader(doc: jsPDF, attempt: Tc6AttemptPayload, attemptNumber: number, selectedReferenceKey: string, y: number): number {
    const pageWidth = doc.internal.pageSize.getWidth();
    const cardX = 15;
    const cardWidth = pageWidth - 30;
    const cardHeight = 30;
    const colWidth = cardWidth / 3;
    const derived = attempt?.derivedMetrics || null;
    const primaryReference = computeTC6PrimaryReference(attempt, selectedReferenceKey);

    doc.setFillColor(...COLORS.slate50);
    doc.setDrawColor(...COLORS.slate200);
    doc.roundedRect(cardX, y, cardWidth, cardHeight, 3, 3, 'FD');

    const topLabels = ['Distância total', 'Comprimento corredor', 'Voltas'];
    const topValues = [
      attempt?.total_distance_m != null ? `${attempt.total_distance_m} m` : '—',
      attempt?.corridorLength_m != null ? `${attempt.corridorLength_m} m` : '—',
      attempt?.laps != null ? String(attempt.laps) : '—',
    ];

    const fallbackPredicted = this.toFiniteNumber(derived?.predictedDistance_m);
    const fallbackPercent = this.toFiniteNumber(derived?.percentPredicted);
    const predictedText =
      primaryReference?.predicted != null
        ? `${primaryReference.predicted.toFixed(1)} m${primaryReference.percentPredicted != null ? ` (${primaryReference.percentPredicted.toFixed(1)}% realizado)` : ''}`
        : fallbackPredicted != null
          ? `${fallbackPredicted.toFixed(1)} m${fallbackPercent != null ? ` (${fallbackPercent.toFixed(1)}% realizado)` : ''}`
          : '—';
    const workText =
      this.toFiniteNumber(derived?.sixMinuteWork_kgm) != null
        ? `${derived.sixMinuteWork_kgm} kg·m${this.toFiniteNumber(derived?.sixMinuteWork_kJ) != null ? `  ${derived.sixMinuteWork_kJ} kJ` : ''}`
        : '—';
    const speedText = this.toFiniteNumber(derived?.meanSpeed_kmh) != null ? `${derived.meanSpeed_kmh} km/h` : '—';
    const bottomLabels = [primaryReference?.label ? `Predito (${primaryReference.label})` : 'Predito', 'Trabalho no TC6', 'Velocidade média'];
    const bottomValues = [predictedText, workText, speedText];

    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.slate500);
    topLabels.forEach((label, index) => {
      doc.text(label.toUpperCase(), cardX + colWidth * index + 4, y + 7);
    });

    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.slate900);
    topValues.forEach((value, index) => {
      doc.text(value, cardX + colWidth * index + 4, y + 15);
    });

    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.slate500);
    bottomLabels.forEach((label, index) => {
      doc.text(label.toUpperCase(), cardX + colWidth * index + 4, y + 21);
    });

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(51, 65, 85);
    bottomValues.forEach((value, index) => {
      const lines = doc.splitTextToSize(value, colWidth - 8);
      doc.text(lines, cardX + colWidth * index + 4, y + 27);
    });

    return y + cardHeight + 8;
  }

  private static renderTC6MinuteTable(
    doc: jsPDF,
    attempt: Tc6AttemptPayload,
    startY: number,
    options?: { compact?: boolean; x?: number; width?: number }
  ): number {
    const pageWidth = doc.internal.pageSize.getWidth();
    const compact = options?.compact ?? false;
    const x = options?.x ?? 15;
    const width = options?.width ?? pageWidth - 30;
    const rows = attempt.per_minute.map((entry, index) => [
      String(entry.minute),
      entry.spo2 ?? '—',
      entry.hr ?? '—',
      typeof attempt.per_minute_distance_m?.[index] === 'number' ? String(attempt.per_minute_distance_m[index]) : '—',
      typeof attempt.per_minute_speed_kmh?.[index] === 'number' ? attempt.per_minute_speed_kmh[index].toFixed(2) : '—',
    ]);

    this.runAutoTable(doc, {
      startY,
      margin: { left: x, right: Math.max(15, pageWidth - x - width) },
      tableWidth: width,
      head: [['Min', 'SpO2 (%)', 'FC (bpm)', 'Dist. acum. (m)', 'Vel. (km/h)']],
      body: rows,
      theme: 'striped',
      headStyles: {
        fillColor: COLORS.brandBlue,
        textColor: COLORS.white,
        fontSize: compact ? 7.3 : 8.4,
        fontStyle: 'bold',
      },
      bodyStyles: {
        fontSize: compact ? 7.1 : 8,
        textColor: COLORS.slate800,
        cellPadding: compact ? 2.1 : 3,
      },
      alternateRowStyles: {
        fillColor: [250, 252, 255],
      },
      columnStyles: {
        0: { cellWidth: width * 0.12, halign: 'center', fontStyle: 'bold' },
        1: { cellWidth: width * 0.18, halign: 'center' },
        2: { cellWidth: width * 0.18, halign: 'center' },
        3: { cellWidth: width * 0.26, halign: 'center', fontStyle: 'bold' },
        4: { cellWidth: width * 0.18, halign: 'center' },
      },
    });

    return (doc as any).lastAutoTable?.finalY ?? startY;
  }

  private static renderTC6VitalTable(
    doc: jsPDF,
    attempt: Tc6AttemptPayload,
    startY: number,
    options?: { compact?: boolean; x?: number; width?: number }
  ): number {
    const pageWidth = doc.internal.pageSize.getWidth();
    const compact = options?.compact ?? false;
    const x = options?.x ?? 15;
    const width = options?.width ?? 142;
    const rows = [
      ['SpO2 (%)', attempt.baseline.spo2 ?? '—', attempt.final.spo2 ?? '—'],
      ['FC (bpm)', attempt.baseline.hr ?? '—', attempt.final.hr ?? '—'],
      ['PA sistólica', attempt.baseline.bp.sys ?? '—', attempt.final.bp.sys ?? '—'],
      ['PA diastólica', attempt.baseline.bp.dia ?? '—', attempt.final.bp.dia ?? '—'],
      [
        'Borg dispneia',
        attempt.baseline.borg.dyspnea != null ? String(attempt.baseline.borg.dyspnea) : '—',
        attempt.final.borg.dyspnea != null ? String(attempt.final.borg.dyspnea) : '—',
      ],
      [
        'Borg fadiga',
        attempt.baseline.borg.fatigue != null ? String(attempt.baseline.borg.fatigue) : '—',
        attempt.final.borg.fatigue != null ? String(attempt.final.borg.fatigue) : '—',
      ],
    ];

    this.runAutoTable(doc, {
      startY,
      margin: { left: x, right: Math.max(15, pageWidth - x - width) },
      tableWidth: width,
      head: [['Parâmetro', 'Início', 'Fim']],
      body: rows,
      theme: 'striped',
      headStyles: {
        fillColor: [241, 245, 249],
        textColor: COLORS.slate600,
        fontSize: compact ? 7.1 : 8.2,
        fontStyle: 'bold',
      },
      bodyStyles: {
        fontSize: compact ? 7 : 8,
        textColor: COLORS.slate800,
        cellPadding: compact ? 2.1 : 3,
      },
      alternateRowStyles: {
        fillColor: [250, 252, 255],
      },
      columnStyles: {
        0: { cellWidth: width * 0.46, fontStyle: 'bold' },
        1: { cellWidth: width * 0.22, halign: 'center' },
        2: { cellWidth: width * 0.22, halign: 'center' },
      },
    });

    return (doc as any).lastAutoTable?.finalY ?? startY;
  }

  private static renderTC6StopBlock(doc: jsPDF, attempt: Tc6AttemptPayload, y: number): number {
    const stopReport = attempt?.stop_report;
    const pageWidth = doc.internal.pageSize.getWidth();
    if (!stopReport?.intervals?.length) return y;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);
    doc.text('Paradas registradas:', 15, y);
    y += 5;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text(
      `Total: ${stopReport.intervals.length}  |  Tempo parado: ${stopReport.total_stopped_fmt || '-'}`,
      18,
      y
    );
    y += 5;

    stopReport.intervals.forEach((interval, index) => {
      const reasonLabel = interval.reason_label || interval.reason || null;
      const baseLine = `${index + 1}. ${interval.from_fmt} - ${interval.to_fmt} (${interval.duration_fmt})`;
      const line = reasonLabel ? `${baseLine} • ${reasonLabel}` : baseLine;
      const wrapped = doc.splitTextToSize(line, pageWidth - 40);
      doc.text(wrapped, 20, y);
      y += wrapped.length * 3.6;
    });

    return y + 4;
  }

  private static renderTC6DessaturationBlock(doc: jsPDF, attempt: Tc6AttemptPayload, y: number): number {
    const baselineSpo2 = Number.parseFloat(String(attempt?.baseline?.spo2 ?? '0'));
    const perMinuteValues = (attempt?.per_minute || [])
      .map((minute) => Number.parseFloat(String(minute?.spo2 ?? '')))
      .filter((value) => !Number.isNaN(value));

    if (!perMinuteValues.length && !baselineSpo2) return y;
    const minSpo2 = Math.min(baselineSpo2 || 100, ...perMinuteValues);
    const drop = baselineSpo2 ? baselineSpo2 - minSpo2 : 0;
    if (!(drop > 4 || minSpo2 < 85)) return y;

    const pageWidth = doc.internal.pageSize.getWidth();
    const blockHeight = 14;
    doc.setFillColor(255, 240, 240);
    doc.setDrawColor(220, 53, 69);
    doc.roundedRect(15, y, pageWidth - 30, blockHeight, 2, 2, 'FD');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(220, 53, 69);
    doc.text('Dessaturação detectada', 18, y + 5);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.text(`Queda de ${drop.toFixed(1)}% • SpO2 mínima: ${minSpo2.toFixed(1)}%`, 18, y + 11);
    doc.setTextColor(0, 0, 0);

    return y + blockHeight + 6;
  }

  private static drawTC6LineChart(
    doc: jsPDF,
    lines: Array<{
      values: Array<number | null>;
      color: RGB;
      lineWidth?: number;
      showPoints?: boolean;
    }>,
    x: number,
    y: number,
    width: number,
    height: number,
    valueRange: [number, number],
  ): void {
    const [minValue, maxValue] = valueRange;
    const pointsCount = Math.max(...lines.map((line) => line.values.length), 0);

    doc.setDrawColor(...COLORS.slate200);
    doc.rect(x, y, width, height);

    for (let grid = 1; grid < 4; grid += 1) {
      const gridY = y + (height / 4) * grid;
      doc.setDrawColor(...COLORS.slate200);
      doc.line(x, gridY, x + width, gridY);
    }

    if (pointsCount <= 1 || maxValue <= minValue) return;

    lines.forEach((line) => {
      let previous: { x: number; y: number } | null = null;
      line.values.forEach((value, index) => {
        if (value == null || Number.isNaN(value)) {
          previous = null;
          return;
        }
        const pointX = x + (width / (pointsCount - 1)) * index;
        const normalized = (value - minValue) / (maxValue - minValue);
        const pointY = y + height - normalized * height;
        if (previous) {
          doc.setDrawColor(...line.color);
          doc.setLineWidth(line.lineWidth ?? 1.2);
          doc.line(previous.x, previous.y, pointX, pointY);
        }
        if (line.showPoints ?? true) {
          doc.setFillColor(...line.color);
          doc.circle(pointX, pointY, 0.9, 'F');
        }
        previous = { x: pointX, y: pointY };
      });
    });
  }

  private static getImageFormatFromDataUrl(imageData: string): 'PNG' | 'JPEG' {
    return /^data:image\/jpe?g/i.test(imageData) ? 'JPEG' : 'PNG';
  }

  private static renderImageContained(
    doc: jsPDF,
    imageData: string,
    x: number,
    y: number,
    width: number,
    height: number,
    padding = 2,
  ): boolean {
    try {
      const props = (doc as any).getImageProperties?.(imageData);
      const ratio =
        props && typeof props.width === 'number' && typeof props.height === 'number' && props.height > 0
          ? props.width / props.height
          : width / Math.max(height, 1);
      const availableWidth = Math.max(8, width - padding * 2);
      const availableHeight = Math.max(8, height - padding * 2);
      let renderWidth = availableWidth;
      let renderHeight = renderWidth / Math.max(ratio, 0.1);

      if (renderHeight > availableHeight) {
        renderHeight = availableHeight;
        renderWidth = renderHeight * Math.max(ratio, 0.1);
      }

      const renderX = x + (width - renderWidth) / 2;
      const renderY = y + (height - renderHeight) / 2;
      doc.addImage(imageData, this.getImageFormatFromDataUrl(imageData), renderX, renderY, renderWidth, renderHeight);
      return true;
    } catch {
      return false;
    }
  }

  private static renderTC6ChartsRow(
    doc: jsPDF,
    payload: Tc6ResultPayload & { chartImages?: Tc6ChartImages },
    attempt: Tc6AttemptPayload,
    attemptKey: 'attempt1' | 'attempt2',
    y: number,
  ): number {
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    const gap = 6;
    const chartWidth = (pageWidth - margin * 2 - gap) / 2;
    const panelHeight = 63;
    const headerHeight = 8;
    const chartHeight = panelHeight - headerHeight - 4;
    const distanceImage = payload.chartImages?.[`${attemptKey}_distance_payload`];
    const vitalsImage = payload.chartImages?.[`${attemptKey}_vitals_payload`];

    const renderChartPanel = (
      x: number,
      title: string,
      imageData: string | undefined,
      fallback: () => void,
    ) => {
      doc.setFillColor(...COLORS.slate50);
      doc.setDrawColor(...COLORS.slate200);
      doc.roundedRect(x, y, chartWidth, panelHeight, 3, 3, 'FD');

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8.1);
      doc.setTextColor(...COLORS.slate500);
      doc.text(title.toUpperCase(), x + 4, y + 5.5);

      const chartAreaX = x + 2.5;
      const chartAreaY = y + headerHeight;
      const chartAreaWidth = chartWidth - 5;

      doc.setFillColor(...COLORS.white);
      doc.setDrawColor(...COLORS.slate100);
      doc.roundedRect(chartAreaX, chartAreaY, chartAreaWidth, chartHeight, 2.5, 2.5, 'FD');

      if (imageData && this.renderImageContained(doc, imageData, chartAreaX + 0.8, chartAreaY + 0.8, chartAreaWidth - 1.6, chartHeight - 1.6, 0)) {
        return;
      }

      fallback();
    };

    renderChartPanel(margin, 'Distância acumulada', distanceImage, () => {
      const distanceSeries = getTc6DistanceSeries(attempt, payload.referenceEquation);
      const values = distanceSeries.flatMap((item) => [item.realDistance, item.predictedDistance]).filter((item): item is number => item != null);
      this.drawTC6LineChart(
        doc,
        [
          {
            values: distanceSeries.map((item) => item.realDistance),
            color: [37, 99, 235],
          },
          {
            values: distanceSeries.map((item) => item.predictedDistance),
            color: [249, 115, 22],
          },
        ],
        margin + 5,
        y + headerHeight + 4,
        chartWidth - 10,
        chartHeight - 10,
        [0, values.length ? Math.max(...values) : 1],
      );
    });

    renderChartPanel(margin + chartWidth + gap, 'SpO2 e FC', vitalsImage, () => {
      const vitalsSeries = getTc6VitalsSeries(attempt);
      const hrValues = vitalsSeries.map((item) => item.hr).filter((item): item is number => item != null);
      const spo2Values = vitalsSeries.map((item) => item.spo2).filter((item): item is number => item != null);
      const values = [...hrValues, ...spo2Values];
      this.drawTC6LineChart(
        doc,
        [
          {
            values: vitalsSeries.map((item) => item.spo2),
            color: [15, 118, 110],
          },
          {
            values: vitalsSeries.map((item) => item.hr),
            color: [37, 99, 235],
          },
        ],
        margin + chartWidth + gap + 5,
        y + headerHeight + 4,
        chartWidth - 10,
        chartHeight - 10,
        [values.length ? Math.min(...values, 70) : 70, values.length ? Math.max(...values, 120) : 120],
      );
    });

    return y + panelHeight + 8;
  }

  private static renderTC6HistoryChart(
    doc: jsPDF,
    result: AssessmentResultV2,
    payload: Tc6ResultPayload,
    previousResults: AssessmentResultV2[],
    y: number
  ): number {
    const pageWidth = doc.internal.pageSize.getWidth();
    const historicalData = previousResults
      .filter((item) => this.extractTC6Payload(item))
      .sort((left, right) => {
        const leftTime = new Date(left.assessmentDate).getTime();
        const rightTime = new Date(right.assessmentDate).getTime();
        if (Number.isFinite(leftTime) && Number.isFinite(rightTime)) return leftTime - rightTime;
        return String(left.assessmentDate).localeCompare(String(right.assessmentDate));
      })
      .slice(-5)
      .map((item) => {
        const itemPayload = this.extractTC6Payload(item);
        const summary = summarizeTc6Result(itemPayload);
        const dateLabel = this.formatDateOnly(item.assessmentDate);
        return {
          label: dateLabel === '—' ? dateLabel : dateLabel.slice(0, 5),
          value: summary.bestDistance_m || 0,
        };
      })
      .filter((item) => item.value > 0);

    const currentSummary = summarizeTc6Result(payload);
    const currentDateLabel = this.formatDateOnly(result.assessmentDate);
    if (currentSummary.bestDistance_m != null) {
      historicalData.push({
        label: currentDateLabel === '—' ? 'Atual' : currentDateLabel.slice(0, 5),
        value: currentSummary.bestDistance_m,
        color: COLORS.brandBlue,
      });
    }

    if (historicalData.length < 3) return y;

    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.brandBlue);
    doc.text('Evolução da Distância Percorrida', 15, y);
    y += 10;

    this.drawBarChart(doc, 15, y, pageWidth - 30, 50, historicalData, '', 'm');
    return y + 58;
  }

  private static getScoreAccent(total: number | null): RGB {
    if (typeof total !== 'number') return COLORS.brandBlue;
    if (total <= 10) return [16, 185, 129];
    if (total <= 20) return [245, 158, 11];
    if (total <= 30) return [249, 115, 22];
    return [244, 63, 94];
  }

  private static getACTAccent(total: number | null): RGB {
    if (typeof total !== 'number') return COLORS.brandBlue;
    if (total >= 20) return [16, 185, 129];
    if (total >= 16) return [245, 158, 11];
    return [239, 68, 68];
  }

  private static getMMRCAccent(grade: number | null): RGB {
    if (typeof grade !== 'number') return COLORS.brandBlue;
    if (grade <= 1) return [16, 185, 129];
    if (grade === 2) return [245, 158, 11];
    return [249, 115, 22];
  }

  private static getTUGAccent(payload: TugResultPayload | null): RGB {
    const summary = summarizeTugResult(payload);
    if (!payload || !summary.completed || summary.bestTimeSeconds == null) return COLORS.brandBlue;
    const hex = getTUGScoreColor(
      summary.bestTimeSeconds,
      summary.completed,
      summary.physicalAssistance,
      summary.referenceComparison,
    );
    const normalizedHex = hex.replace('#', '');
    const r = Number.parseInt(normalizedHex.slice(0, 2), 16);
    const g = Number.parseInt(normalizedHex.slice(2, 4), 16);
    const b = Number.parseInt(normalizedHex.slice(4, 6), 16);
    return Number.isFinite(r) && Number.isFinite(g) && Number.isFinite(b) ? [r, g, b] : COLORS.brandBlue;
  }

  private static getPeakFlowAccent(payload: PeakFlowResultPayload | null): RGB {
    const summary = summarizePeakFlowPayload(payload);
    if (!payload || summary.bestPreFlow == null) return COLORS.brandBlue;
    const hex = getPeakFlowScoreColor(payload);
    const normalizedHex = hex.replace('#', '');
    const r = Number.parseInt(normalizedHex.slice(0, 2), 16);
    const g = Number.parseInt(normalizedHex.slice(2, 4), 16);
    const b = Number.parseInt(normalizedHex.slice(4, 6), 16);
    return Number.isFinite(r) && Number.isFinite(g) && Number.isFinite(b) ? [r, g, b] : COLORS.brandBlue;
  }

  private static getGaitSpeedAccent(payload: GaitSpeedResultPayload | null): RGB {
    const summary = summarizeGaitSpeedResult(payload);
    if (!payload || !summary.completed || summary.bestSpeedMs == null) return COLORS.brandBlue;
    const hex = getGaitSpeedScoreColor(
      summary.bestSpeedMs,
      summary.completed,
      summary.physicalAssistance,
      summary.referenceComparison,
    );
    const normalizedHex = hex.replace('#', '');
    const r = Number.parseInt(normalizedHex.slice(0, 2), 16);
    const g = Number.parseInt(normalizedHex.slice(2, 4), 16);
    const b = Number.parseInt(normalizedHex.slice(4, 6), 16);
    return Number.isFinite(r) && Number.isFinite(g) && Number.isFinite(b) ? [r, g, b] : COLORS.brandBlue;
  }

  private static getHADSAccent(score: number | null): RGB {
    if (typeof score !== 'number') return COLORS.brandBlue;
    return score >= 9 ? [239, 68, 68] : [34, 197, 94];
  }

  private static getMLHFQAccent(total: number | null): RGB {
    if (typeof total !== 'number') return COLORS.brandBlue;
    if (total <= 23) return [16, 185, 129];
    if (total <= 45) return [245, 158, 11];
    return [239, 68, 68];
  }

  private static getPFSDQAccent(total: number | null): RGB {
    if (typeof total !== 'number') return COLORS.brandBlue;
    if (total <= 100) return [16, 185, 129];
    if (total <= 200) return [245, 158, 11];
    return [239, 68, 68];
  }

  private static getSAQAccent(score: number | null): RGB {
    return COLORS.slate600;
  }

  private static getAQLQAccent(score: number | null): RGB {
    return COLORS.slate600;
  }

  private static getSGRQAccent(total: number | null): RGB {
    return COLORS.slate600;
  }

  private static trimLineToWidth(doc: jsPDF, text: string, maxWidth: number): string {
    const normalized = text.trim();
    if (!normalized) return '';
    if (doc.getTextWidth(normalized) <= maxWidth) return normalized;

    const ellipsis = '...';
    let trimmed = normalized;

    while (trimmed.length > 1 && doc.getTextWidth(`${trimmed}${ellipsis}`) > maxWidth) {
      trimmed = trimmed.slice(0, -1).trimEnd();
    }

    return trimmed ? `${trimmed}${ellipsis}` : ellipsis;
  }

  private static fitTextLines(doc: jsPDF, text: string, maxWidth: number, maxLines: number): string[] {
    const content = String(text || '').trim();
    if (!content) return [];

    const lines = doc.splitTextToSize(content, maxWidth) as string[];
    if (lines.length <= maxLines) return lines;

    const fitted = lines.slice(0, maxLines);
    fitted[maxLines - 1] = this.trimLineToWidth(doc, fitted[maxLines - 1], maxWidth);
    return fitted;
  }

  private static getHeaderContactLines(
    doc: jsPDF,
    branding: ResolvedClientReportBranding,
    maxWidth: number,
    maxLines = 6
  ): string[] {
    const rawLines = [
      branding.addressLine1,
      branding.addressLine2,
      branding.contactLine,
      branding.cnpj && branding.cnpj !== '—' ? `CNPJ: ${branding.cnpj}` : '',
    ].filter((line) => line && line !== '—') as string[];

    return this.fitTextLines(doc, rawLines.join(' | '), maxWidth, maxLines);
  }

  private static async renderHeader(doc: jsPDF, branding: ResolvedClientReportBranding): Promise<number> {
    const pageContext = this.getPDFDocumentPageContext(doc);
    pageContext.branding = branding;
    pageContext.fullHeaderPages.add(doc.getCurrentPageInfo().pageNumber);

    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    const logoSize = 18;
    const headerTop = 9;
    const minHeaderHeight = 38;
    const leftX = margin + 24;
    const rightMaxWidth = 60;
    const columnGap = 8;
    const leftMaxWidth = Math.max(58, pageWidth - margin - leftX - rightMaxWidth - columnGap);
    const logoBase64 = await this.loadImageAsDataUrl(branding.logoPrintUrl);

    doc.setTextColor(...COLORS.slate800);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14.5);
    const titleLines = this.fitTextLines(
      doc,
      branding.displayName || branding.legalName || 'Relatório Clínico',
      leftMaxWidth,
      2
    );

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.slate500);
    const subtitleLines = this.fitTextLines(
      doc,
      branding.reportSubtitle || 'Relatório clínico individual',
      leftMaxWidth,
      1
    );

    doc.setFontSize(7.8);
    const contactLines = this.getHeaderContactLines(doc, branding, rightMaxWidth, 5);

    const leftTextHeight =
      titleLines.length * 5.8 +
      (subtitleLines.length > 0 ? 1.8 + subtitleLines.length * 4 : 0);
    const rightTextHeight = Math.max(contactLines.length, 1) * 3.8;
    const contentHeight = Math.max(logoSize, leftTextHeight, rightTextHeight);
    const headerHeight = Math.max(minHeaderHeight, headerTop + contentHeight + 7);
    const logoY = headerTop + Math.max(0, (contentHeight - logoSize) / 2);
    const titleStartY = headerTop + Math.max(5, (contentHeight - leftTextHeight) / 2) + 4.5;
    const contactStartY = headerTop + Math.max(4, (contentHeight - rightTextHeight) / 2) + 2.8;

    doc.setFillColor(...COLORS.slate50);
    doc.rect(0, 0, pageWidth, headerHeight, 'F');

    if (logoBase64) {
      try {
        doc.addImage(logoBase64, 'PNG', margin, logoY, logoSize, logoSize);
      } catch (error) {
        console.warn('Não foi possível renderizar o logo do tenant no PDF V2:', error);
      }
    } else {
      const logoCenterY = logoY + logoSize / 2;
      doc.setFillColor(...COLORS.brandBlue);
      doc.circle(margin + logoSize / 2, logoCenterY, logoSize / 2, 'F');
      doc.setTextColor(...COLORS.white);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.text((branding.displayName || 'R').slice(0, 1).toUpperCase(), margin + logoSize / 2, logoCenterY + 1.5, {
        align: 'center',
      });
    }

    doc.setTextColor(...COLORS.slate800);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(titleLines.length > 1 ? 13.2 : 14.5);
    titleLines.forEach((line, index) => {
      doc.text(line, leftX, titleStartY + index * 5.8);
    });

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.slate500);
    subtitleLines.forEach((line, index) => {
      doc.text(line, leftX, titleStartY + titleLines.length * 5.8 + 1.8 + index * 4);
    });

    doc.setFontSize(7.8);
    contactLines.forEach((line, index) => {
      doc.text(line, pageWidth - margin, contactStartY + index * 3.8, {
        align: 'right',
      });
    });

    return headerHeight + 7;
  }

  private static renderPatientRibbon(
    doc: jsPDF,
    result: AssessmentResultV2,
    y: number,
    labelOverride?: string,
  ): number {
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    const width = pageWidth - margin * 2;
    const testLabel =
      labelOverride?.trim()
      || this.getAssessmentPDFLabel(result.testKey || 'CAT');
    this.getPDFDocumentPageContext(doc).testLabel = testLabel;

    doc.setFillColor(...COLORS.brandBlue);
    doc.roundedRect(margin, y, width, 13, 2.5, 2.5, 'F');

    doc.setTextColor(...COLORS.white);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9.6);
    const displayLabel = this.trimLineToWidth(doc, testLabel, width - 10);
    doc.text(displayLabel, pageWidth / 2, y + 7.9, { align: 'center' });

    return y + 18;
  }

  private static formatNumber(value: number | null, decimals = 1): string {
    if (value == null || Number.isNaN(value)) return '—';
    return Number(value.toFixed(decimals)).toString().replace('.', ',');
  }

  private static renderPatientClinicalCard(doc: jsPDF, result: AssessmentResultV2, y: number): number {
    this.getPDFDocumentPageContext(doc).testLabel = this.getAssessmentPDFLabel(result.testKey || 'CAT');
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    const width = pageWidth - margin * 2;
    const context = this.extractPatientContext(result);
    const sexValue = context.sex?.trim().toLowerCase();
    const sexLabel = sexValue === 'male' || sexValue === 'm' ? 'Masculino'
      : sexValue === 'female' || sexValue === 'f' ? 'Feminino' : context.sex;
    const assessmentDate = new Date(result.assessmentDate);
    const dateLabel = Number.isNaN(assessmentDate.getTime()) ? '—' : assessmentDate.toLocaleDateString('pt-BR');
    const timeLabel = Number.isNaN(assessmentDate.getTime())
      ? '—'
      : assessmentDate.toLocaleTimeString('pt-BR', {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        });
    const fields = [
      { label: 'Nome', value: result.patientNameSnapshot || '—' },
      { label: isAssessmentV2ReportKey(result.testKey) ? 'Emissão' : 'Data do exame', value: dateLabel },
      { label: 'Hora', value: timeLabel },
      { label: 'DN', value: this.formatDateOnly(context.birthDate) },
      {
        label: 'Idade',
        value: context.ageYears != null ? `${context.ageYears} anos` : '—',
      },
      { label: 'Sexo', value: sexLabel || '—' },
      {
        label: 'Peso',
        value: context.weightKg != null ? `${this.formatNumber(context.weightKg, 1)} kg` : '—',
      },
      {
        label: 'Altura',
        value: context.heightCm != null ? `${this.formatNumber(context.heightCm, 0)} cm` : '—',
      },
      { label: 'Identificação', value: context.identifier || '—' },
    ];

    doc.setDrawColor(...COLORS.slate200);
    doc.setFillColor(...COLORS.white);
    doc.roundedRect(margin, y, width, 26, 3, 3, 'FD');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.2);
    doc.setTextColor(...COLORS.slate500);
    doc.text('Dados do paciente', margin + 4, y + 5.5);

    const columnWidth = width / 3;
    fields.forEach((field, index) => {
      const column = index % 3;
      const row = Math.floor(index / 3);
      const x = margin + 4 + column * columnWidth;
      const currentY = y + 10 + row * 5.8;
      const labelText = `${field.label}:`;

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7.5);
      doc.setTextColor(...COLORS.slate500);
      doc.text(labelText, x, currentY);

      doc.setFont('helvetica', 'normal');
      doc.setTextColor(...COLORS.slate800);
      const valueX = x + doc.getTextWidth(labelText) + 3;
      doc.text(String(field.value), valueX, currentY);
    });

    return y + 31;
  }

  private static renderAssessmentReportMetadataCard(
    doc: jsPDF,
    y: number,
    fields: Array<{ label: string; value: string; fullWidth?: boolean }>,
  ): number {
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    const width = pageWidth - margin * 2;
    const innerPadding = 4;
    const innerWidth = width - innerPadding * 2;
    const columnGap = 7;
    const columnWidth = (innerWidth - columnGap) / 2;
    const headerHeight = 4;
    const rowGap = 1;
    const valueLineHeight = 3.7;
    const rows: Array<Array<{ label: string; value: string; fullWidth?: boolean }>> = [];

    fields.forEach((field) => {
      if (field.fullWidth) {
        rows.push([field]);
        return;
      }
      const previous = rows[rows.length - 1];
      if (previous && previous.length === 1 && !previous[0].fullWidth) {
        previous.push(field);
        return;
      }
      rows.push([field]);
    });

    const measuredRows = rows.map((row) => {
      const cells = row.map((field) => {
        const availableWidth = field.fullWidth ? innerWidth : columnWidth;
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7.4);
        const labelText = `${field.label}:`;
        const labelWidth = doc.getTextWidth(labelText);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        const valueWidth = Math.max(22, availableWidth - labelWidth - 2.5);
        const valueLines = doc.splitTextToSize(field.value, valueWidth) as string[];
        return {
          ...field,
          labelText,
          labelWidth,
          valueLines: valueLines.length > 0 ? valueLines : ['Não informado'],
        };
      });
      const height =
        Math.max(...cells.map((cell) => cell.valueLines.length), 1) * valueLineHeight
        + 1.6;
      return { cells, height };
    });
    const cardHeight =
      headerHeight
      + measuredRows.reduce((total, row) => total + row.height, 0)
      + Math.max(0, measuredRows.length - 1) * rowGap
      + 3;

    doc.setDrawColor(...COLORS.slate200);
    doc.setFillColor(...COLORS.white);
    doc.roundedRect(margin, y, width, cardHeight, 3, 3, 'FD');

    let currentY = y + headerHeight + 2;
    measuredRows.forEach((row, rowIndex) => {
      row.cells.forEach((cell, cellIndex) => {
        const x =
          margin
          + innerPadding
          + (cell.fullWidth ? 0 : cellIndex * (columnWidth + columnGap));
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7.4);
        doc.setTextColor(...COLORS.slate500);
        doc.text(cell.labelText, x, currentY);

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(...COLORS.slate800);
        const valueX = x + cell.labelWidth + 2.5;
        cell.valueLines.forEach((line, lineIndex) => {
          doc.text(line, valueX, currentY + lineIndex * valueLineHeight);
        });
      });

      currentY += row.height;
      if (rowIndex < measuredRows.length - 1) {
        doc.setDrawColor(...COLORS.slate100);
        doc.line(
          margin + innerPadding,
          currentY - 0.8,
          margin + width - innerPadding,
          currentY - 0.8,
        );
        currentY += rowGap;
      }
    });

    return y + cardHeight + 5;
  }

  private static renderScoreCard(doc: jsPDF, result: AssessmentResultV2, y: number, gradientBarDataUrl: string | null): number {
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    const contentWidth = pageWidth - margin * 2;
    const leftWidth = 88;
    const gap = 6;
    const rightWidth = contentWidth - leftWidth - gap;
    const cardHeight = 48;
    const { total } = this.extractCATPayload(result);
    const scoreColor = this.getScoreAccent(total);
    const rangeLabel = getCATSeverityRangeLabel(total);
    const interpretation = resolveAssessmentInterpretationText(this.extractCATPayload(result), getCATInterpretationText(total));
    const rightX = margin + leftWidth + gap;
    const interpretationWidth = Math.max(40, rightWidth - 4);
    const interpretationLines = doc.splitTextToSize(interpretation, interpretationWidth);
    const barY = Math.max(y + 31, y + 18 + interpretationLines.length * 4.2 + 4);
    const resolvedCardHeight = Math.max(cardHeight, barY + 11 - y);

    doc.setDrawColor(...COLORS.slate200);
    doc.setFillColor(...COLORS.white);
    doc.roundedRect(margin, y, leftWidth, resolvedCardHeight, 4, 4, 'FD');

    doc.setTextColor(...COLORS.slate800);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text('PONTUAÇÃO TOTAL', margin + leftWidth / 2, y + 10, {
      align: 'center',
    });

    doc.setTextColor(...scoreColor);
    doc.setFontSize(42);
    doc.text(typeof total === 'number' ? String(total) : '—', margin + leftWidth / 2, y + 28, { align: 'center' });

    this.renderQuestionnaireClassification(doc, rangeLabel, margin + leftWidth / 2, y + resolvedCardHeight - 8, scoreColor);

    doc.setTextColor(...COLORS.slate800);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.text('Interpretação', rightX, y + 10);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.8);
    doc.setTextColor(...COLORS.slate600);
    doc.text(interpretationLines, rightX, y + 18);

    const barHeight = 8;

    if (gradientBarDataUrl) {
      doc.addImage(gradientBarDataUrl, 'PNG', rightX, barY, rightWidth, barHeight);
    } else {
      this.renderGradientBarFallback(doc, rightX, barY, rightWidth, barHeight);
    }

    if (typeof total === 'number') {
      const markerX = rightX + (Math.max(0, Math.min(40, total)) / 40) * rightWidth;
      doc.setDrawColor(...COLORS.slate900);
      doc.setLineWidth(1.2);
      doc.line(markerX, barY - 3, markerX, barY + barHeight + 3);
    }

    return y + resolvedCardHeight + 10;
  }

  private static renderGradientBarFallback(doc: jsPDF, x: number, y: number, width: number, height: number): void {
    const steps = 48;
    const colors: RGB[] = [
      [28, 200, 160],
      [156, 210, 110],
      [242, 214, 26],
      [255, 158, 68],
      [244, 63, 94],
    ];

    for (let step = 0; step < steps; step += 1) {
      const progress = step / (steps - 1);
      const section = Math.min(colors.length - 2, Math.floor(progress * (colors.length - 1)));
      const localProgress = progress * (colors.length - 1) - section;
      const from = colors[section];
      const to = colors[section + 1];
      const color: RGB = [
        Math.round(from[0] + (to[0] - from[0]) * localProgress),
        Math.round(from[1] + (to[1] - from[1]) * localProgress),
        Math.round(from[2] + (to[2] - from[2]) * localProgress),
      ];
      const segmentWidth = width / steps;

      doc.setFillColor(...color);
      doc.roundedRect(x + step * segmentWidth, y, segmentWidth + 0.3, height, height / 2, height / 2, 'F');
    }
  }

  private static renderQuestionnaireClassification(
    doc: jsPDF,
    label: string,
    centerX: number,
    baselineY: number,
    color: RGB,
  ): void {
    const radius = 1.05;
    const gap = 1.5;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    const textWidth = doc.getTextWidth(label);
    const groupWidth = radius * 2 + gap + textWidth;
    const startX = centerX - groupWidth / 2;
    doc.setFillColor(...color);
    doc.circle(startX + radius, baselineY - 1, radius, 'F');
    doc.setTextColor(...color);
    doc.text(label, startX + radius * 2 + gap, baselineY);
  }

  private static renderTUGGradientBar(doc: jsPDF, x: number, y: number, width: number, height: number): void {
    const toRgb = (hexColor: string): RGB => {
      const normalized = hexColor.replace('#', '');
      return [Number.parseInt(normalized.slice(0, 2), 16), Number.parseInt(normalized.slice(2, 4), 16), Number.parseInt(normalized.slice(4, 6), 16)];
    };
    const colorAt = (progress: number): RGB => {
      for (let index = 0; index < TUG_BAR_SCALE_TICKS.length - 1; index += 1) {
        const start = TUG_BAR_SCALE_TICKS[index];
        const end = TUG_BAR_SCALE_TICKS[index + 1];
        if (progress > end.position) continue;

        const from = toRgb(start.color);
        const to = toRgb(end.color);
        const localProgress = (progress - start.position) / (end.position - start.position);
        return [
          Math.round(from[0] + (to[0] - from[0]) * localProgress),
          Math.round(from[1] + (to[1] - from[1]) * localProgress),
          Math.round(from[2] + (to[2] - from[2]) * localProgress),
        ];
      }

      return toRgb(TUG_BAR_SCALE_TICKS[TUG_BAR_SCALE_TICKS.length - 1].color);
    };

    const radius = height / 2;
    const innerWidth = Math.max(0, width - height);
    const steps = 72;
    const segmentWidth = innerWidth / steps;

    doc.setFillColor(...toRgb(TUG_BAR_SCALE_TICKS[0].color));
    doc.circle(x + radius, y + radius, radius, 'F');
    for (let step = 0; step < steps; step += 1) {
      const progress = (step + 0.5) / steps;
      doc.setFillColor(...colorAt(progress));
      doc.rect(x + radius + step * segmentWidth, y, segmentWidth + 0.15, height, 'F');
    }
    doc.setFillColor(...toRgb(TUG_BAR_SCALE_TICKS[TUG_BAR_SCALE_TICKS.length - 1].color));
    doc.circle(x + width - radius, y + radius, radius, 'F');
  }

  private static renderGaitSpeedGradientBar(doc: jsPDF, x: number, y: number, width: number, height: number): void {
    const toRgb = (hexColor: string): RGB => {
      const normalized = hexColor.replace('#', '');
      return [Number.parseInt(normalized.slice(0, 2), 16), Number.parseInt(normalized.slice(2, 4), 16), Number.parseInt(normalized.slice(4, 6), 16)];
    };
    const colorAt = (progress: number): RGB => {
      for (let index = 0; index < GAIT_SPEED_BAR_SCALE_TICKS.length - 1; index += 1) {
        const start = GAIT_SPEED_BAR_SCALE_TICKS[index];
        const end = GAIT_SPEED_BAR_SCALE_TICKS[index + 1];
        if (progress > end.position) continue;

        const from = toRgb(start.color);
        const to = toRgb(end.color);
        const localProgress = (progress - start.position) / (end.position - start.position);
        return [
          Math.round(from[0] + (to[0] - from[0]) * localProgress),
          Math.round(from[1] + (to[1] - from[1]) * localProgress),
          Math.round(from[2] + (to[2] - from[2]) * localProgress),
        ];
      }

      return toRgb(GAIT_SPEED_BAR_SCALE_TICKS[GAIT_SPEED_BAR_SCALE_TICKS.length - 1].color);
    };

    const radius = height / 2;
    const innerWidth = Math.max(0, width - height);
    const steps = 72;
    const segmentWidth = innerWidth / steps;

    doc.setFillColor(...toRgb(GAIT_SPEED_BAR_SCALE_TICKS[0].color));
    doc.circle(x + radius, y + radius, radius, 'F');
    for (let step = 0; step < steps; step += 1) {
      const progress = (step + 0.5) / steps;
      doc.setFillColor(...colorAt(progress));
      doc.rect(x + radius + step * segmentWidth, y, segmentWidth + 0.15, height, 'F');
    }
    doc.setFillColor(...toRgb(GAIT_SPEED_BAR_SCALE_TICKS[GAIT_SPEED_BAR_SCALE_TICKS.length - 1].color));
    doc.circle(x + width - radius, y + radius, radius, 'F');
  }

  private static renderResultTable(doc: jsPDF, result: AssessmentResultV2, startY: number): number {
    const margin = 15;
    const pageWidth = doc.internal.pageSize.getWidth();
    const contentWidth = pageWidth - margin * 2;
    const { answers } = this.extractCATPayload(result);

    const rows = CAT_ITEM_DEFINITIONS.map((item, index) => {
      const value = typeof answers[index] === 'number' && answers[index] >= 0 ? answers[index] : null;
      return [item.fullLabel, `${value ?? '—'} / 5`, getCATItemImpactLabel(value)];
    });

    this.runAutoTable(doc, {
      startY,
      margin: { left: margin, right: margin },
      head: [['Domínio avaliado', 'Score', 'Interpretação']],
      body: rows,
      theme: 'striped',
      headStyles: {
        fillColor: COLORS.brandBlue,
        textColor: COLORS.white,
        fontSize: 9,
        fontStyle: 'bold',
        halign: 'left',
      },
      bodyStyles: {
        fontSize: 7.8,
        textColor: COLORS.slate800,
        cellPadding: 2.3,
      },
      alternateRowStyles: {
        fillColor: [250, 252, 255],
      },
      columnStyles: {
        0: { cellWidth: contentWidth * 0.54, fontStyle: 'bold' },
        1: {
          cellWidth: contentWidth * 0.16,
          halign: 'center',
          fontStyle: 'bold',
        },
        2: { cellWidth: contentWidth * 0.3 },
      },
    });

    return (doc as any).lastAutoTable?.finalY ?? startY;
  }

  private static renderACTScoreCard(doc: jsPDF, result: AssessmentResultV2, y: number): number {
    const payload = this.extractACTPayload(result);
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    const contentWidth = pageWidth - margin * 2;
    const leftWidth = 72;
    const gap = 6;
    const rightWidth = contentWidth - leftWidth - gap;
    const total = payload?.total ?? null;
    const severity = getACTSeverityLabel(total);
    const interpretation = resolveAssessmentInterpretationText(payload, getACTInterpretationText(total));
    const interpretationLines = doc.splitTextToSize(interpretation, rightWidth - 4);
    const thresholds = ['20–25: controlada', '16–19: parcialmente controlada', '5–15: não controlada'];
    const cardHeight = Math.max(48, 22 + interpretationLines.length * 4.2 + thresholds.length * 4.2);
    const rightX = margin + leftWidth + gap;

    doc.setDrawColor(...COLORS.slate200);
    doc.setFillColor(...COLORS.white);
    doc.roundedRect(margin, y, leftWidth, cardHeight, 4, 4, 'FD');

    doc.setTextColor(...COLORS.slate800);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text('ACT TOTAL', margin + leftWidth / 2, y + 10, { align: 'center' });

    doc.setTextColor(...this.getACTAccent(total));
    doc.setFontSize(28);
    doc.text(typeof total === 'number' ? String(total) : '—', margin + leftWidth / 2, y + 24, { align: 'center' });

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...COLORS.slate600);
    doc.text(typeof total === 'number' ? `${total}/25` : 'Pontuação indisponível', margin + leftWidth / 2, y + 31, {
      align: 'center',
    });

    this.renderQuestionnaireClassification(doc, severity, margin + leftWidth / 2, y + cardHeight - 8, this.getACTAccent(total));

    doc.setTextColor(...COLORS.slate800);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.text('Interpretação', rightX, y + 10);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(...COLORS.slate600);
    doc.text(interpretationLines, rightX, y + 18);

    const thresholdsY = y + 20 + interpretationLines.length * 4.2 + 3;
    thresholds.forEach((label, index) => {
      doc.setFillColor(...COLORS.slate50);
      doc.setDrawColor(...COLORS.slate200);
      doc.roundedRect(rightX, thresholdsY + index * 5.2, rightWidth, 4.2, 2, 2, 'FD');
      doc.text(label, rightX + 3, thresholdsY + 2.9 + index * 5.2);
    });

    return y + cardHeight + 8;
  }

  private static renderACTTable(doc: jsPDF, result: AssessmentResultV2, startY: number): number {
    const payload = this.extractACTPayload(result);
    const rows = ACT_QUESTIONS.map((question, index) => {
      const score = typeof payload?.answers[index] === 'number' && payload.answers[index] > 0 ? payload.answers[index] : null;
      const option = question.options.find((item) => item.score === score);
      return [String(index + 1), question.prompt, option?.label ?? 'Não respondido', score != null ? String(score) : '—'];
    });

    this.runAutoTable(doc, {
      startY,
      margin: { left: 15, right: 15 },
      head: [['#', 'Questão', 'Resposta selecionada', 'Pontuação']],
      body: rows,
      theme: 'striped',
      headStyles: {
        fillColor: COLORS.brandBlue,
        textColor: COLORS.white,
        fontSize: 8.6,
        fontStyle: 'bold',
      },
      bodyStyles: {
        fontSize: 7.4,
        textColor: COLORS.slate800,
        cellPadding: 2.2,
      },
      alternateRowStyles: {
        fillColor: [250, 252, 255],
      },
      columnStyles: {
        0: { cellWidth: 10, halign: 'center', fontStyle: 'bold' },
        1: { cellWidth: 86, fontStyle: 'bold' },
        2: { cellWidth: 66 },
        3: { cellWidth: 18, halign: 'center', fontStyle: 'bold' },
      },
    });

    return (doc as any).lastAutoTable?.finalY ?? startY;
  }

  private static renderACTHistoryTable(doc: jsPDF, previousResults: AssessmentResultV2[], startY: number): number {
    if (!previousResults.length) return startY;

    const rows = previousResults.slice(0, 5).map((item) => {
      const payload = this.extractACTPayload(item);
      return [
        this.formatDate(item.assessmentDate),
        payload?.total != null ? String(payload.total) : '—',
        getACTSeverityLabel(payload?.total ?? null),
      ];
    });

    this.runAutoTable(doc, {
      startY,
      margin: { left: 15, right: 15 },
      head: [['Histórico recente', 'Pontuação', 'Classificação']],
      body: rows,
      theme: 'grid',
      headStyles: {
        fillColor: [248, 250, 252],
        textColor: COLORS.slate600,
        fontSize: 8,
        fontStyle: 'bold',
      },
      bodyStyles: {
        fontSize: 8.2,
        textColor: COLORS.slate800,
        cellPadding: 3.2,
      },
      styles: {
        lineColor: COLORS.slate200,
        lineWidth: 0.1,
      },
      columnStyles: {
        0: { cellWidth: 58 },
        1: { cellWidth: 26, halign: 'center', fontStyle: 'bold' },
        2: { cellWidth: 96 },
      },
    });

    return (doc as any).lastAutoTable?.finalY ?? startY;
  }

  private static renderMMRCScoreCard(doc: jsPDF, result: AssessmentResultV2, y: number, gradientBarDataUrl: string | null): number {
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    const contentWidth = pageWidth - margin * 2;
    const leftWidth = 88;
    const gap = 6;
    const rightWidth = contentWidth - leftWidth - gap;
    const cardHeight = 48;
    const { grade, description } = this.extractMMRCPayload(result);
    const scoreColor = this.getMMRCAccent(grade);
    const rangeLabel = getMMRCSeverityRangeLabel(grade);
    const interpretation = resolveAssessmentInterpretationText(this.extractMMRCPayload(result), getMMRCInterpretationText(grade));
    const rightX = margin + leftWidth + gap;
    const interpretationWidth = Math.max(40, rightWidth - 4);
    const interpretationLines = doc.splitTextToSize(`${interpretation} ${description}`, interpretationWidth);
    const barY = Math.max(y + 31, y + 18 + interpretationLines.length * 4.2 + 4);
    const resolvedCardHeight = Math.max(cardHeight, barY + 11 - y);

    doc.setDrawColor(...COLORS.slate200);
    doc.setFillColor(...COLORS.white);
    doc.roundedRect(margin, y, leftWidth, resolvedCardHeight, 4, 4, 'FD');

    doc.setTextColor(...COLORS.slate800);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text('ESCALA mMRC', margin + leftWidth / 2, y + 10, {
      align: 'center',
    });

    doc.setTextColor(...scoreColor);
    doc.setFontSize(42);
    doc.text(typeof grade === 'number' ? String(grade) : '—', margin + leftWidth / 2, y + 28, { align: 'center' });

    this.renderQuestionnaireClassification(doc, rangeLabel, margin + leftWidth / 2, y + resolvedCardHeight - 8, scoreColor);

    doc.setTextColor(...COLORS.slate800);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.text('Interpretação', rightX, y + 10);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.7);
    doc.setTextColor(...COLORS.slate600);
    doc.text(interpretationLines, rightX, y + 18);

    const barHeight = 8;

    if (gradientBarDataUrl) {
      doc.addImage(gradientBarDataUrl, 'PNG', rightX, barY, rightWidth, barHeight);
    } else {
      this.renderGradientBarFallback(doc, rightX, barY, rightWidth, barHeight);
    }

    if (typeof grade === 'number') {
      const markerX = rightX + (Math.max(0, Math.min(4, grade)) / 4) * rightWidth;
      doc.setDrawColor(...COLORS.slate900);
      doc.setLineWidth(1.2);
      doc.line(markerX, barY - 3, markerX, barY + barHeight + 3);
    }

    return y + resolvedCardHeight + 10;
  }

  private static renderMMRCTable(doc: jsPDF, result: AssessmentResultV2, startY: number): number {
    const { grade } = this.extractMMRCPayload(result);

    const rows = MMRC_ITEMS.map((item) => [
      String(item.grade),
      item.text,
      item.grade === grade ? 'Selecionado' : '—',
    ]);

    this.runAutoTable(doc, {
      startY,
      margin: { left: 15, right: 15 },
      head: [['Grau', 'Descrição funcional', 'Status']],
      body: rows,
      theme: 'striped',
      headStyles: {
        fillColor: COLORS.brandBlue,
        textColor: COLORS.white,
        fontSize: 9,
        fontStyle: 'bold',
        halign: 'left',
      },
      bodyStyles: {
        fontSize: 8.6,
        textColor: COLORS.slate800,
        cellPadding: 3.6,
      },
      alternateRowStyles: {
        fillColor: [250, 252, 255],
      },
      didParseCell: (hookData) => {
        if (hookData.section === 'body' && hookData.column.index === 2 && hookData.cell.raw === 'Selecionado') {
          hookData.cell.styles.fillColor = [219, 234, 254];
          hookData.cell.styles.textColor = [29, 78, 216];
          hookData.cell.styles.fontStyle = 'bold';
        }
      },
      columnStyles: {
        0: { cellWidth: 22, halign: 'center', fontStyle: 'bold' },
        1: { cellWidth: 128 },
        2: { cellWidth: 30, halign: 'center' },
      },
    });

    return (doc as any).lastAutoTable?.finalY ?? startY;
  }

  private static renderTUGScoreCard(doc: jsPDF, result: AssessmentResultV2, y: number): number {
    const payload = this.extractTUGPayload(result);
    const summary = summarizeTugResult(payload);
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    const contentWidth = pageWidth - margin * 2;
    const leftWidth = 88;
    const gap = 6;
    const rightWidth = contentWidth - leftWidth - gap;
    const cardHeight = 56;
    const rightX = margin + leftWidth + gap;
    const scoreColor = this.getTUGAccent(payload);
    const severity = summary.severity;
    const interpretation = summary.interpretation.replace(
      /\s+O melhor tempo entre \d+ tentativas registradas foi utilizado para a interpretação\.$/,
      '',
    );
    const bestAttemptLabel = summary.bestAttempt ? `${summary.bestAttempt}ª tentativa` : '—';
    const normalizedAidLabel = summary.walkingAidLabel.toLocaleLowerCase('pt-BR');
    const walkingAidMeta =
      normalizedAidLabel === 'sem dispositivo'
        ? 'sem dispositivo de marcha'
        : normalizedAidLabel === 'sem informação'
          ? 'dispositivo de marcha não informado'
          : `com ${normalizedAidLabel}`;
    const attemptMetaLabel = `${summary.attemptCount === 2 ? `Melhor de 2 tentativas • ${bestAttemptLabel}` : '1 tentativa registrada'} • ${walkingAidMeta}`;
    const interpretationY = y + 16;
    const interpretationLines = doc.splitTextToSize(interpretation, Math.max(40, rightWidth - 4));
    const interpretationLineHeight = 3.8;
    const barHeight = 6;
    const barY = Math.max(y + 34, interpretationY + interpretationLines.length * interpretationLineHeight + 3);
    const labelsY = barY + barHeight + 4;
    const rangeY = labelsY + 5;
    const resolvedCardHeight = Math.max(cardHeight, rangeY + 3 - y);
    const markerProgress = getTUGBarProgress(summary.bestTimeSeconds);

    doc.setDrawColor(...COLORS.slate200);
    doc.setFillColor(...COLORS.white);
    doc.roundedRect(margin, y, leftWidth, resolvedCardHeight, 4, 4, 'FD');

    doc.setTextColor(...COLORS.brandBlue);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.2);
    doc.text('TIMED UP AND GO', margin + leftWidth / 2, y + 7, {
      align: 'center',
    });

    doc.setTextColor(...scoreColor);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(30);
    doc.text(summary.completed && summary.bestTimeSeconds != null ? formatTugSeconds(summary.bestTimeSeconds) : '—', margin + leftWidth / 2, y + 20, {
      align: 'center',
    });

    doc.setTextColor(...COLORS.slate600);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.8);
    doc.text(summary.completed && summary.bestTimeSeconds != null ? 'segundos' : 'não concluído', margin + leftWidth / 2, y + 26, {
      align: 'center',
    });

    doc.setTextColor(...scoreColor);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.2);
    const severityTextWidth = doc.getTextWidth(severity);
    const severityDotRadius = 0.75;
    const severityGap = 1.4;
    const severityGroupWidth = severityDotRadius * 2 + severityGap + severityTextWidth;
    const severityStartX = margin + (leftWidth - severityGroupWidth) / 2;
    doc.setFillColor(...scoreColor);
    doc.circle(severityStartX + severityDotRadius, y + 33.2, severityDotRadius, 'F');
    doc.text(severity, severityStartX + severityDotRadius * 2 + severityGap, y + 34.2);

    doc.setTextColor(...COLORS.slate500);
    doc.setFont('helvetica', 'normal');
    let metaFontSize = 6.3;
    doc.setFontSize(metaFontSize);
    while (metaFontSize > 5.2 && doc.getTextWidth(attemptMetaLabel) > leftWidth - 8) {
      metaFontSize -= 0.2;
      doc.setFontSize(metaFontSize);
    }
    doc.text(attemptMetaLabel, margin + leftWidth / 2, y + 42, {
      align: 'center',
    });

    doc.setTextColor(...COLORS.slate800);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('Interpretação', rightX, y + 8);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.2);
    doc.setTextColor(...COLORS.slate600);
    doc.text(interpretationLines, rightX, interpretationY);

    this.renderTUGGradientBar(doc, rightX, barY, rightWidth, barHeight);
    if (markerProgress != null) {
      const markerX = rightX + markerProgress * rightWidth;
      doc.setDrawColor(...COLORS.slate900);
      doc.setLineWidth(1.1);
      doc.line(markerX, barY - 2, markerX, barY + barHeight + 2);
    }

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(5.8);
    doc.setTextColor(...COLORS.slate500);
    TUG_BAR_SCALE_TICKS.forEach((tick, index) => {
      const tickX = rightX + tick.position * rightWidth;
      doc.text(tick.label, tickX, labelsY, {
        align: index === 0 ? 'left' : index === TUG_BAR_SCALE_TICKS.length - 1 ? 'right' : 'center',
      });
    });

    const rangeLabel = this.normalizePdfParagraphText(summary.severityRange);
    doc.setFontSize(7.2);
    doc.setTextColor(...COLORS.slate600);
    doc.text('Faixa atual:', rightX, rangeY);
    const rangeValueX = rightX + doc.getTextWidth('Faixa atual:') + 1.2;
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.slate800);
    doc.text(rangeLabel, rangeValueX, rangeY);

    return y + resolvedCardHeight + 6;
  }

  private static renderTUGProtocolTable(doc: jsPDF, result: AssessmentResultV2, startY: number): number {
    const payload = this.extractTUGPayload(result);
    if (!payload) return startY;
    const summary = summarizeTugResult(payload);
    const attempts = getTugAttemptEntries(payload);
    const attempt1 = attempts.find((entry) => entry.index === 1)?.attempt ?? null;
    const attempt2 = attempts.find((entry) => entry.index === 2)?.attempt ?? null;
    const formatAttemptValue = (attempt: typeof attempt1) =>
      !attempt
        ? '—'
        : attempt.completed !== false && attempt.timeSeconds != null
          ? `${formatTugSeconds(attempt.timeSeconds)} s`
          : attempt.reasonNotCompleted || 'Não concluiu';

    const rows = [
      ['1ª tentativa', formatAttemptValue(attempt1)],
      ...(attempt2 ? [['2ª tentativa', formatAttemptValue(attempt2)]] : []),
      ['Auxílio de marcha', summary.walkingAidLabel],
      ['Familiarização', summary.familiarizationPerformed ? 'Sim' : 'Não registrada'],
      ['Assistência física', summary.physicalAssistance ? 'Sim' : 'Não'],
      ['Distância do percurso', `${payload.protocol?.distanceMeters ?? TUG_STANDARD_PROTOCOL.distanceMeters} m`],
      ['Altura da cadeira', `${payload.protocol?.chairHeightCm ?? TUG_STANDARD_PROTOCOL.chairHeightCm} cm`],
    ];
    const compactRows: string[][] = [];
    for (let index = 0; index < rows.length; index += 2) {
      compactRows.push([...rows[index], ...(rows[index + 1] ?? ['', ''])]);
    }

    this.runAutoTable(doc, {
      startY,
      margin: { left: 15, right: 15 },
      head: [['Item', 'Registro', 'Item', 'Registro']],
      body: compactRows,
      theme: 'striped',
      headStyles: {
        fillColor: COLORS.brandBlue,
        textColor: COLORS.white,
        fontSize: 7.2,
        fontStyle: 'bold',
        halign: 'left',
        cellPadding: 1.6,
      },
      bodyStyles: {
        fontSize: 6.8,
        textColor: COLORS.slate800,
        cellPadding: 1.5,
        valign: 'middle',
      },
      alternateRowStyles: {
        fillColor: [250, 252, 255],
      },
      didParseCell: (hookData) => {
        if (hookData.section === 'body' && (hookData.column.index === 1 || hookData.column.index === 3)) {
          hookData.cell.styles.fontStyle = 'bold';
        }
      },
      columnStyles: {
        0: { cellWidth: 39, fontStyle: 'bold' },
        1: { cellWidth: 47 },
        2: { cellWidth: 40, fontStyle: 'bold' },
        3: { cellWidth: 54 },
      },
    });

    return (doc as any).lastAutoTable?.finalY ?? startY;
  }

  private static renderTUGInterpretationBlock(doc: jsPDF, result: AssessmentResultV2, startY: number): number {
    const payload = this.extractTUGPayload(result);
    const summary = summarizeTugResult(payload);
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    const width = pageWidth - margin * 2;
    const evidenceNote = `${getTUGReferenceComparisonNote(summary.referenceComparison, payload?.patientContext)} Referência: Furlanetto et al., 2022.`;
    const evidenceLines = doc.splitTextToSize(evidenceNote, width - 8);
    const observationLines = payload?.observations ? doc.splitTextToSize(payload.observations, width - 14) : [];
    const evidenceLineHeight = 3.5;
    const observationBlockHeight = observationLines.length ? 7 + observationLines.length * 3.6 : 0;
    const height = 11 + evidenceLines.length * evidenceLineHeight + (observationLines.length ? observationBlockHeight + 4 : 0);

    doc.setDrawColor(...COLORS.slate200);
    doc.setFillColor(...COLORS.white);
    doc.roundedRect(margin, startY, width, height, 3, 3, 'FD');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.2);
    doc.setTextColor(...COLORS.slate800);
    doc.text('Referência individual', margin + 4, startY + 5);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.3);
    doc.setTextColor(...COLORS.slate600);
    doc.text(evidenceLines, margin + 4, startY + 9.5);

    if (observationLines.length) {
      const observationY = startY + 9.5 + evidenceLines.length * evidenceLineHeight + 1.5;
      doc.setFillColor(248, 250, 252);
      doc.roundedRect(margin + 4, observationY, width - 8, observationBlockHeight, 2.5, 2.5, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7.2);
      doc.setTextColor(...COLORS.slate800);
      doc.text('Observações do teste', margin + 7, observationY + 4);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.1);
      doc.setTextColor(...COLORS.slate600);
      doc.text(observationLines, margin + 7, observationY + 7.5);
    }

    return startY + height + 4;
  }

  private static renderGaitSpeedScoreCard(doc: jsPDF, result: AssessmentResultV2, y: number): number {
    const payload = this.extractGaitSpeedPayload(result);
    const summary = summarizeGaitSpeedResult(payload);
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    const contentWidth = pageWidth - margin * 2;
    const leftWidth = 88;
    const gap = 6;
    const rightWidth = contentWidth - leftWidth - gap;
    const rightX = margin + leftWidth + gap;
    const scoreColor = this.getGaitSpeedAccent(payload);
    const compactInterpretation = (() => {
      if (!summary.completed || summary.bestSpeedMs == null) {
        return 'Teste não concluído. Consulte o motivo registrado nos dados técnicos.';
      }
      if (summary.physicalAssistance) {
        return `Velocidade de ${formatGaitSpeed(summary.bestSpeedMs)} m/s, obtida com assistência física. O resultado deve ser interpretado com cautela.`;
      }
      if (summary.bestSpeedMs < 0.8) {
        return `Velocidade de ${formatGaitSpeed(summary.bestSpeedMs)} m/s, abaixo do ponto de corte de 0,8 m/s usado para classificar marcha lenta em pessoas com DPOC.`;
      }
      if (summary.referenceComparison?.withinNormalLimit === false) {
        return `Velocidade de ${formatGaitSpeed(summary.bestSpeedMs)} m/s, abaixo do limite inferior individual calculado para este paciente.`;
      }
      return `Velocidade de ${formatGaitSpeed(summary.bestSpeedMs)} m/s, dentro da faixa esperada para o ponto de corte clínico adotado.`;
    })();
    const interpretationY = y + 16;
    const interpretationLines = doc.splitTextToSize(compactInterpretation, Math.max(40, rightWidth - 4));
    const interpretationLineHeight = 3.8;
    const barHeight = 6;
    const barY = Math.max(y + 34, interpretationY + interpretationLines.length * interpretationLineHeight + 3);
    const labelsY = barY + barHeight + 4;
    const rangeY = labelsY + 5;
    const resolvedCardHeight = Math.max(56, rangeY + 3 - y);
    const markerProgress = getGaitSpeedBarProgress(summary.bestSpeedMs);
    const bestAttemptLabel = summary.bestAttempt ? `${summary.bestAttempt}ª tentativa` : '—';
    const normalizedAidLabel = summary.walkingAidLabel.toLocaleLowerCase('pt-BR');
    const walkingAidMeta =
      normalizedAidLabel === 'sem dispositivo'
        ? 'sem dispositivo de marcha'
        : normalizedAidLabel === 'sem informação'
          ? 'auxílio de marcha não informado'
          : `com ${normalizedAidLabel}`;
    const attemptMetaLabel = `${
      summary.attemptCount > 1 ? `Melhor de ${summary.attemptCount} tentativas • ${bestAttemptLabel}` : '1 tentativa registrada'
    } • ${walkingAidMeta}`;

    doc.setDrawColor(...COLORS.slate200);
    doc.setFillColor(...COLORS.white);
    doc.roundedRect(margin, y, leftWidth, resolvedCardHeight, 4, 4, 'FD');

    doc.setTextColor(...COLORS.brandBlue);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.2);
    doc.text('VELOCIDADE DE MARCHA EM 4 METROS', margin + leftWidth / 2, y + 7, { align: 'center' });

    doc.setTextColor(...scoreColor);
    doc.setFontSize(32);
    doc.text(summary.completed && summary.bestSpeedMs != null ? formatGaitSpeed(summary.bestSpeedMs) : '—', margin + leftWidth / 2, y + 20, {
      align: 'center',
    });

    doc.setTextColor(...COLORS.slate600);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.8);
    doc.text(summary.completed && summary.bestSpeedMs != null ? 'm/s' : 'não concluído', margin + leftWidth / 2, y + 26, {
      align: 'center',
    });

    doc.setTextColor(...scoreColor);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.2);
    const severity = this.normalizePdfParagraphText(summary.severity);
    const severityTextWidth = doc.getTextWidth(severity);
    const severityDotRadius = 0.75;
    const severityGap = 1.4;
    const severityGroupWidth = severityDotRadius * 2 + severityGap + severityTextWidth;
    const severityStartX = margin + (leftWidth - severityGroupWidth) / 2;
    doc.setFillColor(...scoreColor);
    doc.circle(severityStartX + severityDotRadius, y + 33.2, severityDotRadius, 'F');
    doc.text(severity, severityStartX + severityDotRadius * 2 + severityGap, y + 34.2);

    doc.setTextColor(...COLORS.slate500);
    doc.setFont('helvetica', 'normal');
    let metaFontSize = 6.3;
    doc.setFontSize(metaFontSize);
    while (metaFontSize > 5.2 && doc.getTextWidth(attemptMetaLabel) > leftWidth - 8) {
      metaFontSize -= 0.2;
      doc.setFontSize(metaFontSize);
    }
    doc.text(attemptMetaLabel, margin + leftWidth / 2, y + 42, {
      align: 'center',
    });

    doc.setTextColor(...COLORS.slate800);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('Interpretação', rightX, y + 8);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.2);
    doc.setTextColor(...COLORS.slate600);
    doc.text(interpretationLines, rightX, interpretationY);

    this.renderGaitSpeedGradientBar(doc, rightX, barY, rightWidth, barHeight);
    if (markerProgress != null) {
      const markerX = rightX + markerProgress * rightWidth;
      doc.setDrawColor(...COLORS.slate900);
      doc.setLineWidth(1.1);
      doc.line(markerX, barY - 2, markerX, barY + barHeight + 2);
    }

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(5.8);
    doc.setTextColor(...COLORS.slate500);
    GAIT_SPEED_BAR_SCALE_TICKS.forEach((tick, index) => {
      const tickX = rightX + tick.position * rightWidth;
      doc.text(tick.label, tickX, labelsY, {
        align: index === 0 ? 'left' : index === GAIT_SPEED_BAR_SCALE_TICKS.length - 1 ? 'right' : 'center',
      });
    });

    const rangeLabel = this.normalizePdfParagraphText(summary.severityRange);
    doc.setFontSize(7.2);
    doc.setTextColor(...COLORS.slate600);
    doc.text('Faixa atual:', rightX, rangeY);
    const rangeValueX = rightX + doc.getTextWidth('Faixa atual:') + 1.2;
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.slate800);
    doc.text(rangeLabel, rangeValueX, rangeY);

    return y + resolvedCardHeight + 6;
  }

  private static renderGaitSpeedProtocolTable(doc: jsPDF, result: AssessmentResultV2, startY: number): number {
    const payload = this.extractGaitSpeedPayload(result);
    if (!payload) return startY;
    const summary = summarizeGaitSpeedResult(payload);
    const attempts = getGaitSpeedAttemptEntries(payload);
    const attempt1 = attempts.find((entry) => entry.index === 1)?.attempt ?? null;
    const attempt2 = attempts.find((entry) => entry.index === 2)?.attempt ?? null;
    const formatAttemptValue = (attempt: (typeof attempts)[number]['attempt'] | null) =>
      !attempt
        ? '—'
        : attempt.completed !== false && attempt.speedMs != null
          ? `${formatGaitSpeed(attempt.speedMs)} m/s (${formatGaitSpeedSeconds(attempt.timeSeconds)} s)`
          : attempt.reasonNotCompleted || 'Não concluiu';

    const rows = [
      ['1ª tentativa', formatAttemptValue(attempt1)],
      ...(attempt2 ? [['2ª tentativa', formatAttemptValue(attempt2)]] : []),
      ['Auxílio de marcha', summary.walkingAidLabel],
      ['Oxigênio suplementar', summary.oxygenUsed ? 'Sim' : 'Não'],
      ['Assistência física', summary.physicalAssistance ? 'Sim' : 'Não'],
      ['Distância do percurso', `${payload.protocol?.distanceMeters ?? GAIT_SPEED_STANDARD_PROTOCOL.distanceMeters} m`],
      ['Ritmo', 'Habitual'],
    ];
    const compactRows: string[][] = [];
    for (let index = 0; index < rows.length; index += 2) {
      compactRows.push([...rows[index], ...(rows[index + 1] ?? ['', ''])]);
    }

    this.runAutoTable(doc, {
      startY,
      margin: { left: 15, right: 15 },
      head: [['Item', 'Registro', 'Item', 'Registro']],
      body: compactRows,
      theme: 'striped',
      headStyles: {
        fillColor: COLORS.brandBlue,
        textColor: COLORS.white,
        fontSize: 7.2,
        fontStyle: 'bold',
        halign: 'left',
        cellPadding: 1.6,
      },
      bodyStyles: {
        fontSize: 6.8,
        textColor: COLORS.slate800,
        cellPadding: 1.5,
        valign: 'middle',
      },
      alternateRowStyles: {
        fillColor: [250, 252, 255],
      },
      didParseCell: (hookData) => {
        if (hookData.section === 'body' && (hookData.column.index === 1 || hookData.column.index === 3)) {
          hookData.cell.styles.fontStyle = 'bold';
        }
      },
      columnStyles: {
        0: { cellWidth: 39, fontStyle: 'bold' },
        1: { cellWidth: 51 },
        2: { cellWidth: 40, fontStyle: 'bold' },
        3: { cellWidth: 50 },
      },
    });

    return (doc as any).lastAutoTable?.finalY ?? startY;
  }

  private static renderGaitSpeedInterpretationBlock(doc: jsPDF, result: AssessmentResultV2, startY: number): number {
    const payload = this.extractGaitSpeedPayload(result);
    const summary = summarizeGaitSpeedResult(payload);
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    const width = pageWidth - margin * 2;
    const referenceComparison = summary.referenceComparison;
    const evidenceNote = referenceComparison
      ? `Valor previsto: ${formatGaitSpeed(referenceComparison.predictedSpeedMs)} m/s. Limite inferior de normalidade: ${formatGaitSpeed(referenceComparison.lowerLimitSpeedMs)} m/s. Resultado: ${formatGaitSpeedPercent(referenceComparison.percentPredicted)} do previsto. Referência: Furlanetto et al., 2022.`
      : 'Referência individual não calculada com os dados disponíveis.';
    const evidenceLines = doc.splitTextToSize(this.normalizePdfParagraphText(evidenceNote), width - 8);
    const observationLines = payload?.observations ? doc.splitTextToSize(payload.observations, width - 14) : [];
    const evidenceLineHeight = 3.5;
    const observationBlockHeight = observationLines.length ? 7 + observationLines.length * 3.6 : 0;
    const height = 11 + evidenceLines.length * evidenceLineHeight + (observationLines.length ? observationBlockHeight + 4 : 0);

    doc.setDrawColor(...COLORS.slate200);
    doc.setFillColor(...COLORS.white);
    doc.roundedRect(margin, startY, width, height, 3, 3, 'FD');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.2);
    doc.setTextColor(...COLORS.slate800);
    doc.text('Referência individual', margin + 4, startY + 5);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.3);
    doc.setTextColor(...COLORS.slate600);
    doc.text(evidenceLines, margin + 4, startY + 9.5);

    if (observationLines.length) {
      const observationY = startY + 9.5 + evidenceLines.length * evidenceLineHeight + 1.5;
      doc.setFillColor(248, 250, 252);
      doc.roundedRect(margin + 4, observationY, width - 8, observationBlockHeight, 2.5, 2.5, 'F');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7.2);
      doc.setTextColor(...COLORS.slate800);
      doc.text('Observações do teste', margin + 7, observationY + 4);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.1);
      doc.setTextColor(...COLORS.slate600);
      doc.text(observationLines, margin + 7, observationY + 7.5);
    }

    return startY + height + 4;
  }

  private static renderHADSDomainScoreCard(
    doc: jsPDF,
    x: number,
    y: number,
    width: number,
    height: number,
    title: string,
    score: number | null,
    gradientBarDataUrl: string | null,
  ): void {
    const accent = this.getHADSAccent(score);
    const classification = getHADSClassificationLabel(score);
    const barX = x + 6;
    const barWidth = width - 12;
    const barY = y + height - 12;
    const barHeight = 7;

    doc.setFillColor(...COLORS.white);
    doc.roundedRect(x, y, width, height, 4, 4, 'F');

    doc.setTextColor(...COLORS.slate800);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text(title.toUpperCase(), x + 6, y + 9);

    doc.setTextColor(...accent);
    doc.setFontSize(34);
    doc.text(typeof score === 'number' ? String(score) : '—', x + 6, y + 26);

    this.renderQuestionnaireClassification(doc, classification, x + width * 0.67, y + 24, accent);

    if (gradientBarDataUrl) {
      doc.addImage(gradientBarDataUrl, 'PNG', barX, barY, barWidth, barHeight);
    } else {
      this.renderGradientBarFallback(doc, barX, barY, barWidth, barHeight);
    }

    if (typeof score === 'number') {
      const markerX = barX + (Math.max(0, Math.min(21, score)) / 21) * barWidth;
      doc.setDrawColor(...COLORS.slate900);
      doc.setLineWidth(1.1);
      doc.line(markerX, barY - 2.5, markerX, barY + barHeight + 2.5);
    }
  }

  private static renderHADSScoreCards(doc: jsPDF, result: AssessmentResultV2, y: number, gradientBarDataUrl: string | null): number {
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    const gap = 6;
    const contentWidth = pageWidth - margin * 2;
    const cardWidth = (contentWidth - gap) / 2;
    const cardHeight = 48;
    const { anxietyScore, depressionScore } = this.extractHADSPayload(result);

    if (result.testVersion < 2 || result.payloadJson?.instrumentVersion !== 'HADS_BR_BOTEGA_1995') {
      doc.setDrawColor(...COLORS.amber200);
      doc.setFillColor(...COLORS.amber50);
      doc.roundedRect(margin, y, contentWidth, cardHeight, 4, 4, 'FD');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(...COLORS.amber900);
      doc.text('RESULTADO HADS LEGADO', margin + 6, y + 11);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.5);
      const legacyLines = doc.splitTextToSize(
        'A versão registrada não identifica a chave validada de pontuação item a item. O conteúdo original foi preservado sem classificação automática ou reclassificação retroativa.',
        contentWidth - 12,
      );
      doc.text(legacyLines, margin + 6, y + 20);
      return y + cardHeight + 8;
    }

    this.renderHADSDomainScoreCard(doc, margin, y, cardWidth, cardHeight, 'Ansiedade', anxietyScore, gradientBarDataUrl);
    this.renderHADSDomainScoreCard(
      doc,
      margin + cardWidth + gap,
      y,
      cardWidth,
      cardHeight,
      'Depressão',
      depressionScore,
      gradientBarDataUrl,
    );

    return y + cardHeight + 8;
  }

  private static renderHADSInterpretationCard(doc: jsPDF, result: AssessmentResultV2, startY: number): number {
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    const width = pageWidth - margin * 2;
    const { anxietyScore, depressionScore } = this.extractHADSPayload(result);
    const isLegacy = result.testVersion < 2 || result.payloadJson?.instrumentVersion !== 'HADS_BR_BOTEGA_1995';
    const interpretation = isLegacy
      ? 'Resultado mantido apenas para consulta histórica, sem reclassificação retroativa.'
      : resolveAssessmentInterpretationText(
          this.extractHADSPayload(result),
          getHADSInterpretationText(anxietyScore, depressionScore),
        );
    const note =
      'Referência brasileira: Botega et al. (1995), com rastreio positivo a partir de 9 pontos em cada subescala. O HADS não substitui diagnóstico clínico.';
    const interpretationTextWidth = width - 16;
    const interpretationLines = doc.splitTextToSize(interpretation, interpretationTextWidth);
    const noteLines = doc.splitTextToSize(note, width - 8);
    const height = 18 + interpretationLines.length * 4 + 10 + noteLines.length * 4.1;

    doc.setDrawColor(...COLORS.slate200);
    doc.setFillColor(...COLORS.white);
    doc.roundedRect(margin, startY, width, height, 3, 3, 'FD');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9.5);
    doc.setTextColor(...COLORS.slate800);
    doc.text('Interpretação', margin + 4, startY + 6);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.2);
    doc.setTextColor(...COLORS.slate600);
    doc.text(interpretationLines, margin + 4, startY + 13);

    const noteY = startY + 15 + interpretationLines.length * 4;
    doc.setFillColor(255, 248, 225);
    doc.roundedRect(margin + 4, noteY, width - 8, 6 + noteLines.length * 4.1, 2.5, 2.5, 'F');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.8);
    doc.setTextColor(...COLORS.slate800);
    doc.text(noteLines, margin + 7, noteY + 4.5);

    return startY + height + 8;
  }

  private static renderHADSTable(doc: jsPDF, result: AssessmentResultV2, startY: number): number {
    const margin = 15;
    const pageWidth = doc.internal.pageSize.getWidth();
    const contentWidth = pageWidth - margin * 2;
    const { answers } = this.extractHADSPayload(result);

    const rows = HADS_ITEMS.map((item, index) => {
      const value = typeof answers[index] === 'number' && answers[index] >= 0 ? answers[index] : null;
      const selectedOption = value != null ? item.options[value] : 'Não respondido';
      const domainLabel = item.domain === 'A' ? 'Ansiedade' : 'Depressão';
      const score = value != null && result.testVersion >= 2
        ? getHADSItemScore(index, value)
        : null;
      return [item.question, domainLabel, selectedOption, result.testVersion < 2 ? 'Legado' : score != null ? String(score) : '—'];
    });

    this.runAutoTable(doc, {
      startY,
      margin: { left: margin, right: margin },
      head: [['Questão', 'Domínio', 'Resposta selecionada', 'Pontuação']],
      body: rows,
      theme: 'striped',
      pageBreak: 'avoid',
      headStyles: {
        fillColor: COLORS.brandBlue,
        textColor: COLORS.white,
        fontSize: 7.1,
        fontStyle: 'bold',
        halign: 'left',
        cellPadding: 1.9,
      },
      bodyStyles: {
        fontSize: 6.25,
        textColor: COLORS.slate800,
        cellPadding: 1.8,
        valign: 'top',
      },
      alternateRowStyles: {
        fillColor: [250, 252, 255],
      },
      didParseCell: (hookData) => {
        if (hookData.section === 'body' && hookData.column.index === 1) {
          const domain = String(hookData.cell.raw || '');
          if (domain === 'Ansiedade') {
            hookData.cell.styles.fillColor = [219, 234, 254];
            hookData.cell.styles.textColor = [29, 78, 216];
            hookData.cell.styles.fontStyle = 'bold';
          }
          if (domain === 'Depressão') {
            hookData.cell.styles.fillColor = [245, 243, 255];
            hookData.cell.styles.textColor = [126, 34, 206];
            hookData.cell.styles.fontStyle = 'bold';
          }
        }
        if (hookData.section === 'body' && hookData.column.index === 3) {
          hookData.cell.styles.halign = 'center';
          hookData.cell.styles.fontStyle = 'bold';
        }
      },
      columnStyles: {
        0: { cellWidth: contentWidth * 0.395, fontStyle: 'bold' },
        1: { cellWidth: contentWidth * 0.14, halign: 'center' },
        2: { cellWidth: contentWidth * 0.365 },
        3: { cellWidth: contentWidth * 0.1 },
      },
    });

    return (doc as any).lastAutoTable?.finalY ?? startY;
  }

  private static renderHADSHistoryTable(doc: jsPDF, previousResults: AssessmentResultV2[], startY: number): number {
    if (!previousResults.length) return startY;

    const rows = previousResults.slice(0, 5).map((item) => {
      const { anxietyScore, depressionScore } = this.extractHADSPayload(item);
      return [
        this.formatDate(item.assessmentDate),
        typeof anxietyScore === 'number' ? `${anxietyScore}` : '—',
        typeof depressionScore === 'number' ? `${depressionScore}` : '—',
        item.testVersion >= 2
          ? getHADSOverallSeverityLabel(anxietyScore, depressionScore)
          : 'Pontuação legada — sem classificação',
      ];
    });

    this.runAutoTable(doc, {
      startY,
      margin: { left: 15, right: 15 },
      head: [['Histórico recente', 'Ansiedade', 'Depressão', 'Classificação']],
      body: rows,
      theme: 'grid',
      headStyles: {
        fillColor: [248, 250, 252],
        textColor: COLORS.slate600,
        fontSize: 8,
        fontStyle: 'bold',
      },
      bodyStyles: {
        fontSize: 8.2,
        textColor: COLORS.slate800,
        cellPadding: 3.2,
      },
      styles: {
        lineColor: COLORS.slate200,
        lineWidth: 0.1,
      },
      columnStyles: {
        0: { cellWidth: 56 },
        1: { cellWidth: 24, halign: 'center', fontStyle: 'bold' },
        2: { cellWidth: 24, halign: 'center', fontStyle: 'bold' },
        3: { cellWidth: 76 },
      },
    });

    return (doc as any).lastAutoTable?.finalY ?? startY;
  }

  private static renderPSQIScoreCard(doc: jsPDF, result: AssessmentResultV2, y: number): number {
    const payload = this.extractPSQIPayload(result);
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    const contentWidth = pageWidth - margin * 2;
    const leftWidth = 72;
    const gap = 6;
    const rightWidth = contentWidth - leftWidth - gap;
    const total = payload?.total ?? null;
    const severity = getPSQISeverityLabel(total);
    const interpretation = resolveAssessmentInterpretationText(payload, getPSQIInterpretationText(payload));
    const accent: RGB =
      total == null ? COLORS.slate600 : total <= 5 ? COLORS.emerald : total <= 10 ? [245, 158, 11] : [244, 63, 94];
    const rightX = margin + leftWidth + gap;
    const interpretationLines = doc.splitTextToSize(interpretation, rightWidth - 4);
    const metricLines = [
      `Eficiência habitual: ${typeof payload?.sleepEfficiencyPercent === 'number' ? `${payload.sleepEfficiencyPercent.toFixed(1).replace('.', ',')}%` : '—'}`,
      `Latência para dormir: ${typeof payload?.sleepLatencyMinutes === 'number' ? `${payload.sleepLatencyMinutes} min` : '—'}`,
      `Tempo na cama: ${typeof payload?.timeInBedHours === 'number' ? `${payload.timeInBedHours.toFixed(1).replace('.', ',')} h` : '—'}`,
    ];
    const cardHeight = Math.max(52, 24 + interpretationLines.length * 4.2 + metricLines.length * 4.5);

    doc.setDrawColor(...COLORS.slate200);
    doc.setFillColor(...COLORS.white);
    doc.roundedRect(margin, y, leftWidth, cardHeight, 4, 4, 'FD');

    doc.setTextColor(...COLORS.slate800);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text('PSQI TOTAL', margin + leftWidth / 2, y + 10, { align: 'center' });

    doc.setTextColor(...accent);
    doc.setFontSize(28);
    doc.text(typeof total === 'number' ? String(total) : '—', margin + leftWidth / 2, y + 24, { align: 'center' });

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...COLORS.slate600);
    doc.text(typeof total === 'number' ? `${total}/21` : 'Pontuação indisponível', margin + leftWidth / 2, y + 31, {
      align: 'center',
    });

    this.renderQuestionnaireClassification(doc, severity, margin + leftWidth / 2, y + cardHeight - 8, accent);

    doc.setTextColor(...COLORS.slate800);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.text('Interpretação', rightX, y + 10);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(...COLORS.slate600);
    doc.text(interpretationLines, rightX, y + 18);

    const metricsStartY = y + 20 + interpretationLines.length * 4.2 + 3;
    metricLines.forEach((line, index) => {
      doc.setFillColor(...COLORS.slate50);
      doc.setDrawColor(...COLORS.slate200);
      doc.roundedRect(rightX, metricsStartY + index * 5.2, rightWidth, 4.2, 2, 2, 'FD');
      doc.text(line, rightX + 3, metricsStartY + 2.9 + index * 5.2);
    });

    return y + cardHeight + 8;
  }

  private static renderPSQIComponentsTable(doc: jsPDF, result: AssessmentResultV2, startY: number): number {
    const payload = this.extractPSQIPayload(result);
    if (!payload) return startY;

    const rows = PSQI_COMPONENT_METADATA.map((item) => {
      let detail = item.description;

      switch (item.key) {
        case 'subjectiveQuality':
          detail = this.getPSQIChoiceLabel(PSQI_SUBJECTIVE_QUALITY_OPTIONS, payload.subjectiveQuality);
          break;
        case 'sleepLatency':
          detail = `${payload.sleepLatencyMinutes} min + ${this.getPSQIChoiceLabel(PSQI_FREQUENCY_OPTIONS, payload.disturbances.cannotSleepWithin30)}`;
          break;
        case 'sleepDuration':
          detail = `${payload.sleepDurationHours.toFixed(1).replace('.', ',')} h por noite`;
          break;
        case 'habitualSleepEfficiency':
          detail = `${payload.sleepEfficiencyPercent.toFixed(1).replace('.', ',')}% em ${payload.timeInBedHours.toFixed(1).replace('.', ',')} h na cama`;
          break;
        case 'sleepDisturbances':
          detail = 'Soma dos eventos 5b a 5j';
          break;
        case 'sleepMedicationUse':
          detail = this.getPSQIChoiceLabel(PSQI_FREQUENCY_OPTIONS, payload.sleepMedicationUse);
          break;
        case 'daytimeDysfunction':
          detail = `${this.getPSQIChoiceLabel(PSQI_FREQUENCY_OPTIONS, payload.daytimeSleepiness)} + ${this.getPSQIChoiceLabel(
            PSQI_DAYTIME_PROBLEM_OPTIONS,
            payload.daytimeEnthusiasmProblem,
          )}`;
          break;
      }

      return [item.label, `${payload.components[item.key]}/3`, detail];
    });

    this.runAutoTable(doc, {
      startY,
      margin: { left: 15, right: 15 },
      head: [['Componente', 'Score', 'Resumo da resposta']],
      body: rows,
      theme: 'striped',
      headStyles: {
        fillColor: COLORS.brandBlue,
        textColor: COLORS.white,
        fontSize: 9,
        fontStyle: 'bold',
        halign: 'left',
      },
      bodyStyles: {
        fontSize: 7.5,
        textColor: COLORS.slate800,
        cellPadding: 2.3,
      },
      alternateRowStyles: {
        fillColor: [250, 252, 255],
      },
      columnStyles: {
        0: { cellWidth: 58, fontStyle: 'bold' },
        1: { cellWidth: 18, halign: 'center', fontStyle: 'bold' },
        2: { cellWidth: 104 },
      },
    });

    return (doc as any).lastAutoTable?.finalY ?? startY;
  }

  private static renderPSQIResponsesTable(doc: jsPDF, result: AssessmentResultV2, startY: number): number {
    const payload = this.extractPSQIPayload(result);
    if (!payload) return startY;

    const rows = [
      ['Horário usual de deitar', payload.bedtime],
      ['Horário usual de levantar', payload.wakeTime],
      ['Horas de sono por noite', `${payload.sleepDurationHours.toFixed(1).replace('.', ',')} h`],
      ['Qualidade subjetiva', this.getPSQIChoiceLabel(PSQI_SUBJECTIVE_QUALITY_OPTIONS, payload.subjectiveQuality)],
      ['Uso de medicação para dormir', this.getPSQIChoiceLabel(PSQI_FREQUENCY_OPTIONS, payload.sleepMedicationUse)],
      ['Dificuldade para ficar acordado', this.getPSQIChoiceLabel(PSQI_FREQUENCY_OPTIONS, payload.daytimeSleepiness)],
      ['Manter entusiasmo nas atividades', this.getPSQIChoiceLabel(PSQI_DAYTIME_PROBLEM_OPTIONS, payload.daytimeEnthusiasmProblem)],
      ['Parceiro(a) ou colega de quarto', this.getPSQIChoiceLabel(PSQI_PARTNER_STATUS_OPTIONS, payload.partnerStatus)],
    ];

    this.runAutoTable(doc, {
      startY,
      margin: { left: 15, right: 15 },
      head: [['Registro clínico', 'Resposta']],
      body: rows,
      theme: 'striped',
      headStyles: {
        fillColor: COLORS.brandBlue,
        textColor: COLORS.white,
        fontSize: 9,
        fontStyle: 'bold',
        halign: 'left',
      },
      bodyStyles: {
        fontSize: 8.4,
        textColor: COLORS.slate800,
        cellPadding: 3.4,
      },
      alternateRowStyles: {
        fillColor: [250, 252, 255],
      },
      columnStyles: {
        0: { cellWidth: 68, fontStyle: 'bold' },
        1: { cellWidth: 122 },
      },
    });

    return (doc as any).lastAutoTable?.finalY ?? startY;
  }

  private static renderPSQIDisturbancesTable(doc: jsPDF, result: AssessmentResultV2, startY: number): number {
    const payload = this.extractPSQIPayload(result);
    if (!payload) return startY;

    const rows: string[][] = PSQI_DISTURBANCE_ITEMS.map((item) => [
      item.label,
      this.getPSQIChoiceLabel(PSQI_FREQUENCY_OPTIONS, payload.disturbances[item.key]),
    ]);

    rows.push(['Descrição da outra razão', payload.disturbances.otherReasonText || 'Não informado']);

    if (payload.partnerStatus !== 'none') {
      PSQI_PARTNER_OBSERVATION_ITEMS.forEach((item) => {
        rows.push([
          `Observação do parceiro: ${item.label}`,
          payload.partnerObservations[item.key] == null
            ? 'Não informado'
            : this.getPSQIChoiceLabel(PSQI_FREQUENCY_OPTIONS, payload.partnerObservations[item.key]),
        ]);
      });
      rows.push([
        'Observação adicional do parceiro',
        payload.partnerObservations.otherRestlessnessText || 'Não informado',
      ]);
    }

    this.runAutoTable(doc, {
      startY,
      margin: { left: 15, right: 15 },
      head: [['Distúrbio ou observação', 'Frequência / registro']],
      body: rows,
      theme: 'striped',
      headStyles: {
        fillColor: COLORS.brandBlue,
        textColor: COLORS.white,
        fontSize: 9,
        fontStyle: 'bold',
        halign: 'left',
      },
      bodyStyles: {
        fontSize: 8.2,
        textColor: COLORS.slate800,
        cellPadding: 3.2,
      },
      alternateRowStyles: {
        fillColor: [250, 252, 255],
      },
      columnStyles: {
        0: { cellWidth: 88, fontStyle: 'bold' },
        1: { cellWidth: 102 },
      },
    });

    return (doc as any).lastAutoTable?.finalY ?? startY;
  }

  private static renderPSQIHistoryTable(doc: jsPDF, previousResults: AssessmentResultV2[], startY: number): number {
    if (!previousResults.length) return startY;

    const rows = previousResults.slice(0, 5).map((item) => {
      const payload = this.extractPSQIPayload(item);
      return [
        this.formatDate(item.assessmentDate),
        payload?.total != null ? String(payload.total) : '—',
        getPSQISeverityLabel(payload?.total ?? null),
      ];
    });

    this.runAutoTable(doc, {
      startY,
      margin: { left: 15, right: 15 },
      head: [['Histórico recente', 'Pontuação', 'Classificação']],
      body: rows,
      theme: 'grid',
      headStyles: {
        fillColor: [248, 250, 252],
        textColor: COLORS.slate600,
        fontSize: 8,
        fontStyle: 'bold',
      },
      bodyStyles: {
        fontSize: 8.2,
        textColor: COLORS.slate800,
        cellPadding: 3.2,
      },
      styles: {
        lineColor: COLORS.slate200,
        lineWidth: 0.1,
      },
      columnStyles: {
        0: { cellWidth: 58 },
        1: { cellWidth: 26, halign: 'center', fontStyle: 'bold' },
        2: { cellWidth: 96 },
      },
    });

    return (doc as any).lastAutoTable?.finalY ?? startY;
  }

  private static renderNotesCard(doc: jsPDF, notes: string | null | undefined, startY: number): number {
    if (!notes) return startY;

    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    const width = pageWidth - margin * 2;
    const lines = doc.splitTextToSize(notes, width - 8);
    const height = Math.max(16, 8 + lines.length * 4.2);

    doc.setDrawColor(...COLORS.slate200);
    doc.setFillColor(...COLORS.white);
    doc.roundedRect(margin, startY, width, height, 3, 3, 'FD');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9.3);
    doc.setTextColor(...COLORS.slate800);
    doc.text('Observações clínicas', margin + 4, startY + 6);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(...COLORS.slate600);
    doc.text(lines, margin + 4, startY + 11);

    return startY + height + 8;
  }

  private static renderHistoryTable(doc: jsPDF, previousResults: AssessmentResultV2[], startY: number): number {
    if (!previousResults.length) return startY;

    const rows = previousResults.slice(0, 5).map((item) => {
      const { total } = this.extractCATPayload(item);
      return [this.formatDate(item.assessmentDate), typeof total === 'number' ? `${total}` : '—', getCATSeverityLabel(total)];
    });

    this.runAutoTable(doc, {
      startY,
      margin: { left: 15, right: 15 },
      head: [['Histórico recente', 'Pontuação', 'Classificação']],
      body: rows,
      theme: 'grid',
      headStyles: {
        fillColor: [248, 250, 252],
        textColor: COLORS.slate600,
        fontSize: 8,
        fontStyle: 'bold',
      },
      bodyStyles: {
        fontSize: 8.2,
        textColor: COLORS.slate800,
        cellPadding: 3.2,
      },
      styles: {
        lineColor: COLORS.slate200,
        lineWidth: 0.1,
      },
      columnStyles: {
        0: { cellWidth: 66 },
        1: { cellWidth: 30, halign: 'center', fontStyle: 'bold' },
        2: { cellWidth: 84 },
      },
    });

    return (doc as any).lastAutoTable?.finalY ?? startY;
  }

  private static renderMMRCHistoryTable(doc: jsPDF, previousResults: AssessmentResultV2[], startY: number): number {
    if (!previousResults.length) return startY;

    const rows = previousResults.slice(0, 5).map((item) => {
      const { grade } = this.extractMMRCPayload(item);
      return [this.formatDate(item.assessmentDate), typeof grade === 'number' ? `${grade}` : '—', getMMRCSeverityLabel(grade)];
    });

    this.runAutoTable(doc, {
      startY,
      margin: { left: 15, right: 15 },
      head: [['Histórico recente', 'Grau', 'Classificação']],
      body: rows,
      theme: 'grid',
      headStyles: {
        fillColor: [248, 250, 252],
        textColor: COLORS.slate600,
        fontSize: 8,
        fontStyle: 'bold',
      },
      bodyStyles: {
        fontSize: 8.2,
        textColor: COLORS.slate800,
        cellPadding: 3.2,
      },
      styles: {
        lineColor: COLORS.slate200,
        lineWidth: 0.1,
      },
      columnStyles: {
        0: { cellWidth: 66 },
        1: { cellWidth: 30, halign: 'center', fontStyle: 'bold' },
        2: { cellWidth: 84 },
      },
    });

    return (doc as any).lastAutoTable?.finalY ?? startY;
  }

  private static renderTUGHistoryTable(doc: jsPDF, previousResults: AssessmentResultV2[], startY: number): number {
    if (!previousResults.length) return startY;

    const rows = previousResults.slice(0, 5).map((item) => {
      const payload = this.extractTUGPayload(item);
      const summary = summarizeTugResult(payload);
      return [
        this.formatDate(item.assessmentDate),
        summary.completed && summary.bestTimeSeconds != null ? `${formatTugSeconds(summary.bestTimeSeconds)} s` : 'Não concluiu',
        summary.severity,
      ];
    });

    this.runAutoTable(doc, {
      startY,
      margin: { left: 15, right: 15 },
      head: [['Histórico recente', 'Tempo', 'Classificação']],
      body: rows,
      theme: 'grid',
      headStyles: {
        fillColor: [248, 250, 252],
        textColor: COLORS.slate600,
        fontSize: 8,
        fontStyle: 'bold',
      },
      bodyStyles: {
        fontSize: 8.2,
        textColor: COLORS.slate800,
        cellPadding: 3.2,
      },
      styles: {
        lineColor: COLORS.slate200,
        lineWidth: 0.1,
      },
      columnStyles: {
        0: { cellWidth: 66 },
        1: { cellWidth: 30, halign: 'center', fontStyle: 'bold' },
        2: { cellWidth: 84 },
      },
    });

    return (doc as any).lastAutoTable?.finalY ?? startY;
  }

  private static renderPeakFlowScoreCard(doc: jsPDF, result: AssessmentResultV2, y: number): number {
    const payload = this.extractPeakFlowPayload(result);
    const summary = summarizePeakFlowPayload(payload);
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    const contentWidth = pageWidth - margin * 2;
    const leftWidth = 74;
    const gap = 6;
    const rightWidth = contentWidth - leftWidth - gap;
    const rightX = margin + leftWidth + gap;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.4);
    const interpretationLines = doc.splitTextToSize(summary.patientInterpretation, Math.max(40, rightWidth - 4));
    const context =
      summary.referencePercentPre != null
        ? `${summary.zoneLabel} • ${formatPeakFlowPercent(summary.referencePercentPre)} da referência`
        : 'Sem referência para classificação em zonas';
    doc.setFontSize(7.6);
    const contextLines = doc.splitTextToSize(context, leftWidth - 10);
    const cardHeight = Math.max(47, 20 + interpretationLines.length * 3.9);
    const accent = this.getPeakFlowAccent(payload);

    doc.setDrawColor(...COLORS.slate200);
    doc.setFillColor(...COLORS.white);
    doc.roundedRect(margin, y, leftWidth, cardHeight, 4, 4, 'FD');

    doc.setTextColor(...COLORS.slate800);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.text('PICO DE FLUXO EXPIRATÓRIO', margin + leftWidth / 2, y + 8, { align: 'center' });

    doc.setTextColor(...accent);
    doc.setFontSize(27);
    doc.text(summary.bestPreFlow != null ? formatPeakFlowValue(summary.bestPreFlow) : '—', margin + leftWidth / 2, y + 21, {
      align: 'center',
    });

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.2);
    doc.setTextColor(...COLORS.slate600);
    doc.text('L/min', margin + leftWidth / 2, y + 27, {
      align: 'center',
    });

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.2);
    const severityTextWidth = doc.getTextWidth(summary.severity);
    const severityStartX = margin + leftWidth / 2 - (severityTextWidth + 4) / 2;
    doc.setFillColor(...accent);
    doc.circle(severityStartX + 1, y + 33, 1.2, 'F');
    doc.setTextColor(...accent);
    doc.text(summary.severity, severityStartX + 4, y + 34.1);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.2);
    doc.setTextColor(...COLORS.slate500);
    doc.text(contextLines, margin + leftWidth / 2, y + 40, { align: 'center', maxWidth: leftWidth - 10 });

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(...COLORS.slate800);
    doc.text('Interpretação', rightX, y + 8);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.4);
    doc.setTextColor(...COLORS.slate600);
    doc.text(interpretationLines, rightX, y + 15, { maxWidth: rightWidth - 2 });

    return y + cardHeight + 5;
  }

  private static renderPeakFlowProtocolTable(doc: jsPDF, result: AssessmentResultV2, startY: number): number {
    const payload = this.extractPeakFlowPayload(result);
    if (!payload) return startY;

    const summary = summarizePeakFlowPayload(payload);
    const entries: string[][] = [
      [
        'Manobras pré-broncodilatador',
        payload.preReadings.map((reading) => `${formatPeakFlowValue(reading)} L/min`).join(' | '),
      ],
    ];

    if (summary.referenceMode !== 'none' && summary.referenceValue != null) {
      const referenceParts = [`${summary.referenceLabel}: ${formatPeakFlowValue(summary.referenceValue)} L/min`];
      if (summary.referencePercentPre != null) referenceParts.push(`${formatPeakFlowPercent(summary.referencePercentPre)} da referência`);
      if (summary.zone !== 'unclassified') referenceParts.push(summary.zoneLabel);
      entries.push(['Referência utilizada', referenceParts.join(' • ')]);
    }

    if (summary.bronchodilatorUsed && payload.postReadings?.length) {
      entries.push([
        'Manobras pós-broncodilatador',
        payload.postReadings.map((reading) => `${formatPeakFlowValue(reading)} L/min`).join(' | '),
      ]);
      if (summary.bronchodilatorMedication) entries.push(['Broncodilatador', summary.bronchodilatorMedication]);
      if (summary.bronchodilatorWaitMinutes != null) {
        entries.push(['Intervalo após uso', `${formatPeakFlowValue(summary.bronchodilatorWaitMinutes)} minutos`]);
      }
      if (summary.bronchodilatorDeltaAbsolute != null && summary.bronchodilatorDeltaPercent != null) {
        entries.push([
          'Variação após broncodilatador',
          `${this.formatSignedValue(summary.bronchodilatorDeltaAbsolute, 'L/min', 1)} (${this.formatSignedValue(
            summary.bronchodilatorDeltaPercent,
            '%',
            1,
          )})`,
        ]);
      }
    }

    if (summary.symptomLabels.length) entries.push(['Sintomas registrados', summary.symptomLabels.join(', ')]);
    if (summary.dangerSignLabels.length) entries.push(['Sinais de alarme', summary.dangerSignLabels.join(', ')]);

    const rows: string[][] = [];
    for (let index = 0; index < entries.length; index += 2) {
      const left = entries[index];
      const right = entries[index + 1] ?? ['', ''];
      rows.push([left[0], left[1], right[0], right[1]]);
    }

    this.runAutoTable(doc, {
      startY,
      margin: { left: 15, right: 15 },
      head: [['Registro técnico', 'Resultado', 'Registro técnico', 'Resultado']],
      body: rows,
      theme: 'striped',
      headStyles: {
        fillColor: COLORS.brandBlue,
        textColor: COLORS.white,
        fontSize: 9,
        fontStyle: 'bold',
        halign: 'left',
      },
      bodyStyles: {
        fontSize: 7.3,
        textColor: COLORS.slate800,
        cellPadding: 1.8,
      },
      alternateRowStyles: {
        fillColor: [250, 252, 255],
      },
      didParseCell: (hookData) => {
        if (hookData.section === 'body' && (hookData.column.index === 0 || hookData.column.index === 2)) {
          hookData.cell.styles.fontStyle = 'bold';
        }
      },
      columnStyles: {
        0: { cellWidth: 38, fontStyle: 'bold' },
        1: { cellWidth: 52 },
        2: { cellWidth: 38, fontStyle: 'bold' },
        3: { cellWidth: 52 },
      },
    });

    return (doc as any).lastAutoTable?.finalY ?? startY;
  }

  private static renderPeakFlowInterpretationBlock(doc: jsPDF, result: AssessmentResultV2, startY: number): number {
    const payload = this.extractPeakFlowPayload(result);
    const summary = summarizePeakFlowPayload(payload);
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    const width = pageWidth - margin * 2;
    const sourceParts: string[] = [];
    if (summary.referenceMode === 'predicted_reference' && summary.referenceSourceLabel) {
      sourceParts.push(`Referência: ${summary.referenceSourceLabel}.`);
    } else if (summary.referenceMode !== 'none' && summary.referenceValue != null) {
      sourceParts.push(`As zonas foram calculadas em relação ao valor registrado como ${summary.referenceLabel}.`);
    }
    sourceParts.push('O PEF isolado deve ser interpretado junto à história clínica e, quando indicado, à espirometria.');
    if (payload?.notes) sourceParts.push(`Observação do teste: ${payload.notes}`);
    const sourceNote = sourceParts.join(' ');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.8);
    const lines = doc.splitTextToSize(sourceNote, width - 10);
    const height = Math.max(15, 9 + lines.length * 3.7);

    doc.setDrawColor(...COLORS.slate200);
    doc.setFillColor(...COLORS.white);
    doc.roundedRect(margin, startY, width, height, 3, 3, 'FD');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(...COLORS.slate800);
    doc.text('Referência e observações', margin + 4, startY + 5.5);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.8);
    doc.setTextColor(...COLORS.slate600);
    doc.text(lines, margin + 4, startY + 10, { maxWidth: width - 8 });

    return startY + height + 4;
  }

  private static renderPeakFlowHistoryTable(
    doc: jsPDF,
    result: AssessmentResultV2,
    previousResults: AssessmentResultV2[],
    startY: number,
  ): number {
    const current = summarizePeakFlowPayload(this.extractPeakFlowPayload(result));
    if (!previousResults.length || current.bestPreFlow == null) return startY;

    const rows = previousResults
      .slice(0, 3)
      .map((item) => ({ item, summary: summarizePeakFlowPayload(this.extractPeakFlowPayload(item)) }))
      .filter(({ summary }) => summary.bestPreFlow != null)
      .map(({ item, summary }) => [
        this.formatDate(item.assessmentDate),
        `${formatPeakFlowValue(summary.bestPreFlow)} L/min`,
        `${formatPeakFlowValue(current.bestPreFlow)} L/min`,
        this.formatSignedValue(current.bestPreFlow! - summary.bestPreFlow!, 'L/min', 1),
      ]);

    if (!rows.length) return startY;

    this.runAutoTable(doc, {
      startY,
      margin: { left: 15, right: 15 },
      head: [['Data anterior', 'Resultado anterior', 'Resultado atual', 'Diferença']],
      body: rows,
      theme: 'grid',
      headStyles: {
        fillColor: [248, 250, 252],
        textColor: COLORS.slate600,
        fontSize: 8,
        fontStyle: 'bold',
      },
      bodyStyles: {
        fontSize: 7.8,
        textColor: COLORS.slate800,
        cellPadding: 2.2,
      },
      styles: {
        lineColor: COLORS.slate200,
        lineWidth: 0.1,
      },
      columnStyles: {
        0: { cellWidth: 40 },
        1: { cellWidth: 50, halign: 'center' },
        2: { cellWidth: 45, halign: 'center', fontStyle: 'bold' },
        3: { cellWidth: 45, halign: 'center', fontStyle: 'bold' },
      },
    });

    return (doc as any).lastAutoTable?.finalY ?? startY;
  }

  private static renderSTSScoreCard(doc: jsPDF, result: AssessmentResultV2, testKey: StsTestKey, y: number): number {
    const payload =
      testKey === '5xSTS'
        ? this.extractFiveSTSPayload(result)
        : this.extractStsRepetitionPayload(result, testKey as 'STS_30S' | 'STS_1MIN');
    const summary = summarizeStsResult(testKey, payload);
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    const contentWidth = pageWidth - margin * 2;
    const leftWidth = 82;
    const gap = 6;
    const rightWidth = contentWidth - leftWidth - gap;
    const rightX = margin + leftWidth + gap;
    const interpretationLines = doc.splitTextToSize(summary.interpretation, Math.max(40, rightWidth - 4));
    const interpretationY = y + 16;
    const interpretationLineHeight = 3.8;
    const referenceLabel = summary.referenceComparison
      ? [
          `Referência individual: previsto ${getStsReferenceUnit(testKey, summary.referenceComparison.predictedValue)}`,
          `${formatStsReferenceNumber(summary.referenceComparison.percentPredicted, 0)}% do previsto`,
          `limite ${summary.referenceComparison.normalLimitDirection === 'upper' ? 'superior' : 'inferior'} ${getStsReferenceUnit(testKey, summary.referenceComparison.normalLimitValue)}`,
          'Furlanetto et al., 2022',
        ].join(' | ')
      : null;
    const referenceLines = referenceLabel ? doc.splitTextToSize(referenceLabel, Math.max(40, rightWidth - 4)) : [];
    const referenceY = interpretationY + interpretationLines.length * interpretationLineHeight + 2;
    const infoY = Math.max(y + 36, referenceY + referenceLines.length * 3.2 + (referenceLines.length ? 2 : 0));
    const cardHeight = Math.max(56, infoY + 11 - y);
    const normalizedColor = summary.scoreColor.replace('#', '');
    const scoreColor: RGB = [
      Number.parseInt(normalizedColor.slice(0, 2), 16),
      Number.parseInt(normalizedColor.slice(2, 4), 16),
      Number.parseInt(normalizedColor.slice(4, 6), 16),
    ];
    const bestAttemptIndex = summary.best == null ? null : summary.validAttempts.findIndex((value) => value === summary.best) + 1;
    const attemptMetaLabel = summary.completed
      ? summary.attemptCount > 1
        ? `Melhor de ${summary.attemptCount} tentativas${bestAttemptIndex ? ` • ${bestAttemptIndex}ª tentativa` : ''}`
        : '1 tentativa registrada'
      : summary.workflowStatus === 'awaiting_second_attempt'
        ? 'Rascunho aguardando 2ª tentativa'
        : 'Sem protocolo concluído';

    doc.setDrawColor(...COLORS.slate200);
    doc.setFillColor(...COLORS.white);
    doc.roundedRect(margin, y, leftWidth, cardHeight, 4, 4, 'FD');

    doc.setTextColor(...COLORS.brandBlue);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.2);
    doc.text(getStsTestLabel(testKey).toUpperCase(), margin + leftWidth / 2, y + 7, { align: 'center' });

    doc.setTextColor(...scoreColor);
    doc.setFontSize(testKey === '5xSTS' ? 29 : 30);
    doc.text(
      summary.best != null ? (testKey === '5xSTS' ? summary.best.toFixed(2) : `${Math.round(summary.best)}`) : '—',
      margin + leftWidth / 2,
      y + 20,
      {
        align: 'center',
      },
    );

    doc.setTextColor(...COLORS.slate600);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.8);
    doc.text(summary.best != null ? summary.measurementUnit : 'sem valor válido', margin + leftWidth / 2, y + 26, {
      align: 'center',
    });

    doc.setTextColor(...scoreColor);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.2);
    const severityTextWidth = doc.getTextWidth(summary.severity);
    const dotRadius = 0.75;
    const severityGap = 1.4;
    const severityGroupWidth = dotRadius * 2 + severityGap + severityTextWidth;
    const severityStartX = margin + (leftWidth - severityGroupWidth) / 2;
    doc.setFillColor(...scoreColor);
    doc.circle(severityStartX + dotRadius, y + 33.2, dotRadius, 'F');
    doc.text(summary.severity, severityStartX + dotRadius * 2 + severityGap, y + 34.2);

    doc.setTextColor(...COLORS.slate500);
    doc.setFont('helvetica', 'normal');
    let metaFontSize = 6.3;
    doc.setFontSize(metaFontSize);
    while (metaFontSize > 5.2 && doc.getTextWidth(attemptMetaLabel) > leftWidth - 8) {
      metaFontSize -= 0.2;
      doc.setFontSize(metaFontSize);
    }
    doc.text(attemptMetaLabel, margin + leftWidth / 2, y + 42, {
      align: 'center',
    });

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(...COLORS.slate800);
    doc.text('Interpretação', rightX, y + 8);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.2);
    doc.setTextColor(...COLORS.slate600);
    doc.text(interpretationLines, rightX, interpretationY);

    if (referenceLines.length) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6.6);
      doc.setTextColor(...COLORS.slate500);
      doc.text(referenceLines, rightX, referenceY);
    }

    const infoGap = 3;
    const infoWidth = (rightWidth - infoGap) / 2;
    const infoItems = [
      {
        label: 'Altura da cadeira',
        value: summary.chairHeightCm != null ? `${summary.chairHeightCm} cm` : '—',
      },
      {
        label: 'Intervalo',
        value: summary.restIntervalSeconds != null ? formatStsRestInterval(summary.restIntervalSeconds) : '—',
      },
    ];
    infoItems.forEach((item, index) => {
      const infoX = rightX + index * (infoWidth + infoGap);
      doc.setFillColor(...COLORS.slate50);
      doc.setDrawColor(...COLORS.slate200);
      doc.roundedRect(infoX, infoY, infoWidth, 9, 2, 2, 'FD');
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6.8);
      doc.setTextColor(...COLORS.slate600);
      const label = `${item.label}:`;
      doc.text(label, infoX + 3, infoY + 5.6);
      const valueX = infoX + 3 + doc.getTextWidth(label) + 1;
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...COLORS.slate800);
      doc.text(item.value, valueX, infoY + 5.6);
    });

    return y + cardHeight + 6;
  }

  private static renderSTSProtocolTable(doc: jsPDF, result: AssessmentResultV2, testKey: StsTestKey, startY: number): number {
    const summary = summarizeStsResult(
      testKey,
      testKey === '5xSTS' ? this.extractFiveSTSPayload(result) : this.extractStsRepetitionPayload(result, testKey as 'STS_30S' | 'STS_1MIN'),
    );

    if (testKey !== '5xSTS') return startY;

    const rows = [
      ...summary.validAttempts.map((value, index) => [`${index + 1}ª tentativa`, `${value.toFixed(2)} segundos`]),
      ['Altura da cadeira', summary.chairHeightCm != null ? `${summary.chairHeightCm} cm` : '—'],
      ['Uso dos braços', summary.usedArms != null ? (summary.usedArms ? 'Sim' : 'Não') : '—'],
    ];
    const compactRows: string[][] = [];
    for (let index = 0; index < rows.length; index += 2) {
      compactRows.push([...rows[index], ...(rows[index + 1] ?? ['', ''])]);
    }

    this.runAutoTable(doc, {
      startY,
      margin: { left: 15, right: 15 },
      head: [['Item', 'Registro', 'Item', 'Registro']],
      body: compactRows,
      theme: 'striped',
      pageBreak: 'avoid',
      headStyles: {
        fillColor: COLORS.brandBlue,
        textColor: COLORS.white,
        fontSize: 7.2,
        fontStyle: 'bold',
        cellPadding: 1.6,
      },
      bodyStyles: {
        fontSize: 6.8,
        textColor: COLORS.slate800,
        cellPadding: 1.5,
      },
      alternateRowStyles: {
        fillColor: [250, 252, 255],
      },
      columnStyles: {
        0: { cellWidth: 40, fontStyle: 'bold' },
        1: { cellWidth: 42 },
        2: { cellWidth: 43, fontStyle: 'bold' },
        3: { cellWidth: 55 },
      },
    });

    return (doc as any).lastAutoTable?.finalY ?? startY;
  }

  private static renderSTSAttemptsTable(
    doc: jsPDF,
    payload: {
      attempt1?: StsRepetitionAttemptPayload | null;
      attempt2?: StsRepetitionAttemptPayload | null;
    },
    startY: number,
  ): number {
    const attempts = [
      payload.attempt1 ? { label: '1ª tentativa', value: payload.attempt1 } : null,
      payload.attempt2 ? { label: '2ª tentativa', value: payload.attempt2 } : null,
    ].filter((entry): entry is { label: string; value: StsRepetitionAttemptPayload } => entry != null);
    if (!attempts.length) return startY;

    const hasAny = (selector: (attempt: StsRepetitionAttemptPayload) => number | null) => attempts.some((entry) => selector(entry.value) != null);
    const formatTransition = (pre: number | null, post: number | null, formatter: (value: number) => string) => {
      const values = [pre != null ? `Pré ${formatter(pre)}` : null, post != null ? `Pós ${formatter(post)}` : null].filter(
        (value): value is string => value != null,
      );
      return values.join(' / ') || '—';
    };
    const rowDefinitions = [
      {
        label: 'Repetições',
        visible: true,
        value: (attempt: StsRepetitionAttemptPayload) => formatStsRepetitionCount(attempt.reps),
      },
      {
        label: 'Altura da cadeira',
        visible: hasAny((attempt) => attempt.chairHeightCm),
        value: (attempt: StsRepetitionAttemptPayload) => (attempt.chairHeightCm != null ? `${attempt.chairHeightCm} cm` : '—'),
      },
      {
        label: 'Borg dispneia',
        visible: hasAny((attempt) => attempt.borgDyspneaPre) || hasAny((attempt) => attempt.borgDyspneaPost),
        value: (attempt: StsRepetitionAttemptPayload) =>
          formatTransition(attempt.borgDyspneaPre, attempt.borgDyspneaPost, (value) => formatBorgValue(value)),
      },
      {
        label: 'Borg fadiga',
        visible: hasAny((attempt) => attempt.borgLegPre) || hasAny((attempt) => attempt.borgLegPost),
        value: (attempt: StsRepetitionAttemptPayload) => formatTransition(attempt.borgLegPre, attempt.borgLegPost, (value) => formatBorgValue(value)),
      },
      {
        label: 'SpO2',
        visible: hasAny((attempt) => attempt.spo2Pre) || hasAny((attempt) => attempt.spo2Post),
        value: (attempt: StsRepetitionAttemptPayload) => formatTransition(attempt.spo2Pre, attempt.spo2Post, (value) => `${value}%`),
      },
      {
        label: 'FC',
        visible: hasAny((attempt) => attempt.hrPre) || hasAny((attempt) => attempt.hrPost),
        value: (attempt: StsRepetitionAttemptPayload) => formatTransition(attempt.hrPre, attempt.hrPost, (value) => `${value} bpm`),
      },
    ];
    const columnStyles: Record<number, { cellWidth: number; fontStyle?: 'bold'; halign?: 'center' }> = {
      0: { cellWidth: 52, fontStyle: 'bold' },
    };
    const attemptColumnWidth = (180 - 52) / attempts.length;
    attempts.forEach((_, index) => {
      columnStyles[index + 1] = {
        cellWidth: attemptColumnWidth,
        halign: 'center',
      };
    });

    this.runAutoTable(doc, {
      startY,
      margin: { left: 15, right: 15 },
      head: [['Registro técnico', ...attempts.map((entry) => entry.label)]],
      body: rowDefinitions.filter((row) => row.visible).map((row) => [row.label, ...attempts.map((entry) => row.value(entry.value))]),
      theme: 'striped',
      pageBreak: 'avoid',
      headStyles: {
        fillColor: COLORS.brandBlue,
        textColor: COLORS.white,
        fontSize: 7.2,
        fontStyle: 'bold',
        cellPadding: 1.6,
      },
      bodyStyles: {
        fontSize: 6.8,
        textColor: COLORS.slate800,
        cellPadding: 1.5,
      },
      alternateRowStyles: {
        fillColor: [250, 252, 255],
      },
      columnStyles,
    });

    return (doc as any).lastAutoTable?.finalY ?? startY;
  }

  private static renderSTSHistoryTable(
    doc: jsPDF,
    previousResults: AssessmentResultV2[],
    testKey: StsTestKey,
    startY: number,
  ): number {
    if (!previousResults.length) return startY;

    const rows = previousResults
      .filter((item) => item.testKey === testKey)
      .slice(0, 5)
      .map((item) => {
        const summary = summarizeStsResult(
          testKey,
          testKey === '5xSTS'
            ? this.extractFiveSTSPayload(item)
            : this.extractStsRepetitionPayload(item, testKey as 'STS_30S' | 'STS_1MIN'),
        );
        return [this.formatDate(item.assessmentDate), formatStsPrimaryValue(testKey, summary.best), summary.severity];
      });

    if (!rows.length) return startY;

    this.runAutoTable(doc, {
      startY,
      margin: { left: 15, right: 15 },
      head: [['Histórico recente', testKey === '5xSTS' ? 'Tempo' : 'Melhor marca', 'Classificação']],
      body: rows,
      theme: 'striped',
      pageBreak: 'avoid',
      headStyles: {
        fillColor: [248, 250, 252],
        textColor: COLORS.slate600,
        fontSize: 7,
        fontStyle: 'bold',
        cellPadding: 1.5,
      },
      bodyStyles: {
        fontSize: 6.8,
        textColor: COLORS.slate800,
        cellPadding: 1.5,
      },
      styles: {
        lineColor: COLORS.slate200,
        lineWidth: 0.1,
      },
      columnStyles: {
        0: { cellWidth: 66 },
        1: { cellWidth: 32, halign: 'center', fontStyle: 'bold' },
        2: { cellWidth: 82 },
      },
    });

    return (doc as any).lastAutoTable?.finalY ?? startY;
  }

  private static renderHandgripScoreCard(doc: jsPDF, result: AssessmentResultV2, y: number): number {
    const payload = this.extractHandgripPayload(result);
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    const contentWidth = pageWidth - margin * 2;
    const leftWidth = 88;
    const gap = 6;
    const rightWidth = contentWidth - leftWidth - gap;
    const summary = summarizeHandgripResult(payload);
    const severity = getHandgripSeverityLabel(summary.absolutePercentile);
    const interpretation = this.normalizePdfParagraphText(getHandgripInterpretationText(payload));
    const scoreColor: RGB =
      summary.absolutePercentile == null
        ? COLORS.brandBlue
        : summary.absolutePercentile < 5
          ? [225, 29, 72]
          : summary.absolutePercentile < 25
            ? [217, 119, 6]
            : summary.absolutePercentile <= 75
              ? COLORS.emerald
              : [14, 116, 144];
    const rightX = margin + leftWidth + gap;
    const interpretationLines = doc.splitTextToSize(interpretation, Math.max(48, rightWidth - 4));
    const cardHeight = Math.max(48, 20 + interpretationLines.length * 4.2);

    doc.setDrawColor(...COLORS.slate200);
    doc.setFillColor(...COLORS.white);
    doc.roundedRect(margin, y, leftWidth, cardHeight, 4, 4, 'FD');

    doc.setTextColor(...COLORS.slate800);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text('HANDGRIP', margin + leftWidth / 2, y + 10, { align: 'center' });

    doc.setTextColor(...scoreColor);
    doc.setFontSize(30);
    doc.text(summary.bestOverall != null ? summary.bestOverall.toFixed(1) : '—', margin + leftWidth / 2, y + 25, { align: 'center' });

    doc.setTextColor(...COLORS.slate600);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text(
      summary.bestOverall != null ? `${summary.unit} • ${summary.bestOverallSideLabel}` : 'Sem tentativas válidas',
      margin + leftWidth / 2,
      y + 32,
      {
        align: 'center',
      },
    );
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...scoreColor);
    doc.text(severity, margin + leftWidth / 2, y + cardHeight - 8, {
      align: 'center',
    });

    doc.setTextColor(...COLORS.slate800);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.text('Interpretação', rightX, y + 10);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.8);
    doc.setTextColor(...COLORS.slate600);
    doc.text(interpretationLines, rightX, y + 18);

    return y + cardHeight + 10;
  }

  private static renderHandgripAttemptTable(doc: jsPDF, result: AssessmentResultV2, startY: number): number {
    const payload = this.extractHandgripPayload(result);
    const summary = summarizeHandgripResult(payload);
    const rows = [
      [
        'Direita',
        summary.rightAttempts.length ? summary.rightAttempts.map((value) => value.toFixed(1)).join(', ') : '—',
        summary.bestRight != null ? summary.bestRight.toFixed(1) : '—',
        summary.meanRight != null ? summary.meanRight.toFixed(1) : '—',
      ],
      [
        'Esquerda',
        summary.leftAttempts.length ? summary.leftAttempts.map((value) => value.toFixed(1)).join(', ') : '—',
        summary.bestLeft != null ? summary.bestLeft.toFixed(1) : '—',
        summary.meanLeft != null ? summary.meanLeft.toFixed(1) : '—',
      ],
    ];

    this.runAutoTable(doc, {
      startY,
      margin: { left: 15, right: 15 },
      head: [['Mão', 'Tentativas (kg)', 'Melhor', 'Média']],
      body: rows,
      theme: 'grid',
      headStyles: {
        fillColor: COLORS.brandBlue,
        textColor: COLORS.white,
        fontSize: 8.2,
        fontStyle: 'bold',
      },
      bodyStyles: {
        fontSize: 7.6,
        textColor: COLORS.slate800,
        cellPadding: 1.8,
      },
      styles: {
        lineColor: COLORS.slate200,
        lineWidth: 0.1,
      },
      columnStyles: {
        0: { cellWidth: 28, fontStyle: 'bold' },
        1: { cellWidth: 96 },
        2: { cellWidth: 28, halign: 'center', fontStyle: 'bold' },
        3: { cellWidth: 28, halign: 'center' },
      },
    });

    return (doc as any).lastAutoTable?.finalY ?? startY;
  }

  private static renderHandgripNormsBlock(doc: jsPDF, result: AssessmentResultV2, y: number): number {
    const payload = this.extractHandgripPayload(result);
    const summary = summarizeHandgripResult(payload);
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    const width = pageWidth - margin * 2;
    const cardWidth = (width - 5) / 2;
    const cardHeight = 34;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...COLORS.slate900);
    doc.text('Referências Tomkinson 2025', margin, y);
    y += 5;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.4);
    doc.setTextColor(...COLORS.slate600);
    doc.text(
      `Sexo: ${summary.sex === 'F' ? 'Feminino' : 'Masculino'} • Idade: ${summary.ageYears != null ? `${summary.ageYears} anos` : '—'} • Altura: ${summary.heightCm != null ? `${summary.heightCm} cm` : '—'}`,
      margin,
      y,
    );
    y += 5;

    const renderNormCard = (
      x: number,
      title: string,
      predicted: number | null,
      referenceP5: number | null,
      percentile: number | null,
      percentPredicted: number | null,
      unitLabel: string,
    ) => {
      doc.setDrawColor(...COLORS.slate200);
      doc.setFillColor(...COLORS.slate50);
      doc.roundedRect(x, y, cardWidth, cardHeight, 3, 3, 'FD');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8.2);
      doc.setTextColor(...COLORS.slate800);
      doc.text(title, x + 4, y + 7);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(...COLORS.slate600);
      doc.text(`Referência mediana (P50): ${predicted != null ? this.formatNumber(predicted, 1) : '—'} ${unitLabel}`, x + 4, y + 14);
      doc.text(`Limite inferior (P5): ${referenceP5 != null ? this.formatNumber(referenceP5, 1) : '—'} ${unitLabel}`, x + 4, y + 20);
      doc.text(`Posição percentílica: ${formatHandgripPercentile(percentile)}`, x + 4, y + 26);
      doc.text(`Resultado em relação ao P50: ${percentPredicted != null ? `${percentPredicted.toFixed(0)}%` : '—'}`, x + 4, y + 32);
    };

    renderNormCard(
      margin,
      'Força absoluta',
      payload?.norms.absoluteKg.predictedP50 ?? null,
      summary.absoluteReferenceP5,
      summary.absolutePercentile,
      summary.absolutePercentPredicted,
      payload?.unit ?? 'kg',
    );
    renderNormCard(
      margin + cardWidth + 5,
      'Força normalizada',
      payload?.norms.normalizedKgPerM2.predictedP50 ?? null,
      summary.normalizedReferenceP5,
      summary.normalizedPercentile,
      summary.normalizedPercentPredicted,
      'kg/m2',
    );

    y += cardHeight + 6;

    doc.setFont('helvetica', 'italic');
    doc.setFontSize(7.2);
    doc.setTextColor(...COLORS.slate500);
    const citationLines = doc.splitTextToSize(summary.citation, width);
    doc.text(citationLines, margin, y);
    return y + citationLines.length * 3.4 + 4;
  }

  private static renderHandgripVectorChart(
    doc: jsPDF,
    title: string,
    rows: Array<Record<string, number>>,
    x: number,
    y: number,
    width: number,
    height: number,
    patientValue: number | null,
    patientAge: number | null,
    unitLabel: string,
  ): void {
    const mode = unitLabel === 'kg' ? 'absolute' : 'normalized';
    const axis = getHandgripChartAxisConfig(rows, patientValue, mode);
    const lines = TOMKINSON_HANDGRIP_2025_PERCENTILES.map((percentile) => ({
      values: rows.map((row) => {
        const value = row[`P${percentile}`];
        return typeof value === 'number' && Number.isFinite(value) ? value : null;
      }),
      color: percentile === 50 ? COLORS.brandBlue : COLORS.slate300,
      lineWidth: percentile === 50 ? 0.45 : 0.2,
      showPoints: false,
    }));
    const [rangeMin, rangeMax] = axis.yDomain;
    const [ageMin, ageMax] = axis.xDomain;
    const plotLeft = x + 15;
    const plotWidth = width - 17;
    const plotBottom = y + height;
    const valueToY = (value: number) => y + height - ((value - rangeMin) / (rangeMax - rangeMin)) * height;
    const ageToX = (age: number) => plotLeft + ((age - ageMin) / (ageMax - ageMin)) * plotWidth;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.4);
    doc.setTextColor(...COLORS.slate700);
    doc.text(title, x, y - 3);

    doc.setLineWidth(0.1);
    axis.yTicks.forEach((value) => {
      const gridY = valueToY(value);
      doc.setDrawColor(...COLORS.slate200);
      doc.line(plotLeft, gridY, plotLeft + plotWidth, gridY);
    });
    axis.xTicks.forEach((age) => {
      const gridX = ageToX(age);
      doc.setDrawColor(...COLORS.slate200);
      doc.line(gridX, y, gridX, plotBottom);
    });
    doc.setDrawColor(...COLORS.slate300);
    doc.rect(plotLeft, y, plotWidth, height);

    lines.forEach((line) => {
      let previous: { x: number; y: number } | null = null;
      line.values.forEach((value, index) => {
        const age = rows[index]?.age;
        if (value == null || age == null || !Number.isFinite(age)) {
          previous = null;
          return;
        }
        const point = { x: ageToX(age), y: valueToY(value) };
        if (previous) {
          doc.setDrawColor(...line.color);
          doc.setLineWidth(line.lineWidth);
          doc.line(previous.x, previous.y, point.x, point.y);
        }
        previous = point;
      });
    });

    doc.setFontSize(5.8);
    doc.setTextColor(...COLORS.slate500);
    axis.yTicks.forEach((value) => {
      doc.text(this.formatNumber(value, 0), plotLeft - 1.5, valueToY(value) + 1, { align: 'right' });
    });
    axis.xTicks.forEach((age) => {
      doc.text(age === 100 ? '100+' : String(age), ageToX(age), plotBottom + 4, { align: 'center' });
    });

    if (patientValue != null && patientAge != null && rangeMax > rangeMin) {
      const patientY = valueToY(patientValue);
      const patientX = ageToX(Math.max(ageMin, Math.min(ageMax, patientAge)));
      doc.setFillColor(220, 38, 38);
      doc.circle(patientX, patientY, 1.4, 'F');
    }

    doc.setFontSize(6.1);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...COLORS.slate500);
    doc.text(axis.xLabel, plotLeft + plotWidth / 2, plotBottom + 8, {
      align: 'center',
    });
    const yAxisLabelWidth = doc.getTextWidth(axis.yLabel);
    doc.text(axis.yLabel, plotLeft - 8, y + height / 2 + yAxisLabelWidth / 2, {
      angle: 90,
    });
    const legend = patientValue != null ? 'Curvas: P5-P95 | P50 em azul | paciente em vermelho' : 'Curvas: P5-P95 | P50 em azul';
    doc.text(legend, plotLeft, plotBottom + 12.5);
  }

  private static renderHandgripChartsSection(doc: jsPDF, result: AssessmentResultV2, startY: number): number {
    const payload = this.extractHandgripPayload(result);
    const summary = summarizeHandgripResult(payload);
    const absoluteRows = payload ? getHandgripChartData(payload.norms.sex, 'absolute') : [];
    const normalizedRows = payload ? getHandgripChartData(payload.norms.sex, 'normalized') : [];
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    const maxWidth = pageWidth - margin * 2;
    let y = startY;
    const chartBlockHeight = 63;
    const continuationY = PDF_CONTINUATION_HEADER_BOTTOM_MM + 3;

    const ensureChartSpace = () => {
      if (y > pageHeight - PDF_BOTTOM_SAFE_MARGIN_MM - chartBlockHeight) {
        doc.addPage();
        y = continuationY;
      }
    };

    const renderChart = (title: string, rows: Array<Record<string, number>>, patientValue: number | null, unitLabel: string) => {
      ensureChartSpace();
      this.renderHandgripVectorChart(doc, title, rows, margin, y + 6, maxWidth, 38, patientValue, summary.plotAge, unitLabel);
      y += chartBlockHeight;
    };

    const chartCount = Number(absoluteRows.length > 0) + Number(normalizedRows.length > 0);
    if (chartCount > 0 && y > pageHeight - PDF_BOTTOM_SAFE_MARGIN_MM - chartBlockHeight * chartCount - 34) {
      doc.addPage();
      y = continuationY;
    }

    if (absoluteRows.length) {
      renderChart('Curvas percentílicas - absoluto', absoluteRows, summary.bestOverall, payload?.unit ?? 'kg');
    }

    if (normalizedRows.length) {
      renderChart('Curvas percentílicas - normalizado', normalizedRows, summary.normalizedOverall, 'kg/m2');
    }

    if (!absoluteRows.length && !normalizedRows.length) {
      doc.setDrawColor(...COLORS.slate200);
      doc.setFillColor(...COLORS.slate50);
      doc.roundedRect(margin, y, maxWidth, 16, 3, 3, 'FD');
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.4);
      doc.setTextColor(...COLORS.slate600);
      doc.text('Dados normativos indisponíveis para gerar os gráficos desta avaliação.', margin + 4, y + 9);
      y += 22;
    }

    return y;
  }

  private static renderHandgripHistoryTable(doc: jsPDF, previousResults: AssessmentResultV2[], startY: number): number {
    if (!previousResults.length) return startY;

    const rows = previousResults.slice(0, 5).map((item) => {
      const summary = summarizeHandgripResult(this.extractHandgripPayload(item));
      return [
        this.formatDate(item.assessmentDate),
        summary.bestOverall != null ? `${summary.bestOverall.toFixed(1)} kg` : '—',
        formatHandgripPercentile(summary.absolutePercentile),
        getHandgripSeverityLabel(summary.absolutePercentile),
      ];
    });

    this.runAutoTable(doc, {
      startY,
      margin: { left: 15, right: 15 },
      head: [['Histórico recente', 'Melhor geral', 'Percentil', 'Classificação']],
      body: rows,
      theme: 'grid',
      headStyles: {
        fillColor: COLORS.brandBlue,
        textColor: COLORS.white,
        fontSize: 7.4,
        fontStyle: 'bold',
      },
      bodyStyles: {
        fontSize: 7.4,
        textColor: COLORS.slate800,
        cellPadding: 1.7,
      },
      styles: {
        lineColor: COLORS.slate200,
        lineWidth: 0.1,
      },
      columnStyles: {
        0: { cellWidth: 56 },
        1: { cellWidth: 34, halign: 'center', fontStyle: 'bold' },
        2: { cellWidth: 28, halign: 'center' },
        3: { cellWidth: 62 },
      },
    });

    return (doc as any).lastAutoTable?.finalY ?? startY;
  }

  private static renderLCADLScoreCard(doc: jsPDF, result: AssessmentResultV2, y: number, gradientBarDataUrl: string | null): number {
    const payload = this.extractLCADLPayload(result);
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    const contentWidth = pageWidth - margin * 2;
    const leftWidth = 88;
    const gap = 6;
    const rightWidth = contentWidth - leftWidth - gap;
    const cardHeight = 48;
    const totalScore = payload?.totalScore ?? null;
    const maxScore = payload?.maxScore ?? null;
    const percentage = payload?.percentage ?? null;
    const scoreColor = percentage != null ? this.getScoreAccent(Math.round((percentage / 100) * 40)) : COLORS.brandBlue;
    const severity = getLCADLSeverityLabel(percentage);
    const interpretation = resolveAssessmentInterpretationText(payload, getLCADLInterpretationText(percentage));
    const rightX = margin + leftWidth + gap;
    const interpretationWidth = Math.max(40, rightWidth - 4);
    const interpretationLines = doc.splitTextToSize(interpretation, interpretationWidth);
    const barY = Math.max(y + 31, y + 18 + interpretationLines.length * 4.2 + 4);
    const resolvedCardHeight = Math.max(cardHeight, barY + 11 - y);

    doc.setDrawColor(...COLORS.slate200);
    doc.setFillColor(...COLORS.white);
    doc.roundedRect(margin, y, leftWidth, resolvedCardHeight, 4, 4, 'FD');

    doc.setTextColor(...COLORS.slate800);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text('LCADL TOTAL', margin + leftWidth / 2, y + 10, {
      align: 'center',
    });

    doc.setTextColor(...scoreColor);
    doc.setFontSize(32);
    doc.text(typeof totalScore === 'number' ? String(totalScore) : '—', margin + leftWidth / 2, y + 24, { align: 'center' });

    doc.setTextColor(...COLORS.slate600);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text(typeof maxScore === 'number' ? `${totalScore ?? '—'} / ${maxScore}` : 'Pontuação indisponível', margin + leftWidth / 2, y + 31, {
      align: 'center',
    });

    this.renderQuestionnaireClassification(
      doc,
      typeof percentage === 'number' ? `${percentage.toFixed(1)}% - ${severity}` : severity,
      margin + leftWidth / 2,
      y + resolvedCardHeight - 8,
      scoreColor,
    );

    doc.setTextColor(...COLORS.slate800);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.text('Interpretação', rightX, y + 10);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.8);
    doc.setTextColor(...COLORS.slate600);
    doc.text(interpretationLines, rightX, y + 18);

    const barHeight = 8;
    if (gradientBarDataUrl) {
      doc.addImage(gradientBarDataUrl, 'PNG', rightX, barY, rightWidth, barHeight);
    } else {
      this.renderGradientBarFallback(doc, rightX, barY, rightWidth, barHeight);
    }

    if (typeof percentage === 'number') {
      const markerX = rightX + (Math.max(0, Math.min(100, percentage)) / 100) * rightWidth;
      doc.setDrawColor(...COLORS.slate900);
      doc.setLineWidth(1.2);
      doc.line(markerX, barY - 3, markerX, barY + barHeight + 3);
    }

    return y + resolvedCardHeight + 10;
  }

  private static renderLCADLDomainTable(doc: jsPDF, result: AssessmentResultV2, startY: number): number {
    const payload = this.extractLCADLPayload(result);
    const margin = 15;
    const pageWidth = doc.internal.pageSize.getWidth();
    const contentWidth = pageWidth - margin * 2;

    const rows = (Object.keys(LCADL_SECTIONS) as LCADLSectionKey[]).map((sectionKey) => {
      const section = payload?.[sectionKey];
      const percent = section && section.maxScore > 0 ? ((section.score / section.maxScore) * 100) : null;
      return [
        LCADL_SECTIONS[sectionKey].title,
        section?.score != null ? String(section.score) : '—',
        section?.maxScore != null ? String(section.maxScore) : '—',
        percent != null ? `${percent.toFixed(1)}%` : '—',
      ];
    });

    this.runAutoTable(doc, {
      startY,
      margin: { left: margin, right: margin },
      head: [['Domínio', 'Pontuação', 'Máximo', 'Impacto']],
      body: rows,
      theme: 'striped',
      headStyles: {
        fillColor: COLORS.brandBlue,
        textColor: COLORS.white,
        fontSize: 9,
        fontStyle: 'bold',
        halign: 'left',
      },
      bodyStyles: {
        fontSize: 7.7,
        textColor: COLORS.slate800,
        cellPadding: 2.4,
      },
      alternateRowStyles: {
        fillColor: [250, 252, 255],
      },
      columnStyles: {
        0: { cellWidth: contentWidth * 0.46, fontStyle: 'bold' },
        1: {
          cellWidth: contentWidth * 0.16,
          halign: 'center',
          fontStyle: 'bold',
        },
        2: { cellWidth: contentWidth * 0.16, halign: 'center' },
        3: { cellWidth: contentWidth * 0.22, halign: 'center' },
      },
    });

    return (doc as any).lastAutoTable?.finalY ?? startY;
  }

  private static renderLCADLActivitiesTable(doc: jsPDF, result: AssessmentResultV2, startY: number): number {
    const payload = this.extractLCADLPayload(result);
    const margin = 15;
    const pageWidth = doc.internal.pageSize.getWidth();
    const contentWidth = pageWidth - margin * 2;

    const rows = (Object.keys(LCADL_SECTIONS) as LCADLSectionKey[]).flatMap((sectionKey) => {
      const section = payload?.[sectionKey];
      if (!section) return [];
      return section.activities.map((activity, index) => {
        const answer = section.answers[index];
        return [
          activity,
          LCADL_SECTIONS[sectionKey].title,
          typeof answer === 'number' ? String(answer) : '—',
          LCADL_OPTIONS.find((option) => option.value === answer)?.label ?? 'Não respondido',
        ];
      });
    });

    this.runAutoTable(doc, {
      startY,
      margin: { left: margin, right: margin },
      head: [['Atividade', 'Domínio', 'Pontuação', 'Resposta selecionada']],
      body: rows,
      theme: 'striped',
      headStyles: {
        fillColor: COLORS.brandBlue,
        textColor: COLORS.white,
        fontSize: 8.6,
        fontStyle: 'bold',
        halign: 'left',
      },
      bodyStyles: {
        fontSize: 7.8,
        textColor: COLORS.slate800,
        cellPadding: 3.1,
      },
      alternateRowStyles: {
        fillColor: [250, 252, 255],
      },
      columnStyles: {
        0: { cellWidth: contentWidth * 0.27, fontStyle: 'bold' },
        1: { cellWidth: contentWidth * 0.2 },
        2: {
          cellWidth: contentWidth * 0.1,
          halign: 'center',
          fontStyle: 'bold',
        },
        3: { cellWidth: contentWidth * 0.43 },
      },
    });

    return (doc as any).lastAutoTable?.finalY ?? startY;
  }

  private static renderLCADLHistoryTable(doc: jsPDF, previousResults: AssessmentResultV2[], startY: number): number {
    if (!previousResults.length) return startY;

    const rows = previousResults.slice(0, 5).map((item) => {
      const payload = this.extractLCADLPayload(item);
      return [
        this.formatDate(item.assessmentDate),
        payload?.totalScore != null ? `${payload.totalScore}` : '—',
        payload?.percentage != null ? `${payload.percentage.toFixed(1)}%` : '—',
        getLCADLSeverityLabel(payload?.percentage ?? null),
      ];
    });

    this.runAutoTable(doc, {
      startY,
      margin: { left: 15, right: 15 },
      head: [['Histórico recente', 'Pontuação', 'Comprometimento', 'Classificação']],
      body: rows,
      theme: 'grid',
      headStyles: {
        fillColor: [248, 250, 252],
        textColor: COLORS.slate600,
        fontSize: 8,
        fontStyle: 'bold',
      },
      bodyStyles: {
        fontSize: 8.2,
        textColor: COLORS.slate800,
        cellPadding: 3.2,
      },
      styles: {
        lineColor: COLORS.slate200,
        lineWidth: 0.1,
      },
      columnStyles: {
        0: { cellWidth: 52 },
        1: { cellWidth: 28, halign: 'center', fontStyle: 'bold' },
        2: { cellWidth: 34, halign: 'center' },
        3: { cellWidth: 66 },
      },
    });

    return (doc as any).lastAutoTable?.finalY ?? startY;
  }

  private static renderMLHFQScoreCard(doc: jsPDF, result: AssessmentResultV2, y: number): number {
    const payload = this.extractMLHFQPayload(result);
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    const contentWidth = pageWidth - margin * 2;
    const leftWidth = 72;
    const gap = 6;
    const rightWidth = contentWidth - leftWidth - gap;
    const total = payload?.total ?? null;
    const maxScore = payload?.maxScore ?? 105;
    const physicalScore = payload?.physical.score ?? null;
    const emotionalScore = payload?.emotional.score ?? null;
    const interpretation = resolveAssessmentInterpretationText(
      payload,
      getMLHFQInterpretationText(total, physicalScore, emotionalScore),
    );
    const severity = getMLHFQSeverityLabel(total);
    const interpretationLines = doc.splitTextToSize(interpretation, rightWidth - 4);
    const rightCardHeight = 18 + interpretationLines.length * 4.2 + 22;
    const cardHeight = Math.max(48, rightCardHeight);
    const otherScore = payload?.other.score ?? null;

    doc.setDrawColor(...COLORS.slate200);
    doc.setFillColor(...COLORS.white);
    doc.roundedRect(margin, y, leftWidth, cardHeight, 4, 4, 'FD');

    doc.setTextColor(...COLORS.slate800);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text('MLHFQ TOTAL', margin + leftWidth / 2, y + 10, {
      align: 'center',
    });

    doc.setTextColor(...this.getMLHFQAccent(total));
    doc.setFontSize(28);
    doc.text(typeof total === 'number' ? String(total) : '—', margin + leftWidth / 2, y + 24, { align: 'center' });

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...COLORS.slate600);
    doc.text(typeof total === 'number' ? `${total}/${maxScore}` : 'Pontuação indisponível', margin + leftWidth / 2, y + 31, {
      align: 'center',
    });

    this.renderQuestionnaireClassification(doc, severity, margin + leftWidth / 2, y + cardHeight - 8, this.getMLHFQAccent(total));

    const rightX = margin + leftWidth + gap;
    doc.setTextColor(...COLORS.slate800);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.text('Interpretação', rightX, y + 10);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(...COLORS.slate600);
    doc.text(interpretationLines, rightX, y + 18);

    const domainY = y + 18 + interpretationLines.length * 4.2 + 4;
    const domainWidth = (rightWidth - 8) / 3;
    const domains = [
      { label: 'Físico', value: physicalScore, color: [244, 63, 94] as RGB },
      {
        label: 'Emocional',
        value: emotionalScore,
        color: [245, 158, 11] as RGB,
      },
      { label: 'Outros', value: otherScore, color: [37, 99, 235] as RGB },
    ];

    domains.forEach((domain, index) => {
      const boxX = rightX + index * (domainWidth + 4);
      doc.setFillColor(...COLORS.slate50);
      doc.setDrawColor(...COLORS.slate200);
      doc.roundedRect(boxX, domainY, domainWidth, 14, 2.5, 2.5, 'FD');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7.4);
      doc.setTextColor(...COLORS.slate500);
      doc.text(domain.label.toUpperCase(), boxX + 3, domainY + 4.8);
      doc.setTextColor(...domain.color);
      doc.setFontSize(11);
      doc.text(typeof domain.value === 'number' ? String(domain.value) : '—', boxX + 3, domainY + 10.5);
    });

    return y + cardHeight + 8;
  }

  private static renderMLHFQDomainTable(doc: jsPDF, result: AssessmentResultV2, startY: number): number {
    const payload = this.extractMLHFQPayload(result);
    const rows = [
      ['Físico', payload?.physical.score != null ? String(payload.physical.score) : '—', payload?.physical.maxScore != null ? String(payload.physical.maxScore) : '—'],
      ['Emocional', payload?.emotional.score != null ? String(payload.emotional.score) : '—', payload?.emotional.maxScore != null ? String(payload.emotional.maxScore) : '—'],
      ['Outros', payload?.other.score != null ? String(payload.other.score) : '—', payload?.other.maxScore != null ? String(payload.other.maxScore) : '—'],
    ];

    this.runAutoTable(doc, {
      startY,
      margin: { left: 15, right: 15 },
      head: [['Domínio', 'Pontuação', 'Máximo']],
      body: rows,
      theme: 'striped',
      headStyles: {
        fillColor: COLORS.brandBlue,
        textColor: COLORS.white,
        fontSize: 9,
        fontStyle: 'bold',
      },
      bodyStyles: {
        fontSize: 8.5,
        textColor: COLORS.slate800,
        cellPadding: 3.6,
      },
      alternateRowStyles: {
        fillColor: [250, 252, 255],
      },
      columnStyles: {
        0: { cellWidth: 92, fontStyle: 'bold' },
        1: { cellWidth: 40, halign: 'center', fontStyle: 'bold' },
        2: { cellWidth: 40, halign: 'center' },
      },
    });

    return (doc as any).lastAutoTable?.finalY ?? startY;
  }

  private static renderMLHFQResponsesTable(doc: jsPDF, result: AssessmentResultV2, startY: number): number {
    const payload = this.extractMLHFQPayload(result);
    const answers = payload?.answers ?? [];

    const rows = MLHFQ_ITEMS_PT.map((question, index) => {
      const itemNumber = index + 1;
      const score = typeof answers[index] === 'number' && answers[index] >= 0 ? answers[index] : null;
      const domain = MLHFQ_PHYSICAL_1BASED.includes(itemNumber as (typeof MLHFQ_PHYSICAL_1BASED)[number])
        ? 'Físico'
        : MLHFQ_EMOTIONAL_1BASED.includes(itemNumber as (typeof MLHFQ_EMOTIONAL_1BASED)[number])
          ? 'Emocional'
          : 'Outros';
      return [String(itemNumber), question, domain, MLHFQ_OPTIONS.find((option) => option.value === score)?.label ?? 'Não respondido', score != null ? String(score) : '—'];
    });

    this.runAutoTable(doc, {
      startY,
      margin: { left: 15, right: 15 },
      head: [['#', 'Questão', 'Domínio', 'Resposta selecionada', 'Pontuação']],
      body: rows,
      theme: 'striped',
      headStyles: {
        fillColor: COLORS.brandBlue,
        textColor: COLORS.white,
        fontSize: 8.4,
        fontStyle: 'bold',
      },
      bodyStyles: {
        fontSize: 7.7,
        textColor: COLORS.slate800,
        cellPadding: 3,
      },
      alternateRowStyles: {
        fillColor: [250, 252, 255],
      },
      didParseCell: (hookData) => {
        if (hookData.section === 'body' && hookData.column.index === 2) {
          const domain = String(hookData.cell.raw || '');
          if (domain === 'Físico') {
            hookData.cell.styles.fillColor = [255, 228, 230];
            hookData.cell.styles.textColor = [190, 24, 93];
            hookData.cell.styles.fontStyle = 'bold';
          } else if (domain === 'Emocional') {
            hookData.cell.styles.fillColor = [255, 247, 237];
            hookData.cell.styles.textColor = [180, 83, 9];
            hookData.cell.styles.fontStyle = 'bold';
          } else {
            hookData.cell.styles.fillColor = [219, 234, 254];
            hookData.cell.styles.textColor = [29, 78, 216];
            hookData.cell.styles.fontStyle = 'bold';
          }
        }
        if (hookData.section === 'body' && hookData.column.index === 4) {
          hookData.cell.styles.halign = 'center';
          hookData.cell.styles.fontStyle = 'bold';
        }
      },
      columnStyles: {
        0: { cellWidth: 10, halign: 'center', fontStyle: 'bold' },
        1: { cellWidth: 78, fontStyle: 'bold' },
        2: { cellWidth: 22, halign: 'center' },
        3: { cellWidth: 65 },
        4: { cellWidth: 15 },
      },
    });

    return (doc as any).lastAutoTable?.finalY ?? startY;
  }

  private static renderMLHFQHistoryTable(doc: jsPDF, previousResults: AssessmentResultV2[], startY: number): number {
    if (!previousResults.length) return startY;

    const rows = previousResults.slice(0, 5).map((item) => {
      const payload = this.extractMLHFQPayload(item);
      return [
        this.formatDate(item.assessmentDate),
        payload?.total != null ? `${payload.total}` : '—',
        payload?.physical.score != null ? `${payload.physical.score}` : '—',
        payload?.emotional.score != null ? `${payload.emotional.score}` : '—',
        getMLHFQSeverityLabel(payload?.total ?? null),
      ];
    });

    this.runAutoTable(doc, {
      startY,
      margin: { left: 15, right: 15 },
      head: [['Histórico recente', 'Total', 'Físico', 'Emocional', 'Classificação']],
      body: rows,
      theme: 'grid',
      headStyles: {
        fillColor: [248, 250, 252],
        textColor: COLORS.slate600,
        fontSize: 8,
        fontStyle: 'bold',
      },
      bodyStyles: {
        fontSize: 8.2,
        textColor: COLORS.slate800,
        cellPadding: 3.2,
      },
      styles: {
        lineColor: COLORS.slate200,
        lineWidth: 0.1,
      },
      columnStyles: {
        0: { cellWidth: 48 },
        1: { cellWidth: 22, halign: 'center', fontStyle: 'bold' },
        2: { cellWidth: 22, halign: 'center' },
        3: { cellWidth: 24, halign: 'center' },
        4: { cellWidth: 64 },
      },
    });

    return (doc as any).lastAutoTable?.finalY ?? startY;
  }

  private static renderPFSDQScoreCard(doc: jsPDF, result: AssessmentResultV2, y: number): number {
    const payload = this.extractPFSDQPayload(result);
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    const contentWidth = pageWidth - margin * 2;
    const leftWidth = 72;
    const gap = 6;
    const rightWidth = contentWidth - leftWidth - gap;
    const total = payload?.total ?? null;
    const dyspneaScore = payload?.dyspnea.score ?? null;
    const fatigueScore = payload?.fatigue.score ?? null;
    const changeScore = payload?.change.score ?? null;
    const interpretation = resolveAssessmentInterpretationText(
      payload,
      getPFSDQInterpretationText(dyspneaScore, fatigueScore, changeScore),
    );
    const severity = getPFSDQSeverityLabel(total);
    const interpretationLines = doc.splitTextToSize(interpretation, rightWidth - 4);
    const rightCardHeight = 18 + interpretationLines.length * 4.2 + 22;
    const cardHeight = Math.max(48, rightCardHeight);

    doc.setDrawColor(...COLORS.slate200);
    doc.setFillColor(...COLORS.white);
    doc.roundedRect(margin, y, leftWidth, cardHeight, 4, 4, 'FD');

    doc.setTextColor(...COLORS.slate800);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text('PFSDQ-M', margin + leftWidth / 2, y + 10, { align: 'center' });

    doc.setTextColor(...this.getPFSDQAccent(total));
    doc.setFontSize(28);
    doc.text(typeof total === 'number' ? String(total) : '—', margin + leftWidth / 2, y + 24, { align: 'center' });

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...COLORS.slate600);
    doc.text(typeof total === 'number' ? `${total}/300` : 'Pontuação indisponível', margin + leftWidth / 2, y + 31, {
      align: 'center',
    });

    this.renderQuestionnaireClassification(doc, severity, margin + leftWidth / 2, y + cardHeight - 8, this.getPFSDQAccent(total));

    const rightX = margin + leftWidth + gap;
    doc.setTextColor(...COLORS.slate800);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.text('Interpretação', rightX, y + 10);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(...COLORS.slate600);
    doc.text(interpretationLines, rightX, y + 18);

    const domainY = y + 18 + interpretationLines.length * 4.2 + 4;
    const domainWidth = (rightWidth - 8) / 3;
    const domains = [
      { label: 'Dispneia', value: dyspneaScore, color: [244, 63, 94] as RGB },
      { label: 'Fadiga', value: fatigueScore, color: [245, 158, 11] as RGB },
      { label: 'Mudança', value: changeScore, color: [37, 99, 235] as RGB },
    ];

    domains.forEach((domain, index) => {
      const boxX = rightX + index * (domainWidth + 4);
      doc.setFillColor(...COLORS.slate50);
      doc.setDrawColor(...COLORS.slate200);
      doc.roundedRect(boxX, domainY, domainWidth, 14, 2.5, 2.5, 'FD');
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7.4);
      doc.setTextColor(...COLORS.slate500);
      doc.text(domain.label.toUpperCase(), boxX + 3, domainY + 4.8);
      doc.setTextColor(...domain.color);
      doc.setFontSize(11);
      doc.text(typeof domain.value === 'number' ? String(domain.value) : '—', boxX + 3, domainY + 10.5);
    });

    return y + cardHeight + 8;
  }

  private static renderPFSDQGeneralTable(doc: jsPDF, result: AssessmentResultV2, startY: number): number {
    const payload = this.extractPFSDQPayload(result);
    const rows = [
      [
        'Dispneia',
        payload?.dyspnea.general.q1 === true ? 'Sim' : payload?.dyspnea.general.q1 === false ? 'Não' : '—',
        payload?.dyspnea.general.q2_per_month || '—',
        payload?.dyspnea.general.q3 != null ? `${payload.dyspnea.general.q3}/10` : '—',
        payload?.dyspnea.general.q4 != null ? `${payload.dyspnea.general.q4}/10` : '—',
        payload?.dyspnea.general.q5 != null ? `${payload.dyspnea.general.q5}/10` : '—',
      ],
      [
        'Fadiga',
        payload?.fatigue.general.q1 === true ? 'Sim' : payload?.fatigue.general.q1 === false ? 'Não' : '—',
        payload?.fatigue.general.q2_per_month || '—',
        payload?.fatigue.general.q3 != null ? `${payload.fatigue.general.q3}/10` : '—',
        payload?.fatigue.general.q4 != null ? `${payload.fatigue.general.q4}/10` : '—',
        payload?.fatigue.general.q5 != null ? `${payload.fatigue.general.q5}/10` : '—',
      ],
    ];

    this.runAutoTable(doc, {
      startY,
      margin: { left: 15, right: 15 },
      head: [['Domínio', 'Já sentiu?', 'Vezes/mês', 'Ano passado', 'Hoje', 'Maioria das AVDs']],
      body: rows,
      theme: 'striped',
      headStyles: {
        fillColor: COLORS.brandBlue,
        textColor: COLORS.white,
        fontSize: 8.2,
        fontStyle: 'bold',
      },
      bodyStyles: {
        fontSize: 7.8,
        textColor: COLORS.slate800,
        cellPadding: 3,
      },
      alternateRowStyles: {
        fillColor: [250, 252, 255],
      },
      columnStyles: {
        0: { cellWidth: 28, fontStyle: 'bold' },
        1: { cellWidth: 22, halign: 'center' },
        2: { cellWidth: 24, halign: 'center' },
        3: { cellWidth: 26, halign: 'center' },
        4: { cellWidth: 18, halign: 'center' },
        5: { cellWidth: 34, halign: 'center' },
      },
    });

    return (doc as any).lastAutoTable?.finalY ?? startY;
  }

  private static renderPFSDQActivitiesTable(doc: jsPDF, result: AssessmentResultV2, startY: number): number {
    const payload = this.extractPFSDQPayload(result);
    const rows = PFSDQ_ACTIVITIES.map((activity, index) => {
      const dyspnea = payload?.dyspnea.activities[index] ?? null;
      const fatigue = payload?.fatigue.activities[index] ?? null;
      const change = payload?.change.activities[index] ?? null;
      return [
        activity,
        dyspnea != null ? `${dyspnea}/10` : '—',
        fatigue != null ? `${fatigue}/10` : '—',
        change?.never ? 'Nunca realizou' : change?.value != null ? `${change.value}/10` : '—',
      ];
    });

    this.runAutoTable(doc, {
      startY,
      margin: { left: 15, right: 15 },
      head: [['Atividade', 'Dispneia', 'Fadiga', 'Mudança']],
      body: rows,
      theme: 'striped',
      headStyles: {
        fillColor: COLORS.brandBlue,
        textColor: COLORS.white,
        fontSize: 8.5,
        fontStyle: 'bold',
      },
      bodyStyles: {
        fontSize: 7.8,
        textColor: COLORS.slate800,
        cellPadding: 3,
      },
      alternateRowStyles: {
        fillColor: [250, 252, 255],
      },
      columnStyles: {
        0: { cellWidth: 86, fontStyle: 'bold' },
        1: { cellWidth: 22, halign: 'center' },
        2: { cellWidth: 22, halign: 'center' },
        3: { cellWidth: 40, halign: 'center' },
      },
    });

    return (doc as any).lastAutoTable?.finalY ?? startY;
  }

  private static renderPFSDQHistoryTable(doc: jsPDF, previousResults: AssessmentResultV2[], startY: number): number {
    if (!previousResults.length) return startY;

    const rows = previousResults.slice(0, 5).map((item) => {
      const payload = this.extractPFSDQPayload(item);
      return [
        this.formatDate(item.assessmentDate),
        payload?.dyspnea.score != null ? `${payload.dyspnea.score}` : '—',
        payload?.fatigue.score != null ? `${payload.fatigue.score}` : '—',
        payload?.change.score != null ? `${payload.change.score}` : '—',
        payload?.total != null ? `${payload.total}` : '—',
      ];
    });

    this.runAutoTable(doc, {
      startY,
      margin: { left: 15, right: 15 },
      head: [['Histórico recente', 'Dispneia', 'Fadiga', 'Mudança', 'Total']],
      body: rows,
      theme: 'grid',
      headStyles: {
        fillColor: [248, 250, 252],
        textColor: COLORS.slate600,
        fontSize: 8,
        fontStyle: 'bold',
      },
      bodyStyles: {
        fontSize: 8.2,
        textColor: COLORS.slate800,
        cellPadding: 3.2,
      },
      styles: {
        lineColor: COLORS.slate200,
        lineWidth: 0.1,
      },
      columnStyles: {
        0: { cellWidth: 52 },
        1: { cellWidth: 30, halign: 'center', fontStyle: 'bold' },
        2: { cellWidth: 30, halign: 'center' },
        3: { cellWidth: 30, halign: 'center' },
        4: { cellWidth: 38, halign: 'center', fontStyle: 'bold' },
      },
    });

    return (doc as any).lastAutoTable?.finalY ?? startY;
  }

  private static renderSAQScoreCard(doc: jsPDF, result: AssessmentResultV2, y: number): number {
    const payload = this.extractSAQPayload(result);
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    const contentWidth = pageWidth - margin * 2;
    const leftWidth = 72;
    const gap = 6;
    const rightWidth = contentWidth - leftWidth - gap;
    const overallScore = getSAQOverallScore(payload?.domainScores ?? null);
    const interpretation = resolveAssessmentInterpretationText(
      payload,
      getSAQInterpretationText(payload?.domainScores ?? null),
    );
    const severity = getSAQSeverityLabel(overallScore);
    const interpretationLines = doc.splitTextToSize(interpretation, rightWidth - 4);
    const rightCardHeight = 18 + interpretationLines.length * 4.2;
    const cardHeight = Math.max(42, rightCardHeight);

    doc.setDrawColor(...COLORS.slate200);
    doc.setFillColor(...COLORS.white);
    doc.roundedRect(margin, y, leftWidth, cardHeight, 4, 4, 'FD');

    doc.setTextColor(...COLORS.slate800);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text('QUESTIONÁRIO ADAPTADO LEGADO', margin + leftWidth / 2, y + 10, { align: 'center' });

    doc.setTextColor(...this.getSAQAccent(overallScore));
    doc.setFontSize(28);
    doc.text(overallScore != null ? overallScore.toFixed(1) : '—', margin + leftWidth / 2, y + 24, { align: 'center' });

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...COLORS.slate600);
    doc.text('0–100', margin + leftWidth / 2, y + 31, { align: 'center' });

    this.renderQuestionnaireClassification(doc, severity, margin + leftWidth / 2, y + cardHeight - 8, this.getSAQAccent(overallScore));

    const rightX = margin + leftWidth + gap;
    doc.setTextColor(...COLORS.slate800);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.text('Interpretação', rightX, y + 10);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(...COLORS.slate600);
    doc.text(interpretationLines, rightX, y + 18);

    return y + cardHeight + 8;
  }

  private static renderSAQDomainTable(doc: jsPDF, result: AssessmentResultV2, startY: number): number {
    const payload = this.extractSAQPayload(result);
    const rows = SAQ_DOMAIN_SEQUENCE.map((key) => [
      SAQ_DOMAIN_LABELS[key],
      payload?.domainScores[key] != null ? payload.domainScores[key]!.toFixed(1) : '—',
      describeSaqScore(payload?.domainScores[key] ?? null),
    ]);

    this.runAutoTable(doc, {
      startY,
      margin: { left: 15, right: 15 },
      head: [['Domínio', 'Pontuação', 'Classificação']],
      body: rows,
      theme: 'striped',
      headStyles: {
        fillColor: COLORS.brandBlue,
        textColor: COLORS.white,
        fontSize: 8.6,
        fontStyle: 'bold',
      },
      bodyStyles: {
        fontSize: 7.5,
        textColor: COLORS.slate800,
        cellPadding: 2.3,
      },
      alternateRowStyles: {
        fillColor: [250, 252, 255],
      },
      columnStyles: {
        0: { cellWidth: 78, fontStyle: 'bold' },
        1: { cellWidth: 28, halign: 'center', fontStyle: 'bold' },
        2: { cellWidth: 74 },
      },
    });

    return (doc as any).lastAutoTable?.finalY ?? startY;
  }

  private static renderSAQInterpretationBlock(doc: jsPDF, result: AssessmentResultV2, startY: number): number {
    const payload = this.extractSAQPayload(result);
    if (getAssessmentInterpretationOverride(payload)) return startY;
    const interpretation = Array.isArray(payload?.interpretation) ? payload.interpretation : [];
    if (!interpretation.length) return startY;

    const margin = 15;
    const pageWidth = doc.internal.pageSize.getWidth();
    const width = pageWidth - margin * 2;
    const lines = interpretation.flatMap((item) => doc.splitTextToSize(`• ${item}`, width - 8));
    const height = Math.max(16, 8 + lines.length * 4.2);

    doc.setDrawColor(...COLORS.slate200);
    doc.setFillColor(...COLORS.slate50);
    doc.roundedRect(margin, startY, width, height, 3, 3, 'FD');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9.3);
    doc.setTextColor(...COLORS.slate800);
    doc.text('Resumo clínico', margin + 4, startY + 6);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.3);
    doc.setTextColor(...COLORS.slate600);
    doc.text(lines, margin + 4, startY + 11);

    return startY + height + 8;
  }

  private static renderSAQPhysicalTable(doc: jsPDF, result: AssessmentResultV2, startY: number): number {
    const payload = this.extractSAQPayload(result);
    const rows = SAQ_PHYSICAL_ITEMS.map((activity, index) => {
      const answerIndex = payload?.physicalAnswers[index] ?? -1;
      const option = answerIndex >= 0 ? SAQ_LIMITATION_OPTIONS[answerIndex] : null;
      return [activity, option?.label ?? 'Não respondido', option?.score != null ? String(option.score) : '—'];
    });

    this.runAutoTable(doc, {
      startY,
      margin: { left: 15, right: 15 },
      head: [['Atividade (limitação física)', 'Resposta', 'Score']],
      body: rows,
      theme: 'striped',
      headStyles: {
        fillColor: COLORS.brandBlue,
        textColor: COLORS.white,
        fontSize: 8.5,
        fontStyle: 'bold',
      },
      bodyStyles: {
        fontSize: 8,
        textColor: COLORS.slate800,
        cellPadding: 3.1,
      },
      alternateRowStyles: {
        fillColor: [250, 252, 255],
      },
      columnStyles: {
        0: { cellWidth: 96, fontStyle: 'bold' },
        1: { cellWidth: 56 },
        2: { cellWidth: 18, halign: 'center', fontStyle: 'bold' },
      },
    });

    return (doc as any).lastAutoTable?.finalY ?? startY;
  }

  private static renderSAQHistoryTable(doc: jsPDF, previousResults: AssessmentResultV2[], startY: number): number {
    if (!previousResults.length) return startY;

    const rows = previousResults.slice(0, 5).map((item) => {
      const payload = this.extractSAQPayload(item);
      const overallScore = getSAQOverallScore(payload?.domainScores ?? null);
      return [
        this.formatDate(item.assessmentDate),
        overallScore != null ? overallScore.toFixed(1) : '—',
        payload?.domainScores.physicalLimitation != null ? payload.domainScores.physicalLimitation.toFixed(1) : '—',
        payload?.domainScores.anginaFrequency != null ? payload.domainScores.anginaFrequency.toFixed(1) : '—',
        getSAQSeverityLabel(overallScore),
      ];
    });

    this.runAutoTable(doc, {
      startY,
      margin: { left: 15, right: 15 },
      head: [['Histórico recente', 'Global', 'Limitação física', 'Frequência', 'Classificação']],
      body: rows,
      theme: 'grid',
      headStyles: {
        fillColor: [248, 250, 252],
        textColor: COLORS.slate600,
        fontSize: 8,
        fontStyle: 'bold',
      },
      bodyStyles: {
        fontSize: 8.2,
        textColor: COLORS.slate800,
        cellPadding: 3.2,
      },
      styles: {
        lineColor: COLORS.slate200,
        lineWidth: 0.1,
      },
      columnStyles: {
        0: { cellWidth: 44 },
        1: { cellWidth: 20, halign: 'center', fontStyle: 'bold' },
        2: { cellWidth: 34, halign: 'center' },
        3: { cellWidth: 26, halign: 'center' },
        4: { cellWidth: 56 },
      },
    });

    return (doc as any).lastAutoTable?.finalY ?? startY;
  }

  private static renderSGRQScoreCard(doc: jsPDF, result: AssessmentResultV2, y: number): number {
    const payload = this.extractSGRQPayload(result);
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    const contentWidth = pageWidth - margin * 2;
    const leftWidth = 72;
    const gap = 6;
    const rightWidth = contentWidth - leftWidth - gap;
    const total = payload?.total ?? null;
    const interpretation = resolveAssessmentInterpretationText(
      payload,
      getSGRQInterpretationText(total, {
        symptoms: payload?.symptoms ?? null,
        activity: payload?.activity ?? null,
        impact: payload?.impact ?? null,
      }),
    );
    const severity = getSGRQSeverityLabel(total);
    const interpretationLines = doc.splitTextToSize(interpretation, rightWidth - 4);
    const rightCardHeight = 18 + interpretationLines.length * 4.2;
    const cardHeight = Math.max(42, rightCardHeight);

    doc.setDrawColor(...COLORS.slate200);
    doc.setFillColor(...COLORS.white);
    doc.roundedRect(margin, y, leftWidth, cardHeight, 4, 4, 'FD');

    doc.setTextColor(...COLORS.slate800);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text('SGRQ TOTAL', margin + leftWidth / 2, y + 10, { align: 'center' });

    doc.setTextColor(...this.getSGRQAccent(total));
    doc.setFontSize(28);
    doc.text(total != null ? total.toFixed(1) : '—', margin + leftWidth / 2, y + 24, { align: 'center' });

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...COLORS.slate600);
    doc.text('0–100', margin + leftWidth / 2, y + 31, { align: 'center' });

    this.renderQuestionnaireClassification(doc, severity, margin + leftWidth / 2, y + cardHeight - 8, this.getSGRQAccent(total));

    const rightX = margin + leftWidth + gap;
    doc.setTextColor(...COLORS.slate800);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.text('Interpretação', rightX, y + 10);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(...COLORS.slate600);
    doc.text(interpretationLines, rightX, y + 18);

    return y + cardHeight + 8;
  }

  private static renderSGRQDomainTable(doc: jsPDF, result: AssessmentResultV2, startY: number): number {
    const payload = this.extractSGRQPayload(result);
    const rows: Array<[string, string, string]> = [
      [
        SGRQ_DOMAIN_LABELS.symptoms,
        payload?.symptoms != null ? payload.symptoms.toFixed(1) : '—',
        payload?.symptoms == null
          ? 'Sem dados'
          : payload.symptoms < 25
            ? 'Baixo comprometimento'
            : payload.symptoms < 50
              ? 'Comprometimento moderado'
              : payload.symptoms < 75
                ? 'Comprometimento elevado'
                : 'Comprometimento muito elevado',
      ],
      [
        SGRQ_DOMAIN_LABELS.activity,
        payload?.activity != null ? payload.activity.toFixed(1) : '—',
        payload?.activity == null
          ? 'Sem dados'
          : payload.activity < 25
            ? 'Baixo comprometimento'
            : payload.activity < 50
              ? 'Comprometimento moderado'
              : payload.activity < 75
                ? 'Comprometimento elevado'
                : 'Comprometimento muito elevado',
      ],
      [
        SGRQ_DOMAIN_LABELS.impact,
        payload?.impact != null ? payload.impact.toFixed(1) : '—',
        payload?.impact == null
          ? 'Sem dados'
          : payload.impact < 25
            ? 'Baixo comprometimento'
            : payload.impact < 50
              ? 'Comprometimento moderado'
              : payload.impact < 75
                ? 'Comprometimento elevado'
                : 'Comprometimento muito elevado',
      ],
    ];

    this.runAutoTable(doc, {
      startY,
      margin: { left: 15, right: 15 },
      head: [['Domínio', 'Pontuação', 'Leitura clínica']],
      body: rows,
      theme: 'striped',
      headStyles: {
        fillColor: COLORS.brandBlue,
        textColor: COLORS.white,
        fontSize: 8.6,
        fontStyle: 'bold',
      },
      bodyStyles: {
        fontSize: 7.5,
        textColor: COLORS.slate800,
        cellPadding: 2.3,
      },
      alternateRowStyles: {
        fillColor: [250, 252, 255],
      },
      columnStyles: {
        0: { cellWidth: 56, fontStyle: 'bold' },
        1: { cellWidth: 26, halign: 'center', fontStyle: 'bold' },
        2: { cellWidth: 98 },
      },
    });

    return (doc as any).lastAutoTable?.finalY ?? startY;
  }

  private static renderSGRQMethodologyBlock(doc: jsPDF, result: AssessmentResultV2, startY: number): number {
    const payload = this.extractSGRQPayload(result);
    const margin = 15;
    const pageWidth = doc.internal.pageSize.getWidth();
    const width = pageWidth - margin * 2;
    const baseNotes = [
      'O SGRQ avalia o impacto da doença respiratória na qualidade de vida.',
      'Pontuações variam de 0 a 100, onde valores mais altos indicam pior qualidade de vida.',
      'Diferenças de 4 pontos ou mais costumam ser clinicamente significativas em reavaliações seriadas.',
    ];
    const payloadNotes = Array.isArray(payload?.clinicalNotes) ? payload.clinicalNotes : [];
    const lines = [...baseNotes, ...payloadNotes].flatMap((item) => doc.splitTextToSize(`• ${item}`, width - 8));
    const height = Math.max(18, 8 + lines.length * 4.2);

    doc.setDrawColor(...COLORS.slate200);
    doc.setFillColor(...COLORS.slate50);
    doc.roundedRect(margin, startY, width, height, 3, 3, 'FD');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9.3);
    doc.setTextColor(...COLORS.slate800);
    doc.text('Leitura clínica e método', margin + 4, startY + 6);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.3);
    doc.setTextColor(...COLORS.slate600);
    doc.text(lines, margin + 4, startY + 11);

    return startY + height + 8;
  }

  private static renderSGRQHistoryTable(doc: jsPDF, previousResults: AssessmentResultV2[], startY: number): number {
    if (!previousResults.length) return startY;

    const rows = previousResults.slice(0, 5).map((item) => {
      const payload = this.extractSGRQPayload(item);
      return [
        this.formatDate(item.assessmentDate),
        payload?.total != null ? payload.total.toFixed(1) : '—',
        payload?.symptoms != null ? payload.symptoms.toFixed(1) : '—',
        payload?.activity != null ? payload.activity.toFixed(1) : '—',
        getSGRQSeverityLabel(payload?.total ?? null),
      ];
    });

    this.runAutoTable(doc, {
      startY,
      margin: { left: 15, right: 15 },
      head: [['Histórico recente', 'Total', 'Sintomas', 'Atividade', 'Classificação']],
      body: rows,
      theme: 'grid',
      headStyles: {
        fillColor: [248, 250, 252],
        textColor: COLORS.slate600,
        fontSize: 8,
        fontStyle: 'bold',
      },
      bodyStyles: {
        fontSize: 8.2,
        textColor: COLORS.slate800,
        cellPadding: 3.2,
      },
      styles: {
        lineColor: COLORS.slate200,
        lineWidth: 0.1,
      },
      columnStyles: {
        0: { cellWidth: 44 },
        1: { cellWidth: 20, halign: 'center', fontStyle: 'bold' },
        2: { cellWidth: 24, halign: 'center' },
        3: { cellWidth: 24, halign: 'center' },
        4: { cellWidth: 68 },
      },
    });

    return (doc as any).lastAutoTable?.finalY ?? startY;
  }

  private static renderAQLQScoreCard(doc: jsPDF, result: AssessmentResultV2, y: number): number {
    const payload = this.extractAQLQPayload(result);
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    const contentWidth = pageWidth - margin * 2;
    const leftWidth = 72;
    const gap = 6;
    const rightWidth = contentWidth - leftWidth - gap;
    const total = payload?.total ?? null;
    const severity = getAQLQSeverityLabel(total);
    const interpretation = resolveAssessmentInterpretationText(
      payload,
      getAQLQInterpretationText(total, payload?.domainScores ?? null, payload?.sourceMissingItems ?? AQLQ_SOURCE_MISSING_ITEMS),
    );
    const interpretationLines = doc.splitTextToSize(interpretation, rightWidth - 4);
    const noteLines =
      payload?.sourceMissingItems?.length
        ? doc.splitTextToSize(
            `Anexo fornecido sem a questão ${payload.sourceMissingItems.join(', ')}. A média foi calculada sobre os itens visíveis.`,
            rightWidth - 10,
          )
        : [];
    const noteHeight = noteLines.length ? 10 + noteLines.length * 4 : 0;
    const cardHeight = Math.max(52, 22 + interpretationLines.length * 4.2 + noteHeight);
    const rightX = margin + leftWidth + gap;

    doc.setDrawColor(...COLORS.slate200);
    doc.setFillColor(...COLORS.white);
    doc.roundedRect(margin, y, leftWidth, cardHeight, 4, 4, 'FD');

    doc.setTextColor(...COLORS.slate800);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text('AQLQ TOTAL', margin + leftWidth / 2, y + 10, { align: 'center' });

    doc.setTextColor(...this.getAQLQAccent(total));
    doc.setFontSize(28);
    doc.text(total != null ? total.toFixed(2) : '—', margin + leftWidth / 2, y + 24, { align: 'center' });

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...COLORS.slate600);
    doc.text(total != null ? `${total.toFixed(2)}/7` : 'Pontuação indisponível', margin + leftWidth / 2, y + 31, {
      align: 'center',
    });
    doc.text('Quanto maior, melhor', margin + leftWidth / 2, y + 36, {
      align: 'center',
    });

    this.renderQuestionnaireClassification(doc, severity, margin + leftWidth / 2, y + cardHeight - 8, this.getAQLQAccent(total));

    doc.setTextColor(...COLORS.slate800);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.text('Interpretação', rightX, y + 10);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(...COLORS.slate600);
    doc.text(interpretationLines, rightX, y + 18);

    if (noteLines.length) {
      const noteY = y + 20 + interpretationLines.length * 4.2 + 3;
      doc.setFillColor(255, 251, 235);
      doc.setDrawColor(253, 230, 138);
      doc.roundedRect(rightX, noteY, rightWidth, noteHeight, 2.5, 2.5, 'FD');
      doc.setFontSize(7.8);
      doc.setTextColor(146, 64, 14);
      doc.text(noteLines, rightX + 3, noteY + 5);
    }

    return y + cardHeight + 8;
  }

  private static renderAQLQDomainTable(doc: jsPDF, result: AssessmentResultV2, startY: number): number {
    const payload = this.extractAQLQPayload(result);
    const rows = (Object.keys(AQLQ_DOMAIN_LABELS) as AQLQDomainKey[]).map((key) => {
      const domain = payload?.domainScores[key];
      return [
        AQLQ_DOMAIN_LABELS[key],
        domain?.score != null ? domain.score.toFixed(2) : '—',
        domain?.answered != null ? String(domain.answered) : '—',
        Array.isArray(domain?.itemNumbers) ? domain.itemNumbers.join(', ') : '—',
      ];
    });

    this.runAutoTable(doc, {
      startY,
      margin: { left: 15, right: 15 },
      head: [['Domínio', 'Score médio', 'Itens respondidos', 'Itens considerados']],
      body: rows,
      theme: 'striped',
      headStyles: {
        fillColor: COLORS.brandBlue,
        textColor: COLORS.white,
        fontSize: 8.5,
        fontStyle: 'bold',
      },
      bodyStyles: {
        fontSize: 7.4,
        textColor: COLORS.slate800,
        cellPadding: 2.3,
      },
      alternateRowStyles: {
        fillColor: [250, 252, 255],
      },
      columnStyles: {
        0: { cellWidth: 48, fontStyle: 'bold' },
        1: { cellWidth: 24, halign: 'center', fontStyle: 'bold' },
        2: { cellWidth: 28, halign: 'center' },
        3: { cellWidth: 80 },
      },
    });

    return (doc as any).lastAutoTable?.finalY ?? startY;
  }

  private static renderAQLQResponsesTable(doc: jsPDF, result: AssessmentResultV2, startY: number): number {
    const payload = this.extractAQLQPayload(result);
    const rows = AQLQ_QUESTIONS.map((question) => {
      const score = payload?.answersByItem[String(question.itemNumber)];
      return [
        String(question.itemNumber),
        question.prompt,
        AQLQ_DOMAIN_LABELS[question.domain],
        getAQLQAnswerLabel(question, typeof score === 'number' ? score : null),
        typeof score === 'number' ? String(score) : '—',
      ];
    });

    this.runAutoTable(doc, {
      startY,
      margin: { left: 15, right: 15 },
      head: [['#', 'Questão', 'Domínio', 'Resposta selecionada', 'Score']],
      body: rows,
      theme: 'striped',
      headStyles: {
        fillColor: COLORS.brandBlue,
        textColor: COLORS.white,
        fontSize: 8.2,
        fontStyle: 'bold',
      },
      bodyStyles: {
        fontSize: 7.5,
        textColor: COLORS.slate800,
        cellPadding: 2.9,
      },
      alternateRowStyles: {
        fillColor: [250, 252, 255],
      },
      columnStyles: {
        0: { cellWidth: 10, halign: 'center', fontStyle: 'bold' },
        1: { cellWidth: 72, fontStyle: 'bold' },
        2: { cellWidth: 24, halign: 'center' },
        3: { cellWidth: 49 },
        4: { cellWidth: 15, halign: 'center', fontStyle: 'bold' },
      },
    });

    return (doc as any).lastAutoTable?.finalY ?? startY;
  }

  private static renderAQLQHistoryTable(doc: jsPDF, previousResults: AssessmentResultV2[], startY: number): number {
    if (!previousResults.length) return startY;

    const rows = previousResults.slice(0, 5).map((item) => {
      const payload = this.extractAQLQPayload(item);
      return [
        this.formatDate(item.assessmentDate),
        payload?.total != null ? payload.total.toFixed(2) : '—',
        payload?.domainScores.symptoms.score != null ? payload.domainScores.symptoms.score.toFixed(2) : '—',
        payload?.domainScores.activityLimitation.score != null ? payload.domainScores.activityLimitation.score.toFixed(2) : '—',
        getAQLQSeverityLabel(payload?.total ?? null),
      ];
    });

    this.runAutoTable(doc, {
      startY,
      margin: { left: 15, right: 15 },
      head: [['Histórico recente', 'Total', 'Sintomas', 'Atividades', 'Classificação']],
      body: rows,
      theme: 'grid',
      headStyles: {
        fillColor: [248, 250, 252],
        textColor: COLORS.slate600,
        fontSize: 8,
        fontStyle: 'bold',
      },
      bodyStyles: {
        fontSize: 8.2,
        textColor: COLORS.slate800,
        cellPadding: 3.2,
      },
      styles: {
        lineColor: COLORS.slate200,
        lineWidth: 0.1,
      },
      columnStyles: {
        0: { cellWidth: 44 },
        1: { cellWidth: 20, halign: 'center', fontStyle: 'bold' },
        2: { cellWidth: 22, halign: 'center' },
        3: { cellWidth: 24, halign: 'center' },
        4: { cellWidth: 70 },
      },
    });

    return (doc as any).lastAutoTable?.finalY ?? startY;
  }

  private static renderProfessionalSignatureAndVerification(
    doc: jsPDF,
    result: AssessmentResultV2,
    branding: ResolvedClientReportBranding,
    startY: number,
    verification?: AssessmentPDFInput['verification'],
    compact = false,
  ): number {
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    const gap = 8;
    const leftWidth = 82;
    const rightWidth = pageWidth - margin * 2 - leftWidth - gap;
    const height = compact ? 30 : 40;
    const professionalInfo = buildAssessmentProfessionalInfo(result);
    const professionalName = professionalInfo.name || 'Profissional responsável';
    const professionalTitle = professionalInfo.professionalTitle;
    const professionalRegistration = getAssessmentProfessionalRegistrationLabel(result);
    const signature = getAssessmentSignatureMetadata(result.assetsJson);
    const signedByName = signature?.signedByName || professionalName;
    const signedByRole = signature?.signedByRole || professionalTitle;
    const signedByRegistration = signature?.signedByRegistration || professionalRegistration;
    const signedAtLabel = signature ? this.formatDate(signature.signedAt) : '—';
    const professionalMeta = [professionalTitle, professionalRegistration].filter(Boolean).join(' - ') || 'Registro profissional não informado';
    const signerMeta = [signedByRole, signedByRegistration].filter(Boolean).join(' • ') || 'Registro profissional não informado';

    doc.setDrawColor(...COLORS.slate200);
    doc.setLineWidth(0.35);
    doc.setFillColor(...COLORS.white);
    doc.roundedRect(margin, startY, leftWidth, height, 3, 3, 'FD');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.2);
    doc.setTextColor(...COLORS.slate800);
    doc.text('Responsável técnico', margin + 4, startY + 5.5);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.6);
    doc.setTextColor(...COLORS.slate600);
    const professionalNameLines = doc.splitTextToSize(professionalName, leftWidth - 8);
    doc.text(professionalNameLines, margin + 4, startY + (compact ? 10.5 : 11));
    const professionalMetaLines = doc.splitTextToSize(professionalMeta, leftWidth - 8);
    doc.text(professionalMetaLines, margin + 4, startY + (compact ? 17.5 : 19.5));

    const rightX = margin + leftWidth + gap;
    doc.setDrawColor(...COLORS.slate200);
    doc.setLineWidth(0.35);
    doc.setFillColor(...COLORS.white);
    doc.roundedRect(rightX, startY, rightWidth, height, 3, 3, 'FD');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.4);
    doc.setTextColor(...COLORS.slate800);
    doc.text('Autenticidade do documento', rightX + 4, startY + 5.5);

    if (verification) {
      const qrSize = compact ? 14 : 18;
      const qrY = startY + (compact ? 7 : 8);
      try {
        doc.addImage(verification.qrDataUrl, 'PNG', rightX + 4, qrY, qrSize, qrSize);
      } catch {
        // ignore QR rendering failures
      }

      const displayUrl = verification.url.replace(/^https?:\/\//, '');
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6.7);
      doc.setTextColor(...COLORS.slate600);
      const textX = rightX + (compact ? 22 : 26);
      const textWidth = rightWidth - (compact ? 26 : 30);
      const signerLines = doc.splitTextToSize(`Assinado por: ${signedByName}`, textWidth);
      doc.text(signerLines, textX, startY + (compact ? 9.5 : 11.5));
      const signerMetaLines = doc.splitTextToSize(signerMeta, textWidth);
      doc.text(signerMetaLines, textX, startY + (compact ? 13.5 : 16));
      doc.text(`Assinado em: ${signedAtLabel}`, textX, startY + (compact ? 17.5 : 22));
      doc.text(`Código: ${verification.token}`, textX, startY + (compact ? 21.5 : 26));
      doc.text('Validação:', textX, startY + (compact ? 25.5 : 30));
      doc.setTextColor(...COLORS.brandBlue);
      const validationLabelWidth = doc.getTextWidth('Validação:') + 2;
      const validationUrlLabel = this.trimLineToWidth(
        doc,
        displayUrl,
        Math.max(10, textWidth - validationLabelWidth),
      );
      const maybeDoc: any = doc;
      if (typeof maybeDoc.textWithLink === 'function') {
        maybeDoc.textWithLink(
          validationUrlLabel,
          textX + validationLabelWidth,
          startY + (compact ? 25.5 : 30),
          { url: verification.url },
        );
      } else {
        doc.text(validationUrlLabel, textX + validationLabelWidth, startY + (compact ? 25.5 : 30));
      }
    } else {
      doc.setDrawColor(...COLORS.slate300);
      doc.setLineDashPattern([1.2, 1.2], 0);
      doc.roundedRect(rightX + 4, startY + (compact ? 7 : 8), rightWidth - 8, compact ? 18 : 24, 2, 2);
      doc.setLineDashPattern([], 0);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.4);
      doc.setTextColor(...COLORS.slate500);
      doc.text('Documento pendente de assinatura eletrônica.', rightX + 8, startY + (compact ? 16 : 18));
    }

    return startY + height + (compact ? 4 : 6);
  }

  private static renderContinuationHeader(doc: jsPDF, branding: ResolvedClientReportBranding): void {
    const pageNumber = doc.getCurrentPageInfo().pageNumber;
    const pageContext = this.getPDFDocumentPageContext(doc);
    pageContext.branding = branding;

    if (pageNumber <= 1 || pageContext.fullHeaderPages.has(pageNumber) || pageContext.continuationHeaderPages.has(pageNumber)) {
      return;
    }

    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    const tenantLabel = branding.displayName || branding.legalName || 'Relatório clínico';
    const testLabel = pageContext.testLabel || 'Documento clínico';

    doc.setFillColor(...COLORS.slate50);
    doc.rect(0, 0, pageWidth, 14, 'F');
    doc.setDrawColor(...COLORS.slate200);
    doc.line(margin, 14, pageWidth - margin, 14);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.2);
    doc.setTextColor(...COLORS.slate800);
    doc.text(tenantLabel, margin, 8.5);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.2);
    doc.setTextColor(...COLORS.slate500);
    doc.text(`${testLabel} - continuação`, pageWidth - margin, 8.5, {
      align: 'right',
    });
    pageContext.continuationHeaderPages.add(pageNumber);
  }

  private static renderFooter(doc: jsPDF, branding: ResolvedClientReportBranding, revahubMarkBase64: string | null, documentLabel = 'documento eletrônico autenticado'): void {
    this.renderContinuationHeader(doc, branding);

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    const pageNumber = doc.getCurrentPageInfo().pageNumber;
    const pageCount = doc.getNumberOfPages();

    doc.setFillColor(...COLORS.slate50);
    doc.rect(0, pageHeight - PDF_FOOTER_HEIGHT_MM, pageWidth, PDF_FOOTER_HEIGHT_MM, 'F');
    doc.setDrawColor(...COLORS.slate200);
    doc.line(margin, pageHeight - PDF_FOOTER_HEIGHT_MM, pageWidth - margin, pageHeight - PDF_FOOTER_HEIGHT_MM);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.6);
    doc.setTextColor(...COLORS.slate500);
    doc.text(this.trimLineToWidth(doc, `${branding.displayName || branding.legalName} - ${documentLabel}`, pageWidth - margin * 2), margin, pageHeight - 15);
    doc.text(branding.footerLegalLine || 'Laudo gerado via sistema de gestão clínica.', margin, pageHeight - 10);
    doc.setFontSize(6.8);
    doc.text(`Página ${pageNumber}/${pageCount}`, margin, pageHeight - 5.8);

    const markHeight = 7.8;
    const markWidth = 7.8;
    const markX = pageWidth - margin - markWidth;
    const markY = pageHeight - 13.2;
    const footerTextRightX = markX - 3;

    if (revahubMarkBase64) {
      try {
        doc.addImage(revahubMarkBase64, 'PNG', markX, markY, markWidth, markHeight);
      } catch {
        // ignore footer logo rendering issues
      }
    }

    doc.setTextColor(...COLORS.brandBlue);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.8);
    doc.text('Powered by Revahub', footerTextRightX, pageHeight - 9.5, {
      align: 'right',
    });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6.5);
    doc.setTextColor(...COLORS.slate500);
    doc.text('© 2026 Revahub. Todos os direitos reservados.', footerTextRightX, pageHeight - 5.8, {
      align: 'right',
    });
  }

  static async generateSTSPDF({ result, branding, previousResults = [], verification = null }: AssessmentPDFInput): Promise<jsPDF> {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });
    const pageHeight = doc.internal.pageSize.getHeight();
    const revahubMarkBase64 = await this.loadImageAsDataUrl('/logos/revahub-mark.png');
    const testKey = result.testKey as StsTestKey;
    const payload =
      testKey === '5xSTS'
        ? this.extractFiveSTSPayload(result)
        : this.extractStsRepetitionPayload(result, testKey as 'STS_30S' | 'STS_1MIN');

    let y = await this.renderHeader(doc, branding);
    y = this.renderPatientRibbon(doc, result, y);
    y = this.renderPatientClinicalCard(doc, result, y);
    y = this.renderSTSScoreCard(doc, result, testKey, y);
    if (testKey === '5xSTS') {
      y = this.renderSTSProtocolTable(doc, result, testKey, y) + 4;
    } else if (payload) {
      y =
        this.renderSTSAttemptsTable(
          doc,
          payload as {
            attempt1?: StsRepetitionAttemptPayload | null;
            attempt2?: StsRepetitionAttemptPayload | null;
          },
          y,
        ) + 4;
    }

    y = this.renderNotesCard(doc, result.notes, y);
    if (previousResults.some((item) => item.testKey === testKey)) {
      y = this.renderSTSHistoryTable(doc, previousResults, testKey, y) + 4;
    }

    if (y > pageHeight - PDF_FOOTER_HEIGHT_MM - 31) {
      doc.addPage();
      this.renderContinuationHeader(doc, branding);
      y = PDF_CONTINUATION_HEADER_BOTTOM_MM + 3;
    }

    this.renderProfessionalSignatureAndVerification(doc, result, branding, y, verification, true);

    const pageCount = doc.getNumberOfPages();
    for (let page = 1; page <= pageCount; page += 1) {
      doc.setPage(page);
      this.renderFooter(doc, branding, revahubMarkBase64);
    }

    return doc;
  }

  static async generateTC6PDF({
    result,
    branding,
    previousResults = [],
    tc6ChartImagesOverride = null,
    verification = null,
  }: AssessmentPDFInput): Promise<jsPDF> {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });
    const pageHeight = doc.internal.pageSize.getHeight();
    const revahubMarkBase64 = await this.loadImageAsDataUrl('/logos/revahub-mark.png');
    const extractedPayload = this.extractTC6Payload(result);
    let stagedEntries: TemporaryChartStageMap = {};

    try {
      let y = await this.renderHeader(doc, branding);
      y = this.renderPatientRibbon(doc, result, y);
      y = this.renderPatientClinicalCard(doc, result, y);

      if (!extractedPayload) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        doc.setTextColor(...COLORS.slate700);
        doc.text('Dados do TC6 indisponíveis para esta avaliação.', 15, y + 8);
        this.renderProfessionalSignatureAndVerification(doc, result, branding, y + 20, verification);
        this.renderFooter(doc, branding, revahubMarkBase64);
        return doc;
      }

      const runtimeChartImages =
        tc6ChartImagesOverride && typeof tc6ChartImagesOverride === 'object'
          ? tc6ChartImagesOverride
          : null;
      const payloadWithRuntimeCharts =
        runtimeChartImages && Object.keys(runtimeChartImages).length > 0
          ? ({
              ...extractedPayload,
              chartImages: {
                ...(extractedPayload.chartImages ?? {}),
                ...runtimeChartImages,
              },
            } as Tc6ResultPayload & { chartImages?: Tc6ChartImages })
          : extractedPayload;

      const stagedCharts = await this.stageTC6ChartsForPdf(result, payloadWithRuntimeCharts);
      stagedEntries = stagedCharts.stagedEntries;
      const payload =
        Object.keys(stagedCharts.chartImages).length > 0
          ? ({
              ...payloadWithRuntimeCharts,
              chartImages: stagedCharts.chartImages,
            } as Tc6ResultPayload & { chartImages?: Tc6ChartImages })
          : payloadWithRuntimeCharts;

      y = this.renderTC6OverviewCards(doc, payload, y);
      y = this.renderTC6InterpretationBlock(doc, payload, y);

      const attempts = getTc6Attempts(payload);
      for (let index = 0; index < attempts.length; index += 1) {
        const attempt = attempts[index];

        doc.addPage();
        y = await this.renderHeader(doc, branding);

        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...COLORS.slate900);
        doc.text(`TC6 — ${index + 1}ª tentativa`, 15, y);
        y += 8;

        y = this.renderTC6AttemptHeader(doc, attempt, index + 1, payload.referenceEquation, y);

        const gap = 6;
        const availableWidth = doc.internal.pageSize.getWidth() - 30;
        const leftWidth = availableWidth * 0.5;
        const rightWidth = availableWidth - leftWidth - gap;
        const leftBottom = this.renderTC6MinuteTable(doc, attempt, y, {
          compact: true,
          x: 15,
          width: leftWidth,
        });
        const rightBottom = this.renderTC6VitalTable(doc, attempt, y, {
          compact: true,
          x: 15 + leftWidth + gap,
          width: rightWidth,
        });
        y = Math.max(leftBottom, rightBottom);

        y = this.renderTC6DessaturationBlock(doc, attempt, y);
        y += 4;
        y = this.renderTC6StopBlock(doc, attempt, y);
        y += 4;
        y = this.renderTC6ChartsRow(doc, payload, attempt, index === 0 ? 'attempt1' : 'attempt2', y);
        y += 6;
      }

      const hasTc6Comparison = Boolean(this.resolveLatestTc6ComparisonSnapshot(result, previousResults));
      if (hasTc6Comparison) {
        doc.addPage();
        this.renderContinuationHeader(doc, branding);
        y = PDF_CONTINUATION_HEADER_BOTTOM_MM + 3;
        const historyChartBottom = this.renderTC6HistoryChart(doc, result, payload, previousResults, y);
        if (historyChartBottom > y) {
          y = historyChartBottom + 8;
        }

        const comparisonBottom = this.renderTC6ComparisonTable(doc, result, payload, previousResults, y);
        if (comparisonBottom > y) {
          y = comparisonBottom + 8;
        }
      }

      if (y > pageHeight - 58) {
        doc.addPage();
        y = await this.renderHeader(doc, branding);
      }

      this.renderProfessionalSignatureAndVerification(doc, result, branding, y, verification);

      const pageCount = doc.getNumberOfPages();
      for (let page = 1; page <= pageCount; page += 1) {
        doc.setPage(page);
        this.renderFooter(doc, branding, revahubMarkBase64);
      }

      return doc;
    } finally {
      await AssessmentTempChartStorageService.cleanup(stagedEntries);
    }
  }

  static async generateISWTPDF({ result, branding, previousResults = [], verification = null }: AssessmentPDFInput): Promise<jsPDF> {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });
    const pageHeight = doc.internal.pageSize.getHeight();
    const revahubMarkBase64 = await this.loadImageAsDataUrl('/logos/revahub-mark.png');
    const payload = this.extractISWTPayload(result);

    let y = await this.renderHeader(doc, branding);
    y = this.renderPatientRibbon(doc, result, y);
    y = this.renderPatientClinicalCard(doc, result, y);

    if (!payload) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(...COLORS.slate700);
      doc.text('Dados do ISWT indisponíveis para esta avaliação.', 15, y + 8);
      this.renderProfessionalSignatureAndVerification(doc, result, branding, y + 20, verification);
      this.renderFooter(doc, branding, revahubMarkBase64);
      return doc;
    }

    const summary = summarizeISWTResult(payload);
    const interpretation = resolveISWTInterpretation(payload);

    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.slate900);
    doc.text('Incremental Shuttle Walk Test (ISWT)', 15, y);
    y += 8;

    y = this.renderMetricCards(
      doc,
      y,
      [
        {
          label: 'Melhor distância',
          value: `${summary.bestDistanceMeters} m`,
          details: summary.bestAttemptNumber != null ? `Tentativa ${summary.bestAttemptNumber}` : 'Sem tentativa identificada',
          accentColor: COLORS.brandBlue,
        },
        {
          label: 'Melhor nível',
          value: summary.bestLevelReached > 0 ? `N${summary.bestLevelReached}` : '—',
          details: summary.bestLevelLabel,
        },
        {
          label: 'Previsto',
          value: summary.predictedDistanceMeters != null ? `${summary.predictedDistanceMeters.toFixed(1)} m` : '—',
          details: summary.percentPredicted != null ? `${summary.percentPredicted.toFixed(1)}% do previsto` : 'Equação de Probst et al. 2011',
        },
        {
          label: 'SpO2 mínima',
          value: summary.minSpo2 != null ? `${summary.minSpo2.toFixed(0)}%` : '—',
          details: summary.peakHr != null ? `FC pico ${summary.peakHr.toFixed(0)} bpm` : 'FC pico não informada',
          accentColor: summary.minSpo2 != null && summary.minSpo2 < 80 ? COLORS.slate900 : COLORS.emerald,
        },
      ],
      { compact: true, columns: 4 },
    );

    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.slate500);
    doc.text('INTERPRETAÇÃO CLÍNICA', 15, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.slate700);
    const interpretationLines = doc.splitTextToSize(interpretation, 180);
    doc.text(interpretationLines, 15, y);
    y += interpretationLines.length * 4 + 4;

    y = this.renderMetricCards(
      doc,
      y,
      [
        {
          label: 'Pista',
          value: `${payload.protocol.courseLengthMeters} m`,
          details: `${payload.protocol.totalLevels} níveis de 1 minuto`,
        },
        {
          label: 'Baseline',
          value: payload.protocol.duplicateAtBaseline ? '2 testes' : '1 teste',
          details: `Intervalo sugerido de ${payload.protocol.restIntervalMinutes} min`,
        },
        {
          label: 'Pacing',
          value: payload.protocol.audioPaced ? 'Áudio padronizado' : 'Sem áudio',
          details: payload.report?.audioSupportNote ?? 'Beep sintético no navegador para contornar a falta de upload',
        },
        {
          label: 'Referência',
          value: 'Probst 2011',
          details: ISWT_PROTOCOL_METADATA.description,
        },
      ],
      { compact: true, columns: 2, dense: true },
    );

    this.runAutoTable(doc, {
      startY: y,
      head: [['Tentativa', 'Tempo', 'Distância', 'Nível', 'SpO2 min', 'FC pico', 'Borg D/MMII', 'Encerramento']],
      body: payload.attempts.map((attempt) => [
        `${attempt.attemptNumber}`,
        formatClock(attempt.timer.elapsed_s),
        `${attempt.distanceMeters} m`,
        formatISWTLevelLabel(attempt.completedLevel, attempt.completedShuttlesOnLevel),
        attempt.observedMinSpo2 != null ? `${attempt.observedMinSpo2.toFixed(0)}%` : '—',
        attempt.observedPeakHr != null ? `${attempt.observedPeakHr.toFixed(0)}` : '—',
        `${formatStepTestBorgValue(attempt.final.borg.dyspnea)} / ${formatStepTestBorgValue(attempt.final.borg.legFatigue)}`,
        formatStepStopReasonLabel(attempt.stopReason),
      ]),
      theme: 'grid',
      styles: {
        fontSize: 8,
        cellPadding: 2.3,
        textColor: COLORS.slate700 as unknown as number[],
      },
      headStyles: {
        fillColor: COLORS.slate100 as unknown as number[],
        textColor: COLORS.slate600 as unknown as number[],
        fontStyle: 'bold',
      },
      margin: { left: 15, right: 15 },
    });
    y = ((doc as any).lastAutoTable?.finalY ?? y) + 6;

    y = this.renderNotesCard(doc, result.notes || payload.notes, y);

    if (previousResults.length) {
      const historyRows = previousResults
        .map((item) => {
          const itemPayload = this.extractISWTPayload(item);
          if (!itemPayload) return null;
          const itemSummary = summarizeISWTResult(itemPayload);
          return [
            this.formatDate(item.assessmentDate),
            `${itemSummary.bestDistanceMeters} m`,
            itemSummary.percentPredicted != null ? `${itemSummary.percentPredicted.toFixed(1)}%` : '—',
            itemSummary.bestLevelLabel,
            itemSummary.severity,
          ];
        })
        .filter((row): row is string[] => row != null);

      if (historyRows.length) {
        if (y > pageHeight - 110) {
          doc.addPage();
          y = await this.renderHeader(doc, branding);
        }
        this.runAutoTable(doc, {
          startY: y,
          head: [['Histórico', 'Distância', '% previsto', 'Nível', 'Leitura']],
          body: historyRows,
          theme: 'grid',
          styles: {
            fontSize: 8,
            cellPadding: 2.6,
            textColor: COLORS.slate700 as unknown as number[],
          },
          headStyles: {
            fillColor: COLORS.slate100 as unknown as number[],
            textColor: COLORS.slate600 as unknown as number[],
            fontStyle: 'bold',
          },
          margin: { left: 15, right: 15 },
        });
        y = ((doc as any).lastAutoTable?.finalY ?? y) + 8;
      }
    }

    if (y > pageHeight - 58) {
      doc.addPage();
      y = 20;
    }

    this.renderProfessionalSignatureAndVerification(doc, result, branding, y, verification);

    const pageCount = doc.getNumberOfPages();
    for (let page = 1; page <= pageCount; page += 1) {
      doc.setPage(page);
      this.renderFooter(doc, branding, revahubMarkBase64);
    }

    return doc;
  }

  static async generateESWTPDF({ result, branding, previousResults = [], verification = null }: AssessmentPDFInput): Promise<jsPDF> {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });
    const pageHeight = doc.internal.pageSize.getHeight();
    const revahubMarkBase64 = await this.loadImageAsDataUrl('/logos/revahub-mark.png');
    const payload = this.extractESWTPayload(result);

    let y = await this.renderHeader(doc, branding);
    y = this.renderPatientRibbon(doc, result, y);
    y = this.renderPatientClinicalCard(doc, result, y);

    if (!payload) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(...COLORS.slate700);
      doc.text('Dados do ESWT indisponíveis para esta avaliação.', 15, y + 8);
      this.renderProfessionalSignatureAndVerification(doc, result, branding, y + 20, verification);
      this.renderFooter(doc, branding, revahubMarkBase64);
      return doc;
    }

    const summary = summarizeESWTResult(payload);
    const interpretation = resolveESWTInterpretation(payload);

    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.slate900);
    doc.text('Endurance Shuttle Walk Test (ESWT)', 15, y);
    y += 8;

    y = this.renderMetricCards(
      doc,
      y,
      [
        {
          label: 'Tempo tolerado',
          value: formatClock(summary.durationSeconds),
          details:
            summary.withinRecommendedWindow == null
              ? 'Sem faixa operacional'
              : summary.withinRecommendedWindow
                ? 'Dentro da faixa de 3 a 8 min'
                : 'Fora da faixa de 3 a 8 min',
          accentColor: COLORS.brandBlue,
        },
        {
          label: 'Distância',
          value: `${summary.distanceMeters} m`,
          details: `${payload.totalShuttles} shuttle(s)`,
        },
        {
          label: 'Velocidade alvo',
          value: `${summary.speedKmh.toFixed(2)} km/h`,
          details: `${summary.speedPercentOfIswt}% da velocidade pico do ISWT`,
        },
        {
          label: 'SpO2 mínima',
          value: summary.minSpo2 != null ? `${summary.minSpo2.toFixed(0)}%` : '—',
          details: summary.peakHr != null ? `FC pico ${summary.peakHr.toFixed(0)} bpm` : 'FC pico não informada',
          accentColor: summary.minSpo2 != null && summary.minSpo2 < 80 ? COLORS.slate900 : COLORS.emerald,
        },
      ],
      { compact: true, columns: 4 },
    );

    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.slate500);
    doc.text('INTERPRETAÇÃO CLÍNICA', 15, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.slate700);
    const interpretationLines = doc.splitTextToSize(interpretation, 180);
    doc.text(interpretationLines, 15, y);
    y += interpretationLines.length * 4 + 4;

    y = this.renderMetricCards(
      doc,
      y,
      [
        {
          label: 'Pista',
          value: `${payload.protocol.courseLengthMeters} m`,
          details: 'Percurso reto ida e volta',
        },
        {
          label: 'Referência ISWT',
          value: payload.protocol.sourceIswtLevel != null ? `Nível ${payload.protocol.sourceIswtLevel}` : 'Manual',
          details: payload.protocol.sourceIswtSpeedKmh != null ? `${payload.protocol.sourceIswtSpeedKmh.toFixed(2)} km/h no incremental` : 'Sem velocidade salva do ISWT',
        },
        { label: 'Janela alvo', value: `${payload.protocol.recommendedWindowMinutes[0]}-${payload.protocol.recommendedWindowMinutes[1]} min`, details: 'Faixa sugerida para calibrar a carga constante' },
        {
          label: 'Pacing',
          value: payload.protocol.audioPaced ? 'Áudio padronizado' : 'Sem áudio',
          details: payload.report?.audioSupportNote ?? ESWT_PROTOCOL_METADATA.description,
        },
      ],
      { compact: true, columns: 2, dense: true },
    );

    this.runAutoTable(doc, {
      startY: y,
      head: [['Bloco', 'FC', 'SpO2', 'PA', 'Borg dispneia', 'Borg MMII']],
      body: [
        [
          'Pré-teste',
          payload.baseline.hr ?? '—',
          payload.baseline.spo2 ?? '—',
          `${payload.baseline.bp.sys ?? '—'}/${payload.baseline.bp.dia ?? '—'}`,
          formatStepTestBorgValue(payload.baseline.borg.dyspnea),
          formatStepTestBorgValue(payload.baseline.borg.legFatigue),
        ],
        [
          'Pós-teste',
          payload.final.hr ?? '—',
          payload.final.spo2 ?? '—',
          `${payload.final.bp.sys ?? '—'}/${payload.final.bp.dia ?? '—'}`,
          formatStepTestBorgValue(payload.final.borg.dyspnea),
          formatStepTestBorgValue(payload.final.borg.legFatigue),
        ],
      ],
      theme: 'grid',
      styles: {
        fontSize: 8,
        cellPadding: 2.6,
        textColor: COLORS.slate700 as unknown as number[],
      },
      headStyles: {
        fillColor: COLORS.slate100 as unknown as number[],
        textColor: COLORS.slate600 as unknown as number[],
        fontStyle: 'bold',
      },
      margin: { left: 15, right: 15 },
    });
    y = ((doc as any).lastAutoTable?.finalY ?? y) + 6;

    this.runAutoTable(doc, {
      startY: y,
      head: [['Tempo', 'Shuttles', 'Distância', 'Velocidade', 'Encerramento']],
      body: [
        [
          formatClock(payload.totalTimeSeconds),
          `${payload.totalShuttles}`,
          `${payload.distanceMeters} m`,
          `${payload.protocol.targetSpeedKmh.toFixed(2)} km/h`,
          formatStepStopReasonLabel(payload.stopReason),
        ],
      ],
      theme: 'grid',
      styles: {
        fontSize: 8,
        cellPadding: 2.6,
        textColor: COLORS.slate700 as unknown as number[],
      },
      headStyles: {
        fillColor: COLORS.slate100 as unknown as number[],
        textColor: COLORS.slate600 as unknown as number[],
        fontStyle: 'bold',
      },
      margin: { left: 15, right: 15 },
    });
    y = ((doc as any).lastAutoTable?.finalY ?? y) + 6;

    y = this.renderNotesCard(doc, result.notes || payload.notes, y);

    if (previousResults.length) {
      const historyRows = previousResults
        .map((item) => {
          const itemPayload = this.extractESWTPayload(item);
          if (!itemPayload) return null;
          const itemSummary = summarizeESWTResult(itemPayload);
          return [
            this.formatDate(item.assessmentDate),
            formatClock(itemSummary.durationSeconds),
            `${itemSummary.distanceMeters} m`,
            `${itemSummary.speedKmh.toFixed(2)} km/h`,
            itemSummary.severity,
          ];
        })
        .filter((row): row is string[] => row != null);

      if (historyRows.length) {
        if (y > pageHeight - 110) {
          doc.addPage();
          y = await this.renderHeader(doc, branding);
        }
        this.runAutoTable(doc, {
          startY: y,
          head: [['Histórico', 'Tempo', 'Distância', 'Velocidade', 'Leitura']],
          body: historyRows,
          theme: 'grid',
          styles: {
            fontSize: 8,
            cellPadding: 2.6,
            textColor: COLORS.slate700 as unknown as number[],
          },
          headStyles: {
            fillColor: COLORS.slate100 as unknown as number[],
            textColor: COLORS.slate600 as unknown as number[],
            fontStyle: 'bold',
          },
          margin: { left: 15, right: 15 },
        });
        y = ((doc as any).lastAutoTable?.finalY ?? y) + 8;
      }
    }

    if (y > pageHeight - 58) {
      doc.addPage();
      y = 20;
    }

    this.renderProfessionalSignatureAndVerification(doc, result, branding, y, verification);

    const pageCount = doc.getNumberOfPages();
    for (let page = 1; page <= pageCount; page += 1) {
      doc.setPage(page);
      this.renderFooter(doc, branding, revahubMarkBase64);
    }

    return doc;
  }

  static async generate6MSTPDF({ result, branding, previousResults = [], verification = null }: AssessmentPDFInput): Promise<jsPDF> {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });
    const pageHeight = doc.internal.pageSize.getHeight();
    const revahubMarkBase64 = await this.loadImageAsDataUrl('/logos/revahub-mark.png');
    const payload = this.extract6MSTPayload(result);

    let y = await this.renderHeader(doc, branding);
    y = this.renderPatientRibbon(doc, result, y);
    y = this.renderPatientClinicalCard(doc, result, y);

    if (!payload) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(...COLORS.slate700);
      doc.text('Dados do 6MST indisponíveis para esta avaliação.', 15, y + 8);
      this.renderProfessionalSignatureAndVerification(doc, result, branding, y + 20, verification);
      this.renderFooter(doc, branding, revahubMarkBase64);
      return doc;
    }

    const summary = summarizeSixMSTResult(payload);
    const interpretation = resolveSixMSTInterpretation(payload, {
      includeNormativeValues: true,
    });
    const populatedMinuteReadings = payload.perMinute.filter(hasSixMSTMinuteReadingData);
    const populatedVitalBlocks = [
      { label: 'Pré-teste', value: payload.baseline },
      { label: 'Pós-teste', value: payload.final },
    ].filter(({ value }) => hasStepTestVitalsData(value));

    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.slate900);
    doc.text('Teste do Degrau de 6 minutos (6MST)', 15, y);
    y += 8;

    const resultCards: MetricCard[] = [
      {
        label: 'Subidas totais',
        value: String(summary.totalSteps),
        details: 'Resultado principal do teste',
        accentColor: COLORS.brandBlue,
      },
    ];
    if (summary.predictedSteps != null) {
      resultCards.push({
        label: 'Previsto',
        value: summary.predictedSteps.toFixed(1),
        details: summary.percentPredicted != null ? `${summary.percentPredicted.toFixed(1)}% do previsto` : undefined,
      });
    }
    if (payload.stop_report.intervals.length) {
      resultCards.push({
        label: 'Tempo parado',
        value: payload.stop_report.total_stopped_fmt,
        details: `${payload.stop_report.intervals.length} pausa(s)`,
      });
    }
    if (summary.minSpo2 != null) {
      resultCards.push({
        label: 'SpO2 mínima',
        value: `${summary.minSpo2.toFixed(1)}%`,
        details: summary.spo2Drop != null ? `Queda de ${summary.spo2Drop.toFixed(1)}%` : undefined,
        accentColor: summary.minSpo2 <= 88 ? COLORS.slate900 : COLORS.emerald,
      });
    }
    y = this.renderMetricCards(doc, y, resultCards, {
      compact: true,
      columns: Math.min(4, resultCards.length),
      detailFontSize: 6.5,
      detailLineHeight: 2.8,
    });

    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.slate500);
    doc.text('INTERPRETAÇÃO CLÍNICA', 15, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.slate700);
    const interpretationLines = doc.splitTextToSize(interpretation, 180);
    doc.text(interpretationLines, 15, y);
    y += interpretationLines.length * 4 + 4;

    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.slate500);
    doc.text('PROTOCOLO E REFERÊNCIA', 15, y);
    y += 4;
    const protocolCards: MetricCard[] = [
      {
        label: 'Degrau',
        value: `${payload.protocol.stepHeightCm} cm`,
        details: 'Cadência livre, sem apoio de MMSS',
      },
      { label: 'Fonte', value: 'Albuquerque et al. 2022' },
    ];
    y = this.renderMetricCards(doc, y, protocolCards, {
      compact: true,
      columns: protocolCards.length,
      dense: true,
      detailFontSize: 6.5,
      detailLineHeight: 2.8,
      minCardHeight: 17.3,
    });

    if (populatedMinuteReadings.length) {
      const minuteColumns = [
        {
          header: 'Min',
          value: (entry: SixMSTResultPayload['perMinute'][number]) => String(entry.minute),
        },
        ...(populatedMinuteReadings.some((entry) => hasRecordedStepTestText(entry.hr))
          ? [
              {
                header: 'FC',
                value: (entry: SixMSTResultPayload['perMinute'][number]) => entry.hr ?? '—',
              },
            ]
          : []),
        ...(populatedMinuteReadings.some((entry) => hasRecordedStepTestText(entry.spo2))
          ? [
              {
                header: 'SpO2',
                value: (entry: SixMSTResultPayload['perMinute'][number]) => entry.spo2 ?? '—',
              },
            ]
          : []),
        ...(populatedMinuteReadings.some((entry) => entry.totalSteps != null)
          ? [
              {
                header: 'Subidas acumuladas',
                value: (entry: SixMSTResultPayload['perMinute'][number]) => (entry.totalSteps != null ? String(entry.totalSteps) : '—'),
              },
            ]
          : []),
        ...(populatedMinuteReadings.some((entry) => hasRecordedStepTestText(entry.notes))
          ? [
              {
                header: 'Observações',
                value: (entry: SixMSTResultPayload['perMinute'][number]) => entry.notes ?? '—',
              },
            ]
          : []),
      ];
      this.runAutoTable(doc, {
        startY: y,
        head: [minuteColumns.map((column) => column.header)],
        body: populatedMinuteReadings.map((entry) => minuteColumns.map((column) => column.value(entry))),
        theme: 'grid',
        pageBreak: 'avoid',
        styles: {
          fontSize: 7.2,
          cellPadding: 1.7,
          textColor: COLORS.slate700 as unknown as number[],
        },
        headStyles: {
          fillColor: COLORS.slate100 as unknown as number[],
          textColor: COLORS.slate600 as unknown as number[],
          fontStyle: 'bold',
        },
        margin: { left: 15, right: 15 },
      });
      y = ((doc as any).lastAutoTable?.finalY ?? y) + 4;
    }

    if (populatedVitalBlocks.length) {
      const vitalColumns = [
        {
          header: 'Momento',
          value: (entry: (typeof populatedVitalBlocks)[number]) => entry.label,
        },
        ...(populatedVitalBlocks.some((entry) => hasRecordedStepTestText(entry.value.hr))
          ? [
              {
                header: 'FC',
                value: (entry: (typeof populatedVitalBlocks)[number]) => entry.value.hr ?? '—',
              },
            ]
          : []),
        ...(populatedVitalBlocks.some((entry) => hasRecordedStepTestText(entry.value.spo2))
          ? [
              {
                header: 'SpO2',
                value: (entry: (typeof populatedVitalBlocks)[number]) => entry.value.spo2 ?? '—',
              },
            ]
          : []),
        ...(populatedVitalBlocks.some((entry) => hasRecordedStepTestText(entry.value.bp.sys) || hasRecordedStepTestText(entry.value.bp.dia))
          ? [
              {
                header: 'PA',
                value: (entry: (typeof populatedVitalBlocks)[number]) =>
                  [
                    hasRecordedStepTestText(entry.value.bp.sys) ? `PAS ${entry.value.bp.sys}` : null,
                    hasRecordedStepTestText(entry.value.bp.dia) ? `PAD ${entry.value.bp.dia}` : null,
                  ]
                    .filter((value): value is string => value != null)
                    .join(' / ') || '—',
              },
            ]
          : []),
        ...(populatedVitalBlocks.some((entry) => entry.value.borg.dyspnea != null)
          ? [
              {
                header: 'Borg dispneia',
                value: (entry: (typeof populatedVitalBlocks)[number]) => formatStepTestBorgValue(entry.value.borg.dyspnea),
              },
            ]
          : []),
        ...(populatedVitalBlocks.some((entry) => entry.value.borg.legFatigue != null)
          ? [
              {
                header: 'Borg fadiga',
                value: (entry: (typeof populatedVitalBlocks)[number]) => formatStepTestBorgValue(entry.value.borg.legFatigue),
              },
            ]
          : []),
      ];
      this.runAutoTable(doc, {
        startY: y,
        head: [vitalColumns.map((column) => column.header)],
        body: populatedVitalBlocks.map((entry) => vitalColumns.map((column) => column.value(entry))),
        theme: 'grid',
        pageBreak: 'avoid',
        styles: {
          fontSize: 7.2,
          cellPadding: 1.8,
          textColor: COLORS.slate700 as unknown as number[],
        },
        headStyles: {
          fillColor: COLORS.slate100 as unknown as number[],
          textColor: COLORS.slate600 as unknown as number[],
          fontStyle: 'bold',
        },
        margin: { left: 15, right: 15 },
      });
      y = ((doc as any).lastAutoTable?.finalY ?? y) + 4;
    }

    if (payload.stop_report.intervals.length) {
      this.runAutoTable(doc, {
        startY: y,
        head: [['Pausa', 'Motivo', 'Intervalo', 'Duração']],
        body: payload.stop_report.intervals.map((entry, index) => [
          `${index + 1}`,
          entry.reason_label ?? '—',
          `${entry.from_fmt} a ${entry.to_fmt}`,
          entry.duration_fmt,
        ]),
        theme: 'grid',
        pageBreak: payload.stop_report.intervals.length <= 12 ? 'avoid' : 'auto',
        styles: {
          fontSize: 7.2,
          cellPadding: 1.8,
          textColor: COLORS.slate700 as unknown as number[],
        },
        headStyles: {
          fillColor: COLORS.slate100 as unknown as number[],
          textColor: COLORS.slate600 as unknown as number[],
          fontStyle: 'bold',
        },
        margin: { left: 15, right: 15 },
      });
      y = ((doc as any).lastAutoTable?.finalY ?? y) + 4;
    }

    y = this.renderNotesCard(doc, result.notes, y);

    if (previousResults.length) {
      const historyRows = previousResults
        .map((item) => {
          const itemPayload = this.extract6MSTPayload(item);
          if (!itemPayload) return null;
          const itemSummary = summarizeSixMSTResult(itemPayload);
          return [
            this.formatDate(item.assessmentDate),
            String(itemSummary.totalSteps),
            itemSummary.percentPredicted != null ? `${itemSummary.percentPredicted.toFixed(1)}%` : '—',
            itemSummary.severity,
          ];
        })
        .filter((row): row is string[] => row != null);

      if (historyRows.length) {
        if (y > pageHeight - 110) {
          doc.addPage();
          y = await this.renderHeader(doc, branding);
        }
        this.runAutoTable(doc, {
          startY: y,
          head: [['Histórico', 'Subidas', '% previsto', 'Leitura']],
          body: historyRows,
          theme: 'grid',
          styles: {
            fontSize: 8,
            cellPadding: 2.6,
            textColor: COLORS.slate700 as unknown as number[],
          },
          headStyles: {
            fillColor: COLORS.slate100 as unknown as number[],
            textColor: COLORS.slate600 as unknown as number[],
            fontStyle: 'bold',
          },
          margin: { left: 15, right: 15 },
        });
        y = ((doc as any).lastAutoTable?.finalY ?? y) + 8;
      }
    }

    if (y > pageHeight - 58) {
      doc.addPage();
      y = 20;
    }

    this.renderProfessionalSignatureAndVerification(doc, result, branding, y, verification);

    const pageCount = doc.getNumberOfPages();
    for (let page = 1; page <= pageCount; page += 1) {
      doc.setPage(page);
      this.renderFooter(doc, branding, revahubMarkBase64);
    }

    return doc;
  }

  static async generateChesterPDF({ result, branding, previousResults = [], verification = null }: AssessmentPDFInput): Promise<jsPDF> {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });
    const pageHeight = doc.internal.pageSize.getHeight();
    const revahubMarkBase64 = await this.loadImageAsDataUrl('/logos/revahub-mark.png');
    const payload = this.extractChesterPayload(result);

    let y = await this.renderHeader(doc, branding);
    y = this.renderPatientRibbon(doc, result, y);
    y = this.renderPatientClinicalCard(doc, result, y);

    if (!payload) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(...COLORS.slate700);
      doc.text('Dados do Chester Step Test indisponíveis para esta avaliação.', 15, y + 8);
      this.renderProfessionalSignatureAndVerification(doc, result, branding, y + 20, verification);
      this.renderFooter(doc, branding, revahubMarkBase64);
      return doc;
    }

    const summary = summarizeChesterResult(payload);
    const interpretation = resolveChesterInterpretation(payload);

    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.slate900);
    doc.text('Chester Step Test', 15, y);
    y += 8;

    y = this.renderMetricCards(
      doc,
      y,
      [
        {
          label: 'Nível alcançado',
          value: `${summary.achievedLevel}/5`,
          details: `${summary.completedLevels} nível(is) concluído(s)`,
          accentColor: COLORS.brandBlue,
        },
        {
          label: 'Ciclos totais',
          value: String(summary.totalSteps),
          details: 'Cada ciclo corresponde a uma subida e descida',
        },
        {
          label: 'Cadência máxima',
          value: summary.maxCadence != null ? `${summary.maxCadence}` : '—',
          details: 'passos por minuto',
        },
        {
          label: 'SpO2 mínima',
          value: summary.minSpo2 != null ? `${summary.minSpo2.toFixed(1)}%` : '—',
          details: summary.peakHr != null ? `FC pico ${summary.peakHr.toFixed(0)} bpm` : 'FC pico não informada',
        },
      ],
      { compact: true, columns: 4 },
    );

    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...COLORS.slate500);
    doc.text('INTERPRETAÇÃO CLÍNICA', 15, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.slate700);
    const interpretationLines = doc.splitTextToSize(interpretation, 180);
    doc.text(interpretationLines, 15, y);
    y += interpretationLines.length * 4 + 4;

    y = this.renderMetricCards(
      doc,
      y,
      [
        {
          label: 'Degrau',
          value: `${payload.protocol.stepHeightCm} cm`,
          details: 'Protocolo incremental em até 5 níveis',
        },
        {
          label: 'Tempo por nível',
          value: `${payload.protocol.levelDurationSeconds / 60} min`,
          details: CHESTER_PROTOCOL_METADATA.description,
        },
        {
          label: 'FC limite',
          value: `${payload.protocol.hrStopPercent}%`,
          details: 'Critério de encerramento do protocolo',
        },
      ],
      { compact: true, columns: 3, dense: true },
    );

    this.runAutoTable(doc, {
      startY: y,
      head: [['Nível', 'Cadência', 'FC', 'SpO2', 'Ciclos', 'Borg D/MMII', 'Status']],
      body: payload.levels
        .filter(
          (entry) =>
            entry.completed ||
            entry.totalSteps != null ||
            entry.hr != null ||
            entry.spo2 != null ||
            entry.borgDyspnea != null ||
            entry.borgLegFatigue != null ||
            Boolean(entry.notes),
        )
        .map((entry) => [
          String(entry.level),
          `${entry.cadenceStepsPerMinute} passos/min`,
          entry.hr ?? '—',
          entry.spo2 ?? '—',
          entry.totalSteps != null ? String(entry.totalSteps) : '—',
          `${formatStepTestBorgValue(entry.borgDyspnea)} / ${formatStepTestBorgValue(entry.borgLegFatigue)}`,
          entry.completed ? 'Concluído' : 'Interrompido',
        ]),
      theme: 'grid',
      styles: {
        fontSize: 8,
        cellPadding: 2.3,
        textColor: COLORS.slate700 as unknown as number[],
      },
      headStyles: {
        fillColor: COLORS.slate100 as unknown as number[],
        textColor: COLORS.slate600 as unknown as number[],
        fontStyle: 'bold',
      },
      margin: { left: 15, right: 15 },
    });
    y = ((doc as any).lastAutoTable?.finalY ?? y) + 6;

    this.runAutoTable(doc, {
      startY: y,
      head: [['Bloco', 'FC', 'SpO2', 'PA', 'Borg dispneia', 'Borg MMII']],
      body: [
        [
          'Pré-teste',
          payload.baseline.hr ?? '—',
          payload.baseline.spo2 ?? '—',
          `${payload.baseline.bp.sys ?? '—'}/${payload.baseline.bp.dia ?? '—'}`,
          formatStepTestBorgValue(payload.baseline.borg.dyspnea),
          formatStepTestBorgValue(payload.baseline.borg.legFatigue),
        ],
        [
          'Pós-teste',
          payload.final.hr ?? '—',
          payload.final.spo2 ?? '—',
          `${payload.final.bp.sys ?? '—'}/${payload.final.bp.dia ?? '—'}`,
          formatStepTestBorgValue(payload.final.borg.dyspnea),
          formatStepTestBorgValue(payload.final.borg.legFatigue),
        ],
      ],
      theme: 'grid',
      styles: {
        fontSize: 8,
        cellPadding: 2.6,
        textColor: COLORS.slate700 as unknown as number[],
      },
      headStyles: {
        fillColor: COLORS.slate100 as unknown as number[],
        textColor: COLORS.slate600 as unknown as number[],
        fontStyle: 'bold',
      },
      margin: { left: 15, right: 15 },
    });
    y = ((doc as any).lastAutoTable?.finalY ?? y) + 6;

    y = this.renderNotesCard(doc, result.notes, y);

    if (previousResults.length) {
      const historyRows = previousResults
        .map((item) => {
          const itemPayload = this.extractChesterPayload(item);
          if (!itemPayload) return null;
          const itemSummary = summarizeChesterResult(itemPayload);
          return [
            this.formatDate(item.assessmentDate),
            `${itemSummary.achievedLevel}/5`,
            String(itemSummary.totalSteps),
            itemSummary.terminationReasonLabel,
          ];
        })
        .filter((row): row is string[] => row != null);

      if (historyRows.length) {
        if (y > pageHeight - 110) {
          doc.addPage();
          y = await this.renderHeader(doc, branding);
        }
        this.runAutoTable(doc, {
          startY: y,
          head: [['Histórico', 'Nível', 'Passos', 'Encerramento']],
          body: historyRows,
          theme: 'grid',
          styles: {
            fontSize: 8,
            cellPadding: 2.6,
            textColor: COLORS.slate700 as unknown as number[],
          },
          headStyles: {
            fillColor: COLORS.slate100 as unknown as number[],
            textColor: COLORS.slate600 as unknown as number[],
            fontStyle: 'bold',
          },
          margin: { left: 15, right: 15 },
        });
        y = ((doc as any).lastAutoTable?.finalY ?? y) + 8;
      }
    }

    if (y > pageHeight - 58) {
      doc.addPage();
      y = 20;
    }

    this.renderProfessionalSignatureAndVerification(doc, result, branding, y, verification);

    const pageCount = doc.getNumberOfPages();
    for (let page = 1; page <= pageCount; page += 1) {
      doc.setPage(page);
      this.renderFooter(doc, branding, revahubMarkBase64);
    }

    return doc;
  }

  static async generateHandgripPDF({ result, branding, previousResults = [], verification = null }: AssessmentPDFInput): Promise<jsPDF> {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });
    const pageHeight = doc.internal.pageSize.getHeight();
    const revahubMarkBase64 = await this.loadImageAsDataUrl('/logos/revahub-mark.png');
    const payload = this.extractHandgripPayload(result);

    let y = await this.renderHeader(doc, branding);
    y = this.renderPatientRibbon(doc, result, y);
    y = this.renderPatientClinicalCard(doc, result, y);

    if (!payload) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(...COLORS.slate700);
      doc.text('Dados do Handgrip indisponíveis para esta avaliação.', 15, y + 8);
      this.renderProfessionalSignatureAndVerification(doc, result, branding, y + 20, verification);
      this.renderFooter(doc, branding, revahubMarkBase64);
      return doc;
    }

    const startContinuationPage = () => {
      doc.addPage();
      this.renderContinuationHeader(doc, branding);
      return PDF_CONTINUATION_HEADER_BOTTOM_MM + 3;
    };

    y = this.renderHandgripScoreCard(doc, result, y);
    y = this.renderHandgripAttemptTable(doc, result, y) + 5;
    y = this.renderHandgripNormsBlock(doc, result, y);
    if (result.notes) {
      y = this.renderNotesCard(doc, result.notes, y);
    }
    if (previousResults.length) {
      y = this.renderHandgripHistoryTable(doc, previousResults, y) + 5;
    }
    y = this.renderHandgripChartsSection(doc, result, y);

    if (y > pageHeight - PDF_FOOTER_HEIGHT_MM - 34) {
      y = startContinuationPage();
    }

    this.renderProfessionalSignatureAndVerification(doc, result, branding, y, verification, true);

    const pageCount = doc.getNumberOfPages();
    for (let page = 1; page <= pageCount; page += 1) {
      doc.setPage(page);
      this.renderFooter(doc, branding, revahubMarkBase64);
    }

    return doc;
  }

  static async generateOneRMPDF({ result, branding, previousResults = [], verification = null }: AssessmentPDFInput): Promise<jsPDF> {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const revahubMarkBase64 = await this.loadImageAsDataUrl('/logos/revahub-mark.png');
    const payload = normalizeOneRMPayload(result.payloadJson as OneRMResultPayload | null | undefined);

    let y = await this.renderHeader(doc, branding);
    y = this.renderPatientRibbon(doc, result, y);
    y = this.renderPatientClinicalCard(doc, result, y);

    if (!payload) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(...COLORS.slate700);
      doc.text('Dados do 1RM indisponíveis para esta avaliação.', 15, y + 8);
      this.renderProfessionalSignatureAndVerification(doc, result, branding, y + 20, verification);
      this.renderFooter(doc, branding, revahubMarkBase64);
      return doc;
    }

    const startContinuationPage = () => {
      doc.addPage();
      this.renderContinuationHeader(doc, branding);
      return PDF_CONTINUATION_HEADER_BOTTOM_MM + 3;
    };

    const interpretation = this.normalizePdfParagraphText(getOneRMInterpretationText(payload));
    const interpretationLines = doc.splitTextToSize(interpretation, pageWidth - 38) as string[];
    const interpretationHeight = Math.max(20, 12 + interpretationLines.length * 3.8);
    if (y > pageHeight - interpretationHeight - 32) {
      y = startContinuationPage();
    }

    doc.setDrawColor(...COLORS.slate200);
    doc.setFillColor(...COLORS.white);
    doc.roundedRect(15, y, pageWidth - 30, interpretationHeight, 3, 3, 'FD');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.slate800);
    doc.text('Interpretação', 19, y + 6);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.2);
    doc.setTextColor(...COLORS.slate600);
    doc.text(interpretationLines, 19, y + 11.5);
    y += interpretationHeight + 5;

    this.runAutoTable(doc, {
      startY: y,
      head: [['Grupo muscular', '1RM direto', 'Carga submáxima', 'Rep.', 'Mayhew', 'Wathen', 'Estimativa final']],
      body: payload.entries.map((entry) => [
        entry.muscleGroup,
        formatOneRMKg(entry.direct1RMKg),
        formatOneRMKg(entry.submaxLoadKg),
        entry.repetitions != null ? String(entry.repetitions) : '—',
        formatOneRMKg(entry.estimated1RMMayhewKg),
        formatOneRMKg(entry.estimated1RMWathenKg),
        formatOneRMKg(entry.estimated1RMKg),
      ]),
      styles: {
        fontSize: 7.2,
        cellPadding: 1.6,
        textColor: COLORS.slate700,
        valign: 'middle',
      },
      headStyles: { fillColor: COLORS.brandBlue, textColor: COLORS.white },
      alternateRowStyles: { fillColor: COLORS.slate50 },
      margin: { left: 15, right: 15 },
    });
    y = ((doc as unknown as { lastAutoTable?: { finalY?: number } }).lastAutoTable?.finalY ?? y) + 5;

    const notesRows = payload.entries
      .filter((entry) => typeof entry.notes === 'string' && entry.notes.trim().length > 0)
      .map((entry) => [entry.muscleGroup, entry.notes as string]);

    if (notesRows.length) {
      this.runAutoTable(doc, {
        startY: y,
        head: [['Observações por grupo', 'Detalhes']],
        body: notesRows,
        styles: {
          fontSize: 7.2,
          cellPadding: 1.6,
          textColor: COLORS.slate700,
          valign: 'top',
        },
        headStyles: { fillColor: COLORS.brandBlue, textColor: COLORS.white },
        alternateRowStyles: { fillColor: COLORS.slate50 },
        margin: { left: 15, right: 15 },
      });
      y = ((doc as unknown as { lastAutoTable?: { finalY?: number } }).lastAutoTable?.finalY ?? y) + 5;
    }

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.4);
    const referenceBlocks = ONE_RM_STUDY_REFERENCES.map((reference) => doc.splitTextToSize(`• ${reference}`, pageWidth - 36) as string[]);
    const referencesHeight = 5 + referenceBlocks.reduce((height, lines) => height + lines.length * 3.4 + 1, 0) + 2;
    if (y > pageHeight - PDF_BOTTOM_SAFE_MARGIN_MM - referencesHeight) {
      y = startContinuationPage();
    }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.slate800);
    doc.text('Referências', 15, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.4);
    doc.setTextColor(...COLORS.slate600);
    referenceBlocks.forEach((lines) => {
      doc.text(lines, 18, y);
      y += lines.length * 3.4 + 1;
    });
    y += 3;

    const notesText = result.notes || payload.notes;
    if (notesText) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.5);
      const notesLines = doc.splitTextToSize(notesText, pageWidth - 38) as string[];
      const notesHeight = Math.max(16, 8 + notesLines.length * 4.2) + 8;
      if (y > pageHeight - PDF_BOTTOM_SAFE_MARGIN_MM - notesHeight) {
        y = startContinuationPage();
      }
      y = this.renderNotesCard(doc, notesText, y);
    }

    const historyRows = previousResults
      .filter((previous) => previous.resultId !== result.resultId && previous.testKey === 'ONE_RM')
      .map((previous) => {
        const previousPayload = normalizeOneRMPayload(previous.payloadJson as OneRMResultPayload | null | undefined);
        const previousSummary = summarizeOneRMPayload(previousPayload);
        return [
          this.formatDateOnly(previous.assessmentDate),
          String(previousSummary.groupCount),
          String(previousSummary.directCount),
          String(previousSummary.estimatedCount),
          previousSummary.bestGroupLabel || '—',
        ];
      });

    if (historyRows.length) {
      if (y > pageHeight - PDF_BOTTOM_SAFE_MARGIN_MM - 22) {
        y = startContinuationPage();
      }
      this.runAutoTable(doc, {
        startY: y,
        head: [['Data', 'Grupos', 'Direto', 'Estimado', 'Maior grupo']],
        body: historyRows,
        styles: { fontSize: 7.2, cellPadding: 1.6, textColor: COLORS.slate700 },
        headStyles: { fillColor: COLORS.brandBlue, textColor: COLORS.white },
        alternateRowStyles: { fillColor: COLORS.slate50 },
        margin: { left: 15, right: 15 },
      });
      y = ((doc as unknown as { lastAutoTable?: { finalY?: number } }).lastAutoTable?.finalY ?? y) + 5;
    }

    if (y > pageHeight - PDF_FOOTER_HEIGHT_MM - 34) {
      y = startContinuationPage();
    }

    this.renderProfessionalSignatureAndVerification(doc, result, branding, y, verification, true);

    const pageCount = doc.getNumberOfPages();
    for (let page = 1; page <= pageCount; page += 1) {
      doc.setPage(page);
      this.renderFooter(doc, branding, revahubMarkBase64);
    }

    return doc;
  }

  static async generatePeripheralMuscleStrengthPDF({ result, branding, verification = null }: AssessmentPDFInput): Promise<jsPDF> {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const revahubMarkBase64 = await this.loadImageAsDataUrl('/logos/revahub-mark.png');
    const payload = normalizePeripheralMuscleStrengthPayload(result.payloadJson as PeripheralMuscleStrengthResultPayload | null | undefined);

    let y = await this.renderHeader(doc, branding);
    y = this.renderPatientRibbon(doc, result, y);
    y = this.renderPatientClinicalCard(doc, result, y);

    if (!payload) {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(...COLORS.slate700);
      doc.text('Dados da força muscular periférica indisponíveis para esta avaliação.', 15, y + 8);
      this.renderProfessionalSignatureAndVerification(doc, result, branding, y + 20, verification);
      this.renderFooter(doc, branding, revahubMarkBase64);
      return doc;
    }

    const startContinuationPage = () => {
      doc.addPage();
      this.renderContinuationHeader(doc, branding);
      return PDF_CONTINUATION_HEADER_BOTTOM_MM + 3;
    };

    const summary = summarizePeripheralMuscleStrengthResult(payload);

    this.runAutoTable(doc, {
      startY: y,
      head: [['Músculo', 'Melhor', 'Predito', '% predito', 'Top 2', 'Status']],
      body: summary.muscles.map((muscle) => [
        muscle.label,
        muscle.bestKgf != null ? `${muscle.bestKgf.toFixed(1)} kgf` : '—',
        muscle.predictedKgf != null ? `${muscle.predictedKgf.toFixed(1)} kgf` : '—',
        muscle.percentPredicted != null ? `${muscle.percentPredicted.toFixed(1)}%` : '—',
        muscle.topTwoDiffPercent != null ? `${muscle.topTwoDiffPercent.toFixed(1)}%` : '—',
        !muscle.completed ? 'Incompleto' : muscle.reproducible ? 'Reprodutível' : 'Variabilidade >5%',
      ]),
      styles: {
        fontSize: 7.4,
        cellPadding: 1.7,
        textColor: COLORS.slate700,
        valign: 'middle',
      },
      headStyles: { fillColor: COLORS.brandBlue, textColor: COLORS.white },
      alternateRowStyles: { fillColor: COLORS.slate50 },
      margin: { left: 15, right: 15 },
    });
    y = ((doc as unknown as { lastAutoTable?: { finalY?: number } }).lastAutoTable?.finalY ?? y) + 5;

    const protocolLines = [
      `Membro avaliado: ${summary.dominantSideLabel}.`,
      `CIVM de ${summary.protocol.contractionSeconds} segundos no lado dominante.`,
      `Entre ${summary.protocol.minAttempts} e ${summary.protocol.maxAttempts} tentativas por grupo muscular.`,
      `Critério de reprodutibilidade: diferença menor que ${summary.protocol.reproducibilityThresholdPercent}% entre os 2 maiores valores.`,
      `Equipamento de referência: ${summary.protocol.supplier}.`,
    ];

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.6);
    const protocolBlocks = protocolLines.map((line) => doc.splitTextToSize(`- ${line}`, pageWidth - 38) as string[]);
    const protocolHeight = 10 + protocolBlocks.reduce((height, lines) => height + lines.length * 3.5 + 0.8, 0);
    if (y > pageHeight - PDF_BOTTOM_SAFE_MARGIN_MM - protocolHeight) {
      y = startContinuationPage();
    }

    doc.setDrawColor(...COLORS.slate200);
    doc.setFillColor(...COLORS.white);
    doc.roundedRect(15, y, pageWidth - 30, protocolHeight, 3, 3, 'FD');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.slate800);
    doc.text('Protocolo aplicado', 19, y + 6);
    let protocolY = y + 11;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.6);
    doc.setTextColor(...COLORS.slate600);
    protocolBlocks.forEach((lines) => {
      doc.text(lines, 19, protocolY);
      protocolY += lines.length * 3.5 + 0.8;
    });
    y += protocolHeight + 5;

    const interpretation = this.normalizePdfParagraphText(getPeripheralStrengthInterpretationText(payload));
    const interpretationLines = doc.splitTextToSize(interpretation, pageWidth - 38) as string[];
    const interpretationHeight = Math.max(18, 10 + interpretationLines.length * 3.8);
    if (y > pageHeight - PDF_BOTTOM_SAFE_MARGIN_MM - interpretationHeight) {
      y = startContinuationPage();
    }

    doc.setDrawColor(...COLORS.slate200);
    doc.setFillColor(...COLORS.white);
    doc.roundedRect(15, y, pageWidth - 30, interpretationHeight, 3, 3, 'FD');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.slate800);
    doc.text('Interpretação', 19, y + 5.5);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.1);
    doc.setTextColor(...COLORS.slate600);
    doc.text(interpretationLines, 19, y + 10.5);
    y += interpretationHeight + 5;

    if (y > pageHeight - PDF_FOOTER_HEIGHT_MM - 34) {
      y = startContinuationPage();
    }

    this.renderProfessionalSignatureAndVerification(doc, result, branding, y, verification, true);

    const pageCount = doc.getNumberOfPages();
    for (let page = 1; page <= pageCount; page += 1) {
      doc.setPage(page);
      this.renderFooter(doc, branding, revahubMarkBase64);
    }

    return doc;
  }

  private static extractRespiratoryMuscleChartImages(result: AssessmentResultV2): Record<string, string> {
    const chartImages =
      result.assetsJson?.chartImages && typeof result.assetsJson.chartImages === 'object'
        ? (result.assetsJson.chartImages as Record<string, unknown>)
        : {};

    return Object.fromEntries(
      Object.entries(chartImages).filter((entry): entry is [string, string] => typeof entry[1] === 'string' && entry[1].trim().length > 0),
    );
  }

  private static async renderRespiratoryMuscleImages(
    doc: jsPDF,
    result: AssessmentResultV2,
    startY: number,
    branding: ResolvedClientReportBranding,
  ): Promise<number> {
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    const width = pageWidth - margin * 2;
    const images = this.extractRespiratoryMuscleChartImages(result);
    const labelByKey: Record<string, string> = {
      comparison: 'Comparação de curvas',
      individual: 'Curva individual',
      pressure_overlapped: 'Curvas de pressão sobrepostas',
      comparison_pressure: 'Pressão',
      comparison_flow: 'Fluxo',
      comparison_volume: 'Volume',
      comparison_work: 'Trabalho',
      individual_pressure: 'Pressão',
      individual_flow: 'Fluxo',
      individual_volume: 'Volume',
      individual_work: 'Trabalho',
      block_evolution: 'Trabalho respiratório ao longo do teste',
    };
    const entries = Object.entries(images);
    if (!entries.length) return startY;

    let y = startY;
    const startContinuationPage = () => {
      doc.addPage();
      this.renderContinuationHeader(doc, branding);
      return PDF_CONTINUATION_HEADER_BOTTOM_MM + 3;
    };
    const loadEntry = async ([key, source]: [string, string]) => {
      const imageData = source.startsWith('data:image') ? source : await this.loadImageAsDataUrl(source);
      return imageData ? { key, imageData, label: labelByKey[key] ?? key.replace(/_/g, ' ') } : null;
    };

    if (result.testKey === 'RESP_ENDURANCE') {
      const smallOrder = ['comparison_pressure', 'comparison_flow', 'comparison_volume', 'comparison_work'];
      const fallbackOrder = ['individual_pressure', 'individual_flow', 'individual_volume', 'individual_work'];
      const activeOrder = smallOrder.some((key) => images[key]) ? smallOrder : fallbackOrder;
      const smallEntries = (await Promise.all(activeOrder.filter((key) => images[key]).map((key) => loadEntry([key, images[key]])))).filter(
        Boolean,
      ) as Array<{
        key: string;
        imageData: string;
        label: string;
      }>;
      const cellGap = 4;
      const cellWidth = (width - cellGap) / 2;
      const cellHeight = 40;
      for (let index = 0; index < smallEntries.length; index += 2) {
        if (y > pageHeight - PDF_BOTTOM_SAFE_MARGIN_MM - cellHeight - 9) y = startContinuationPage();
        for (let column = 0; column < 2; column += 1) {
          const entry = smallEntries[index + column];
          if (!entry) continue;
          const x = margin + column * (cellWidth + cellGap);
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(8);
          doc.setTextColor(...COLORS.slate700);
          doc.text(entry.label, x, y);
          doc.setDrawColor(...COLORS.slate200);
          doc.setFillColor(...COLORS.white);
          doc.roundedRect(x, y + 2.5, cellWidth, cellHeight, 2.5, 2.5, 'FD');
          this.renderImageContained(doc, entry.imageData, x + 1, y + 3.5, cellWidth - 2, cellHeight - 2, 1);
        }
        y += cellHeight + 8;
      }

      if (images.block_evolution) {
        const entry = await loadEntry(['block_evolution', images.block_evolution]);
        if (entry) {
          if (y > pageHeight - PDF_BOTTOM_SAFE_MARGIN_MM - 52) y = startContinuationPage();
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(8);
          doc.setTextColor(...COLORS.slate700);
          doc.text(entry.label, margin, y);
          doc.setDrawColor(...COLORS.slate200);
          doc.setFillColor(...COLORS.white);
          doc.roundedRect(margin, y + 2.5, width, 44, 2.5, 2.5, 'FD');
          this.renderImageContained(doc, entry.imageData, margin + 1, y + 3.5, width - 2, 42, 1);
          y += 52;
        }
      }
      return y;
    }

    for (const entry of entries) {
      if (y > pageHeight - PDF_BOTTOM_SAFE_MARGIN_MM - 66) {
        y = startContinuationPage();
      }

      const loaded = await loadEntry(entry);
      if (!loaded) continue;

      const { label, imageData } = loaded;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(...COLORS.slate800);
      doc.text(label, margin, y);
      y += 4;

      doc.setDrawColor(...COLORS.slate200);
      doc.setFillColor(...COLORS.white);
      doc.roundedRect(margin, y, width, 54, 3, 3, 'FD');
      this.renderImageContained(doc, imageData, margin + 1, y + 1, width - 2, 52, 1);
      y += 61;
    }

    return y;
  }

  private static renderRespiratoryInterpretation(
    doc: jsPDF,
    title: string,
    lines: string[],
    startY: number,
    branding?: ResolvedClientReportBranding,
  ): number {
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    const width = pageWidth - margin * 2;
    const lineText = this.normalizePdfParagraphText(lines.length ? lines.join(' ') : title);
    const splitLines = doc.splitTextToSize(lineText, width - 8);
    const height = Math.max(18, 10 + splitLines.length * 3.7);

    if (startY > pageHeight - PDF_BOTTOM_SAFE_MARGIN_MM - height) {
      doc.addPage();
      if (branding) this.renderContinuationHeader(doc, branding);
      startY = PDF_CONTINUATION_HEADER_BOTTOM_MM + 3;
    }

    doc.setDrawColor(...COLORS.slate200);
    doc.setFillColor(...COLORS.white);
    doc.roundedRect(margin, startY, width, height, 3, 3, 'FD');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.slate800);
    doc.text(title, margin + 4, startY + 5.5);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.8);
    doc.setTextColor(...COLORS.slate600);
    doc.text(splitLines, margin + 4, startY + 10.5);

    return startY + height + 8;
  }

  static async generateRespiratoryMusclePDF({ result, branding, previousResults = [], verification = null }: AssessmentPDFInput): Promise<jsPDF> {
    if (result.testKey === 'PEMAX' && Array.isArray(result.payloadJson?.positionSeries) && result.payloadJson.positionSeries.length) {
      return this.generatePemaxPositionsPDF({ result, branding, previousResults, verification });
    }
    if (result.testKey === 'PIMAX' && Array.isArray(result.payloadJson?.positionSeries) && result.payloadJson.positionSeries.length) {
      return this.generateNeuromuscularPDF({ result, branding, previousResults, verification });
    }
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });
    const pageHeight = doc.internal.pageSize.getHeight();
    const revahubMarkBase64 = await this.loadImageAsDataUrl('/logos/revahub-mark.png');

    let y = await this.renderHeader(doc, branding);
    y = this.renderPatientRibbon(doc, result, y);
    y = this.renderPatientClinicalCard(doc, result, y);

    if (result.testKey === 'PIMAX') {
      const payload = {
        ...(result.payloadJson as PimaxResultPayload),
        patientContext: this.extractPatientContext(result),
      } as PimaxResultPayload;
      const summary = summarizePimaxPayload(payload);
      const cards = [
        summary.max != null ? { label: 'PImax pico', value: `${summary.max.toFixed(1)} cmH2O` } : null,
        summary.sustained1s != null
          ? {
              label: 'Sustentada por 1 segundo',
              value: `${summary.sustained1s.toFixed(1)} cmH2O`,
            }
          : null,
        summary.percentPredicted != null
          ? {
              label: '% do predito',
              value: `${summary.percentPredicted.toFixed(1)}%`,
            }
          : null,
        summary.lowerLimit != null
          ? {
              label: 'Limite inferior esperado',
              value: `${summary.lowerLimit.toFixed(1)} cmH2O`,
            }
          : null,
        {
          label: 'Curvas/medidas',
          value: String(summary.quality?.maneuversAnalyzed ?? summary.measurements.length),
        },
      ].filter((card): card is MetricCard => card != null);
      y =
        this.renderMetricCards(doc, y, cards, {
          compact: true,
          dense: true,
          columns: Math.min(cards.length, 4),
        }) + 8;
      y = this.renderRespiratoryInterpretation(
        doc,
        summary.interpretation.title,
        summary.interpretation.lines.filter((line) => !line.startsWith('PImax de ')),
        y,
        branding,
      );

      if (payload.breathelink?.metrics?.length) {
        const hasSustained = payload.breathelink.metrics.some((metric) => metric.sustainedPressure1s != null);
        this.runAutoTable(doc, {
          startY: y,
          margin: { left: 15, right: 15 },
          head: [[...['Curva', 'Tins', 'Pico'], ...(hasSustained ? ['Sustentada 1 s'] : [])]],
          body: payload.breathelink.metrics.map((metric) => [
            String(metric.breathNumber),
            `${metric.tins.toFixed(2)} s`,
            `${metric.peakPressure.toFixed(2)} cmH2O`,
            ...(hasSustained ? [metric.sustainedPressure1s != null ? `${metric.sustainedPressure1s.toFixed(2)} cmH2O` : 'Não informada'] : []),
          ]),
          theme: 'striped',
          headStyles: {
            fillColor: COLORS.brandBlue,
            textColor: COLORS.white,
            fontSize: 8,
            fontStyle: 'bold',
          },
          bodyStyles: {
            fontSize: 7.6,
            textColor: COLORS.slate800,
            cellPadding: 2.2,
          },
        });
        y = ((doc as any).lastAutoTable?.finalY ?? y) + 6;
      } else if (summary.measurements.length) {
        this.runAutoTable(doc, {
          startY: y,
          margin: { left: 15, right: 15 },
          head: [['Medida', 'PImax']],
          body: summary.measurements.map((value, index) => [`${index + 1}`, `${value.toFixed(1)} cmH2O`]),
          theme: 'striped',
          headStyles: {
            fillColor: COLORS.brandBlue,
            textColor: COLORS.white,
            fontSize: 8,
            fontStyle: 'bold',
          },
          bodyStyles: {
            fontSize: 7.6,
            textColor: COLORS.slate800,
            cellPadding: 2.2,
          },
        });
        y = ((doc as any).lastAutoTable?.finalY ?? y) + 6;
      }
    } else if (result.testKey === 'PEMAX') {
      const payload = {
        ...(result.payloadJson as PemaxResultPayload),
        patientContext: this.extractPatientContext(result),
      } as PemaxResultPayload;
      const summary = summarizePemaxPayload(payload);
      const cards = [
        summary.max != null ? { label: 'PEmax pico', value: `${summary.max.toFixed(1)} cmH2O` } : null,
        summary.percentPredicted != null
          ? {
              label: '% do predito',
              value: `${summary.percentPredicted.toFixed(1)}%`,
            }
          : null,
        summary.lowerLimit != null
          ? {
              label: 'Limite inferior esperado',
              value: `${summary.lowerLimit.toFixed(1)} cmH2O`,
            }
          : null,
        summary.sustained1s != null
          ? {
              label: 'Sustentada por 1 segundo',
              value: `${summary.sustained1s.toFixed(1)} cmH2O`,
            }
          : null,
        {
          label: 'Manobras/medidas',
          value: String(summary.quality.maneuversAnalyzed),
        },
      ].filter((card): card is MetricCard => card != null);
      y =
        this.renderMetricCards(doc, y, cards, {
          compact: true,
          dense: true,
          columns: Math.min(cards.length, 4),
        }) + 8;
      y = this.renderRespiratoryInterpretation(
        doc,
        summary.interpretation.title,
        summary.interpretation.lines.filter((line) => !line.startsWith('PEmax de ')),
        y,
        branding,
      );

      this.runAutoTable(doc, {
        startY: y,
        margin: { left: 15, right: 15 },
        head: [['Medida', 'PEmax']],
        body: summary.measurements.map((value, index) => [`${index + 1}`, `${value.toFixed(1)} cmH2O`]),
        theme: 'striped',
        headStyles: {
          fillColor: COLORS.brandBlue,
          textColor: COLORS.white,
          fontSize: 8.5,
          fontStyle: 'bold',
        },
        bodyStyles: {
          fontSize: 7.6,
          textColor: COLORS.slate800,
          cellPadding: 2.2,
        },
      });
      y = ((doc as any).lastAutoTable?.finalY ?? y) + 8;
    } else if (result.testKey === 'S_INDEX') {
      const payload = {
        ...(result.payloadJson as SIndexResultPayload),
        patientContext: this.extractPatientContext(result),
      } as SIndexResultPayload;
      const summary = summarizeSIndexPayload(payload);
      const cards = [
        summary.maxPressure != null
          ? {
              label: 'S-Index',
              value: `${summary.maxPressure.toFixed(1)} cmH2O`,
            }
          : null,
        summary.predictedSIndex != null
          ? {
              label: 'Predito',
              value: `${summary.predictedSIndex.toFixed(1)} cmH2O`,
            }
          : null,
        summary.percentPredicted != null
          ? {
              label: '% do predito',
              value: `${summary.percentPredicted.toFixed(1)}%`,
            }
          : null,
        summary.llnSIndex != null
          ? {
              label: 'Limite inferior esperado',
              value: `${summary.llnSIndex.toFixed(1)} cmH2O`,
            }
          : null,
      ].filter((card): card is MetricCard => card != null);
      y =
        this.renderMetricCards(doc, y, cards, {
          compact: true,
          dense: true,
          columns: Math.min(cards.length, 4),
        }) + 8;
      y = this.renderRespiratoryInterpretation(
        doc,
        summary.interpretation.title,
        summary.interpretation.lines.filter((line) => !line.startsWith('S-Index de ')),
        y,
        branding,
      );
      if (summary.metrics.length) {
        this.runAutoTable(doc, {
          startY: y,
          margin: { left: 15, right: 15 },
          head: [['Resp.', 'Target', 'Tins', 'Pico pressão', 'Pico fluxo', 'Volume', 'Trabalho']],
          body: summary.metrics.map((metric) => [
            String(metric.breathNumber),
            metric.targetResistance != null ? metric.targetResistance.toFixed(1) : '-',
            `${metric.tins.toFixed(2)} s`,
            `${metric.peakPressure.toFixed(2)} cmH2O`,
            `${metric.peakFlow.toFixed(2)} L/s`,
            `${metric.peakVolume.toFixed(2)} L`,
            metric.totalWork.toFixed(2),
          ]),
          theme: 'striped',
          headStyles: {
            fillColor: COLORS.brandBlue,
            textColor: COLORS.white,
            fontSize: 8,
            fontStyle: 'bold',
          },
          bodyStyles: {
            fontSize: 7.7,
            textColor: COLORS.slate800,
            cellPadding: 2.5,
          },
        });
        y = ((doc as any).lastAutoTable?.finalY ?? y) + 8;
      } else if (summary.measurements.length) {
        this.runAutoTable(doc, {
          startY: y,
          margin: { left: 15, right: 15 },
          head: [['Medida', 'S-Index']],
          body: summary.measurements.map((value, index) => [`${index + 1}`, `${value.toFixed(1)} cmH2O`]),
          theme: 'striped',
          headStyles: {
            fillColor: COLORS.brandBlue,
            textColor: COLORS.white,
            fontSize: 8.5,
            fontStyle: 'bold',
          },
          bodyStyles: {
            fontSize: 8,
            textColor: COLORS.slate800,
            cellPadding: 3,
          },
        });
        y = ((doc as any).lastAutoTable?.finalY ?? y) + 8;
      }
    } else {
      const payload = result.payloadJson as RespiratoryEnduranceResultPayload;
      const summary = summarizeRespiratoryEndurancePayload(payload);
      const interpretation = buildEnduranceInterpretation(payload);
      const protocolLines = getRespiratoryEnduranceProtocolLines(payload.protocol);
      const cards = [
        summary.tlimFormatted ? { label: 'Tlim', value: summary.tlimFormatted } : null,
        summary.targetResistance != null
          ? {
              label: 'Carga alvo',
              value: `${summary.targetResistance.toFixed(1)} cmH2O`,
            }
          : null,
        summary.flowReduction != null
          ? {
              label: 'Redução do fluxo',
              value: `${summary.flowReduction.toFixed(1)}%`,
            }
          : null,
        summary.volumeReduction != null
          ? {
              label: 'Redução do volume',
              value: `${summary.volumeReduction.toFixed(1)}%`,
            }
          : null,
        summary.workReduction != null
          ? {
              label: 'Redução do trabalho',
              value: `${summary.workReduction.toFixed(1)}%`,
            }
          : null,
      ].filter((card): card is MetricCard => card != null);
      y =
        this.renderMetricCards(doc, y, cards, {
          compact: true,
          dense: true,
          columns: Math.min(cards.length, 5),
          detailFontSize: 6.6,
        }) + 8;
      if (protocolLines.length) y = this.renderRespiratoryInterpretation(doc, 'Protocolo aplicado', protocolLines, y, branding);
      y = this.renderRespiratoryInterpretation(
        doc,
        interpretation.title,
        interpretation.lines.filter((line) => !line.startsWith('Tlim registrado:') && !line.startsWith('Resistência alvo estimada:')),
        y,
        branding,
      );
      if ((payload.blocks ?? []).length) {
        this.runAutoTable(doc, {
          startY: y,
          margin: { left: 15, right: 15 },
          head: [['Bloco', 'Respirações', 'Pico fluxo', 'Pico volume', 'Trabalho']],
          body: (payload.blocks ?? []).map((block) => [
            `Bloco ${block.index}`,
            block.breathRange,
            `${block.meanPeakFlow.toFixed(2)} L/s`,
            `${block.meanPeakVolume.toFixed(2)} L`,
            block.meanTotalWork.toFixed(2),
          ]),
          theme: 'striped',
          headStyles: {
            fillColor: COLORS.brandBlue,
            textColor: COLORS.white,
            fontSize: 8,
            fontStyle: 'bold',
          },
          bodyStyles: {
            fontSize: 7.3,
            textColor: COLORS.slate800,
            cellPadding: 2,
          },
        });
        y = ((doc as any).lastAutoTable?.finalY ?? y) + 6;
      }
    }

    y = this.renderNotesCard(doc, result.notes, y);
    y = await this.renderRespiratoryMuscleImages(doc, result, y, branding);

    if (y > pageHeight - PDF_BOTTOM_SAFE_MARGIN_MM - 31) {
      doc.addPage();
      this.renderContinuationHeader(doc, branding);
      y = PDF_CONTINUATION_HEADER_BOTTOM_MM + 3;
    }

    this.renderProfessionalSignatureAndVerification(doc, result, branding, y, verification, true);

    const pageCount = doc.getNumberOfPages();
    for (let page = 1; page <= pageCount; page += 1) {
      doc.setPage(page);
      this.renderFooter(doc, branding, revahubMarkBase64);
    }

    return doc;
  }

  static async generateTUGPDF({ result, branding, previousResults = [], verification = null }: AssessmentPDFInput): Promise<jsPDF> {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });
    const pageHeight = doc.internal.pageSize.getHeight();
    const revahubMarkBase64 = await this.loadImageAsDataUrl('/logos/revahub-mark.png');

    let y = await this.renderHeader(doc, branding);
    y = this.renderPatientRibbon(doc, result, y);
    y = this.renderPatientClinicalCard(doc, result, y);
    y = this.renderTUGScoreCard(doc, result, y);
    y = this.renderTUGProtocolTable(doc, result, y) + 4;
    y = this.renderTUGInterpretationBlock(doc, result, y);
    y = this.renderNotesCard(doc, result.notes, y);
    if (previousResults.length) {
      y = this.renderTUGHistoryTable(doc, previousResults, y) + 4;
    }

    if (y > pageHeight - PDF_FOOTER_HEIGHT_MM - 31) {
      doc.addPage();
      y = 20;
    }

    this.renderProfessionalSignatureAndVerification(doc, result, branding, y, verification, true);

    const pageCount = doc.getNumberOfPages();
    for (let page = 1; page <= pageCount; page += 1) {
      doc.setPage(page);
      this.renderFooter(doc, branding, revahubMarkBase64);
    }

    return doc;
  }

  private static renderClinicalAssessmentContent(doc: jsPDF, value: unknown, startY: number): number {
    const payload = normalizeInitialAssessmentPayload(value);
    if (!payload) return startY;
    let y = startY;
    const table = (heading: string[], rows: string[][], widths?: number[]): void => {
      if (!rows.length) return;
      doc.setFont('helvetica', 'normal'); doc.setFontSize(8);
      const firstRowLines = Math.max(...rows[0].map((cell, index) => (doc.splitTextToSize(cell || ' ', (widths?.[index] ?? 180 / heading.length) - 6) as string[]).length));
      const minimumHeight = Math.min(75, 14 + firstRowLines * 3.8);
      if (y + minimumHeight > doc.internal.pageSize.getHeight() - PDF_BOTTOM_SAFE_MARGIN_MM) {
        doc.addPage();
        const context = this.getPDFDocumentPageContext(doc);
        if (context.branding) this.renderContinuationHeader(doc, context.branding);
        y = PDF_CONTINUATION_HEADER_BOTTOM_MM + 3;
      }
      this.runAutoTable(doc, {
        startY: y, margin: { left: 15, right: 15 }, head: [heading], body: rows, theme: 'grid',
        headStyles: { fillColor: COLORS.brandBlue, textColor: COLORS.white, fontSize: 8, fontStyle: 'bold' },
        bodyStyles: { fontSize: 8, textColor: COLORS.slate700, cellPadding: 2.6, valign: 'top' },
        alternateRowStyles: { fillColor: COLORS.slate50 },
        ...(widths ? { columnStyles: Object.fromEntries(widths.map((width, index) => [index, { cellWidth: width }])) } : {}),
      });
      y = ((doc as unknown as { lastAutoTable?: { finalY?: number } }).lastAutoTable?.finalY ?? y) + 5;
    };
    const finalKeys = new Set(['professionalSynthesis', 'prognosis', 'initialPlan', 'followUpPlan', 'closureReason', 'additionalNotes']);
    const contextRows: string[][] = [
      ['Momento', INITIAL_ASSESSMENT_MOMENT_LABELS[payload.assessmentMoment]],
      ...(payload.episodeLabel ? [['Episódio de cuidado', payload.episodeLabel]] : []),
      ...INITIAL_ASSESSMENT_FIELD_LABELS.filter(({ key }) => !finalKeys.has(key))
        .flatMap(({ key, label }) => payload[key] ? [[label, payload[key]]] : []),
    ];
    table(['Contexto clínico-funcional', 'Registro'], contextRows, [52, 128]);
    if (payload.anamnesisSnapshot) {
      const source = payload.anamnesisSnapshot;
      table(['Anamnese', `${this.formatDate(source.sourceRecordDate)} · versão ${source.documentVersion}`],
        ANAMNESIS_SECTION_DEFINITIONS.flatMap(({ key, label }) => source.sections[key]?.trim() ? [[label, source.sections[key]]] : []), [52, 128]);
    }
    table(['Domínio', 'Exame físico-funcional', 'Situação', 'Achado'], payload.physicalExamination.map((item) => [
      item.domain, item.label, PHYSICAL_EXAMINATION_STATUS_LABELS[item.status], item.finding,
    ]), [35, 45, 32, 68]);
    table(['Exame complementar referenciado', 'Data', 'Nota do profissional'], payload.externalExams.map((exam) => [
      exam.title, this.formatDateOnly(exam.recordDate), exam.note,
    ]), [65, 25, 90]);
    table(['Teste selecionado', 'Data', 'Resultado', 'Referência registrada'], payload.selectedResults.map((source) => [
      source.snapshot.testLabel, this.formatDateOnly(source.snapshot.assessmentDate),
      this.formatAssessmentReportPrimaryMeasure(source), source.finding.referenceLabel || source.snapshot.referenceText || '',
    ]), [48, 22, 45, 65]);
    table(['Síntese e plano', 'Registro profissional'], INITIAL_ASSESSMENT_FIELD_LABELS
      .filter(({ key }) => finalKeys.has(key)).flatMap(({ key, label }) => payload[key] ? [[label, payload[key]]] : []), [52, 128]);
    table(['Atividade / meta', 'Situação inicial', 'Alvo', 'Prazo', 'Situação'], payload.measurableGoals.map((goal) => [
      goal.activity, goal.baseline, goal.target, goal.deadline ? this.formatDateOnly(goal.deadline) : '', MEASURABLE_GOAL_STATUS_LABELS[goal.status],
    ]), [48, 35, 40, 25, 32]);
    return y;
  }

  /** Gráfico vetorial com dimensões próprias de impressão e escala temporal real. */
  private static renderLongitudinalChart(doc: jsPDF, series: AssessmentLongitudinalSeries, startY: number): number {
    const left = 30; const right = 188; const top = startY + 4; const bottom = startY + 39;
    const values = series.points.flatMap((point) => point.referenceValue != null ? [point.value, point.referenceValue] : [point.value]);
    const minimum = Math.min(...values); const maximum = Math.max(...values);
    const pad = Math.max((maximum - minimum) * 0.15, Math.abs(maximum) * 0.05, 0.1);
    const lower = Math.min(0, minimum) < 0 ? minimum - pad : Math.max(0, minimum - pad);
    const upper = maximum + pad;
    const first = series.points[0].timestamp; const last = series.points[series.points.length - 1].timestamp;
    const x = (timestamp: number) => first === last ? (left + right) / 2 : left + (timestamp - first) / (last - first) * (right - left);
    const y = (value: number) => bottom - (value - lower) / (upper - lower) * (bottom - top);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(6.5);
    for (const fraction of [0, 0.5, 1]) {
      const value = lower + (upper - lower) * fraction;
      doc.setDrawColor(...COLORS.slate200); doc.setLineWidth(0.15); doc.line(left, y(value), right, y(value));
      doc.setTextColor(...COLORS.slate700); doc.text(formatLongitudinalNumber(value, 1), left - 2, y(value) + 1, { align: 'right' });
    }
    series.points.forEach((point, index) => {
      const previous = series.points[index - 1];
      doc.setDrawColor(...COLORS.brandBlue); doc.setFillColor(...COLORS.brandBlue); doc.setLineWidth(0.5);
      if (previous && series.comparable) doc.line(x(previous.timestamp), y(previous.value), x(point.timestamp), y(point.value));
      doc.circle(x(point.timestamp), y(point.value), 0.9, 'F');
      if (point.referenceValue != null) {
        doc.setDrawColor(...COLORS.slate500); doc.setFillColor(...COLORS.slate500);
        if (previous?.referenceValue != null && series.comparable) {
          doc.setLineDashPattern([1, 1], 0);
          doc.line(x(previous.timestamp), y(previous.referenceValue), x(point.timestamp), y(point.referenceValue));
          doc.setLineDashPattern([], 0);
        }
        doc.circle(x(point.timestamp), y(point.referenceValue), 0.6, 'F');
      }
      if (index === 0 || index === series.points.length - 1 || (series.points.length > 2 && index === Math.floor(series.points.length / 2))) {
        doc.setTextColor(...COLORS.slate700);
        doc.text(this.formatDateOnly(point.assessmentDate), x(point.timestamp), bottom + 4, { align: 'center' });
      }
    });
    doc.text(`Medido (${series.unit})${series.points.some((point) => point.referenceValue != null) ? ' | Tracejado: referência registrada na data' : ''}`, 15, bottom + 9);
    return bottom + 14;
  }

  static async generateInitialAssessmentPDF({ result, branding, verification = null }: AssessmentPDFInput): Promise<jsPDF> {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });
    const pageHeight = doc.internal.pageSize.getHeight();
    const revahubMarkBase64 = await this.loadImageAsDataUrl('/logos/revahub-mark.png');
    const payload = normalizeInitialAssessmentPayload(result.payloadJson);
    const autoTableDocument = doc as jsPDF & { lastAutoTable?: { finalY?: number } };

    let y = await this.renderHeader(doc, branding);
    y = this.renderPatientRibbon(doc, result, y, payload ? INITIAL_ASSESSMENT_MOMENT_LABELS[payload.assessmentMoment] : undefined);
    y = this.renderPatientClinicalCard(doc, result, y);

    if (payload) {
      y = this.renderClinicalAssessmentContent(doc, payload, y);
    } else {
      y = this.renderNotesCard(doc, 'Os dados estruturados desta avaliação não estão disponíveis.', y);
    }

    if (result.notes?.trim()) {
      this.runAutoTable(doc, {
        startY: y,
        margin: { left: 15, right: 15 },
        head: [['Observações clínicas']],
        body: [[result.notes.trim()]],
        theme: 'grid',
        headStyles: {
          fillColor: COLORS.slate100,
          textColor: COLORS.slate800,
          fontSize: 8.5,
          fontStyle: 'bold',
        },
        bodyStyles: {
          fontSize: 8.2,
          textColor: COLORS.slate700,
          cellPadding: 3.2,
          valign: 'top',
        },
      });
      y = (autoTableDocument.lastAutoTable?.finalY ?? y) + 5;
    }
    if (y > pageHeight - PDF_BOTTOM_SAFE_MARGIN_MM - 31) {
      doc.addPage();
      this.renderContinuationHeader(doc, branding);
      y = PDF_CONTINUATION_HEADER_BOTTOM_MM + 3;
    }
    this.renderProfessionalSignatureAndVerification(doc, result, branding, y, verification, true);

    const pageCount = doc.getNumberOfPages();
    for (let page = 1; page <= pageCount; page += 1) {
      doc.setPage(page);
      this.renderFooter(doc, branding, revahubMarkBase64);
    }

    return doc;
  }

  private static normalizeRespiratoryPdfText(value: string): string {
    return String(value).replace(/SpO[₂¸]/g, 'SpO2').replace(/₂/g, '2').replace(/Δ/g, 'Delta')
      .replace(/≥/g, '>=').replace(/≤/g, '<=').replace(/[‐‑‒–—−]/g, '-').replace(/→/g, 'a')
      .replace(/[“”]/g, '"').replace(/[‘’]/g, "'").replace(/\r\n/g, '\n').trim();
  }

  private static renderRespiratoryReportTable(doc: jsPDF, branding: ResolvedClientReportBranding, y: number, title: string, head: string[], body: string[][], compact = false): number {
    if (!body.length) return y;
    if (y > doc.internal.pageSize.getHeight() - PDF_BOTTOM_SAFE_MARGIN_MM - 35) {
      doc.addPage();
      this.renderContinuationHeader(doc, branding);
      y = PDF_CONTINUATION_HEADER_BOTTOM_MM + 3;
    }
    doc.setFont('helvetica', 'bold'); doc.setFontSize(8.5); doc.setTextColor(...COLORS.slate800);
    doc.text(title, 15, y + 3);
    this.runAutoTable(doc, {
      startY: y + (compact ? 4 : 5), margin: { left: 15, right: 15 }, head: [head],
      body: body.map(row => row.map(cell => this.normalizeRespiratoryPdfText(cell))), theme: 'grid',
      headStyles: { fillColor: COLORS.slate100, textColor: COLORS.slate700, fontSize: 7.8, fontStyle: 'bold', cellPadding: compact ? 1.2 : 1.8 },
      bodyStyles: { fontSize: 7.6, textColor: COLORS.slate700, cellPadding: compact ? 1.2 : 1.8, valign: 'top' },
      styles: { lineColor: COLORS.slate200, lineWidth: 0.15, overflow: 'linebreak' },
    });
    return ((doc as jsPDF & { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? y) + (compact ? 3 : 4);
  }

  private static renderRespiratoryReference(doc: jsPDF, branding: ResolvedClientReportBranding, y: number, text: string): number {
    doc.setFont('helvetica', 'normal'); doc.setFontSize(7); doc.setTextColor(...COLORS.slate500);
    const lines = doc.splitTextToSize(`Referências: ${text}`, 180) as string[];
    const height = lines.length * 3.2 + 3;
    if (y + height > doc.internal.pageSize.getHeight() - PDF_BOTTOM_SAFE_MARGIN_MM) {
      doc.addPage(); this.renderContinuationHeader(doc, branding); y = PDF_CONTINUATION_HEADER_BOTTOM_MM + 3;
    }
    doc.setFont('helvetica', 'normal'); doc.setFontSize(7); doc.setTextColor(...COLORS.slate500);
    doc.text(lines, 15, y + 3);
    return y + height;
  }

  static async generatePemaxPositionsPDF({ result, branding, previousResults = [], verification = null }: AssessmentPDFInput): Promise<jsPDF> {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const mark = await this.loadImageAsDataUrl('/logos/revahub-mark.png');
    const payload = result.payloadJson as PemaxResultPayload;
    const positions = getPemaxPositionSeries(payload);
    const hasReference = positions.some((series) => !series.notPerformedReason && summarizePemaxPayload(series.result).predicted != null);
    let y = await this.renderHeader(doc, branding);
    y = this.renderPatientRibbon(doc, result, y);
    y = this.renderPatientClinicalCard(doc, result, y);
    y = this.renderRespiratoryReportTable(doc, branding, y, 'PEmax por posição', ['Posição', 'Medidas (cmH2O)', 'Maior valor', ...(hasReference ? ['Referência / % previsto'] : []), 'Condição de aplicação'], positions.map((series) => {
      const summary = summarizePemaxPayload(series.result);
      return [getPimaxPositionLabel(series), series.result.measurements.map((value) => formatLongitudinalNumber(value, 1)).join('; '),
        series.notPerformedReason || (summary.max != null ? `${formatLongitudinalNumber(summary.max, 1)} cmH2O` : 'Não realizada'),
        ...(hasReference ? [summary.predicted != null && !series.notPerformedReason ? `${formatLongitudinalNumber(summary.predicted, 1)} cmH2O${summary.percentPredicted != null ? ` / ${formatLongitudinalNumber(summary.percentPredicted, 1)}%` : ''}` : ''] : []),
        [series.technique.initialVolume === 'TLC' ? 'CPT' : series.technique.initialVolume === 'unknown' ? '' : series.technique.initialVolume,
          series.technique.interface === 'mouthpiece' ? 'Bocal' : series.technique.interface === 'mask' ? 'Máscara' : '', series.technique.device, series.technique.notes].filter(Boolean).join(' · ')];
    }));
    const summary = summarizePemaxPayload(payload);
    y = this.renderRespiratoryInterpretation(doc, summary.interpretation.title, summary.interpretation.lines, y, branding);
    for (const current of positions.filter((series) => !series.notPerformedReason)) {
      const comparisons = previousResults.filter((previous) => previous.testKey === 'PEMAX' && previous.status === 'completed'
        && previous.patientId === result.patientId && previous.clientId === result.clientId).flatMap((previous) =>
        getPemaxPositionSeries(previous.payloadJson as PemaxResultPayload).filter((series) => !series.notPerformedReason && pemaxSeriesAreComparable(current, series)).flatMap((previousSeries) => {
          const currentValue = summarizePemaxPayload(current.result).max;
          const previousValue = summarizePemaxPayload(previousSeries.result).max;
          if (currentValue == null || previousValue == null) return [];
          const delta = currentValue - previousValue;
          return [[this.formatDateOnly(previous.assessmentDate), `${formatLongitudinalNumber(previousValue, 1)} cmH2O`, `${formatLongitudinalNumber(currentValue, 1)} cmH2O`, `${delta > 0 ? '+' : ''}${formatLongitudinalNumber(delta, 1)} cmH2O`]];
        }));
      y = this.renderRespiratoryReportTable(doc, branding, y, `Comparação - ${getPimaxPositionLabel(current)}`, ['Data anterior', 'Anterior', 'Atual', 'Variação'], comparisons);
    }
    if (payload.notes?.trim()) y = this.renderRespiratoryReportTable(doc, branding, y, 'Observações', ['Registro'], [[payload.notes.trim()]]);
    y = this.renderRespiratoryReportTable(doc, branding, y, 'Referência', ['Fonte'], [['Neder et al., 1999. Referência aplicada somente ao protocolo compatível; cada posição mantém suas próprias medidas.']]);
    if (y > doc.internal.pageSize.getHeight() - PDF_BOTTOM_SAFE_MARGIN_MM - 31) {
      doc.addPage(); this.renderContinuationHeader(doc, branding); y = PDF_CONTINUATION_HEADER_BOTTOM_MM + 3;
    }
    this.renderProfessionalSignatureAndVerification(doc, result, branding, y, verification, true);
    for (let page = 1; page <= doc.getNumberOfPages(); page += 1) { doc.setPage(page); this.renderFooter(doc, branding, mark); }
    return doc;
  }

  private static renderCoughFlowReferenceScales(doc: jsPDF, branding: ResolvedClientReportBranding, startY: number, scales: PeakCoughFlowReferenceScale[]): number {
    if (!scales.length) return startY;
    // Fixed PDF geometry; the domain supplies the same numeric scale used by HTML.
    const gap = 6;
    const width = (180 - gap * (scales.length - 1)) / scales.length;
    const cards = scales.map(scale => {
      doc.setFont('helvetica', 'normal'); doc.setFontSize(7);
      const footnote = scale.footnote ? doc.splitTextToSize(this.normalizeRespiratoryPdfText(scale.footnote), width - 8) as string[] : [];
      doc.setFont('helvetica', 'bold'); doc.setFontSize(8);
      const title = doc.splitTextToSize(this.normalizeRespiratoryPdfText(scale.title), width - 8) as string[];
      doc.setFont('helvetica', 'normal'); doc.setFontSize(7);
      const subtitle = scale.subtitle ? doc.splitTextToSize(this.normalizeRespiratoryPdfText(scale.subtitle), width - 8) as string[] : [];
      const showClassification = scales.length > 1 && scale.id !== 'PCF_ADULT_NMD_ATTENTION';
      return { scale, title, subtitle, footnote, showClassification, height: 20 + (showClassification ? 4 : 0) + title.length * 3.2 + subtitle.length * 3 + footnote.length * 3 };
    });
    const height = Math.max(...cards.map(card => card.height));
    let y = startY;
    if (y + height > doc.internal.pageSize.getHeight() - PDF_BOTTOM_SAFE_MARGIN_MM) {
      doc.addPage(); this.renderContinuationHeader(doc, branding); y = PDF_CONTINUATION_HEADER_BOTTOM_MM + 3;
    }
    const rgb = (hex: string): RGB => {
      const match = /^#([0-9a-f]{6})$/i.exec(hex);
      return match ? [0, 2, 4].map(offset => Number.parseInt(match[1].slice(offset, offset + 2), 16)) as RGB : COLORS.slate500;
    };
    cards.forEach(({ scale, title, subtitle, footnote, showClassification }, index) => {
      const x = 15 + index * (width + gap);
      const barX = x + 5, barWidth = width - 10;
      const progress = (value: number) => Math.max(0, Math.min(1, (value - scale.min) / (scale.max - scale.min)));
      doc.setDrawColor(...COLORS.slate200); doc.setFillColor(...COLORS.white);
      doc.roundedRect(x, y, width, height, 2, 2, 'FD');
      doc.setFont('helvetica', 'bold'); doc.setFontSize(8); doc.setTextColor(...COLORS.slate800);
      doc.text(title, x + 4, y + 5);
      const subtitleY = y + 5 + title.length * 3.2;
      doc.setFont('helvetica', 'normal'); doc.setFontSize(7); doc.setTextColor(...COLORS.slate600);
      if (subtitle.length) doc.text(subtitle, x + 4, subtitleY);
      const barY = subtitleY + subtitle.length * 3 + 4;
      scale.segments.forEach(segment => {
        doc.setFillColor(...rgb(segment.color));
        doc.rect(barX + progress(segment.from) * barWidth, barY, (progress(segment.to) - progress(segment.from)) * barWidth, 3, 'F');
      });
      const markerX = barX + progress(scale.value) * barWidth;
      doc.setDrawColor(...rgb(scale.color)); doc.setLineWidth(0.7);
      doc.line(markerX, barY - 2, markerX, barY + 4);
      doc.setFont('helvetica', 'bold'); doc.setFontSize(7); doc.setTextColor(...rgb(scale.color));
      const measuredLabel = `${this.formatNumber(scale.value)} L/min`;
      const measuredAlign = progress(scale.value) < 0.15 ? 'left' : progress(scale.value) > 0.85 ? 'right' : 'center';
      doc.text(measuredLabel, markerX, barY - 3, { align: measuredAlign });
      doc.setFont('helvetica', 'normal'); doc.setFontSize(6.3); doc.setTextColor(...COLORS.slate600);
      scale.markers.forEach(marker => {
        const p = progress(marker.value), markerPosition = barX + p * barWidth;
        doc.setDrawColor(...COLORS.slate500); doc.setLineWidth(0.2); doc.line(markerPosition, barY + 3, markerPosition, barY + 4.5);
        doc.text(this.normalizeRespiratoryPdfText(marker.label), markerPosition, barY + 7.5, { align: p < 0.12 ? 'left' : p > 0.88 ? 'right' : 'center' });
      });
      if (showClassification) {
        doc.setFont('helvetica', 'bold'); doc.setFontSize(7); doc.setTextColor(...rgb(scale.color));
        doc.text(scale.classification, x + 4, barY + 12);
      }
      if (footnote.length) {
        doc.setFont('helvetica', 'normal'); doc.setFontSize(7); doc.setTextColor(...COLORS.slate500); doc.text(footnote, x + 4, barY + (showClassification ? 16 : 12));
      }
    });
    return y + height + 2;
  }

  static async generateNeuromuscularPDF({ result, branding, previousResults = [], verification = null }: AssessmentPDFInput): Promise<jsPDF> {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const compact = result.testKey === 'PEAK_COUGH_FLOW' || previousResults.length > 0;
    const renderReportTable = (startY: number, title: string, head: string[], body: string[][]): number => {
      if (!body.length) return startY;
      if (result.testKey !== 'PEAK_COUGH_FLOW' || head.length !== 1) return this.renderRespiratoryReportTable(doc, branding, startY, title, head, body, compact);
      doc.setFont('helvetica', 'normal'); doc.setFontSize(7.6);
      const paragraphs = body.map(row => doc.splitTextToSize(this.normalizeRespiratoryPdfText(row[0]), 176) as string[]);
      const height = 7 + paragraphs.reduce((total, lines) => total + lines.length * 3.4 + 1, 0);
      // Longer narratives use the paginated table engine; short context fields
      // remain distinct labelled paragraphs without a redundant column header.
      if (height > 65) return this.renderRespiratoryReportTable(doc, branding, startY, title, head, body, compact);
      let sectionY = startY;
      if (sectionY + height > doc.internal.pageSize.getHeight() - PDF_BOTTOM_SAFE_MARGIN_MM) {
        doc.addPage(); this.renderContinuationHeader(doc, branding); sectionY = PDF_CONTINUATION_HEADER_BOTTOM_MM + 3;
      }
      doc.setFont('helvetica', 'bold'); doc.setFontSize(8.5); doc.setTextColor(...COLORS.slate800); doc.text(title, 15, sectionY + 3);
      doc.setFont('helvetica', 'normal'); doc.setFontSize(7.6); doc.setTextColor(...COLORS.slate700);
      let textY = sectionY + 7;
      paragraphs.forEach(lines => { doc.text(lines, 15, textY); textY += lines.length * 3.4 + 1; });
      return sectionY + height + 2;
    };
    const mark = await this.loadImageAsDataUrl('/logos/revahub-mark.png');
    const report = buildNeuromuscularReport(result.testKey as NeuromuscularReportKey, {
      ...result.payloadJson, patientContext: this.extractPatientContext(result),
    });
    let y = await this.renderHeader(doc, branding);
    y = this.renderPatientRibbon(doc, result, y);
    y = this.renderPatientClinicalCard(doc, result, y);
    const hex = report.color.replace('#', '');
    const accent: RGB = [Number.parseInt(hex.slice(0, 2), 16), Number.parseInt(hex.slice(2, 4), 16), Number.parseInt(hex.slice(4, 6), 16)];
    const longInterpretation = report.interpretation.length > 500;
    doc.setFontSize(8.5); doc.setFont('helvetica', 'normal');
    const explanation = doc.splitTextToSize(this.normalizeRespiratoryPdfText(longInterpretation ? 'Interpretação clínica apresentada a seguir.' : report.interpretation), 102) as string[];
    doc.setFontSize(8); doc.setFont('helvetica', 'bold');
    const classification = doc.splitTextToSize(report.classification, 58) as string[];
    const heroHeight = Math.max((compact ? 35 : 37) + Math.max(0, classification.length - 1) * 3.5, 16 + explanation.length * 3.9);
    doc.setDrawColor(...COLORS.slate200); doc.setFillColor(...COLORS.white);
    doc.roundedRect(15, y, 180, heroHeight, 3, 3, 'FD');
    doc.setTextColor(...COLORS.slate500); doc.setFontSize(8); doc.text(this.trimLineToWidth(doc, report.label, 58), 47, y + 7, { align: 'center' });
    doc.setFontSize(report.value.length > 10 ? 15 : 27); doc.setTextColor(...accent); doc.text(report.value, 47, y + 21, { align: 'center' });
    doc.setFontSize(9); doc.setTextColor(...COLORS.slate700); doc.text(report.unit, 47, y + 26, { align: 'center' });
    doc.setFontSize(8); doc.setTextColor(...accent); doc.text(classification, 47, y + 32, { align: 'center' });
    doc.setFillColor(...accent); doc.circle(47 - doc.getTextWidth(classification[0] || '') / 2 - 2, y + 31.3, 0.7, 'F');
    doc.setTextColor(...COLORS.slate800); doc.setFontSize(9); doc.text('Interpretação', 85, y + 7);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5); doc.setTextColor(...COLORS.slate600); doc.text(explanation, 85, y + 13);
    y += heroHeight + 6;
    if (longInterpretation) y = renderReportTable(y, 'Interpretação clínica', ['Descrição'], [[report.interpretation]]);
    y = this.renderCoughFlowReferenceScales(doc, branding, y, report.referenceScales ?? []);
    report.tables.forEach(table => { y = renderReportTable(y, table.title, table.head, table.body); });
    const notes = [...new Set([...report.notes, ...(result.notes?.trim() ? [result.notes.trim()] : [])])];
    if (report.qualityNotes?.length) y = renderReportTable(y, 'Qualidade da medida', ['Registro técnico'], [...new Set(report.qualityNotes)].map(note => [note]));
    if (notes.length) y = renderReportTable(y, result.testKey === 'PEAK_COUGH_FLOW' ? 'Observações' : 'Observações e qualidade da medida', ['Registro'], notes.map(note => [note]));
    const currentDate = parseNeuromuscularHistoryDate(result.assessmentDate);
    const history = previousResults.filter(item => {
      const date = parseNeuromuscularHistoryDate(item.assessmentDate);
      return item.testKey === result.testKey && item.resultId !== result.resultId && item.status === 'completed' && currentDate != null && date != null && date < currentDate;
    }).sort((a, b) => parseNeuromuscularHistoryDate(b.assessmentDate)! - parseNeuromuscularHistoryDate(a.assessmentDate)!).slice(0, 5);
    if (result.testKey === 'PIMAX') {
      const currentSeries = getPimaxPositionSeries(result.payloadJson as PimaxResultPayload);
      const rows = history.flatMap(item => currentSeries.filter(series => !series.notPerformedReason).flatMap(series => {
        const current = summarizePimaxPayload(series.result).max;
        if (current == null) return [];
        const previous = getPimaxPositionSeries(item.payloadJson as PimaxResultPayload).find(prior => !prior.notPerformedReason && pimaxSeriesAreComparable(series, prior));
        const old = previous ? summarizePimaxPayload(previous.result).max : null;
        const delta = old != null ? current - old : null;
        return [[this.formatDate(item.assessmentDate), getPimaxPositionLabel(series), old != null ? `${this.formatNumber(old)} cmH2O` : 'Sem série compatível', `${this.formatNumber(current)} cmH2O`, delta != null ? `${delta > 0 ? '+' : ''}${this.formatNumber(delta)} cmH2O` : 'Posição ou técnica não comparável']];
      }));
      y = renderReportTable(y, 'Comparação com avaliações anteriores', ['Data anterior', 'Posição', 'Anterior', 'Atual', 'Variação'], rows);
      for (const series of currentSeries) {
        const images = result.assetsJson?.positionChartImages?.[series.id] ?? series.result.chartImages;
        if (!images || !Object.keys(images).length) continue;
        y = renderReportTable(y, 'Curvas registradas', ['Posição'], [[getPimaxPositionLabel(series)]]);
        y = await this.renderRespiratoryMuscleImages(doc, { ...result, payloadJson: series.result, assetsJson: { chartImages: images } }, y, branding);
      }
    } else {
      const rows = buildNeuromuscularHistoryRows(result.testKey, result.payloadJson, history.map(item => ({ id: item.resultId, assessmentDate: item.assessmentDate, payload: item.payloadJson })));
      y = renderReportTable(y, 'Comparação com avaliações anteriores', ['Data anterior', 'Medida', 'Anterior', 'Atual', 'Variação'], rows.map(row => [row.date, row.label, row.previous, row.current, row.comparable ? `${row.delta}${row.relativeDelta ? ` (${row.relativeDelta})` : ''}` : row.note]));
    }
    y = this.renderRespiratoryReference(doc, branding, y, report.reference);
    if (y > doc.internal.pageSize.getHeight() - PDF_BOTTOM_SAFE_MARGIN_MM - 31) {
      doc.addPage(); this.renderContinuationHeader(doc, branding); y = PDF_CONTINUATION_HEADER_BOTTOM_MM + 3;
    }
    this.renderProfessionalSignatureAndVerification(doc, result, branding, y, verification, true);
    for (let page = 1; page <= doc.getNumberOfPages(); page += 1) { doc.setPage(page); this.renderFooter(doc, branding, mark, verification ? 'documento eletrônico autenticado' : 'documento clínico'); }
    return doc;
  }

  static async generatePeakFlowDiaryPDF({ data, branding, patientName, patientBirthDate = null }: {
    data: PeakFlowDiaryExportData; branding: ResolvedClientReportBranding; patientName: string; patientBirthDate?: string | null;
  }): Promise<jsPDF> {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const mark = await this.loadImageAsDataUrl('/logos/revahub-mark.png');
    const { program } = data;
    const entries = data.entries.filter(entry => entry.programId === program.id && !entry.invalidatedAt).sort((a, b) => Date.parse(a.measuredAt) - Date.parse(b.measuredAt));
    const summary = summarizePeakFlowDiary(entries, program);
    const result: AssessmentResultV2 = { resultId: program.id, sessionId: program.id, clientId: program.clientId, patientId: program.patientId,
      testKey: 'PEAK_FLOW', testVersion: 1, assessmentDate: program.createdAt, groupIndex: 1, groupTotal: 1, status: 'completed',
      patientNameSnapshot: patientName, patientBirthDateSnapshot: patientBirthDate, summaryJson: {}, payloadJson: { patientContext: { birthDate: patientBirthDate, ageYears: program.patientAgeYears } }, assetsJson: {}, createdAt: program.createdAt };
    let y = await this.renderHeader(doc, branding);
    y = this.renderPatientClinicalCard(doc, result, y);
    y = this.renderPatientRibbon(doc, result, y, 'Diário domiciliar de pico de fluxo expiratório');
    y = this.renderRespiratoryReportTable(doc, branding, y, 'Período de acompanhamento', ['Período', 'Origem dos dados', 'Variabilidade média'], [[
      `${this.formatDateOnly(program.startsOn)} a ${this.formatDateOnly(program.endsOn)}\n${program.timezone}`,
      entries.length ? 'Informados por paciente ou cuidador' : 'Nenhuma aferição informada',
      summary.averageVariability != null ? `${this.formatNumber(summary.averageVariability)}%\n${summary.pairedDays} dias com pares comparáveis` : 'Sem pares comparáveis',
    ]]);
    y = this.renderRespiratoryReportTable(doc, branding, y, 'Leitura do acompanhamento', ['Contexto'], [[
      `Variabilidade calculada somente com pares de rotina comparáveis. ${summary.pairedDays < 14 ? 'Período com dados parciais para investigação de variabilidade.' : ''} Registros extras e ausências ficam identificados. Este diário deve ser interpretado com os sintomas e a avaliação clínica.`,
    ]]);
    // Vector chart with fixed dimensions and null gaps, independent of the screen size.
    const chart = (title: string, labels: string[], curves: Array<{ values: Array<number | null>; color: RGB; label: string }>) => {
      const height = 55;
      if (y > doc.internal.pageSize.getHeight() - PDF_BOTTOM_SAFE_MARGIN_MM - height - 14) {
        doc.addPage(); this.renderContinuationHeader(doc, branding); y = PDF_CONTINUATION_HEADER_BOTTOM_MM + 3;
      }
      const x = 28, top = y + 10, width = 163, graphHeight = 34;
      doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(...COLORS.slate800); doc.text(title, 15, y + 3);
      const values = curves.flatMap(curve => curve.values.filter((value): value is number => value != null));
      const max = values.length ? Math.max(100, Math.ceil(Math.max(...values) / 100) * 100) : 100;
      doc.setFont('helvetica', 'normal'); doc.setFontSize(7);
      for (let tick = 0; tick <= 2; tick += 1) {
        const coordinate = top + graphHeight - graphHeight * tick / 2;
        doc.setDrawColor(...COLORS.slate200); doc.setLineWidth(0.2); doc.line(x, coordinate, x + width, coordinate);
        doc.setTextColor(...COLORS.slate500); doc.text(String(max * tick / 2), x - 3, coordinate + 1, { align: 'right' });
      }
      doc.text('L/min', 15, top - 2);
      const pointX = (i: number) => x + (labels.length <= 1 ? width / 2 : i * width / (labels.length - 1));
      curves.forEach(curve => {
        doc.setDrawColor(...curve.color); doc.setFillColor(...curve.color); doc.setLineWidth(0.4);
        curve.values.forEach((value, i) => {
          if (value == null) return;
          const pointY = top + graphHeight - value / max * graphHeight;
          const previous = i > 0 ? curve.values[i - 1] : null;
          if (previous != null) doc.line(pointX(i - 1), top + graphHeight - previous / max * graphHeight, pointX(i), pointY);
          doc.circle(pointX(i), pointY, 0.55, 'F');
        });
      });
      const interval = Math.max(1, Math.ceil(labels.length / 7));
      doc.setTextColor(...COLORS.slate500);
      labels.forEach((label, i) => { if (i % interval === 0 || i === labels.length - 1) doc.text(label, pointX(i), top + graphHeight + 4, { align: 'center' }); });
      curves.forEach((curve, i) => { doc.setTextColor(...curve.color); doc.text(curve.label, 34 + i * 65, top + graphHeight + 9); });
      y += height + 7;
    };
    if (entries.length) {
      chart('Evolução das aferições de rotina', summary.days.map(day => day.date.slice(5).split('-').reverse().join('/')), [
        { label: 'Manhã', color: [37, 99, 235], values: summary.days.map(day => day.morning) },
        { label: 'Noite', color: [124, 58, 237], values: summary.days.map(day => day.evening) },
      ]);
      const hourly = peakFlowDiaryHourlyProfile(entries, program.timezone);
      chart('Perfil por hora - média descritiva de todas as aferições', hourly.map(item => item.hour), [{ label: 'Inclui registros extras; contexto variável', color: [15, 118, 110], values: hourly.map(item => item.average) }]);
    }
    y = this.renderRespiratoryReportTable(doc, branding, y, 'Resumo diário', ['Data', 'Manhã (L/min)', 'Noite (L/min)', 'Variabilidade', 'Extras / qualidade'], entries.length ? summary.days.map(day => [
      this.formatDateOnly(day.date), day.morning == null ? 'Sem medida' : this.formatNumber(day.morning, 0), day.evening == null ? 'Sem medida' : this.formatNumber(day.evening, 0),
      day.variabilityPercent == null ? 'Não calculada' : `${this.formatNumber(day.variabilityPercent)}%`, [day.extraCount ? `${day.extraCount} extra(s)` : '', day.reason || ''].filter(Boolean).join('; '),
    ]) : []);
    const dateTime = (value: string) => new Date(value).toLocaleString('pt-BR', { timeZone: program.timezone, dateStyle: 'short', timeStyle: 'short' });
    y = this.renderRespiratoryReportTable(doc, branding, y, 'Aferições informadas', ['Data / momento', 'Tentativas / maior', 'Contexto e observações'], entries.map(entry => [
      `${dateTime(entry.measuredAt)}\n${diaryPeriodLabel(entry.period)}`,
      `${entry.readings.join(' / ')} L/min\nMaior: ${entry.best} L/min`,
      [entry.author === 'caregiver' ? 'Cuidador' : 'Paciente', diaryMedicationLabel(entry.medicationContext), entry.medicationName,
        entry.medicationAt ? `Medicação: ${dateTime(entry.medicationAt)}` : null, entry.device,
        ...PEAK_FLOW_DIARY_SYMPTOMS.filter(item => entry.symptoms.includes(item.key)).map(item => item.label),
        ...PEAK_FLOW_DIARY_DANGER_SIGNS.filter(item => entry.dangerSigns.includes(item.key)).map(item => item.label), entry.notes,
        `Enviado em ${dateTime(entry.submittedAt)}`].filter(Boolean).join('; '),
    ]));
    if (program.actionPlan) y = this.renderRespiratoryReportTable(doc, branding, y, 'Plano de ação individual', ['Orientações registradas pelo profissional'], [[program.actionPlan]]);
    this.renderRespiratoryReference(doc, branding, y, 'GINA, 2026. Registros domiciliares autorreferidos; o diário não representa monitoramento contínuo de urgência.');
    for (let page = 1; page <= doc.getNumberOfPages(); page += 1) { doc.setPage(page); this.renderFooter(doc, branding, mark, 'registros domiciliares autorreferidos'); }
    return doc;
  }

  static async generatePeakFlowPDF({ result, branding, previousResults = [], verification = null }: AssessmentPDFInput): Promise<jsPDF> {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });
    const pageHeight = doc.internal.pageSize.getHeight();
    const revahubMarkBase64 = await this.loadImageAsDataUrl('/logos/revahub-mark.png');

    let y = await this.renderHeader(doc, branding);
    y = this.renderPatientRibbon(doc, result, y);
    y = this.renderPatientClinicalCard(doc, result, y);
    y = this.renderPeakFlowScoreCard(doc, result, y);
    y = this.renderPeakFlowProtocolTable(doc, result, y) + 4;
    y = this.renderPeakFlowInterpretationBlock(doc, result, y);
    y = this.renderPeakFlowHistoryTable(doc, result, previousResults, y) + 4;

    if (y > pageHeight - PDF_FOOTER_HEIGHT_MM - 31) {
      doc.addPage();
      this.renderContinuationHeader(doc, branding);
      y = PDF_CONTINUATION_HEADER_BOTTOM_MM + 3;
    }

    this.renderProfessionalSignatureAndVerification(doc, result, branding, y, verification, true);

    const pageCount = doc.getNumberOfPages();
    for (let page = 1; page <= pageCount; page += 1) {
      doc.setPage(page);
      this.renderFooter(doc, branding, revahubMarkBase64);
    }

    return doc;
  }

  static async generateACTPDF({ result, branding, previousResults = [], verification = null }: AssessmentPDFInput): Promise<jsPDF> {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });
    const pageHeight = doc.internal.pageSize.getHeight();
    const revahubMarkBase64 = await this.loadImageAsDataUrl('/logos/revahub-mark.png');

    let y = await this.renderHeader(doc, branding);
    y = this.renderPatientRibbon(doc, result, y);
    y = this.renderPatientClinicalCard(doc, result, y);
    y = this.renderACTScoreCard(doc, result, y);
    y = this.renderACTTable(doc, result, y) + 4;
    y = this.renderNotesCard(doc, result.notes, y);
    y = this.renderACTHistoryTable(doc, previousResults, y) + 4;

    if (y > pageHeight - PDF_FOOTER_HEIGHT_MM - 31) {
      doc.addPage();
      y = 20;
    }

    this.renderProfessionalSignatureAndVerification(doc, result, branding, y, verification, true);

    const pageCount = doc.getNumberOfPages();
    for (let page = 1; page <= pageCount; page += 1) {
      doc.setPage(page);
      this.renderFooter(doc, branding, revahubMarkBase64);
    }

    return doc;
  }

  static async generateGaitSpeedPDF({ result, branding, verification = null }: AssessmentPDFInput): Promise<jsPDF> {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });
    const pageHeight = doc.internal.pageSize.getHeight();
    const revahubMarkBase64 = await this.loadImageAsDataUrl('/logos/revahub-mark.png');

    let y = await this.renderHeader(doc, branding);
    y = this.renderPatientRibbon(doc, result, y);
    y = this.renderPatientClinicalCard(doc, result, y);
    y = this.renderGaitSpeedScoreCard(doc, result, y);
    y = this.renderGaitSpeedProtocolTable(doc, result, y) + 4;
    y = this.renderGaitSpeedInterpretationBlock(doc, result, y);
    y = this.renderNotesCard(doc, result.notes, y);

    if (y > pageHeight - PDF_FOOTER_HEIGHT_MM - 31) {
      doc.addPage();
      y = 20;
    }

    this.renderProfessionalSignatureAndVerification(doc, result, branding, y, verification, true);

    const pageCount = doc.getNumberOfPages();
    for (let page = 1; page <= pageCount; page += 1) {
      doc.setPage(page);
      this.renderFooter(doc, branding, revahubMarkBase64);
    }

    return doc;
  }

  static async generateAQLQPDF({ result, branding, previousResults = [], verification = null }: AssessmentPDFInput): Promise<jsPDF> {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });
    const pageHeight = doc.internal.pageSize.getHeight();
    const revahubMarkBase64 = await this.loadImageAsDataUrl('/logos/revahub-mark.png');

    let y = await this.renderHeader(doc, branding);
    y = this.renderPatientRibbon(doc, result, y);
    y = this.renderPatientClinicalCard(doc, result, y);
    y = this.renderAQLQScoreCard(doc, result, y);
    y = this.renderAQLQDomainTable(doc, result, y) + 4;
    y = this.renderNotesCard(doc, result.notes, y);
    y = this.renderAQLQHistoryTable(doc, previousResults, y) + 4;

    if (y > pageHeight - PDF_FOOTER_HEIGHT_MM - 31) {
      doc.addPage();
      y = 20;
    }

    this.renderProfessionalSignatureAndVerification(doc, result, branding, y, verification, true);

    const pageCount = doc.getNumberOfPages();
    for (let page = 1; page <= pageCount; page += 1) {
      doc.setPage(page);
      this.renderFooter(doc, branding, revahubMarkBase64);
    }

    return doc;
  }

  static async generateCATPDF({ result, branding, previousResults = [], verification = null }: AssessmentPDFInput): Promise<jsPDF> {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });
    const pageHeight = doc.internal.pageSize.getHeight();
    const gradientBarDataUrl = await this.createGradientBarDataUrl();
    const revahubMarkBase64 = await this.loadImageAsDataUrl('/logos/revahub-mark.png');

    let y = await this.renderHeader(doc, branding);
    y = this.renderPatientRibbon(doc, result, y);
    y = this.renderPatientClinicalCard(doc, result, y);
    y = this.renderScoreCard(doc, result, y, gradientBarDataUrl);
    y = this.renderResultTable(doc, result, y) + 4;
    y = this.renderNotesCard(doc, result.notes, y);
    y = this.renderHistoryTable(doc, previousResults, y) + 4;

    if (y > pageHeight - PDF_FOOTER_HEIGHT_MM - 31) {
      doc.addPage();
      y = 20;
    }

    this.renderProfessionalSignatureAndVerification(doc, result, branding, y, verification, true);

    const pageCount = doc.getNumberOfPages();
    for (let page = 1; page <= pageCount; page += 1) {
      doc.setPage(page);
      this.renderFooter(doc, branding, revahubMarkBase64);
    }

    return doc;
  }

  static async generateMMRCPDF({ result, branding, previousResults = [], verification = null }: AssessmentPDFInput): Promise<jsPDF> {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });
    const pageHeight = doc.internal.pageSize.getHeight();
    const gradientBarDataUrl = await this.createGradientBarDataUrl();
    const revahubMarkBase64 = await this.loadImageAsDataUrl('/logos/revahub-mark.png');

    let y = await this.renderHeader(doc, branding);
    y = this.renderPatientRibbon(doc, result, y);
    y = this.renderPatientClinicalCard(doc, result, y);
    y = this.renderMMRCScoreCard(doc, result, y, gradientBarDataUrl);
    y = this.renderNotesCard(doc, result.notes, y);
    y = this.renderMMRCHistoryTable(doc, previousResults, y) + 4;

    if (y > pageHeight - PDF_FOOTER_HEIGHT_MM - 31) {
      doc.addPage();
      y = 20;
    }

    this.renderProfessionalSignatureAndVerification(doc, result, branding, y, verification, true);

    const pageCount = doc.getNumberOfPages();
    for (let page = 1; page <= pageCount; page += 1) {
      doc.setPage(page);
      this.renderFooter(doc, branding, revahubMarkBase64);
    }

    return doc;
  }

  static async generateHADSPDF({ result, branding, previousResults = [], verification = null }: AssessmentPDFInput): Promise<jsPDF> {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });
    const pageHeight = doc.internal.pageSize.getHeight();
    const gradientBarDataUrl = await this.createGradientBarDataUrl();
    const revahubMarkBase64 = await this.loadImageAsDataUrl('/logos/revahub-mark.png');

    let y = await this.renderHeader(doc, branding);
    y = this.renderPatientRibbon(doc, result, y);
    y = this.renderPatientClinicalCard(doc, result, y);
    y = this.renderHADSScoreCards(doc, result, y, gradientBarDataUrl);
    y = this.renderHADSInterpretationCard(doc, result, y);
    y = this.renderNotesCard(doc, result.notes, y);
    y = this.renderHADSHistoryTable(doc, previousResults, y) + 4;

    if (y > pageHeight - PDF_FOOTER_HEIGHT_MM - 31) {
      doc.addPage();
      y = 20;
    }

    this.renderProfessionalSignatureAndVerification(doc, result, branding, y, verification, true);

    const pageCount = doc.getNumberOfPages();
    for (let page = 1; page <= pageCount; page += 1) {
      doc.setPage(page);
      this.renderFooter(doc, branding, revahubMarkBase64);
    }

    return doc;
  }

  static async generatePSQIPDF({ result, branding, previousResults = [], verification = null }: AssessmentPDFInput): Promise<jsPDF> {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });
    const pageHeight = doc.internal.pageSize.getHeight();
    const revahubMarkBase64 = await this.loadImageAsDataUrl('/logos/revahub-mark.png');

    let y = await this.renderHeader(doc, branding);
    y = this.renderPatientRibbon(doc, result, y);
    y = this.renderPatientClinicalCard(doc, result, y);
    y = this.renderPSQIScoreCard(doc, result, y);
    y = this.renderPSQIComponentsTable(doc, result, y) + 4;
    y = this.renderNotesCard(doc, result.notes, y);
    y = this.renderPSQIHistoryTable(doc, previousResults, y) + 4;

    if (y > pageHeight - PDF_FOOTER_HEIGHT_MM - 31) {
      doc.addPage();
      y = 20;
    }

    this.renderProfessionalSignatureAndVerification(doc, result, branding, y, verification, true);

    const pageCount = doc.getNumberOfPages();
    for (let page = 1; page <= pageCount; page += 1) {
      doc.setPage(page);
      this.renderFooter(doc, branding, revahubMarkBase64);
    }

    return doc;
  }

  static async generateLCADLPDF({ result, branding, previousResults = [], verification = null }: AssessmentPDFInput): Promise<jsPDF> {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });
    const pageHeight = doc.internal.pageSize.getHeight();
    const gradientBarDataUrl = await this.createGradientBarDataUrl();
    const revahubMarkBase64 = await this.loadImageAsDataUrl('/logos/revahub-mark.png');

    let y = await this.renderHeader(doc, branding);
    y = this.renderPatientRibbon(doc, result, y);
    y = this.renderPatientClinicalCard(doc, result, y);
    y = this.renderLCADLScoreCard(doc, result, y, gradientBarDataUrl);
    y = this.renderLCADLDomainTable(doc, result, y) + 4;
    y = this.renderNotesCard(doc, result.notes, y);
    y = this.renderLCADLHistoryTable(doc, previousResults, y) + 4;

    if (y > pageHeight - PDF_FOOTER_HEIGHT_MM - 31) {
      doc.addPage();
      y = 20;
    }

    this.renderProfessionalSignatureAndVerification(doc, result, branding, y, verification, true);

    const pageCount = doc.getNumberOfPages();
    for (let page = 1; page <= pageCount; page += 1) {
      doc.setPage(page);
      this.renderFooter(doc, branding, revahubMarkBase64);
    }

    return doc;
  }

  static async generateMLHFQPDF({ result, branding, previousResults = [], verification = null }: AssessmentPDFInput): Promise<jsPDF> {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });
    const pageHeight = doc.internal.pageSize.getHeight();
    const revahubMarkBase64 = await this.loadImageAsDataUrl('/logos/revahub-mark.png');

    let y = await this.renderHeader(doc, branding);
    y = this.renderPatientRibbon(doc, result, y);
    y = this.renderPatientClinicalCard(doc, result, y);
    y = this.renderMLHFQScoreCard(doc, result, y);
    y = this.renderNotesCard(doc, result.notes, y);
    y = this.renderMLHFQHistoryTable(doc, previousResults, y) + 4;

    if (y > pageHeight - PDF_FOOTER_HEIGHT_MM - 31) {
      doc.addPage();
      y = 20;
    }

    this.renderProfessionalSignatureAndVerification(doc, result, branding, y, verification, true);

    const pageCount = doc.getNumberOfPages();
    for (let page = 1; page <= pageCount; page += 1) {
      doc.setPage(page);
      this.renderFooter(doc, branding, revahubMarkBase64);
    }

    return doc;
  }

  static async generatePFSDQPDF({ result, branding, previousResults = [], verification = null }: AssessmentPDFInput): Promise<jsPDF> {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });
    const pageHeight = doc.internal.pageSize.getHeight();
    const revahubMarkBase64 = await this.loadImageAsDataUrl('/logos/revahub-mark.png');

    let y = await this.renderHeader(doc, branding);
    y = this.renderPatientRibbon(doc, result, y);
    y = this.renderPatientClinicalCard(doc, result, y);
    y = this.renderPFSDQScoreCard(doc, result, y);
    y = this.renderNotesCard(doc, result.notes, y);
    y = this.renderPFSDQHistoryTable(doc, previousResults, y) + 4;

    if (y > pageHeight - PDF_FOOTER_HEIGHT_MM - 31) {
      doc.addPage();
      y = 20;
    }

    this.renderProfessionalSignatureAndVerification(doc, result, branding, y, verification, true);

    const pageCount = doc.getNumberOfPages();
    for (let page = 1; page <= pageCount; page += 1) {
      doc.setPage(page);
      this.renderFooter(doc, branding, revahubMarkBase64);
    }

    return doc;
  }

  static async generateSAQPDF({ result, branding, previousResults = [], verification = null }: AssessmentPDFInput): Promise<jsPDF> {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });
    const pageHeight = doc.internal.pageSize.getHeight();
    const revahubMarkBase64 = await this.loadImageAsDataUrl('/logos/revahub-mark.png');

    let y = await this.renderHeader(doc, branding);
    y = this.renderPatientRibbon(doc, result, y);
    y = this.renderPatientClinicalCard(doc, result, y);
    y = this.renderSAQScoreCard(doc, result, y);
    y = this.renderSAQDomainTable(doc, result, y) + 4;
    y = this.renderNotesCard(doc, result.notes, y);
    y = this.renderSAQHistoryTable(doc, previousResults, y) + 4;

    if (y > pageHeight - PDF_FOOTER_HEIGHT_MM - 31) {
      doc.addPage();
      y = 20;
    }

    this.renderProfessionalSignatureAndVerification(doc, result, branding, y, verification, true);

    const pageCount = doc.getNumberOfPages();
    for (let page = 1; page <= pageCount; page += 1) {
      doc.setPage(page);
      this.renderFooter(doc, branding, revahubMarkBase64);
    }

    return doc;
  }

  static async generateSGRQPDF({ result, branding, previousResults = [], verification = null }: AssessmentPDFInput): Promise<jsPDF> {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });
    const pageHeight = doc.internal.pageSize.getHeight();
    const revahubMarkBase64 = await this.loadImageAsDataUrl('/logos/revahub-mark.png');

    let y = await this.renderHeader(doc, branding);
    y = this.renderPatientRibbon(doc, result, y);
    y = this.renderPatientClinicalCard(doc, result, y);
    y = this.renderSGRQScoreCard(doc, result, y);
    y = this.renderSGRQDomainTable(doc, result, y) + 4;
    y = this.renderNotesCard(doc, result.notes, y);
    y = this.renderSGRQHistoryTable(doc, previousResults, y) + 4;

    if (y > pageHeight - PDF_FOOTER_HEIGHT_MM - 31) {
      doc.addPage();
      y = 20;
    }

    this.renderProfessionalSignatureAndVerification(doc, result, branding, y, verification, true);

    const pageCount = doc.getNumberOfPages();
    for (let page = 1; page <= pageCount; page += 1) {
      doc.setPage(page);
      this.renderFooter(doc, branding, revahubMarkBase64);
    }

    return doc;
  }

  private static getAssessmentReportPayload(result: AssessmentResultV2): AssessmentReportPayloadV2 {
    const payload = normalizeAssessmentReportPayload(result.payloadJson);
    if (!payload) {
      throw new Error(
        'Não foi possível gerar o relatório de avaliações: o conteúdo não corresponde ao contrato ASSESSMENT_REPORT v2.',
      );
    }

    const validation = validateAssessmentReportPayload(payload);
    if (!validation.valid) {
      const historyRequiresReview = validation.errors.some(
        (issue) => issue.code === 'history_snapshot_review_required',
      );
      if (historyRequiresReview) {
        const signed = Boolean(getAssessmentSignatureMetadata(result.assetsJson));
        throw new Error(
          signed
            ? 'Não foi possível gerar o pacote: este relatório assinado possui uma seleção histórica salva sem snapshot verificável. Gere uma nova versão do relatório e revise o histórico.'
            : 'Não foi possível gerar o pacote: a seleção histórica precisa ser revisada. Abra o editor e selecione novamente os resultados históricos antes de exportar.',
        );
      }

      const details = validation.errors
        .slice(0, 4)
        .map((issue) => issue.message)
        .join(' ');
      throw new Error(
        `Não foi possível gerar o relatório de avaliações: revise o conteúdo antes de exportar. ${details}`,
      );
    }

    if (payload.patient.patientId !== result.patientId) {
      throw new Error(
        'Não foi possível gerar o relatório de avaliações: o paciente registrado no conteúdo difere do paciente do documento.',
      );
    }

    const nestedReportSource = payload.sources.find((source) =>
      isAssessmentV2ReportKey(source.snapshot.testKey),
    );
    if (nestedReportSource) {
      throw new Error(
        `Não foi possível gerar o relatório de avaliações: relatórios não podem ser usados como fonte ("${nestedReportSource.snapshot.testLabel}").`,
      );
    }

    return payload;
  }

  private static formatAssessmentReportPrimaryMeasure(source: AssessmentReportSourceSnapshotV2): string {
    const measure = source.snapshot.primaryMeasure;
    const formattedValue = measure.formattedValue.trim();
    const unit = measure.unit?.trim() || '';
    const value =
      unit && !formattedValue.toLocaleLowerCase('pt-BR').includes(unit.toLocaleLowerCase('pt-BR'))
        ? `${formattedValue} ${unit}`
        : formattedValue;
    return `${measure.label}: ${value}`;
  }

  static async generateAssessmentReportPDF({
    result,
    branding,
    verification = null,
  }: AssessmentPDFInput): Promise<jsPDF> {
    // Presentation cleanup never changes the stored payload or signed content.
    const payload = getAssessmentReportPresentation(this.getAssessmentReportPayload(result));
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    const contentWidth = pageWidth - margin * 2;
    const bottomLimit = pageHeight - PDF_BOTTOM_SAFE_MARGIN_MM;
    const revahubMarkBase64 = await this.loadImageAsDataUrl('/logos/revahub-mark.png');
    const modeLabel = getAssessmentReportModeLabel(payload.mode);
    const period = getAssessmentReportPeriod(payload.sources);
    const sourceLabelById = new Map(
      payload.sources.map((source) => [source.resultId, `${source.snapshot.testLabel} (${this.formatDateOnly(source.snapshot.assessmentDate)})`]),
    );
    const pageContext = this.getPDFDocumentPageContext(doc);
    const audienceBaseLabel =
      payload.audience.type === 'other' && payload.audience.customLabel
        ? payload.audience.customLabel
        : getAssessmentReportAudienceLabel(payload.audience.type);
    const recipientDetails = [
      payload.audience.recipientName,
      payload.audience.recipientRole,
      payload.audience.organization,
    ].filter((item): item is string => Boolean(item?.trim()));
    const audienceLabel = recipientDetails.length
      ? `${audienceBaseLabel} - ${recipientDetails.join(' - ')}`
      : audienceBaseLabel;
    const purposeLabel = getAssessmentReportPurposeText(payload.purpose);

    let y = await this.renderHeader(doc, branding);
    y = this.renderPatientRibbon(doc, result, y, payload.title);
    y = this.renderPatientClinicalCard(doc, result, y);
    pageContext.testLabel = payload.title || modeLabel;
    y = this.renderAssessmentReportMetadataCard(doc, y, [
      {
        label: 'Finalidade',
        value: purposeLabel,
        fullWidth: true,
      },
      ...(period ? [{ label: 'Período avaliado', value: period.label }] : []),
      {
        label: 'Destinatário',
        value: audienceLabel,
      },
    ]);

    const addContinuationPage = (): void => {
      doc.addPage();
      this.renderContinuationHeader(doc, branding);
      y = 20;
    };

    const ensureSpace = (height: number): void => {
      if (y + height <= bottomLimit) return;
      addContinuationPage();
    };

    const renderSectionTitle = (title: string): void => {
      ensureSpace(11);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(...COLORS.brandBlue);
      doc.text(title, margin, y);
      y += 6;
    };

    const renderParagraph = (text?: string | null, options?: { bold?: boolean }): void => {
      const normalized = String(text || '').trim();
      if (!normalized) return;

      doc.setFont('helvetica', options?.bold ? 'bold' : 'normal');
      doc.setFontSize(9.2);
      doc.setTextColor(...COLORS.slate700);
      const lines = doc.splitTextToSize(normalized, contentWidth) as string[];
      lines.forEach((line) => {
        ensureSpace(5);
        doc.text(line, margin, y);
        y += 4.5;
      });
      y += 2;
    };

    const renderTextSection = (
      title: string,
      paragraphs: Array<string | null | undefined>,
    ): void => {
      const content = paragraphs.map((item) => String(item || '').trim()).filter(Boolean);
      if (!content.length) return;
      renderSectionTitle(title);
      content.forEach((paragraph) => renderParagraph(paragraph));
      y += 1;
    };

    const renderListSection = (title: string, items: readonly string[]): void => {
      const normalizedItems = items.map((item) => item.trim()).filter(Boolean);
      if (!normalizedItems.length) return;
      renderSectionTitle(title);
      normalizedItems.forEach((item) => renderParagraph(`- ${item}`));
      y += 1;
    };

    renderTextSection('Contexto do relatório', [
      payload.purpose.type === 'other' ? null : payload.purpose.description,
      payload.purpose.referralQuestion,
    ]);

    for (const source of payload.sources.filter((item) => item.snapshot.clinicalAssessment)) {
      renderSectionTitle(`História, exame e plano - ${this.formatDateOnly(source.snapshot.assessmentDate)}`);
      y = this.renderClinicalAssessmentContent(doc, source.snapshot.clinicalAssessment, y);
    }

    if (payload.documentModel === 'longitudinal') {
      const series = buildAssessmentLongitudinalSeries(payload.sources);
      for (const item of series) {
        const seriesTitle = item.testLabel.trim().toLocaleLowerCase() === item.measureLabel.trim().toLocaleLowerCase()
          ? item.measureLabel : `${item.testLabel} - ${item.measureLabel}`;
        // Reserve the series title, condition, table header and first data row.
        ensureSpace(item.seriesLabel ? 42 : 35);
        renderSectionTitle(seriesTitle);
        if (item.seriesLabel) renderParagraph(item.seriesLabel);
        const hasReference = item.points.some((point) => point.referenceValue != null);
        const hasPercent = item.points.some((point) => point.percentPredicted != null);
        this.runAutoTable(doc, {
          startY: y, margin: { left: margin, right: margin },
          head: [['Data', `Valor (${item.unit})`, ...(hasReference ? ['Referência registrada'] : []), ...(hasPercent ? ['% do previsto'] : [])]],
          body: item.points.map((point) => [this.formatDateOnly(point.assessmentDate), formatLongitudinalNumber(point.value),
            ...(hasReference ? [point.referenceValue != null ? `${formatLongitudinalNumber(point.referenceValue)} ${point.unit}${point.referenceLabel ? `\n${point.referenceLabel}` : ''}` : 'Não registrada nesta data'] : []),
            ...(hasPercent ? [point.percentPredicted != null ? `${formatLongitudinalNumber(point.percentPredicted, 1)}%` : 'Não disponível'] : []),
          ]),
          theme: 'grid', styles: { fontSize: 8, cellPadding: 2.4, textColor: COLORS.slate700 },
          headStyles: { fillColor: COLORS.brandBlue, textColor: COLORS.white, fontStyle: 'bold' },
          alternateRowStyles: { fillColor: COLORS.slate50 },
        });
        y = ((doc as unknown as { lastAutoTable?: { finalY?: number } }).lastAutoTable?.finalY ?? y) + 5;
        if (item.comparisonNote) renderParagraph(item.comparisonNote);
        if (item.delta != null) renderParagraph(`Variação entre o primeiro e o último registro: ${item.delta > 0 ? '+' : ''}${formatLongitudinalNumber(item.delta)} ${item.unit}${item.deltaPercent != null ? ` (${item.deltaPercent > 0 ? '+' : ''}${formatLongitudinalNumber(item.deltaPercent, 1)}%)` : ''}.`);
        if (payload.exportOptions.longitudinalCharts !== false && item.points.length > 1) {
          const previousPage = doc.getNumberOfPages();
          ensureSpace(79);
          if (doc.getNumberOfPages() !== previousPage) {
            renderSectionTitle(seriesTitle);
            if (item.seriesLabel) renderParagraph(item.seriesLabel);
          }
          y = this.renderLongitudinalChart(doc, item, y);
        }
      }
    }

    const displayedSources = payload.sources.filter((source) => !source.snapshot.clinicalAssessment
      && (payload.documentModel !== 'longitudinal' || !source.snapshot.longitudinalMeasures?.length));
    if (displayedSources.length) {
      ensureSpace(24);
      renderSectionTitle(payload.documentModel === 'longitudinal' ? 'Resultados complementares' : 'Testes e medidas');
      const hasClassification = displayedSources.some((source) =>
        source.finding.status === 'within_expected'
        || source.finding.status === 'borderline'
        || source.finding.status === 'altered');
      const hasInterpretation = displayedSources.some((source) => source.finding.conciseSummary?.trim());
      this.runAutoTable(doc, {
        startY: y,
        margin: { left: margin, right: margin },
        head: [['Avaliação', 'Data', 'Resultado',
          ...(hasClassification ? ['Classificação'] : []), ...(hasInterpretation ? ['Interpretação'] : [])]],
        body: displayedSources.map((source) => [
          source.snapshot.testLabel,
          this.formatDateOnly(source.snapshot.assessmentDate),
          this.formatAssessmentReportPrimaryMeasure(source),
          ...(hasClassification ? [
            source.finding.status === 'within_expected'
              || source.finding.status === 'borderline'
              || source.finding.status === 'altered'
              ? getAssessmentReportFindingStatusLabel(source.finding.status)
              : '',
          ] : []),
          ...(hasInterpretation ? [source.finding.conciseSummary || ''] : []),
        ]),
        theme: 'grid',
        styles: {
          fontSize: 7.3,
          cellPadding: 2,
          lineColor: COLORS.slate200,
          lineWidth: 0.1,
          textColor: COLORS.slate700,
          valign: 'top',
          overflow: 'linebreak',
        },
        headStyles: {
          fillColor: COLORS.brandBlue,
          textColor: COLORS.white,
          fontSize: 7.1,
          fontStyle: 'bold',
        },
        alternateRowStyles: { fillColor: COLORS.slate50 },
        columnStyles: {
          0: { cellWidth: 31, fontStyle: 'bold' },
          1: { cellWidth: 20 },
          2: { cellWidth: hasClassification || hasInterpretation ? 29 : 'auto' },
        },
      });
      y = ((doc as unknown as { lastAutoTable?: { finalY?: number } }).lastAutoTable?.finalY ?? y) + 8;
    }

    if (payload.mode === 'assessment_summary') {
      renderTextSection('Síntese profissional', [payload.content.professionalOverview]);
      renderTextSection('Plano e reavaliação', [payload.content.followUpGuidance]);
      renderListSection('Observações do profissional', payload.content.observations);
    } else {
      renderTextSection('Contexto clínico-funcional', [payload.content.clinicalContext]);
      renderTextSection('Análise integrada', [payload.content.integratedAnalysis]);
      renderTextSection('Conclusão fisioterapêutica', [payload.content.physiotherapyConclusion]);
      renderListSection('Plano e reavaliação', payload.content.recommendations);
      renderTextSection('Observações do profissional', [payload.content.limitations]);
    }

    if (payload.cbdf.entries.length > 0 || payload.cbdf.professionalNote) {
      // Keep the section title with the table header and at least its first row.
      // Otherwise autoTable may leave an orphan header at the bottom of a page.
      ensureSpace(payload.cbdf.entries.length > 0 ? 40 : 11);
      renderSectionTitle(`Diagnóstico fisioterapêutico - CBDF (${payload.cbdf.classificationVersion})`);
      renderParagraph(payload.cbdf.professionalNote);
      if (payload.cbdf.entries.length > 0) {
        this.runAutoTable(doc, {
          startY: y,
          margin: { left: margin, right: margin },
          head: [['Código', 'Capítulo-base', 'Descrição profissional', 'Fundamentação e autoria']],
          body: payload.cbdf.entries.map((entry) => {
            const provenance = [
              entry.rationale,
              `Evidências: ${entry.evidenceResultIds
                .map((resultId) => sourceLabelById.get(resultId) || resultId)
                .join(', ')}.`,
              `Registrado por ${entry.recordedBy.name || 'profissional não identificado'} em ${this.formatDate(entry.recordedAt)}.`,
            ].filter(Boolean).join('\n');
            return [
              entry.manualCode || entry.chapterCode,
              getCbdfChapterLabel(entry.chapterCode),
              entry.professionalStatement,
              provenance,
            ];
          }),
          theme: 'grid',
          styles: {
            fontSize: 7.2,
            cellPadding: 2.2,
            lineColor: COLORS.slate200,
            lineWidth: 0.1,
            textColor: COLORS.slate700,
            valign: 'top',
          },
          headStyles: {
            fillColor: COLORS.slate700,
            textColor: COLORS.white,
            fontStyle: 'bold',
          },
          columnStyles: {
            0: { cellWidth: 27, fontStyle: 'bold' },
            1: { cellWidth: 48 },
            2: { cellWidth: 59 },
            3: { cellWidth: 46 },
          },
        });
        y = ((doc as unknown as { lastAutoTable?: { finalY?: number } }).lastAutoTable?.finalY ?? y) + 8;
      }
    }

    const icfEntries = [
      ...payload.icf.domains.bodyFunctions,
      ...payload.icf.domains.bodyStructures,
      ...payload.icf.domains.activitiesAndParticipation,
      ...payload.icf.domains.environmentalFactors,
    ];
    if (icfEntries.length > 0 || payload.icf.professionalSynthesis) {
      // Keep the section introduction together with the first classification row.
      ensureSpace(icfEntries.length > 0 ? 40 : 11);
      renderSectionTitle(
        `Funcionalidade - CIF (${payload.icf.classificationVersion}, ${payload.icf.language})`,
      );
      renderParagraph(payload.icf.professionalSynthesis);
      if (icfEntries.length > 0) {
        this.runAutoTable(doc, {
          startY: y,
          margin: { left: margin, right: margin },
          head: [['Domínio', 'Código', 'Contexto e qualificador', 'Descrição profissional', 'Observação e autoria']],
          body: icfEntries.map((entry) => {
            const provenance = [
              entry.note,
              `Evidências: ${entry.evidenceResultIds
                .map((resultId) => sourceLabelById.get(resultId) || resultId)
                .join(', ')}.`,
              `Registrado por ${entry.recordedBy.name || 'profissional não identificado'} em ${this.formatDate(entry.recordedAt)}.`,
            ].filter(Boolean).join('\n');
            return [
              getIcfDomainByCode(entry.code)?.label || entry.domain,
              formatIcfQualifiedCode(
                entry.code,
                entry.qualifier,
                entry.capacityQualifier,
              ),
              entry.domain === 'activities_and_participation'
                ? [
                    `Desempenho: ${getIcfQualifierLabel(entry.qualifier, entry.domain)}`,
                    entry.capacityQualifier !== null
                      ? `Capacidade: ${getIcfQualifierLabel(entry.capacityQualifier, entry.domain)}`
                      : null,
                  ].filter(Boolean).join('\n')
                : [
                    getIcfQualifierContextLabel(entry.qualifierContext),
                    getIcfQualifierLabel(entry.qualifier, entry.domain),
                  ].join('\n'),
              entry.professionalDescription,
              provenance,
            ];
          }),
          theme: 'grid',
          styles: {
            fontSize: 7.1,
            cellPadding: 2.1,
            lineColor: COLORS.slate200,
            lineWidth: 0.1,
            textColor: COLORS.slate700,
            valign: 'top',
          },
          headStyles: {
            fillColor: COLORS.slate700,
            textColor: COLORS.white,
            fontStyle: 'bold',
          },
          columnStyles: {
            0: { cellWidth: 33 },
            1: { cellWidth: 20, fontStyle: 'bold' },
            2: { cellWidth: 38 },
            3: { cellWidth: 49 },
            4: { cellWidth: 40 },
          },
        });
        y = ((doc as unknown as { lastAutoTable?: { finalY?: number } }).lastAutoTable?.finalY ?? y) + 8;
      }
    }

    ensureSpace(30);
    this.renderProfessionalSignatureAndVerification(doc, result, branding, y, verification, true);

    const pageCount = doc.getNumberOfPages();
    for (let page = 1; page <= pageCount; page += 1) {
      doc.setPage(page);
      this.renderFooter(doc, branding, revahubMarkBase64);
    }

    return doc;
  }

  static async generateCompositeReportPDF({ result, branding }: AssessmentPDFInput): Promise<jsPDF> {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 18;
    const contentWidth = pageWidth - margin * 2;
    const payload = result.payloadJson && typeof result.payloadJson === 'object' ? result.payloadJson : {};
    const sourceResults = Array.isArray(payload.sourceResults) ? payload.sourceResults : [];
    const getPriorityRank = (priority?: string) => {
      switch (priority) {
        case 'alta':
          return 3;
        case 'moderada':
          return 2;
        default:
          return 1;
      }
    };
    const getPriorityLabel = (priority?: string) => {
      switch (priority) {
        case 'alta':
          return 'Alta relevância';
        case 'moderada':
          return 'Relevância moderada';
        default:
          return 'Contextual';
      }
    };
    const sortedSourceResults = [...sourceResults].sort((a, b) => getPriorityRank(b?.priority) - getPriorityRank(a?.priority));
    const coveredDomains = [...new Set(sortedSourceResults.map((source) => String(source?.domain || '').trim()).filter(Boolean))];
    const highPriorityCount = sortedSourceResults.filter((source) => source?.priority === 'alta').length;
    const assessmentDates = sortedSourceResults
      .map((source) => (source?.assessmentDate ? new Date(source.assessmentDate) : null))
      .filter((value): value is Date => value instanceof Date && !Number.isNaN(value.getTime()))
      .sort((a, b) => a.getTime() - b.getTime());
    const periodLabel =
      assessmentDates.length === 0
        ? 'Sem período definido'
        : assessmentDates.length === 1 ||
            assessmentDates[0].toLocaleDateString('pt-BR') === assessmentDates[assessmentDates.length - 1].toLocaleDateString('pt-BR')
          ? assessmentDates[0].toLocaleDateString('pt-BR')
          : `${assessmentDates[0].toLocaleDateString('pt-BR')} a ${assessmentDates[assessmentDates.length - 1].toLocaleDateString('pt-BR')}`;
    let cursorY = margin;

    const ensureSpace = (requiredHeight: number) => {
      if (cursorY + requiredHeight <= pageHeight - margin) return;
      doc.addPage();
      cursorY = margin;
    };

    const writeText = (
      text: string,
      options?: {
        size?: number;
        color?: RGB;
        bold?: boolean;
        spacing?: number;
      },
    ) => {
      const lines = doc.splitTextToSize(text || 'Sem conteúdo registrado.', contentWidth);
      ensureSpace(lines.length * 5 + 3);
      doc.setFont('helvetica', options?.bold ? 'bold' : 'normal');
      doc.setFontSize(options?.size ?? 10);
      doc.setTextColor(...(options?.color ?? COLORS.slate700));
      doc.text(lines, margin, cursorY);
      cursorY += lines.length * 5 + (options?.spacing ?? 5);
    };

    const section = (title: string, content: string) => {
      ensureSpace(16);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(...COLORS.brandBlue);
      doc.text(title, margin, cursorY);
      cursorY += 7;
      writeText(content, { size: 10 });
    };

    const sectionList = (title: string, items: unknown) => {
      const normalizedItems = Array.isArray(items)
        ? items.map((item) => String(item || '').trim()).filter(Boolean)
        : [];
      section(
        title,
        normalizedItems.length
          ? normalizedItems.map((item, index) => `${index + 1}. ${item}`).join('\n')
          : 'Nenhum item registrado.',
      );
    };

    doc.setFillColor(...COLORS.slate900);
    doc.rect(0, 0, pageWidth, 54, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.setTextColor(...COLORS.white);
    doc.text(String(payload.title || 'Relatório de avaliações'), margin, 24, {
      maxWidth: contentWidth,
    });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...COLORS.slate200);
    doc.text(branding.displayName || 'Clínica', margin, 35, {
      maxWidth: contentWidth,
    });
    doc.text(`Paciente: ${result.patientNameSnapshot || 'Paciente'} | Data: ${new Date(result.assessmentDate).toLocaleString('pt-BR')}`, margin, 42, {
      maxWidth: contentWidth,
    });
    cursorY = 66;

    section(
      'Visão geral',
      `Período avaliado: ${periodLabel}\nDomínios cobertos: ${coveredDomains.length ? coveredDomains.join(', ') : 'Não informados'}\nAchados prioritários: ${highPriorityCount}`,
    );
    section('Resumo clínico', String(payload.clinicalSummary || ''));
    section('Interpretação integrada', String(payload.integratedInterpretation || ''));
    section('Texto do laudo', String(payload.finalText || ''));
    sectionList(
      'Achados clinicamente relevantes',
      sortedSourceResults.length > 0
        ? sortedSourceResults.map((source: Record<string, any>) => {
            const evidence = String(source.evidence || [source.primaryMetric, source.severity].filter(Boolean).join(' • ') || 'Sem evidência');
            const clinicalMeaning = String(source.clinicalMeaning || 'Sem interpretação clínica estruturada.');
            return `${getPriorityLabel(String(source.priority || ''))} | ${String(source.label || source.testKey || 'Avaliação')}: ${evidence}. ${clinicalMeaning}`;
          })
        : payload.mainFindings,
    );
    sectionList('Pontos de atenção', payload.attentionPoints);

    ensureSpace(26);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...COLORS.brandBlue);
    doc.text('Avaliações incluídas', margin, cursorY);
    cursorY += 5;
    this.runAutoTable(doc, {
      startY: cursorY,
      margin: { left: margin, right: margin },
      head: [['Avaliação', 'Domínio', 'Data', 'Resultado', 'Relevância']],
      body: sortedSourceResults.map((source: Record<string, any>) => [
        String(source.label || source.testKey || 'Avaliação'),
        String(source.domain || '-'),
        source.assessmentDate ? new Date(source.assessmentDate).toLocaleDateString('pt-BR') : '-',
        String(source.primaryMetric || '-'),
        `${String(source.severity || '-')}${source.priority ? ` | ${getPriorityLabel(String(source.priority))}` : ''}`,
      ]),
      styles: { fontSize: 8, cellPadding: 2.5, textColor: COLORS.slate700 },
      headStyles: { fillColor: COLORS.brandBlue, textColor: COLORS.white },
      alternateRowStyles: { fillColor: COLORS.slate50 },
    });
    cursorY = ((doc as unknown as { lastAutoTable?: { finalY?: number } }).lastAutoTable?.finalY ?? cursorY) + 10;

    section('Limitações', String(payload.limitations || ''));

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...COLORS.slate500);
    doc.text(
      'Relatório gerado como rascunho editável no sistema. A interpretação final deve ser validada pelo profissional responsável.',
      margin,
      pageHeight - 10,
      { maxWidth: contentWidth },
    );

    return doc;
  }

  static async generateResultPDF(input: AssessmentPDFInput): Promise<jsPDF> {
    switch (input.result.testKey) {
      case ASSESSMENT_REPORT_TEST_KEY:
        return this.generateAssessmentReportPDF(input);
      case 'COMPOSITE_REPORT':
        return this.generateCompositeReportPDF(input);
      case 'INITIAL_ASSESSMENT':
        await InitialAssessmentIntegrityService.assertSourcesCurrent(input.result.payloadJson, { clientId: input.result.clientId, patientId: input.result.patientId });
        return this.generateInitialAssessmentPDF(input);
      case 'TC6':
        return this.generateTC6PDF(input);
      case 'ISWT':
        return this.generateISWTPDF(input);
      case 'ESWT':
        return this.generateESWTPDF(input);
      case '6MST':
        return this.generate6MSTPDF(input);
      case 'CHESTER':
        return this.generateChesterPDF(input);
      case '5xSTS':
      case 'STS_30S':
      case 'STS_1MIN':
        return this.generateSTSPDF(input);
      case 'HANDGRIP':
        return this.generateHandgripPDF(input);
      case 'PERIPHERAL_MUSCLE_STRENGTH':
        return this.generatePeripheralMuscleStrengthPDF(input);
      case 'ONE_RM':
        return this.generateOneRMPDF(input);
      case 'PIMAX':
      case 'PEMAX':
      case 'S_INDEX':
      case 'RESP_ENDURANCE':
        return this.generateRespiratoryMusclePDF(input);
      case 'PEAK_FLOW':
        return this.generatePeakFlowPDF(input);
      case 'PEAK_COUGH_FLOW':
      case 'CV_POSTURAL':
      case 'SNIP':
        return this.generateNeuromuscularPDF(input);
      case 'TUG':
        return this.generateTUGPDF(input);
      case '4MGS':
        return this.generateGaitSpeedPDF(input);
      case 'AQLQ':
        return this.generateAQLQPDF(input);
      case 'ACT':
        return this.generateACTPDF(input);
      case 'SGRQ':
        return this.generateSGRQPDF(input);
      case 'SAQ':
        return this.generateSAQPDF(input);
      case 'PFSDQ-M':
        return this.generatePFSDQPDF(input);
      case 'MLHFQ':
        return this.generateMLHFQPDF(input);
      case 'LCADL':
        return this.generateLCADLPDF(input);
      case 'HADS':
        return this.generateHADSPDF(input);
      case 'PSQI':
        return this.generatePSQIPDF(input);
      case 'mMRC':
        return this.generateMMRCPDF(input);
      case 'CAT':
        return this.generateCATPDF(input);
      default:
        throw new Error(`Não existe gerador de PDF para a avaliação "${input.result.testKey}".`);
    }
  }

  private static async appendPdfBytes(target: PDFDocument, pdfBytes: Uint8Array): Promise<void> {
    const source = await PDFDocument.load(pdfBytes);
    const copiedPages = await target.copyPages(source, source.getPageIndices());
    copiedPages.forEach((page) => target.addPage(page));
  }

  private static getCompositeReportSourceIds(result: AssessmentResultV2): string[] {
    const payload = result.payloadJson && typeof result.payloadJson === 'object' ? result.payloadJson : {};
    const sourceResults = Array.isArray(payload.sourceResults) ? payload.sourceResults : [];
    const sourceIdsFromPayload = sourceResults
      .map((item) => (item && typeof item === 'object' && typeof item.resultId === 'string' ? item.resultId.trim() : ''))
      .filter(Boolean);
    const summary = result.summaryJson && typeof result.summaryJson === 'object' ? result.summaryJson : {};
    const sourceIdsFromSummary = Array.isArray(summary.sourceResultIds)
      ? summary.sourceResultIds.map((item) => String(item || '').trim()).filter(Boolean)
      : [];

    return [...new Set([...sourceIdsFromPayload, ...sourceIdsFromSummary])];
  }

  private static async resolveCompositeReportSourceResults(result: AssessmentResultV2): Promise<AssessmentResultV2[]> {
    const sourceIds = this.getCompositeReportSourceIds(result);
    if (!sourceIds.length) return [];

    const sourceResults = await AssessmentResultsV2Service.listByIds(result.clientId, sourceIds);
    const orderedResults = sourceIds
      .map((resultId) => sourceResults.find((item) => item.resultId === resultId))
      .filter((item): item is AssessmentResultV2 => !!item);

    return orderedResults.filter((item) => item.status !== 'cancelled' && item.testKey !== 'COMPOSITE_REPORT');
  }

  private static assessmentReportRevisionsMatch(
    expectedUpdatedAt: string | null,
    expectedCreatedAt: string | null,
    currentUpdatedAt?: string | null,
    currentCreatedAt?: string | null,
  ): boolean {
    const expected = expectedUpdatedAt?.trim() || expectedCreatedAt?.trim() || '';
    const current = currentUpdatedAt?.trim() || currentCreatedAt?.trim() || '';
    return Boolean(expected && current && expected === current);
  }

  private static async resolveAssessmentReportPackage(
    result: AssessmentResultV2,
  ): Promise<ResolvedAssessmentReportPackage> {
    const payload = this.getAssessmentReportPayload(result);
    const sourceIds = payload.sources.map((source) => source.resultId);
    const sourceSnapshotById = new Map(
      payload.sources.map((source) => [source.resultId, source]),
    );
    let attachmentIds: string[] = [];
    switch (payload.exportOptions.attachments.mode) {
      case 'cover_only':
        attachmentIds = [];
        break;
      case 'selected_assessments':
        attachmentIds = payload.exportOptions.attachments.resultIds;
        break;
      case 'all_source_assessments':
        attachmentIds = sourceIds;
        break;
    }

    const historySourceIds =
      payload.exportOptions.history.mode === 'selected'
        ? payload.exportOptions.history.selections.map((selection) => selection.sourceResultId)
        : [];
    const attachmentIdSet = new Set(attachmentIds);
    historySourceIds.forEach((sourceResultId) => {
      if (!attachmentIdSet.has(sourceResultId)) {
        throw new Error(
          `Não foi possível gerar o pacote: a avaliação com histórico selecionado (${sourceResultId}) precisa estar entre os anexos.`,
        );
      }
    });
    const requiredLiveSourceIds = [...new Set([...sourceIds, ...attachmentIds, ...historySourceIds])];
    const fetchedSources =
      requiredLiveSourceIds.length > 0
        ? await AssessmentResultsV2Service.listByIds(result.clientId, requiredLiveSourceIds)
        : [];
    const fetchedSourceById = new Map(fetchedSources.map((source) => [source.resultId, source]));

    const sourceResults = requiredLiveSourceIds.map((sourceResultId) => {
      const sourceSnapshot = sourceSnapshotById.get(sourceResultId);
      if (!sourceSnapshot) {
        throw new Error(
          `Não foi possível gerar o pacote: a avaliação solicitada (${sourceResultId}) não pertence às fontes deste relatório.`,
        );
      }
      const sourceLabel = sourceSnapshot.snapshot.testLabel || sourceSnapshot.resultId;
      const sourceResult = fetchedSourceById.get(sourceSnapshot.resultId);

      if (!sourceResult) {
        throw new Error(
          `Não foi possível gerar o pacote: a avaliação-fonte "${sourceLabel}" não foi encontrada para este tenant.`,
        );
      }
      if (sourceResult.clientId !== result.clientId) {
        throw new Error(
          `Não foi possível gerar o pacote: a avaliação-fonte "${sourceLabel}" pertence a outro tenant.`,
        );
      }
      if (sourceResult.patientId !== result.patientId) {
        throw new Error(
          `Não foi possível gerar o pacote: a avaliação-fonte "${sourceLabel}" pertence a outro paciente.`,
        );
      }
      if (sourceResult.status !== 'completed' || sourceSnapshot.snapshot.sourceStatus !== 'completed') {
        throw new Error(
          `Não foi possível gerar o pacote: a avaliação-fonte "${sourceLabel}" não está concluída.`,
        );
      }
      if (isAssessmentV2ReportKey(sourceResult.testKey)) {
        throw new Error(
          `Não foi possível gerar o pacote: relatórios não podem ser usados como avaliação-fonte ("${sourceLabel}").`,
        );
      }
      if (
        sourceResult.sessionId !== sourceSnapshot.sessionId
        || sourceResult.testKey !== sourceSnapshot.snapshot.testKey
        || sourceResult.testVersion !== sourceSnapshot.snapshot.testVersion
        || sourceResult.assessmentDate !== sourceSnapshot.snapshot.assessmentDate
      ) {
        throw new Error(
          `Não foi possível gerar o pacote: a identidade da avaliação-fonte "${sourceLabel}" difere do snapshot salvo.`,
        );
      }

      if (
        !this.assessmentReportRevisionsMatch(
          sourceSnapshot.snapshot.sourceUpdatedAt,
          sourceSnapshot.snapshot.sourceCreatedAt,
          sourceResult.updatedAt,
          sourceResult.createdAt,
        )
      ) {
        throw new Error(
          `Não foi possível gerar o pacote: a avaliação-fonte "${sourceLabel}" foi alterada após o snapshot. Revise e salve o relatório novamente.`,
        );
      }

      return sourceResult;
    });

    for (const source of sourceResults) {
      if (source.testKey === 'INITIAL_ASSESSMENT') {
        await InitialAssessmentIntegrityService.assertSourcesCurrent(source.payloadJson, { clientId: result.clientId, patientId: result.patientId });
      }
    }
    const sourceResultById = new Map(sourceResults.map((source) => [source.resultId, source]));
    const attachmentResults = attachmentIds.map((resultId) => {
      const attachment = sourceResultById.get(resultId);
      if (!attachment) {
        throw new Error(
          `Não foi possível gerar o pacote: o anexo selecionado (${resultId}) não corresponde a uma avaliação-fonte validada.`,
        );
      }
      if (isAssessmentV2ReportKey(attachment.testKey)) {
        throw new Error('Não foi possível gerar o pacote: um relatório não pode ser anexado como avaliação-fonte.');
      }
      return attachment;
    });

    const historyBySourceId = new Map<
      string,
      AssessmentReportHistoricalResultSnapshotV2[]
    >();
    if (payload.exportOptions.history.mode === 'selected') {
      const historicalIds = [
        ...new Set(
          payload.exportOptions.history.selections.flatMap((selection) => selection.historicalResultIds),
        ),
      ];
      const historicalResults = await AssessmentResultsV2Service.listByIds(result.clientId, historicalIds);
      const historicalResultById = new Map(historicalResults.map((item) => [item.resultId, item]));

      payload.exportOptions.history.selections.forEach((selection) => {
        const sourceResult = sourceResultById.get(selection.sourceResultId);
        if (!sourceResult) {
          throw new Error(
            `Não foi possível gerar o pacote: a origem da comparação histórica (${selection.sourceResultId}) não foi validada.`,
          );
        }
        if (historyBySourceId.has(selection.sourceResultId)) {
          throw new Error(
            `Não foi possível gerar o pacote: há mais de uma seleção histórica para a avaliação "${this.getAssessmentPDFLabel(sourceResult.testKey)}".`,
          );
        }

        const historicalSnapshotById = new Map(
          selection.historicalResultSnapshots.map((snapshot) => [
            snapshot.resultId,
            snapshot,
          ]),
        );
        const selectedHistory = selection.historicalResultIds.map((historicalResultId) => {
          const historicalSnapshot = historicalSnapshotById.get(historicalResultId);
          const historicalResult = historicalResultById.get(historicalResultId);
          if (!historicalSnapshot) {
            throw new Error(
              `Não foi possível gerar o pacote: a avaliação histórica selecionada (${historicalResultId}) não possui snapshot verificável. Revise a seleção histórica.`,
            );
          }
          if (!historicalResult) {
            throw new Error(
              `Não foi possível gerar o pacote: a avaliação histórica selecionada (${historicalResultId}) não foi encontrada para este tenant.`,
            );
          }
          if (historicalResult.clientId !== result.clientId) {
            throw new Error(
              `Não foi possível gerar o pacote: a avaliação histórica selecionada (${historicalResultId}) pertence a outro tenant.`,
            );
          }
          if (historicalResult.patientId !== result.patientId) {
            throw new Error(
              `Não foi possível gerar o pacote: uma avaliação histórica selecionada pertence a outro paciente (${historicalResultId}).`,
            );
          }
          if (historicalResult.status !== 'completed') {
            throw new Error(
              `Não foi possível gerar o pacote: a avaliação histórica selecionada não está concluída (${historicalResultId}).`,
            );
          }
          if (isAssessmentV2ReportKey(historicalResult.testKey)) {
            throw new Error(
              `Não foi possível gerar o pacote: relatórios não podem compor o histórico (${historicalResultId}).`,
            );
          }
          if (historicalResult.testKey !== sourceResult.testKey) {
            throw new Error(
              `Não foi possível gerar o pacote: a avaliação histórica selecionada não corresponde ao instrumento "${this.getAssessmentPDFLabel(sourceResult.testKey)}".`,
            );
          }
          if (
            historicalResult.sessionId !== historicalSnapshot.sessionId
            || historicalResult.testKey !== historicalSnapshot.testKey
            || historicalResult.testVersion !== historicalSnapshot.testVersion
            || historicalResult.assessmentDate !== historicalSnapshot.assessmentDate
            || historicalSnapshot.sourceStatus !== 'completed'
            || Date.parse(historicalResult.assessmentDate)
              >= Date.parse(sourceResult.assessmentDate)
          ) {
            throw new Error(
              `Não foi possível gerar o pacote: a identidade da avaliação histórica selecionada (${historicalResultId}) difere do snapshot revisado.`,
            );
          }
          if (
            !this.assessmentReportRevisionsMatch(
              historicalSnapshot.sourceUpdatedAt,
              historicalSnapshot.sourceCreatedAt,
              historicalResult.updatedAt,
              historicalResult.createdAt,
            )
          ) {
            const signed = Boolean(getAssessmentSignatureMetadata(result.assetsJson));
            throw new Error(
              signed
                ? `Não foi possível gerar o pacote: a avaliação histórica de ${this.formatDateOnly(historicalSnapshot.assessmentDate)} foi alterada após a assinatura. Gere uma nova versão do relatório.`
                : `Não foi possível gerar o pacote: a avaliação histórica de ${this.formatDateOnly(historicalSnapshot.assessmentDate)} foi alterada após a seleção. Revise e salve o relatório novamente.`,
            );
          }
          return historicalSnapshot;
        });

        historyBySourceId.set(selection.sourceResultId, selectedHistory);
      });
    }

    return {
      payload,
      sourceResults,
      attachmentResults,
      historyBySourceId,
    };
  }

  static async assertAssessmentReportPackageIsCurrent(
    result: AssessmentResultV2,
  ): Promise<void> {
    if (result.testKey !== ASSESSMENT_REPORT_TEST_KEY) return;
    await this.resolveAssessmentReportPackage(result);
  }

  private static async renderGlobalPackagePagination(pdf: PDFDocument): Promise<void> {
    const pages = pdf.getPages();
    const pageCount = pages.length;
    if (pageCount === 0) return;

    const font = await pdf.embedFont(StandardFonts.Helvetica);
    const millimetersToPoints = (value: number): number => value * (72 / 25.4);
    const patchX = millimetersToPoints(12);
    const patchY = millimetersToPoints(3);
    const patchWidth = millimetersToPoints(43);
    const patchHeight = millimetersToPoints(7);
    const textX = millimetersToPoints(15);
    const textY = millimetersToPoints(4.1);

    pages.forEach((page, index) => {
      page.drawRectangle({
        x: patchX,
        y: patchY,
        width: patchWidth,
        height: patchHeight,
        color: rgb(248 / 255, 250 / 255, 252 / 255),
      });
      page.drawText(`Página ${index + 1}/${pageCount}`, {
        x: textX,
        y: textY,
        size: 6.8,
        font,
        color: rgb(100 / 255, 116 / 255, 139 / 255),
      });
    });
  }

  private static async generateAssessmentReportHistoryPDF({
    sourceResult,
    sourceSnapshot,
    historySnapshots,
    branding,
  }: {
    sourceResult: AssessmentResultV2;
    sourceSnapshot: AssessmentReportSourceSnapshotV2;
    historySnapshots: AssessmentReportHistoricalResultSnapshotV2[];
    branding: ResolvedClientReportBranding;
  }): Promise<jsPDF> {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });
    const margin = 15;
    const pageWidth = doc.internal.pageSize.getWidth();
    const revahubMarkBase64 = await this.loadImageAsDataUrl('/logos/revahub-mark.png');
    let y = await this.renderHeader(doc, branding);
    y = this.renderPatientRibbon(doc, sourceResult, y);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(...COLORS.slate900);
    doc.text('Comparação histórica selecionada', margin, y);
    y += 7;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(...COLORS.slate600);
    const explanation = doc.splitTextToSize(
      'Esta seção contém somente os resultados históricos explicitamente selecionados pelo profissional para este pacote.',
      pageWidth - margin * 2,
    ) as string[];
    doc.text(explanation, margin, y);
    y += explanation.length * 4 + 6;

    this.runAutoTable(doc, {
      startY: y,
      margin: { left: margin, right: margin },
      head: [['Data', 'Registro', 'Resultado principal', 'Classificação', 'Profissional']],
      body: [
        [
          this.formatDateOnly(sourceSnapshot.snapshot.assessmentDate),
          'Avaliação de referência',
          this.formatAssessmentReportPrimaryMeasure(sourceSnapshot),
          getAssessmentReportFindingStatusLabel(sourceSnapshot.finding.status),
          sourceSnapshot.professional.name || sourceResult.professionalNameSnapshot || 'Não identificado',
        ],
        ...historySnapshots.map((historySnapshot) => [
          this.formatDateOnly(historySnapshot.assessmentDate),
          'Histórico selecionado',
          historySnapshot.primaryResult,
          historySnapshot.classificationLabel || 'Sem classificação registrada',
          historySnapshot.professional.name || 'Não identificado',
        ]),
      ],
      theme: 'grid',
      styles: {
        fontSize: 7.8,
        cellPadding: 2.4,
        lineColor: COLORS.slate200,
        lineWidth: 0.1,
        textColor: COLORS.slate700,
        valign: 'top',
      },
      headStyles: {
        fillColor: COLORS.brandBlue,
        textColor: COLORS.white,
        fontStyle: 'bold',
      },
      alternateRowStyles: {
        fillColor: COLORS.slate50,
      },
      columnStyles: {
        0: { cellWidth: 24 },
        1: { cellWidth: 34 },
        2: { cellWidth: 46, fontStyle: 'bold' },
        3: { cellWidth: 39 },
        4: { cellWidth: 37 },
      },
    });

    const pageCount = doc.getNumberOfPages();
    for (let page = 1; page <= pageCount; page += 1) {
      doc.setPage(page);
      this.renderFooter(doc, branding, revahubMarkBase64);
    }
    return doc;
  }

  private static async buildAssessmentReportMergedPdfBytes(input: AssessmentPDFInput): Promise<Uint8Array> {
    const resolvedPackage =
      input.assessmentReportPackage ?? await this.resolveAssessmentReportPackage(input.result);
    const resolvedInput = {
      ...input,
      assessmentReportPackage: resolvedPackage,
    };
    const mergedPdf = await PDFDocument.create();
    const coverDoc = await this.generateAssessmentReportPDF(resolvedInput);
    await this.appendPdfBytes(mergedPdf, new Uint8Array(coverDoc.output('arraybuffer')));

    for (const sourceResult of resolvedPackage.attachmentResults) {
      const sourceSignature = getAssessmentSignatureMetadata(sourceResult.assetsJson);
      let sourceVerification: AssessmentPDFInput['verification'] = null;
      if (sourceSignature?.verificationToken) {
        const sourceVerificationUrl = buildVerificationUrl(
          sourceSignature.verificationToken,
          input.tenantSlug || null,
        );
        sourceVerification = {
          token: sourceSignature.verificationToken,
          url: sourceVerificationUrl,
          qrDataUrl: await createVerificationQrDataUrl(sourceVerificationUrl),
        };
      }
      const sourceDoc = await this.generateResultPDF({
        result: sourceResult,
        branding: input.branding,
        previousResults: [],
        tenantSlug: input.tenantSlug,
        verification: sourceVerification,
      });
      await this.appendPdfBytes(mergedPdf, new Uint8Array(sourceDoc.output('arraybuffer')));

      const selectedHistory = resolvedPackage.historyBySourceId.get(sourceResult.resultId) ?? [];
      if (selectedHistory.length > 0) {
        const sourceSnapshot = resolvedPackage.payload.sources.find(
          (source) => source.resultId === sourceResult.resultId,
        );
        if (!sourceSnapshot) {
          throw new Error(
            `Não foi possível gerar o pacote: o snapshot da avaliação com histórico (${sourceResult.resultId}) não foi encontrado.`,
          );
        }
        const historyDoc = await this.generateAssessmentReportHistoryPDF({
          sourceResult,
          sourceSnapshot,
          historySnapshots: selectedHistory,
          branding: input.branding,
        });
        await this.appendPdfBytes(mergedPdf, new Uint8Array(historyDoc.output('arraybuffer')));
      }
    }

    await this.renderGlobalPackagePagination(mergedPdf);
    return mergedPdf.save();
  }

  private static async buildCompositeReportMergedPdfBytes(input: AssessmentPDFInput): Promise<Uint8Array> {
    const mergedPdf = await PDFDocument.create();
    const coverDoc = await this.generateCompositeReportPDF(input);
    await this.appendPdfBytes(mergedPdf, new Uint8Array(coverDoc.output('arraybuffer')));

    const sourceResults = await this.resolveCompositeReportSourceResults(input.result);

    for (const sourceResult of sourceResults) {
      const previousResults = await AssessmentResultsV2Service.listHistoryForPatient(
        sourceResult.clientId,
        sourceResult.patientId,
        sourceResult.testKey,
        {
          excludeResultId: sourceResult.resultId,
          limit: 6,
        },
      ).catch(() => []);

      const sourceDoc = await this.generateResultPDF({
        result: sourceResult,
        branding: input.branding,
        previousResults,
      });

      await this.appendPdfBytes(mergedPdf, new Uint8Array(sourceDoc.output('arraybuffer')));
    }

    return mergedPdf.save();
  }

  private static async openPdfBytesForPrint(pdfBytes: Uint8Array): Promise<void> {
    const blob = new Blob([pdfBytes], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const printWindow = window.open(url, '_blank');

    if (printWindow) {
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
          printWindow.onafterprint = () => {
            printWindow.close();
            URL.revokeObjectURL(url);
          };
        }, 500);
      };
      return;
    }

    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.top = '-9999px';
    iframe.style.left = '-9999px';
    iframe.style.width = '0px';
    iframe.style.height = '0px';
    document.body.appendChild(iframe);
    iframe.src = url;

    setTimeout(() => {
      iframe.contentWindow?.print();
      setTimeout(() => {
        document.body.removeChild(iframe);
        URL.revokeObjectURL(url);
      }, 1000);
    }, 500);
  }

  private static sanitizeFileNamePart(value?: string | null): string {
    return String(value || 'paciente')
      .trim()
      .replace(/\s+/g, '_');
  }

  private static buildResultPdfFileName(result: AssessmentResultV2): string {
    const patientName = this.sanitizeFileNamePart(result.patientNameSnapshot || 'paciente');
    const dateLabel = new Date().toISOString().slice(0, 10);

    switch (result.testKey) {
      case ASSESSMENT_REPORT_TEST_KEY: {
        const payload = this.getAssessmentReportPayload(result);
        const prefix =
          payload.mode === 'physiotherapy_technical_report'
            ? 'Relatorio_Tecnico_com_Diagnostico_Fisioterapeutico'
            : 'Resumo_Avaliacoes';
        return `${prefix}_${patientName}_${dateLabel}.pdf`;
      }
      case 'COMPOSITE_REPORT':
        return `Relatorio_Avaliacoes_${patientName}_${dateLabel}.pdf`;
      case 'INITIAL_ASSESSMENT': {
        const moment = normalizeInitialAssessmentPayload(result.payloadJson)?.assessmentMoment;
        const prefix = moment === 'final' ? 'Avaliacao_Final' : moment === 'reassessment' ? 'Reavaliacao' : 'Avaliacao_Inicial';
        return `${prefix}_${patientName}_${dateLabel}.pdf`;
      }
      case 'TC6':
        return `Avaliacao_TC6_${patientName}_${dateLabel}.pdf`;
      case 'ISWT':
        return `Avaliacao_ISWT_${patientName}_${dateLabel}.pdf`;
      case 'ESWT':
        return `Avaliacao_ESWT_${patientName}_${dateLabel}.pdf`;
      case '6MST':
        return `Avaliacao_6MST_${patientName}_${dateLabel}.pdf`;
      case 'CHESTER':
        return `Avaliacao_CHESTER_${patientName}_${dateLabel}.pdf`;
      case 'HANDGRIP':
        return `Avaliacao_HANDGRIP_${patientName}_${dateLabel}.pdf`;
      case 'PERIPHERAL_MUSCLE_STRENGTH':
        return `Avaliacao_FORCA_MUSCULAR_PERIFERICA_${patientName}_${dateLabel}.pdf`;
      case 'ONE_RM':
        return `Avaliacao_1RM_${patientName}_${dateLabel}.pdf`;
      case 'TUG':
        return `Avaliacao_TUG_${patientName}_${dateLabel}.pdf`;
      case 'PEAK_FLOW':
        return `Avaliacao_PEAK_FLOW_${patientName}_${dateLabel}.pdf`;
      case 'PEAK_COUGH_FLOW':
        return `Avaliacao_Pico_Fluxo_Tosse_${patientName}_${dateLabel}.pdf`;
      case 'CV_POSTURAL':
        return `Avaliacao_Capacidade_Vital_Postural_${patientName}_${dateLabel}.pdf`;
      case 'SNIP':
        return `Avaliacao_SNIP_${patientName}_${dateLabel}.pdf`;
      case '4MGS':
        return `Avaliacao_4MGS_${patientName}_${dateLabel}.pdf`;
      case 'ACT':
        return `Avaliacao_ACT_${patientName}_${dateLabel}.pdf`;
      case 'AQLQ':
        return `Avaliacao_AQLQ_${patientName}_${dateLabel}.pdf`;
      case 'mMRC':
        return `Avaliacao_mMRC_${patientName}_${dateLabel}.pdf`;
      case 'HADS':
        return `Avaliacao_HADS_${patientName}_${dateLabel}.pdf`;
      case 'PSQI':
        return `Avaliacao_PSQI_${patientName}_${dateLabel}.pdf`;
      case 'LCADL':
        return `Avaliacao_LCADL_${patientName}_${dateLabel}.pdf`;
      case 'MLHFQ':
        return `Avaliacao_MLHFQ_${patientName}_${dateLabel}.pdf`;
      case 'PFSDQ-M':
        return `Avaliacao_PFSDQ-M_${patientName}_${dateLabel}.pdf`;
      case 'SAQ':
        return `Avaliacao_SAQ_${patientName}_${dateLabel}.pdf`;
      case 'SGRQ':
        return `Avaliacao_SGRQ_${patientName}_${dateLabel}.pdf`;
      case 'CAT':
        return `Avaliacao_CAT_${patientName}_${dateLabel}.pdf`;
      default:
        return `Avaliacao_${result.testKey}_${patientName}_${dateLabel}.pdf`;
    }
  }

  private static triggerPdfDownload(pdfBytes: Uint8Array, fileName: string): void {
    const blob = new Blob([pdfBytes], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  }

  private static async buildResultPdfBytes(input: AssessmentPDFInput): Promise<Uint8Array> {
    if (input.result.testKey === ASSESSMENT_REPORT_TEST_KEY) {
      return this.buildAssessmentReportMergedPdfBytes(input);
    }
    if (input.result.testKey === 'COMPOSITE_REPORT') {
      return this.buildCompositeReportMergedPdfBytes(input);
    }

    const doc = await this.generateResultPDF(input);
    return new Uint8Array(doc.output('arraybuffer'));
  }

  private static async prepareGeneratedFileVerification(
    input: AssessmentPDFInput,
    generationChannel: VerificationGenerationChannel,
    fileName: string,
  ): Promise<{
    resolvedInput: AssessmentPDFInput;
    registrationInput: GeneratedDocumentFileVerificationInput | null;
  }> {
    const assessmentReportPackage =
      input.result.testKey === ASSESSMENT_REPORT_TEST_KEY
        ? input.assessmentReportPackage ?? await this.resolveAssessmentReportPackage(input.result)
        : input.assessmentReportPackage;
    const baseInput: AssessmentPDFInput = {
      ...input,
      assessmentReportPackage,
    };
    const signature = getAssessmentSignatureMetadata(input.result.assetsJson);
    if (!signature?.verificationToken) {
      return {
        resolvedInput: {
          ...baseInput,
          verification: input.verification ?? null,
        },
        registrationInput: null,
      };
    }

    const fileToken = generateVerificationToken('rvhf');
    const verificationUrl = buildVerificationUrl(fileToken, input.tenantSlug || null);
    const qrDataUrl = await createVerificationQrDataUrl(verificationUrl);
    const generatedAt = new Date().toISOString();
    const documentTitle = getAssessmentV2ResultLabel(input.result) || 'Avaliação';
    const sourceResults =
      input.result.testKey === ASSESSMENT_REPORT_TEST_KEY
        ? assessmentReportPackage?.sourceResults ?? []
        : input.result.testKey === 'COMPOSITE_REPORT'
        ? await this.resolveCompositeReportSourceResults(input.result)
        : [];
    const registrationInput: GeneratedDocumentFileVerificationInput = {
      clientId: input.result.clientId,
      tenantSlug: input.tenantSlug || null,
      tenantName: input.tenantName || input.branding.displayName || input.branding.legalName || null,
      documentType: 'assessment_result',
      documentId: input.result.resultId,
      documentTitle,
      patientId: input.result.patientId,
      patientName: input.result.patientNameSnapshot || null,
      patientMedicalRecordNumber: input.result.patientMedicalRecordSnapshot || null,
      professionalName: signature.signedByName || input.result.professionalNameSnapshot || null,
      professionalRole: signature.signedByRole || input.result.professionalRoleSnapshot || null,
      professionalRegistration:
        signature.signedByRegistration || input.result.professionalRegistrationSnapshot || null,
      signedAt: signature.signedAt,
      status: input.result.status === 'cancelled' ? 'invalidated' : 'valid',
      documentHash: signature.documentHash,
      templateVersion: signature.templateVersion,
      snapshot: {
        resultId: input.result.resultId,
        sessionId: input.result.sessionId,
        testKey: input.result.testKey,
        assessmentDate: input.result.assessmentDate,
        sourceDocumentTokens:
          sourceResults.length > 0
            ? sourceResults
                .map((item) => getAssessmentSignatureMetadata(item.assetsJson)?.verificationToken || null)
                .filter((item): item is string => Boolean(item))
            : [signature.verificationToken],
        sourceResults:
          sourceResults.length > 0
            ? sourceResults.map((item) => ({
                resultId: item.resultId,
                testKey: item.testKey,
                assessmentDate: item.assessmentDate,
                verificationToken: getAssessmentSignatureMetadata(item.assetsJson)?.verificationToken || null,
                documentHash: getAssessmentSignatureMetadata(item.assetsJson)?.documentHash || null,
              }))
            : undefined,
      },
      fileToken,
      sourceDocumentToken: signature.verificationToken,
      fileHash: '',
      generatedAt,
      generatedByUserId: input.generatedByUserId || null,
      generatedByName: input.generatedByName || null,
      generationChannel,
      fileName,
      fileSizeBytes: 0,
    };

    return {
      resolvedInput: {
        ...baseInput,
        verification: {
          token: fileToken,
          url: verificationUrl,
          qrDataUrl,
        },
      },
      registrationInput,
    };
  }

  private static async registerGeneratedPdfIfNeeded(
    registrationInput: GeneratedDocumentFileVerificationInput | null,
    pdfBytes: Uint8Array,
  ): Promise<void> {
    if (!registrationInput) {
      return;
    }

    await DocumentVerificationService.registerGeneratedDocumentFileVerification({
      ...registrationInput,
      fileHash: await hashBinaryContent(pdfBytes),
      fileSizeBytes: pdfBytes.byteLength,
    });
  }

  static async downloadCATPDF(input: AssessmentPDFInput): Promise<void> {
    const doc = await this.generateCATPDF(input);
    const patientName = (input.result.patientNameSnapshot || 'paciente').replace(/\s+/g, '_');
    doc.save(`Avaliacao_CAT_${patientName}_${new Date().toISOString().slice(0, 10)}.pdf`);
  }

  static async downloadTC6PDF(input: AssessmentPDFInput): Promise<void> {
    const doc = await this.generateTC6PDF(input);
    const patientName = (input.result.patientNameSnapshot || 'paciente').replace(/\s+/g, '_');
    doc.save(`Avaliacao_TC6_${patientName}_${new Date().toISOString().slice(0, 10)}.pdf`);
  }

  static async downloadISWTPDF(input: AssessmentPDFInput): Promise<void> {
    const doc = await this.generateISWTPDF(input);
    const patientName = (input.result.patientNameSnapshot || 'paciente').replace(/\s+/g, '_');
    doc.save(`Avaliacao_ISWT_${patientName}_${new Date().toISOString().slice(0, 10)}.pdf`);
  }

  static async downloadESWTPDF(input: AssessmentPDFInput): Promise<void> {
    const doc = await this.generateESWTPDF(input);
    const patientName = (input.result.patientNameSnapshot || 'paciente').replace(/\s+/g, '_');
    doc.save(`Avaliacao_ESWT_${patientName}_${new Date().toISOString().slice(0, 10)}.pdf`);
  }

  static async download6MSTPDF(input: AssessmentPDFInput): Promise<void> {
    const doc = await this.generate6MSTPDF(input);
    const patientName = (input.result.patientNameSnapshot || 'paciente').replace(/\s+/g, '_');
    doc.save(`Avaliacao_6MST_${patientName}_${new Date().toISOString().slice(0, 10)}.pdf`);
  }

  static async downloadChesterPDF(input: AssessmentPDFInput): Promise<void> {
    const doc = await this.generateChesterPDF(input);
    const patientName = (input.result.patientNameSnapshot || 'paciente').replace(/\s+/g, '_');
    doc.save(`Avaliacao_CHESTER_${patientName}_${new Date().toISOString().slice(0, 10)}.pdf`);
  }

  static async downloadSTSPDF(input: AssessmentPDFInput): Promise<void> {
    const doc = await this.generateSTSPDF(input);
    const patientName = (input.result.patientNameSnapshot || 'paciente').replace(/\s+/g, '_');
    doc.save(`Avaliacao_${input.result.testKey}_${patientName}_${new Date().toISOString().slice(0, 10)}.pdf`);
  }

  static async downloadHandgripPDF(input: AssessmentPDFInput): Promise<void> {
    const doc = await this.generateHandgripPDF(input);
    const patientName = (input.result.patientNameSnapshot || 'paciente').replace(/\s+/g, '_');
    doc.save(`Avaliacao_HANDGRIP_${patientName}_${new Date().toISOString().slice(0, 10)}.pdf`);
  }

  static async downloadPeripheralMuscleStrengthPDF(input: AssessmentPDFInput): Promise<void> {
    const doc = await this.generatePeripheralMuscleStrengthPDF(input);
    const patientName = (input.result.patientNameSnapshot || 'paciente').replace(/\s+/g, '_');
    doc.save(`Avaliacao_FORCA_MUSCULAR_PERIFERICA_${patientName}_${new Date().toISOString().slice(0, 10)}.pdf`);
  }

  static async downloadOneRMPDF(input: AssessmentPDFInput): Promise<void> {
    const doc = await this.generateOneRMPDF(input);
    const patientName = (input.result.patientNameSnapshot || 'paciente').replace(/\s+/g, '_');
    doc.save(`Avaliacao_1RM_${patientName}_${new Date().toISOString().slice(0, 10)}.pdf`);
  }

  static async downloadRespiratoryMusclePDF(input: AssessmentPDFInput): Promise<void> {
    const doc = await this.generateRespiratoryMusclePDF(input);
    const patientName = (input.result.patientNameSnapshot || 'paciente').replace(/\s+/g, '_');
    doc.save(`Avaliacao_${input.result.testKey}_${patientName}_${new Date().toISOString().slice(0, 10)}.pdf`);
  }

  static async downloadTUGPDF(input: AssessmentPDFInput): Promise<void> {
    const doc = await this.generateTUGPDF(input);
    const patientName = (input.result.patientNameSnapshot || 'paciente').replace(/\s+/g, '_');
    doc.save(`Avaliacao_TUG_${patientName}_${new Date().toISOString().slice(0, 10)}.pdf`);
  }

  static async downloadPeakFlowPDF(input: AssessmentPDFInput): Promise<void> {
    const doc = await this.generatePeakFlowPDF(input);
    const patientName = (input.result.patientNameSnapshot || 'paciente').replace(/\s+/g, '_');
    doc.save(`Avaliacao_PEAK_FLOW_${patientName}_${new Date().toISOString().slice(0, 10)}.pdf`);
  }

  static async downloadGaitSpeedPDF(input: AssessmentPDFInput): Promise<void> {
    const doc = await this.generateGaitSpeedPDF(input);
    const patientName = (input.result.patientNameSnapshot || 'paciente').replace(/\s+/g, '_');
    doc.save(`Avaliacao_4MGS_${patientName}_${new Date().toISOString().slice(0, 10)}.pdf`);
  }

  static async downloadACTPDF(input: AssessmentPDFInput): Promise<void> {
    const doc = await this.generateACTPDF(input);
    const patientName = (input.result.patientNameSnapshot || 'paciente').replace(/\s+/g, '_');
    doc.save(`Avaliacao_ACT_${patientName}_${new Date().toISOString().slice(0, 10)}.pdf`);
  }

  static async downloadAQLQPDF(input: AssessmentPDFInput): Promise<void> {
    const doc = await this.generateAQLQPDF(input);
    const patientName = (input.result.patientNameSnapshot || 'paciente').replace(/\s+/g, '_');
    doc.save(`Avaliacao_AQLQ_${patientName}_${new Date().toISOString().slice(0, 10)}.pdf`);
  }

  static async downloadMMRCPDF(input: AssessmentPDFInput): Promise<void> {
    const doc = await this.generateMMRCPDF(input);
    const patientName = (input.result.patientNameSnapshot || 'paciente').replace(/\s+/g, '_');
    doc.save(`Avaliacao_mMRC_${patientName}_${new Date().toISOString().slice(0, 10)}.pdf`);
  }

  static async downloadHADSPDF(input: AssessmentPDFInput): Promise<void> {
    const doc = await this.generateHADSPDF(input);
    const patientName = (input.result.patientNameSnapshot || 'paciente').replace(/\s+/g, '_');
    doc.save(`Avaliacao_HADS_${patientName}_${new Date().toISOString().slice(0, 10)}.pdf`);
  }

  static async downloadPSQIPDF(input: AssessmentPDFInput): Promise<void> {
    const doc = await this.generatePSQIPDF(input);
    const patientName = (input.result.patientNameSnapshot || 'paciente').replace(/\s+/g, '_');
    doc.save(`Avaliacao_PSQI_${patientName}_${new Date().toISOString().slice(0, 10)}.pdf`);
  }

  static async downloadLCADLPDF(input: AssessmentPDFInput): Promise<void> {
    const doc = await this.generateLCADLPDF(input);
    const patientName = (input.result.patientNameSnapshot || 'paciente').replace(/\s+/g, '_');
    doc.save(`Avaliacao_LCADL_${patientName}_${new Date().toISOString().slice(0, 10)}.pdf`);
  }

  static async downloadMLHFQPDF(input: AssessmentPDFInput): Promise<void> {
    const doc = await this.generateMLHFQPDF(input);
    const patientName = (input.result.patientNameSnapshot || 'paciente').replace(/\s+/g, '_');
    doc.save(`Avaliacao_MLHFQ_${patientName}_${new Date().toISOString().slice(0, 10)}.pdf`);
  }

  static async downloadPFSDQPDF(input: AssessmentPDFInput): Promise<void> {
    const doc = await this.generatePFSDQPDF(input);
    const patientName = (input.result.patientNameSnapshot || 'paciente').replace(/\s+/g, '_');
    doc.save(`Avaliacao_PFSDQ-M_${patientName}_${new Date().toISOString().slice(0, 10)}.pdf`);
  }

  static async downloadSAQPDF(input: AssessmentPDFInput): Promise<void> {
    const doc = await this.generateSAQPDF(input);
    const patientName = (input.result.patientNameSnapshot || 'paciente').replace(/\s+/g, '_');
    doc.save(`Avaliacao_SAQ_${patientName}_${new Date().toISOString().slice(0, 10)}.pdf`);
  }

  static async downloadSGRQPDF(input: AssessmentPDFInput): Promise<void> {
    const doc = await this.generateSGRQPDF(input);
    const patientName = (input.result.patientNameSnapshot || 'paciente').replace(/\s+/g, '_');
    doc.save(`Avaliacao_SGRQ_${patientName}_${new Date().toISOString().slice(0, 10)}.pdf`);
  }

  static async downloadCompositeReportPDF(input: AssessmentPDFInput): Promise<void> {
    const pdfBytes = await this.buildCompositeReportMergedPdfBytes(input);
    const patientName = (input.result.patientNameSnapshot || 'paciente').replace(/\s+/g, '_');
    const blob = new Blob([pdfBytes], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `Relatorio_Avaliacoes_${patientName}_${new Date().toISOString().slice(0, 10)}.pdf`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  }

  static async downloadResultPDF(input: AssessmentPDFInput): Promise<void> {
    const fileName = this.buildResultPdfFileName(input.result);
    const { resolvedInput, registrationInput } = await this.prepareGeneratedFileVerification(
      input,
      'download',
      fileName,
    );
    const pdfBytes = await this.buildResultPdfBytes(resolvedInput);
    await this.registerGeneratedPdfIfNeeded(registrationInput, pdfBytes);
    this.triggerPdfDownload(pdfBytes, fileName);
  }

  static async printCATPDF(input: AssessmentPDFInput): Promise<void> {
    const doc = await this.generateCATPDF(input);
    const blob = doc.output('blob');
    const url = URL.createObjectURL(blob);

    const printWindow = window.open(url, '_blank');

    if (printWindow) {
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
          printWindow.onafterprint = () => {
            printWindow.close();
            URL.revokeObjectURL(url);
          };
        }, 500);
      };
      return;
    }

    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.top = '-9999px';
    iframe.style.left = '-9999px';
    iframe.style.width = '0px';
    iframe.style.height = '0px';
    document.body.appendChild(iframe);
    iframe.src = url;

    setTimeout(() => {
      iframe.contentWindow?.print();
      setTimeout(() => {
        document.body.removeChild(iframe);
        URL.revokeObjectURL(url);
      }, 1000);
    }, 500);
  }

  static async printTC6PDF(input: AssessmentPDFInput): Promise<void> {
    const doc = await this.generateTC6PDF(input);
    const blob = doc.output('blob');
    const url = URL.createObjectURL(blob);

    const printWindow = window.open(url, '_blank');

    if (printWindow) {
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
          printWindow.onafterprint = () => {
            printWindow.close();
            URL.revokeObjectURL(url);
          };
        }, 500);
      };
      return;
    }

    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.top = '-9999px';
    iframe.style.left = '-9999px';
    iframe.style.width = '0px';
    iframe.style.height = '0px';
    document.body.appendChild(iframe);
    iframe.src = url;

    setTimeout(() => {
      iframe.contentWindow?.print();
      setTimeout(() => {
        document.body.removeChild(iframe);
        URL.revokeObjectURL(url);
      }, 1000);
    }, 500);
  }

  static async printISWTPDF(input: AssessmentPDFInput): Promise<void> {
    const doc = await this.generateISWTPDF(input);
    const blob = doc.output('blob');
    const url = URL.createObjectURL(blob);

    const printWindow = window.open(url, '_blank');

    if (printWindow) {
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
          printWindow.onafterprint = () => {
            printWindow.close();
            URL.revokeObjectURL(url);
          };
        }, 500);
      };
      return;
    }

    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.top = '-9999px';
    iframe.style.left = '-9999px';
    iframe.style.width = '0px';
    iframe.style.height = '0px';
    document.body.appendChild(iframe);
    iframe.src = url;

    setTimeout(() => {
      iframe.contentWindow?.print();
      setTimeout(() => {
        document.body.removeChild(iframe);
        URL.revokeObjectURL(url);
      }, 1000);
    }, 500);
  }

  static async printESWTPDF(input: AssessmentPDFInput): Promise<void> {
    const doc = await this.generateESWTPDF(input);
    const blob = doc.output('blob');
    const url = URL.createObjectURL(blob);

    const printWindow = window.open(url, '_blank');

    if (printWindow) {
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
          printWindow.onafterprint = () => {
            printWindow.close();
            URL.revokeObjectURL(url);
          };
        }, 500);
      };
      return;
    }

    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.top = '-9999px';
    iframe.style.left = '-9999px';
    iframe.style.width = '0px';
    iframe.style.height = '0px';
    document.body.appendChild(iframe);
    iframe.src = url;

    setTimeout(() => {
      iframe.contentWindow?.print();
      setTimeout(() => {
        document.body.removeChild(iframe);
        URL.revokeObjectURL(url);
      }, 1000);
    }, 500);
  }

  static async print6MSTPDF(input: AssessmentPDFInput): Promise<void> {
    const doc = await this.generate6MSTPDF(input);
    const blob = doc.output('blob');
    const url = URL.createObjectURL(blob);

    const printWindow = window.open(url, '_blank');

    if (printWindow) {
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
          printWindow.onafterprint = () => {
            printWindow.close();
            URL.revokeObjectURL(url);
          };
        }, 500);
      };
      return;
    }

    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.top = '-9999px';
    iframe.style.left = '-9999px';
    iframe.style.width = '0px';
    iframe.style.height = '0px';
    document.body.appendChild(iframe);
    iframe.src = url;

    setTimeout(() => {
      iframe.contentWindow?.print();
      setTimeout(() => {
        document.body.removeChild(iframe);
        URL.revokeObjectURL(url);
      }, 1000);
    }, 500);
  }

  static async printChesterPDF(input: AssessmentPDFInput): Promise<void> {
    const doc = await this.generateChesterPDF(input);
    const blob = doc.output('blob');
    const url = URL.createObjectURL(blob);

    const printWindow = window.open(url, '_blank');

    if (printWindow) {
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
          printWindow.onafterprint = () => {
            printWindow.close();
            URL.revokeObjectURL(url);
          };
        }, 500);
      };
      return;
    }

    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.top = '-9999px';
    iframe.style.left = '-9999px';
    iframe.style.width = '0px';
    iframe.style.height = '0px';
    document.body.appendChild(iframe);
    iframe.src = url;

    setTimeout(() => {
      iframe.contentWindow?.print();
      setTimeout(() => {
        document.body.removeChild(iframe);
        URL.revokeObjectURL(url);
      }, 1000);
    }, 500);
  }

  static async printSTSPDF(input: AssessmentPDFInput): Promise<void> {
    const doc = await this.generateSTSPDF(input);
    const blob = doc.output('blob');
    const url = URL.createObjectURL(blob);

    const printWindow = window.open(url, '_blank');

    if (printWindow) {
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
          printWindow.onafterprint = () => {
            printWindow.close();
            URL.revokeObjectURL(url);
          };
        }, 500);
      };
      return;
    }

    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.top = '-9999px';
    iframe.style.left = '-9999px';
    iframe.style.width = '0px';
    iframe.style.height = '0px';
    document.body.appendChild(iframe);
    iframe.src = url;

    setTimeout(() => {
      iframe.contentWindow?.print();
      setTimeout(() => {
        document.body.removeChild(iframe);
        URL.revokeObjectURL(url);
      }, 1000);
    }, 500);
  }

  static async printHandgripPDF(input: AssessmentPDFInput): Promise<void> {
    const doc = await this.generateHandgripPDF(input);
    const blob = doc.output('blob');
    const url = URL.createObjectURL(blob);

    const printWindow = window.open(url, '_blank');

    if (printWindow) {
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
          printWindow.onafterprint = () => {
            printWindow.close();
            URL.revokeObjectURL(url);
          };
        }, 500);
      };
      return;
    }

    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.top = '-9999px';
    iframe.style.left = '-9999px';
    iframe.style.width = '0px';
    iframe.style.height = '0px';
    document.body.appendChild(iframe);
    iframe.src = url;

    setTimeout(() => {
      iframe.contentWindow?.print();
      setTimeout(() => {
        document.body.removeChild(iframe);
        URL.revokeObjectURL(url);
      }, 1000);
    }, 500);
  }

  static async printPeripheralMuscleStrengthPDF(input: AssessmentPDFInput): Promise<void> {
    const doc = await this.generatePeripheralMuscleStrengthPDF(input);
    const blob = doc.output('blob');
    const url = URL.createObjectURL(blob);

    const printWindow = window.open(url, '_blank');

    if (printWindow) {
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
          printWindow.onafterprint = () => {
            printWindow.close();
            URL.revokeObjectURL(url);
          };
        }, 500);
      };
      return;
    }

    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.top = '-9999px';
    iframe.style.left = '-9999px';
    iframe.style.width = '0px';
    iframe.style.height = '0px';
    document.body.appendChild(iframe);
    iframe.src = url;

    setTimeout(() => {
      iframe.contentWindow?.print();
      setTimeout(() => {
        document.body.removeChild(iframe);
        URL.revokeObjectURL(url);
      }, 1000);
    }, 500);
  }

  static async printOneRMPDF(input: AssessmentPDFInput): Promise<void> {
    const doc = await this.generateOneRMPDF(input);
    const blob = doc.output('blob');
    const url = URL.createObjectURL(blob);

    const printWindow = window.open(url, '_blank');

    if (printWindow) {
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
          printWindow.onafterprint = () => {
            printWindow.close();
            URL.revokeObjectURL(url);
          };
        }, 500);
      };
      return;
    }

    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.top = '-9999px';
    iframe.style.left = '-9999px';
    iframe.style.width = '0px';
    iframe.style.height = '0px';
    document.body.appendChild(iframe);
    iframe.src = url;

    setTimeout(() => {
      iframe.contentWindow?.print();
      setTimeout(() => {
        document.body.removeChild(iframe);
        URL.revokeObjectURL(url);
      }, 1000);
    }, 500);
  }

  static async printRespiratoryMusclePDF(input: AssessmentPDFInput): Promise<void> {
    const doc = await this.generateRespiratoryMusclePDF(input);
    const blob = doc.output('blob');
    const url = URL.createObjectURL(blob);

    const printWindow = window.open(url, '_blank');

    if (printWindow) {
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
          printWindow.onafterprint = () => {
            printWindow.close();
            URL.revokeObjectURL(url);
          };
        }, 500);
      };
      return;
    }

    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.top = '-9999px';
    iframe.style.left = '-9999px';
    iframe.style.width = '0px';
    iframe.style.height = '0px';
    document.body.appendChild(iframe);
    iframe.src = url;

    setTimeout(() => {
      iframe.contentWindow?.print();
      setTimeout(() => {
        document.body.removeChild(iframe);
        URL.revokeObjectURL(url);
      }, 1000);
    }, 500);
  }

  static async printTUGPDF(input: AssessmentPDFInput): Promise<void> {
    const doc = await this.generateTUGPDF(input);
    const blob = doc.output('blob');
    const url = URL.createObjectURL(blob);

    const printWindow = window.open(url, '_blank');

    if (printWindow) {
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
          printWindow.onafterprint = () => {
            printWindow.close();
            URL.revokeObjectURL(url);
          };
        }, 500);
      };
      return;
    }

    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.top = '-9999px';
    iframe.style.left = '-9999px';
    iframe.style.width = '0px';
    iframe.style.height = '0px';
    document.body.appendChild(iframe);
    iframe.src = url;

    setTimeout(() => {
      iframe.contentWindow?.print();
      setTimeout(() => {
        document.body.removeChild(iframe);
        URL.revokeObjectURL(url);
      }, 1000);
    }, 500);
  }

  static async printGaitSpeedPDF(input: AssessmentPDFInput): Promise<void> {
    const doc = await this.generateGaitSpeedPDF(input);
    const blob = doc.output('blob');
    const url = URL.createObjectURL(blob);

    const printWindow = window.open(url, '_blank');

    if (printWindow) {
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
          printWindow.onafterprint = () => {
            printWindow.close();
            URL.revokeObjectURL(url);
          };
        }, 500);
      };
      return;
    }

    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.top = '-9999px';
    iframe.style.left = '-9999px';
    iframe.style.width = '0px';
    iframe.style.height = '0px';
    document.body.appendChild(iframe);
    iframe.src = url;

    setTimeout(() => {
      iframe.contentWindow?.print();
      setTimeout(() => {
        document.body.removeChild(iframe);
        URL.revokeObjectURL(url);
      }, 1000);
    }, 500);
  }

  static async printPeakFlowPDF(input: AssessmentPDFInput): Promise<void> {
    const doc = await this.generatePeakFlowPDF(input);
    const blob = doc.output('blob');
    const url = URL.createObjectURL(blob);

    const printWindow = window.open(url, '_blank');

    if (printWindow) {
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
          printWindow.onafterprint = () => {
            printWindow.close();
            URL.revokeObjectURL(url);
          };
        }, 500);
      };
      return;
    }

    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.top = '-9999px';
    iframe.style.left = '-9999px';
    iframe.style.width = '0px';
    iframe.style.height = '0px';
    document.body.appendChild(iframe);
    iframe.src = url;

    setTimeout(() => {
      iframe.contentWindow?.print();
      setTimeout(() => {
        document.body.removeChild(iframe);
        URL.revokeObjectURL(url);
      }, 1000);
    }, 500);
  }

  static async printACTPDF(input: AssessmentPDFInput): Promise<void> {
    const doc = await this.generateACTPDF(input);
    const blob = doc.output('blob');
    const url = URL.createObjectURL(blob);

    const printWindow = window.open(url, '_blank');

    if (printWindow) {
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
          printWindow.onafterprint = () => {
            printWindow.close();
            URL.revokeObjectURL(url);
          };
        }, 500);
      };
      return;
    }

    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.top = '-9999px';
    iframe.style.left = '-9999px';
    iframe.style.width = '0px';
    iframe.style.height = '0px';
    document.body.appendChild(iframe);
    iframe.src = url;

    setTimeout(() => {
      iframe.contentWindow?.print();
      setTimeout(() => {
        document.body.removeChild(iframe);
        URL.revokeObjectURL(url);
      }, 1000);
    }, 500);
  }

  static async printAQLQPDF(input: AssessmentPDFInput): Promise<void> {
    const doc = await this.generateAQLQPDF(input);
    const blob = doc.output('blob');
    const url = URL.createObjectURL(blob);

    const printWindow = window.open(url, '_blank');

    if (printWindow) {
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
          printWindow.onafterprint = () => {
            printWindow.close();
            URL.revokeObjectURL(url);
          };
        }, 500);
      };
      return;
    }

    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.top = '-9999px';
    iframe.style.left = '-9999px';
    iframe.style.width = '0px';
    iframe.style.height = '0px';
    document.body.appendChild(iframe);
    iframe.src = url;

    setTimeout(() => {
      iframe.contentWindow?.print();
      setTimeout(() => {
        document.body.removeChild(iframe);
        URL.revokeObjectURL(url);
      }, 1000);
    }, 500);
  }

  static async printMMRCPDF(input: AssessmentPDFInput): Promise<void> {
    const doc = await this.generateMMRCPDF(input);
    const blob = doc.output('blob');
    const url = URL.createObjectURL(blob);

    const printWindow = window.open(url, '_blank');

    if (printWindow) {
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
          printWindow.onafterprint = () => {
            printWindow.close();
            URL.revokeObjectURL(url);
          };
        }, 500);
      };
      return;
    }

    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.top = '-9999px';
    iframe.style.left = '-9999px';
    iframe.style.width = '0px';
    iframe.style.height = '0px';
    document.body.appendChild(iframe);
    iframe.src = url;

    setTimeout(() => {
      iframe.contentWindow?.print();
      setTimeout(() => {
        document.body.removeChild(iframe);
        URL.revokeObjectURL(url);
      }, 1000);
    }, 500);
  }

  static async printHADSPDF(input: AssessmentPDFInput): Promise<void> {
    const doc = await this.generateHADSPDF(input);
    const blob = doc.output('blob');
    const url = URL.createObjectURL(blob);

    const printWindow = window.open(url, '_blank');

    if (printWindow) {
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
          printWindow.onafterprint = () => {
            printWindow.close();
            URL.revokeObjectURL(url);
          };
        }, 500);
      };
      return;
    }

    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.top = '-9999px';
    iframe.style.left = '-9999px';
    iframe.style.width = '0px';
    iframe.style.height = '0px';
    document.body.appendChild(iframe);
    iframe.src = url;

    setTimeout(() => {
      iframe.contentWindow?.print();
      setTimeout(() => {
        document.body.removeChild(iframe);
        URL.revokeObjectURL(url);
      }, 1000);
    }, 500);
  }

  static async printPSQIPDF(input: AssessmentPDFInput): Promise<void> {
    const doc = await this.generatePSQIPDF(input);
    const blob = doc.output('blob');
    const url = URL.createObjectURL(blob);

    const printWindow = window.open(url, '_blank');

    if (printWindow) {
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
          printWindow.onafterprint = () => {
            printWindow.close();
            URL.revokeObjectURL(url);
          };
        }, 500);
      };
      return;
    }

    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.top = '-9999px';
    iframe.style.left = '-9999px';
    iframe.style.width = '0px';
    iframe.style.height = '0px';
    document.body.appendChild(iframe);
    iframe.src = url;

    setTimeout(() => {
      iframe.contentWindow?.print();
      setTimeout(() => {
        document.body.removeChild(iframe);
        URL.revokeObjectURL(url);
      }, 1000);
    }, 500);
  }

  static async printLCADLPDF(input: AssessmentPDFInput): Promise<void> {
    const doc = await this.generateLCADLPDF(input);
    const blob = doc.output('blob');
    const url = URL.createObjectURL(blob);

    const printWindow = window.open(url, '_blank');

    if (printWindow) {
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
          printWindow.onafterprint = () => {
            printWindow.close();
            URL.revokeObjectURL(url);
          };
        }, 500);
      };
      return;
    }

    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.top = '-9999px';
    iframe.style.left = '-9999px';
    iframe.style.width = '0px';
    iframe.style.height = '0px';
    document.body.appendChild(iframe);
    iframe.src = url;

    setTimeout(() => {
      iframe.contentWindow?.print();
      setTimeout(() => {
        document.body.removeChild(iframe);
        URL.revokeObjectURL(url);
      }, 1000);
    }, 500);
  }

  static async printMLHFQPDF(input: AssessmentPDFInput): Promise<void> {
    const doc = await this.generateMLHFQPDF(input);
    const blob = doc.output('blob');
    const url = URL.createObjectURL(blob);

    const printWindow = window.open(url, '_blank');

    if (printWindow) {
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
          printWindow.onafterprint = () => {
            printWindow.close();
            URL.revokeObjectURL(url);
          };
        }, 500);
      };
      return;
    }

    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.top = '-9999px';
    iframe.style.left = '-9999px';
    iframe.style.width = '0px';
    iframe.style.height = '0px';
    document.body.appendChild(iframe);
    iframe.src = url;

    setTimeout(() => {
      iframe.contentWindow?.print();
      setTimeout(() => {
        document.body.removeChild(iframe);
        URL.revokeObjectURL(url);
      }, 1000);
    }, 500);
  }

  static async printPFSDQPDF(input: AssessmentPDFInput): Promise<void> {
    const doc = await this.generatePFSDQPDF(input);
    const blob = doc.output('blob');
    const url = URL.createObjectURL(blob);

    const printWindow = window.open(url, '_blank');

    if (printWindow) {
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
          printWindow.onafterprint = () => {
            printWindow.close();
            URL.revokeObjectURL(url);
          };
        }, 500);
      };
      return;
    }

    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.top = '-9999px';
    iframe.style.left = '-9999px';
    iframe.style.width = '0px';
    iframe.style.height = '0px';
    document.body.appendChild(iframe);
    iframe.src = url;

    setTimeout(() => {
      iframe.contentWindow?.print();
      setTimeout(() => {
        document.body.removeChild(iframe);
        URL.revokeObjectURL(url);
      }, 1000);
    }, 500);
  }

  static async printSAQPDF(input: AssessmentPDFInput): Promise<void> {
    const doc = await this.generateSAQPDF(input);
    const blob = doc.output('blob');
    const url = URL.createObjectURL(blob);

    const printWindow = window.open(url, '_blank');

    if (printWindow) {
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
          printWindow.onafterprint = () => {
            printWindow.close();
            URL.revokeObjectURL(url);
          };
        }, 500);
      };
      return;
    }

    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.top = '-9999px';
    iframe.style.left = '-9999px';
    iframe.style.width = '0px';
    iframe.style.height = '0px';
    document.body.appendChild(iframe);
    iframe.src = url;

    setTimeout(() => {
      iframe.contentWindow?.print();
      setTimeout(() => {
        document.body.removeChild(iframe);
        URL.revokeObjectURL(url);
      }, 1000);
    }, 500);
  }

  static async printSGRQPDF(input: AssessmentPDFInput): Promise<void> {
    const doc = await this.generateSGRQPDF(input);
    const blob = doc.output('blob');
    const url = URL.createObjectURL(blob);

    const printWindow = window.open(url, '_blank');

    if (printWindow) {
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
          printWindow.onafterprint = () => {
            printWindow.close();
            URL.revokeObjectURL(url);
          };
        }, 500);
      };
      return;
    }

    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.top = '-9999px';
    iframe.style.left = '-9999px';
    iframe.style.width = '0px';
    iframe.style.height = '0px';
    document.body.appendChild(iframe);
    iframe.src = url;

    setTimeout(() => {
      iframe.contentWindow?.print();
      setTimeout(() => {
        document.body.removeChild(iframe);
        URL.revokeObjectURL(url);
      }, 1000);
    }, 500);
  }

  static async printResultPDF(input: AssessmentPDFInput): Promise<void> {
    const fileName = this.buildResultPdfFileName(input.result);
    const { resolvedInput, registrationInput } = await this.prepareGeneratedFileVerification(
      input,
      'print',
      fileName,
    );
    const pdfBytes = await this.buildResultPdfBytes(resolvedInput);
    await this.registerGeneratedPdfIfNeeded(registrationInput, pdfBytes);
    await this.openPdfBytesForPrint(pdfBytes);
  }
}
